import { Document } from './document.js'
import { Canvas } from './canvas.js'
import { Input } from './input.js'
import { LocalVolumeBootBlocked, Storage } from './storage.js'
import type { LocalConsolidationActivity } from './storage.js'
import { Commands } from './commands.js'
import type { IndexPaletteRow, IndexPaletteState, IndexPaletteStatus } from './commands.js'
import { History } from './history.js'
import { snapToGrid, snapXToColumn, snapXToColumnBiased, snapToPaperWidth, snapYToEven, snapYToEvenNearest } from './grid.js'
import { GRID_SIZE, VIEW_FIT_MARGIN_UNITS, VIEW_START_MARGIN_DESKTOP_UNITS, VIEW_START_MARGIN_TOUCH_UNITS, DESKTOP_MAX_ZOOM, MIN_ZOOM, LINE_HEIGHT, HANDLE_HIT_RADIUS_PX, TOUCH_MAX_ZOOM, DESKTOP_WHEEL_ZOOM_LINEAR_STEP, TOUCH_ZOOM_SCRUB_LINEAR_STEP, CONNECTION_HIT_TOLERANCE_PX, TOUCH_TAP_MAX_DURATION_MS, TOUCH_TAP_MOVE_TOLERANCE_PX, AREA_SELECT_LONG_PRESS_MS, AREA_SELECT_LONG_PRESS_MOVE_TOLERANCE_PX, AREA_SELECT_SHIFT_DRAG_THRESHOLD_PX, DRAG_INTENT_THRESHOLD_PX, TEXT_PADDING_X_UNITS, TEXT_PADDING_TOP_PX, CSS_BASELINE_ADJUST_PX, FONT_SIZE, TEXT_BASELINE_SHIFT_PX, DEFAULT_PAPER_TYPE_ID, SYNC_DEBOUNCE_MS, SYNC_SHELF_HYDRATION_QUIET_GAP_MS, SYNC_SHELF_HYDRATION_RETRY_BASE_MS, SYNC_SHELF_HYDRATION_RETRY_MAX_MS, META_SPACES_ID } from './constants.js'
import { isLiveBlock, isDeletionReceipt } from './lib/spaceCodec.js'
import type { Block, SpaceEntry, Camera, ConnectionSelection, PaperSpec } from './types.js'
import type { PaperTypeId } from './paper.js'
import { findPaperTypeIdByLabel, getDefaultPaperType, getPaperType } from './paper.js'
import { createLilodataBlock, extractLilodataBlock, parseLilodataText, updateLilodataText } from './lilodata.js'
import { generateId, seedClock } from './id.js'
import { authEnabled, clearPersistedBrowserAuthState, createSessionDataClient, supabase } from './auth.js'
import { createDeferredSerialQueue, createLatestWinsSerialQueue } from './boot.js'
import { createCloseIcon } from './ui/closeIcon.js'
import { hasCleanSeedMetadata, hasMeaningfulLocalDraft, isCleanSeedSpace, mergeBlocks, mergeSpaceSnapshots, serializeBlocks, parseBlocksFromJson } from './lib/sync.js'
import type { SyncStatus } from './lib/sync.js'
import { collectHits, stepHitIndex, type SearchHit } from './lib/searchEngine.js'
import {
  SyncEngine,
  type SyncEngineState,
  type SyncShelfSaveInput,
  type SyncShelfSaveOutcome,
  type SyncShelfSaveResult,
  type SyncSpacePayloadLoadOutcome
} from './lib/syncEngine.js'
import { hasSyncAccessFromPlan, resolveAccountAccessState, type AccountAccessState } from './lib/billing.js'
import {
  createAccountMachineRuntimeState,
  hasAcceptedSession,
  hasMountedAccount,
  hasVerifiedCloudAuthority,
  isIntentAllowed,
  parseAccountExitSignal,
  reduceAccountMachineEvent,
  resolveAccountMachineState,
  resolveSessionAcceptance,
  shouldUnmountForAccountExitSignal,
  type AccountExitSignal,
  type AccountIntent,
  type AccountMachineEvent,
  type AccountMachineRuntimeState,
  selectSyncControlFeedback,
  selectSyncSurfaceActionDescription,
  selectIsTerminationScheduled,
  selectAccountSyncCommandAvailability,
  type AccountSnapshot,
  type SessionAcceptanceIngress
} from './lib/accountStateMachine.js'
import {
  cleanupSyncSpaceFiles,
  deleteSyncSpace as deleteSyncSpaceDirect,
  getSyncOwnerId,
  isSyncSpaceSlugConflictError,
  listSyncSpaces as listSyncSpacesDirect,
  loadSyncSpaceJson,
  saveSyncSpaceSnapshot,
  type SyncSpaceMeta
} from './lib/syncSpaces.js'
import { normalizeHandle, validateSpaceSlugHandle, validateUsernameHandle } from './lib/handle.js'
import { exceedsDragIntent } from './lib/pointerIntentPolicy.js'
import type { Capabilities } from './lib/capabilities.js'
import { resolveConnectivity, type SyncReachability } from './lib/connectivity.js'
import { collectConnectedBlockIds, type BlockGraphDirection } from './lib/blockGraph.js'
import type { SpaceKind } from './lib/contextModel.js'
import { resolveLaneState, type LaneState } from './lib/laneMachine.js'
import { canCloseModalWithEscape, selectModalFacts, type AuthModalMode, type ModalFacts } from './lib/modalAdapter.js'
import {
  EMPTY_MODAL_FLOW_STATE,
  authLoginStepToModalMode,
  isAuthLoginModalMode,
  isSubscriptionControlModalMode,
  isSyncActivationModalMode,
  isVerifiedProvisioningExitModalMode,
  reduceModalFlowEvent,
  subscriptionControlStepToModalMode,
  syncActivationStepToModalMode,
  type AuthLoginStep,
  type ModalFlowEvent,
  type ModalFlowState,
  type SyncActivationStep
} from './lib/modalFlow.js'
import {
  authLoginModalModeToDescriptorStep,
  selectAccountDeleteModalDescriptor,
  selectAuthLoginModalDescriptor,
  selectLocalProtectionModalDescriptor,
  selectMachineConsentModalDescriptor,
  selectSyncExitUnsafeModalDescriptor,
  selectSyncActivationModalDescriptor,
  selectSubscriptionControlModalDescriptor,
  selectUsernameModalDescriptor,
  syncActivationModalModeToDescriptorStep,
  subscriptionControlModalModeToStep,
  type LocalProtectionStep,
  type ModalDescriptorAction,
  type ModalStepDescriptor
} from './lib/modalDescriptor.js'
import {
  captureMachineConsentRequest,
  keepMachineConsentRequest,
  releaseMachineConsentRequest,
  selectMachineConsentNext,
  selectMachineConsentStep,
  type MachineConsentRequest,
  type MachineConsentStep
} from './lib/machineConsent.js'
import {
  applyCloudPosturePaletteActionOverlay,
  selectIndexPaletteActionAvailability,
  selectPaletteActionDescription
} from './lib/paletteActions.js'
import { resolveAccountSyncBentoPresentation, resolveOrbitalAccountActivity, resolveRuntimePolicy, selectFailedAuthPresentation, type AccountShelfReadiness, type OrbitalVisualState, type RuntimePolicy, type RuntimeSyncAction } from './lib/runtimePolicy.js'
import { resolveSyncReprobe, type SyncReprobeSource } from './lib/syncReprobePolicy.js'
import { normalizeEditorCommitText } from './lib/editorText.js'
import {
  selectShelfExportSource,
  selectActiveMemoryShelfRowBySlug,
  projectSyncSpaceMeta,
  selectLandingSpace,
  selectLiveShelfRows,
  selectMemoryOpenFailureMessage,
  formatMemoryRowDescription,
  shouldHydrateMissingShelfPayload,
  shouldCollapsePaletteAfterIndexNavigation,
  resolveAnonymousLoginGateBeforeAccountEntry as runAnonymousLoginGateBeforeAccountEntry,
  resolveDeactivatedLoginLocalMemoryGate as runDeactivatedLoginLocalMemoryGate,
  spaceIdToDefaultSlug,
  isDefaultSpaceSlug
} from './lib/indexNavigation.js'
import { resolveLayoutNormalizationPolicy } from './lib/layoutNormalizationPolicy.js'
import { resolveOpenFileGuard, type OpenFileGuardMode } from './lib/openFileGuard.js'
import { resolveClusteredSoftDeoverlapPositions, resolveStackSpacingPositions } from './lib/deoverlap.js'
import { createSpaceGeometrySnapshot, type BlockGeometry, type TextLayoutOracle } from './lib/spatialEngine.js'
import { deriveSpaceIded } from './lib/spaceIdentity.js'
import { decodeSpacePayload } from './lib/spaceCodec.js'
import { instantiateSeedSpace, serializeSeedComparableBlocks, serializeSeedComparableSpace } from './lib/seedSpace.js'
import { incrementUsageOdometer, measureUtf8Bytes } from './lib/usageOdometer.js'
import { readThemeMode, resolveThemeMode, setThemeMode, type ThemeMode } from './lib/themePreference.js'
import { resolveStartupAccountWorkbenchPlan } from './lib/startupAccountWorkbench.js'
import { deriveLocalSpaceAvailability } from './lib/databaseMemory.js'
import type {
  AccountVolumeMountMode,
  AccountUnmountAuthority,
  AccountUnmountMode,
  AccountUnmountResult,
  LocalAccountProfile,
  LocalAccountPassport,
  LocalMemoryIndexRow,
  LocalShelfSpace,
  LocalSpaceAvailability,
  LocalSpaceMeta,
  LocalVolumeAuthority,
  RemoteSpaceMetaInput,
  SeedSpaceProof,
  ShelfOwnerLane,
  UnsafeOwnerShelfRow
} from './lib/databaseMemory.js'
import type { AuthChangeEvent, Session } from '@supabase/supabase-js'
import type { CommandDescriptor, EngineContext, StepSelectResult } from './commandengine.js'

const SUPPORT_EMAIL = 'hello@lilowrite.com'
const POST_CHECKOUT_MAX_ATTEMPTS = 40
const CHECKOUT_ACTIVATING_MIN_MS = 800
const PROFILE_COLD_START_RETRY_DELAYS_MS = [200, 500, 1200] as const
const PROFILE_UNRESOLVED_RETRY_DELAYS_MS = [2000, 5000, 15000] as const
// Compatibility runtime wake-up hint. Durable sync truth lives in spaces_meta.dirty.
const PENDING_SYNC_KEY_PREFIX = 'lilo.pending-sync.'
const IDENTITY_REHOME_KEY_PREFIX = 'lilo.identity-rehome.'
const OTP_AWAITING_CODE_KEY = 'lilo.auth.otp-awaiting-code'
const OTP_COOLDOWN_UNTIL_KEY = 'lilo.auth.otp-cooldown-until'
const OTP_FLOW_STARTED_AT_KEY = 'lilo.auth.otp-flow-started-at'
const VERIFIED_PROVISIONING_USER_KEY = 'lilo.auth.verified-provisioning-user'
const OTP_FLOW_TTL_MS = 15 * 60 * 1000
const TURNSTILE_SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim()
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const SYNC_REALTIME_NUDGE_DEBOUNCE_MS = 250
const PROFILE_REALTIME_REFRESH_DEBOUNCE_MS = 250
const SYNC_REACHABILITY_REPROBE_COOLDOWN_MS = 4000
const DETACHED_RESUME_INTENT_KEY = 'lilo.detached-resume-intent'
const ACCOUNT_EXIT_TAB_ID_KEY = 'lilo.account-exit.tab-id'
const ACCOUNT_EXIT_CHANNEL_NAME = 'lilo.account-exit'
const ACCOUNT_EXIT_STORAGE_KEY = 'lilo.account-exit.signal'
const ACCOUNT_DELETE_CONFIRM_KEYWORD = 'DELETE'
const SYNC_SURFACE_COMMAND_ACTIONS = new Set([
  'account-activate-sync',
  'user-rename',
  'account-cancel-sync',
  'account-delete-all!',
  'index-export-all',
  'space-export-file'
])
const AUTH_MODAL_HIDE_INPUT_MODES = new Set<string>([
  'checking-human',
  'checking-human-failed',
  'checkout-start',
  'sync-subscription-threshold',
  'checkout-activating',
  'checkout-landing',
  'checkout-timeout',
  'sync-cancel-subscription',
  'sync-cancelled',
  'sync-reactivate-from-deactivated',
  'sync-reactivate-subscription',
  'sync-reactivated',
  'local-found',
  'anonymous-login-gate',
  'deactivated-local-found',
  'protect-local-space-open-file',
  'protect-current-space-open-file',
  'account-delete-export',
  'sync-exit-unsafe',
  'local-memory-drive-recovery'
] as const)
const AUTH_MODAL_HIDE_CLOSE_MODES = new Set<string>([
  'username',
  'local-found',
  'local-memory-drive-recovery'
] as const)
const AUTH_MODAL_NO_AUTOFOCUS_MODES = new Set<string>([
  'checking-human',
  'checking-human-failed',
  'checkout-start',
  'sync-subscription-threshold',
  'checkout-activating',
  'checkout-landing',
  'sync-cancel-subscription',
  'sync-cancelled',
  'sync-reactivate-from-deactivated',
  'sync-reactivate-subscription',
  'sync-reactivated',
  'local-found',
  'anonymous-login-gate',
  'deactivated-local-found',
  'protect-local-space-open-file',
  'protect-current-space-open-file',
  'account-delete-export',
  'sync-exit-unsafe',
  'local-memory-drive-recovery',
  'machine-consent',
  'machine-consent-expired'
] as const)
const SYNC_BENTO_INLINE_ACTIONS = [
  'user-login',
  'user-logout',
  'sync-reactivate',
  'sync-export-shelf'
] as const
type SyncBentoInlineAction = (typeof SYNC_BENTO_INLINE_ACTIONS)[number]

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed', platform?: string }>
}

type ClipboardBlock = {
  text: string
  xyzw: [number, number, number, number]
  link: string[]
  ided: [string, string]
}

type ClipboardPayload = ClipboardBlock[]

type OtpSendResult = {
  ok: boolean
  throttled?: boolean
  cooldownSeconds?: number
  error?: 'human_check_required' | 'human_check_failed' | 'human_check_unavailable' | 'otp_failed'
}

type PendingOtpSendIntent = {
  email: string
  source: 'initial' | 'resend'
}

type InitSyncOptions = {
  startupAccountWorkbenchResolution?: boolean
}

type ActiveMemoryShelfOpenResult =
  | { ok: true, meta: LocalSpaceMeta }
  | { ok: false, reason: 'missing' | 'missing-payload' | 'invalid-payload' }

type BrowserStorageLike = {
  length: number
  key(index: number): string | null
  removeItem(key: string): void
}

type CheckingHumanFailureReason = 'failed' | 'unavailable' | 'expired' | 'timeout' | 'otp'

type AuthProfileRefreshTrigger =
  | 'otp-verify'
  | 'auth-state-change'
  | 'startup'
  | 'navigation'
  | 'stripe-return'
  | 'profile-realtime'
  | 'profile-unresolved-retry'
  | 'sync-lifecycle-action'

type AuthProfileRefreshOptions = {
  preferredSession?: Session | null
  preferAccountAirlock?: boolean
  settleSession?: boolean
  acceptanceSource?: SessionAcceptanceIngress
  commitAccountEntry?: boolean
}

type PendingAuthRefresh = {
  trigger: AuthProfileRefreshTrigger
  options: AuthProfileRefreshOptions
}

type ProfileStatus = {
  username?: string
  accountState: AccountAccessState
  isSync: boolean
  stripeCancelAtPeriodEnd: boolean
  stripeCurrentPeriodEnd?: string
  missing?: boolean
  error?: boolean
}

type StripeCheckoutReturnResult = {
  ok: boolean
  loginEmail?: string
}

type CloudIdentity = {
  session: Session | null
  username: string | null
  accountState: AccountAccessState
  isSyncPlan: boolean
  syncSpaceId: string | null
  isSyncPaired: boolean
}

type AuthObserverNotice = {
  event: AuthChangeEvent
  session: Session | null
}

type SessionAcceptanceGatewayResult = {
  accepted: boolean
  mounted: boolean
  passport?: LocalAccountPassport
  freshMountLandingSpaceId?: string
}

type AccountEntryMountPlan = {
  mode: AccountVolumeMountMode
  successStatus?: string
}

const CLOUD_IDENTITY_NULL: CloudIdentity = {
  session: null,
  username: null,
  accountState: 'none',
  isSyncPlan: false,
  syncSpaceId: null,
  isSyncPaired: false
}

export class App {
  private document: Document
  private canvas: Canvas
  private input: Input
  private storage: Storage
  private commands: Commands
  private history: History
  private canvasElement: HTMLCanvasElement
  private appRoot?: HTMLDivElement
  private animationFrame?: number
  private suppressNextClick = false
  private lastCanvasCssSize?: { w: number, h: number }
  // Drag state
  private dragArmed = false
  private isDragging = false
  private dragBlockIds: string[] = []
  private dragStartWorld?: { x: number, y: number }
  private dragStartScreen?: { x: number, y: number }
  private dragCurrentWorld?: { x: number, y: number }
  private dragPointerId?: number
  private dragOrigUnitsMap = new Map<string, { x: number, y: number }>()
  private pendingCreateHistory?: { blockId: string, snapshot: SpaceEntry[] }
  private pendingEditSnapshot?: { blockId: string, snapshot: SpaceEntry[] }
  private pendingEphemeralCreate?: { blockId: string, snapshot: SpaceEntry[], promoted?: boolean }
  private lastCreateWidthUnits: number = getDefaultPaperType().quarter.w
  private paperTypeId: PaperTypeId = DEFAULT_PAPER_TYPE_ID as PaperTypeId
  private isSyncPaired = false
  private syncEngine: SyncEngine
  private syncRealtimeChannel?: { unsubscribe: () => Promise<'ok' | 'timed out' | 'error'> }
  private syncRealtimeChannelKey?: string
  private syncRealtimeNudgeTimer?: number
  private syncRealtimeCatchupInFlight = false
  private syncRealtimeCatchupQueued = false
  private syncRealtimePendingSpaceIds = new Set<string>()
  private profileRealtimeChannel?: { unsubscribe: () => Promise<'ok' | 'timed out' | 'error'> }
  private profileRealtimeUserId?: string
  private profileRealtimeRefreshTimer?: number
  private accountExitTabId = ''
  private accountExitChannel?: BroadcastChannel
  private localSyncEchoSuppress = new Map<string, { syncAt: string, expiresAt: number }>()
  private syncEngineStatus: SyncStatus = 'idle'
  private syncReachability: SyncReachability = 'unknown'
  private localMemorySettling = false
  private localMemorySettlingKey = 0
  private accountCloudDebt = false
  private accountCloudActivityDepth = 0
  private cloudSyncAfterLocalConsolidationPending = false
  private cloudDebtRefreshSeq = 0
  private syncReprobeInFlight = false
  private syncReprobeLastAt = 0
  private devPolicyWarningsShown = new Set<string>()
  private syncSpaceId?: string
  private syncSpaceSlug?: string
  private activeSpaceId?: string
  private indexSpaces = new Map<string, {
    slug: string
    availability: LocalSpaceAvailability
    sizeBytes?: number
    spaceEd?: string
  }>()
  private indexPaletteReadableState?: boolean
  private localBootReady = false
  private appState: 'edit' | 'view' = 'edit'
  private accountIdentityInFlight = false
  // Resize state (End+ handle)
  private resizeArmed = false
  private isResizing = false
  private resizeBlockId?: string
  private resizeOrig?: { xUnits: number, yUnits: number, wUnits: number }
  private resizeCurrentWorld?: { x: number, y: number }
  private resizePointerId?: number
  private resizeHoverTargetId?: string
  private outgoingConnectionIds = new Set<string>()
  private incomingConnectionIds = new Set<string>()
  private importInput?: HTMLInputElement
  private pendingFileLoadMode: 'open' | 'import' = 'open'
  // Camera state
  private camera: Camera = { scale: 1, tx: 0, ty: 0 }
  private scaleMin = 0.1
  private scaleMax = 4
  private fitScales = {
    quarter: 1,
    half: 1,
    nominal: 1,
    fourNominal: 0.1,
  }
  private spaceLabel = 'local'
  private authSession?: Session | null
  private authUsername?: string
  private authAccountState: AccountAccessState = 'none'
  private authIsSync = false
  private cloudIdentity: CloudIdentity = CLOUD_IDENTITY_NULL
  private authStripeCancelAtPeriodEnd = false
  private authStripeCurrentPeriodEnd?: string
  private authModalBackdrop?: HTMLDivElement
  private authModalPanel?: HTMLDivElement
  private authModalTitle?: HTMLHeadingElement
  private authModalCopy?: HTMLParagraphElement
  private authModalCustom?: HTMLDivElement
  private authModalInput?: HTMLInputElement
  private authModalPrimary?: HTMLButtonElement
  private authModalSecondary?: HTMLButtonElement
  private authModalPrimaryAction?: ModalDescriptorAction
  private authModalSecondaryAction?: ModalDescriptorAction
  private authModalInlineOtpResend?: HTMLButtonElement
  private authModalFeedback?: HTMLDivElement
  private authModalClose?: HTMLButtonElement
  private authModalMode?: AuthModalMode
  private authCheckoutEmail?: string
  private authModalMenuIntro?: HTMLParagraphElement
  private authModalMenuWrap?: HTMLDivElement
  private authModalMenuNote?: HTMLParagraphElement
  private authModalFooterNote?: HTMLParagraphElement
  private authModalActions?: HTMLDivElement
  private authModalWait?: HTMLDivElement
  private authModalKeydownHandler?: (event: KeyboardEvent) => void
  private postCheckoutTimer?: number
  private postCheckoutAttempts = 0
  private checkoutActivatingShownAt?: number
  private checkoutCancelPending = false
  private otpResendCooldownUntil?: number
  private otpResendTicker?: number
  private otpAwaitingCode = false
  private turnstileWidgetId?: string
  private turnstileScriptPromise?: Promise<void>
  private turnstileVerificationInFlight = false
  private pendingOtpSendIntent?: PendingOtpSendIntent
  private checkingHumanFailureReason: CheckingHumanFailureReason = 'failed'
  private localFoundResolver?: (choice: 'sync' | 'delete') => void
  private anonymousLoginGateResolver?: (choice: 'import' | 'delete' | 'cancel') => void
  private anonymousLoginGatePrompt?: Promise<'import' | 'delete' | 'cancel'>
  private anonymousLoginGateLabels: string[] = []
  private syncExitUnsafeLabels: string[] = []
  private pendingSyncExitAuthority?: AccountUnmountAuthority
  private deactivatedLocalFoundResolver?: (choice: 'export-and-continue' | 'delete-and-continue' | 'cancel') => void
  private deactivatedLocalFoundPrompt?: Promise<'export-and-continue' | 'delete-and-continue' | 'cancel'>
  private deactivatedLocalFoundLabels: string[] = []
  private verifiedProvisioningUserId?: string
  private recoveredVerifiedProvisioningUserId?: string
  private openFileGuardResolver?: (confirmed: boolean) => void
  private pendingIndexRefresh = false
  private pendingRoute?: { username: string, slug: string }
  // The machine editor's sign-in request, waiting for you to allow it.
  private machineConsent?: MachineConsentRequest
  private machineConsentAsk?: Extract<MachineConsentStep, { kind: 'ask' }>
  private machineConsentInFlight = false
  private initSyncInFlight?: Promise<void>
  private syncOwnerMirrorInFlight = false
  private syncOwnerMirrorQueued = false
  private syncOwnerMirrorTimer?: number
  private syncOwnerMirrorTimerDueAt?: number
  private syncOwnerMirrorEpoch = 0
  private syncForegroundPointerIds = new Set<number>()
  private syncForegroundShelfDepth = 0
  private accountShelfReadiness: AccountShelfReadiness = 'unknown'
  private userRenameDescriptor?: CommandDescriptor
  private spaceRenameDescriptor?: CommandDescriptor
  private connectDescriptor?: CommandDescriptor
  private disconnectDescriptor?: CommandDescriptor
  private mergeTextDescriptor?: CommandDescriptor
  private selectDirectionAboveDescriptor?: CommandDescriptor
  private selectDirectionBelowDescriptor?: CommandDescriptor
  private copyDescriptor?: CommandDescriptor
  private pasteDescriptor?: CommandDescriptor
  private selectConnectedDescriptor?: CommandDescriptor
  private searchSession?: { query: string, hits: SearchHit[], index: number, activeText?: string }
  private deleteDescriptor?: CommandDescriptor
  private selectDescriptor?: CommandDescriptor
  private authRefreshReady = false
  private pendingAuthRefresh?: PendingAuthRefresh
  private authObserverInstalled = false
  private authObserverQueue = createDeferredSerialQueue<AuthObserverNotice>(
    notice => this.handleAuthObserverNotice(notice),
    error => console.warn('[auth] observer delivery failed', error)
  )
  private passiveSessionLossInFlight?: Promise<void>
  private isInitialSync = false
  private initialAuthBootstrapSettled = false
  private initialAuthBootstrapResolver?: () => void
  private unresolvedProfileRetryTimer?: number
  private unresolvedProfileRetryAttempt = 0
  private accountAirlockInFlight = false
  private autoSyncLocalFallbackOnEdit = false
  private localFallbackPromotionInFlight = false
  private logoutInProgress = false
  private accountDeleteExportReady = false
  private accountDeleteInFlight = false
  private syncTerminateInFlight = false
  private modalFlow: ModalFlowState = EMPTY_MODAL_FLOW_STATE
  private pendingAppUpdate?: { apply: () => Promise<void>, dismissed: boolean, shown: boolean }
  private remoteLifecycleStarted = false
  private authProfileRefreshQueue = createLatestWinsSerialQueue<PendingAuthRefresh>(
    pending => this.runAuthProfileRefresh(pending.trigger, pending.options).then(() => {
      void this.offerMachineConsent()
    }),
    error => this.handleAuthProfileRefreshQueueError(error)
  )
  private accountMachine: AccountMachineRuntimeState = createAccountMachineRuntimeState({
    mountedAccountOwnerId: null,
    acceptedSessionOwnerId: null,
    hasVerifiedProfileAuthority: false,
    hasUsername: false,
    isSyncPaired: false,
    isIndexSpace: true,
    isViewMode: false,
    accountState: 'none',
    hasSyncPlan: false,
    stripeCancelAtPeriodEnd: false
  })

  private computeStartOffsets(): { xPx: number, yPx: number } {
    const startOffsetUnitsX = this.isTouchDevice ? VIEW_START_MARGIN_TOUCH_UNITS : VIEW_START_MARGIN_DESKTOP_UNITS
    const startOffsetUnitsY = this.isTouchDevice ? VIEW_START_MARGIN_TOUCH_UNITS : VIEW_START_MARGIN_DESKTOP_UNITS
    return {
      xPx: startOffsetUnitsX * GRID_SIZE,
      yPx: startOffsetUnitsY * GRID_SIZE
    }
  }

  private applyPaperMeta(metaBlock: Block | undefined, blocks: SpaceEntry[], opts?: { persist?: boolean }): boolean {
    const persist = opts?.persist !== false
    const defaultId = DEFAULT_PAPER_TYPE_ID as PaperTypeId
    let paperTypeId = defaultId
    let nextMetaBlock = metaBlock
    let shouldPersist = false
    if (metaBlock) {
      const { paperLabel } = parseLilodataText(metaBlock.text)
      const resolved = paperLabel ? findPaperTypeIdByLabel(paperLabel) : undefined
      paperTypeId = resolved ?? defaultId
      if (!paperLabel || !resolved) {
        const label = getPaperType(paperTypeId).label
        nextMetaBlock = { ...metaBlock, text: updateLilodataText(metaBlock.text, { paperLabel: label }) }
        shouldPersist = true
      }
    } else {
      const label = getPaperType(defaultId).label
      const derived = deriveSpaceIded(blocks)
      nextMetaBlock = createLilodataBlock(label, {
        createdId: derived.createdId,
        editedId: derived.editedId
      })
      shouldPersist = true
    }
    if (persist && nextMetaBlock) {
      this.storage.setMetaBlock(nextMetaBlock)
    }
    this.setPaperType(paperTypeId, { refreshView: false })
    return shouldPersist
  }

  private getPaperSpec(): PaperSpec {
    return getPaperType(this.paperTypeId)
  }

  private getColumnWidthUnits(): number {
    return this.getPaperSpec().quarter.w
  }

  private setupAuthUI(): void {
    this.createAuthModal()
    const cachedCheckoutEmail = (localStorage.getItem('lilo.auth.checkout-email') ?? '').trim()
    if (cachedCheckoutEmail) {
      this.authCheckoutEmail = cachedCheckoutEmail
    }
    const recoveredVerifiedProvisioningUserId = (localStorage.getItem(VERIFIED_PROVISIONING_USER_KEY) ?? '').trim()
    this.recoveredVerifiedProvisioningUserId = recoveredVerifiedProvisioningUserId || undefined
    this.restoreOtpFlowState()
    this.commands.setAccountBar({
      userLabel: this.buildUserLabel(),
      spaceLabel: this.buildSpaceLabel(),
      userTone: this.getUserContextTone(),
      spaceTone: this.getSpaceContextTone(),
      onUserClick: (action) => this.handleUserChipAction(action),
      onSpaceClick: undefined,
      isUserActionEnabled: (action) => !(this.isIndexSpace() && action.trim().toLowerCase() === 'info'),
      isSpaceActionEnabled: (action) => !(this.isIndexSpace() && action.trim().toLowerCase() === 'info')
    })
    this.commands.setSyncIndicatorOnClick(() => {
      this.openSyncSurfaceDeterministic()
    })
    this.commands.setIndexIndicatorOnClick(() => {
      this.openIndexSurfaceDeterministic()
    })
    this.setSyncEngineState('idle', 0)
    this.applySyncUiState()
    this.syncAccountMachineFromSnapshot()
  }

  private setupAuthObserver(): void {
    if (this.authObserverInstalled || !authEnabled || !supabase) return
    this.authObserverInstalled = true
    supabase.auth.onAuthStateChange((event, session) => {
      this.authObserverQueue.enqueue({ event, session })
    })
  }

  private async handleAuthObserverNotice(notice: AuthObserverNotice): Promise<void> {
    if (this.logoutInProgress) return
    if (notice.event === 'INITIAL_SESSION') {
      this.markInitialAuthBootstrapSettled()
    }
    if (notice.event === 'SIGNED_OUT') {
      await this.handlePassiveSessionLoss()
      return
    }

    const session = notice.session
    if (session && this.recoveredVerifiedProvisioningUserId === session.user.id) {
      const recovery = await this.runSessionAcceptanceGateway({
        source: 'recovery',
        session
      })
      if (!recovery.accepted) return
      this.recoveredVerifiedProvisioningUserId = undefined
      if (!this.storage.getMountedPassport()) {
        await this.abandonVerifiedProvisioningSession()
        return
      }
      this.clearVerifiedProvisioningLatch()
    }

    const profileTrigger: AuthProfileRefreshTrigger = notice.event === 'INITIAL_SESSION'
      ? 'startup'
      : notice.event === 'SIGNED_IN'
        ? 'auth-state-change'
        : 'profile-realtime'
    const acceptanceSource: SessionAcceptanceIngress = notice.event === 'INITIAL_SESSION'
      ? 'startup'
      : 'observer'
    const options: AuthProfileRefreshOptions = {
      preferredSession: session,
      acceptanceSource,
      commitAccountEntry: true
    }
    if (!this.authRefreshReady) {
      this.pendingAuthRefresh = { trigger: profileTrigger, options }
      return
    }
    await this.refreshAuthProfile(profileTrigger, options)
  }

  private buildUserLabel(): string {
    const identity = this.getCloudIdentitySnapshot()
    const passport = this.storage.getMountedPassport()
    const localUsername = passport?.kind === 'verified'
      ? passport.profile.username
      : null
    const username = identity.username ?? localUsername
    if (username) {
      return `user : ${username} | logout`
    }
    if (passport || identity.session) {
      return 'user : account | logout'
    }
    return 'user : anonymous | login'
  }

  private buildSpaceLabel(): string {
    return `space : ${this.spaceLabel}`
  }

  private updateAccountBar(): void {
    this.commands.setAccountBar({
      userLabel: this.buildUserLabel(),
      spaceLabel: this.buildSpaceLabel(),
      userTone: this.getUserContextTone(),
      spaceTone: this.getSpaceContextTone(),
      onUserClick: (action) => this.handleUserChipAction(action),
      onSpaceClick: undefined
    })
    this.updateSyncBentoState()
  }

  private handlePaletteSurfaceChange(): void {
    if (!this.commands.isIndexSurfaceActive()) {
      this.indexPaletteReadableState = undefined
    }
    this.updateAccountBar()
  }

  private isMountedDeactivatedVolume(): boolean {
    const passport = this.storage.getMountedPassport()
    return Boolean(
      passport?.kind === 'verified'
      && passport.profile.account_state === 'deactivated'
    )
  }

  private isBootMemoryLanding(
    landing = this.storage.getBootLanding()
  ): boolean {
    return landing.kind === 'memory'
      || (landing.kind === 'proven-empty' && this.isMountedDeactivatedVolume())
  }

  private revealBootProvisionalSheet(): void {
    if (
      !this.isIndexSpace()
      || !this.isBootMemoryLanding()
      || this.storage.getActiveDeskState() !== 'provisional'
    ) return
    const meta = this.storage.getMetaBlock()
    if (!meta) return
    this.setActiveSpace(meta.ided[0])
    this.setAppState(this.isMountedDeactivatedVolume() ? 'view' : 'edit')
    this.requestRender()
  }

  private openIndexSurfaceDeterministic(): void {
    this.primeIndexPaletteHydrationState()
    this.indexPaletteReadableState = this.canAttemptIndexRead()
    this.requestSyncReachabilityReprobe('surface')
    this.commands.openIndexSurface()
    void this.refreshIndexPaletteRows()
  }

  private openSyncSurfaceDeterministic(): void {
    this.requestSyncReachabilityReprobe('surface')
    this.commands.openSyncSurface()
  }

  private getContextTone(): 'neutral' | 'sync' | 'offline' {
    if (!this.storage.getMountedPassport()) return 'neutral'
    if (this.isFailedAuthPresentation()) return 'sync'
    const visualState = this.resolveOrbitalVisualState()
    if (visualState.account === 'offline' || visualState.account === 'deactivated') return 'offline'
    return 'sync'
  }

  private getUserContextTone(): 'neutral' | 'sync' | 'offline' {
    if (!this.buildAccountSnapshot().hasUsername && !this.storage.getMountedPassport()) return 'neutral'
    return this.getContextTone()
  }

  private getSpaceContextTone(): 'neutral' | 'sync' | 'offline' {
    if (!this.storage.getMountedPassport()) return 'neutral'
    return this.getContextTone()
  }

  private buildIndexSyncStatusLabel(): string {
    const snapshot = this.buildAccountSnapshot()
    const runtimePolicy = resolveRuntimePolicy({
      snapshot,
      connectivity: this.resolveConnectivityState(),
      lane: this.resolveCurrentLaneState(),
      space: 'authored'
    })
    const isPaused = snapshot.accountState === 'deactivated'
    const syncOperational = runtimePolicy.syncOperationalState
    if (isPaused) return 'sync : deactivated'
    if (syncOperational === 'offline' || syncOperational === 'unbound') return 'sync : offline'
    if (syncOperational === 'local_only') return 'sync : off'
    return 'sync : connected'
  }

  private getIndexSyncTone(): 'neutral' | 'sync' | 'offline' {
    const label = this.buildIndexSyncStatusLabel()
    if (label.endsWith('offline') || label.endsWith('deactivated')) return 'offline'
    if (label.endsWith('connected')) return 'sync'
    return 'neutral'
  }

  private buildIndexPaletteState(input: {
    status: IndexPaletteStatus
    title: string
    description: string
    rows: IndexPaletteRow[]
    actions: string[]
    resolveActionAvailability?: IndexPaletteState['resolveActionAvailability']
    onAction?: IndexPaletteState['onAction']
    onSelectRow?: (rowId: string) => void | Promise<void>
    onOpenRow?: (rowId: string) => void | Promise<void>
  }): IndexPaletteState {
    return input
  }

  private canAttemptIndexRead(): boolean {
    return this.canEnterIndex()
  }

  private resolveIndexPaletteActions(): {
    actions: string[]
    resolveActionAvailability: NonNullable<IndexPaletteState['resolveActionAvailability']>
    onAction: NonNullable<IndexPaletteState['onAction']>
  } {
    const snapshot = this.buildAccountSnapshot()
    const runtimePolicy = resolveRuntimePolicy({
      snapshot,
      connectivity: this.resolveConnectivityState(),
      lane: this.resolveCurrentLaneState(),
      space: this.resolveSpaceKind()
    })
    const availability = applyCloudPosturePaletteActionOverlay(
      selectIndexPaletteActionAvailability(snapshot),
      runtimePolicy.cloudPosture
    )
    const actionIds = availability.map(entry => entry.action)
    const actionStates = new Map<string, RuntimeSyncAction['state']>(availability.map(entry => [entry.action, entry.state]))
    const isOfflineUnverified = runtimePolicy.cloudPosture === 'offline-unverified'
    return {
      actions: actionIds,
      resolveActionAvailability: (action) => {
        const state = actionStates.get(action)
        return {
          state: state === 'armed' || state === 'gated' ? state : 'disabled',
          gatedReason: isOfflineUnverified && action === 'space-rename'
            ? 'Connection is offline right now. Rename stays cloud-resolved until Account sync is reachable again.'
            : selectPaletteActionDescription(snapshot, action)
        }
      },
      onAction: (action) => {
        if (action === 'export sync shelf') {
          void this.handleDeactivatedExportCloudShelf()
          return
        }
        this.commands.executeCommand(action)
        if (action === 'space-delete-!') {
          this.commands.ensurePaletteVisible()
        }
      }
    }
  }

  private syncRuntimePendingHintKey(spaceId: string): string {
    return `${PENDING_SYNC_KEY_PREFIX}${spaceId}`
  }

  private identityRehomeKey(spaceId: string): string {
    return `${IDENTITY_REHOME_KEY_PREFIX}${spaceId}`
  }

  private hasIdentityRehomeAttempt(spaceId: string): boolean {
    return localStorage.getItem(this.identityRehomeKey(spaceId)) === '1'
  }

  private markIdentityRehomeAttempt(spaceId: string): void {
    localStorage.setItem(this.identityRehomeKey(spaceId), '1')
  }

  private setSyncRuntimePendingHint(spaceId: string, pending: boolean): void {
    const key = this.syncRuntimePendingHintKey(spaceId)
    if (pending) {
      localStorage.setItem(key, '1')
      return
    }
    localStorage.removeItem(key)
  }

  private hasSyncRuntimePendingHint(spaceId: string): boolean {
    return localStorage.getItem(this.syncRuntimePendingHintKey(spaceId)) === '1'
  }

  private setSyncRuntimePendingHintForActiveSpace(pending: boolean): void {
    const spaceId = this.syncSpaceId ?? this.activeSpaceId
    if (!spaceId) return
    this.setSyncRuntimePendingHint(spaceId, pending)
  }

  private hasPendingSyncForActiveSpace(): boolean {
    const spaceId = this.syncSpaceId ?? this.activeSpaceId
    if (!spaceId) return false
    return this.hasSyncRuntimePendingHint(spaceId)
  }

  private resolveOrbitalVisualState(): OrbitalVisualState {
    const runtimePolicy = resolveRuntimePolicy({
      snapshot: this.buildAccountSnapshot(),
      connectivity: this.resolveConnectivityState(),
      lane: this.resolveCurrentLaneState(),
      space: this.resolveSpaceKind(),
      syncEngineStatus: this.syncEngineStatus
    })
    return {
      ...runtimePolicy.orbitalVisualState,
      memoryActivity: this.localMemorySettling ? 'settling' : undefined,
      memoryActivityKey: this.localMemorySettling ? this.localMemorySettlingKey : undefined,
      accountActivity: this.resolveAccountCloudActivity(runtimePolicy.orbitalVisualState)
    }
  }

  private resolveAccountCloudActivity(orbitalState: OrbitalVisualState): OrbitalVisualState['accountActivity'] {
    return resolveOrbitalAccountActivity({
      account: orbitalState.account,
      localMemorySettling: this.localMemorySettling,
      accountCloudActivityActive: this.accountCloudActivityDepth > 0,
      accountCloudDebt: this.accountCloudDebt,
      cloudSyncAfterLocalConsolidationPending: this.cloudSyncAfterLocalConsolidationPending,
      syncEngineStatus: this.syncEngineStatus,
      accountShelfReadiness: this.accountShelfReadiness
    })
  }

  private updateSyncBentoState(): void {
    const snapshot = this.buildAccountSnapshot()
    const connectivity = this.resolveConnectivityState()
    const runtimePolicy = resolveRuntimePolicy({
      snapshot,
      connectivity,
      lane: this.resolveCurrentLaneState(),
      space: this.resolveSpaceKind()
    })
    const failedAuth = this.isFailedAuthPresentation(snapshot, connectivity)
    const scheduledEndLabel = this.formatPeriodEndDate(snapshot.stripeCurrentPeriodEnd)
    const syncPresentation = resolveAccountSyncBentoPresentation({
      lifecycle: runtimePolicy.lifecycle,
      scheduledEndLabel,
      cloudPosture: runtimePolicy.cloudPosture,
      failedAuth
    })
    let syncActionAvailability = runtimePolicy.syncActions
    if (snapshot.accountState === 'deactivated' && this.commands.isSyncSurfaceActive() && this.resolveSpaceKind() !== 'detached') {
      const infoActionAllowlist = new Set<string>(['reactivate sync', 'account-delete-all!'])
      syncActionAvailability = syncActionAvailability.filter(entry => infoActionAllowlist.has(entry.action))
    }
    const syncActions = syncActionAvailability.map(entry => entry.action)
    const pickerSyncActions = syncActions.map(action => this.toSyncPickerActionId(action))
    const syncActionState = new Map<string, string>(syncActionAvailability.map(entry => [
      this.toSyncPickerActionId(entry.action),
      entry.state
    ]))
    if (this.resolveSpaceKind() === 'detached') {
      const hasAnyEnabled = syncActionAvailability.some(entry => entry.state === 'armed' || entry.state === 'gated')
      if (!hasAnyEnabled && !syncActions.length) {
        console.warn('[sync-surface] index action list resolved empty')
      }
    }
    this.assertSelectorParity(runtimePolicy, syncActionAvailability)
    this.commands.setSyncBentoState({
      userLabel: this.buildUserLabel(),
      userTone: this.getUserContextTone(),
      syncLabel: syncPresentation.syncLabel,
      failedAuth,
      networkLabel: syncPresentation.networkLabel,
      syncDetail: syncPresentation.syncDetail,
      feedback: { message: syncPresentation.feedback },
      description: syncPresentation.description,
      syncPickerPlaceholder: runtimePolicy.syncPickerPlaceholder,
      cloudActions: pickerSyncActions,
      resolveCloudActionAvailability: (action) => {
        const sourceAction = syncActions.find(candidate => this.toSyncPickerActionId(candidate) === action)
        if (!sourceAction) return { state: 'disabled' as const }
        const state = syncActionState.get(action)
        return {
          state: state === 'gated' || state === 'armed' ? state : 'disabled',
          gatedReason: selectSyncSurfaceActionDescription(snapshot, sourceAction)
        }
      },
      onCloudAction: (action) => this.handleCloudBentoAction(action),
      onUserAction: (action) => this.handleUserChipAction(action)
    })
  }

  private handleCloudBentoAction(action: string): void {
    const canonicalAction = this.toSyncPickerActionId(action)
    if (this.isSyncBentoInlineAction(canonicalAction)) {
      this.runInlineSyncBentoAction(canonicalAction)
      return
    }
    if (SYNC_SURFACE_COMMAND_ACTIONS.has(canonicalAction)) {
      this.commands.executeCommand(canonicalAction)
      if (canonicalAction === 'space-delete-!') {
        this.commands.ensurePaletteVisible()
      }
      return
    }
  }

  private isSyncBentoInlineAction(action: string): action is SyncBentoInlineAction {
    return (SYNC_BENTO_INLINE_ACTIONS as readonly string[]).includes(action)
  }

  private runInlineSyncBentoAction(action: SyncBentoInlineAction): void {
    if (action === 'user-login') {
      this.handleUserChip()
      return
    }
    if (action === 'user-logout') {
      this.handleUserLogoutCommand()
      return
    }
    if (action === 'sync-reactivate') {
      this.startSyncActivationFlow()
      return
    }
    if (action === 'sync-export-shelf') {
      void this.handleDeactivatedExportCloudShelf()
      return
    }
  }

  private toSyncPickerActionId(action: string): string {
    if (action === 'reactivate sync') return 'sync-reactivate'
    if (action === 'export sync shelf') return 'sync-export-shelf'
    if (action === 'log out') return 'user-logout'
    return action
  }

  private assertSelectorParity(runtimePolicy: RuntimePolicy, actions: RuntimeSyncAction[]): void {
    if (!import.meta.env.DEV) return
    const actionIds = actions.map(entry => entry.action)
    const uniqueCount = new Set(actionIds).size
    if (uniqueCount !== actionIds.length) {
      console.warn('[account-parity] duplicate sync actions emitted', actionIds)
    }
    const caps = runtimePolicy.capabilities
    if (actionIds.includes('account-activate-sync') && !caps.canActivateSync) {
      console.warn('[account-parity] activate action visible but capability disabled')
    }
    const hasUnexpectedSpaceMutator = actionIds.includes('space-new') || actionIds.includes('space-rename') || actionIds.includes('space-delete-!')
    if (hasUnexpectedSpaceMutator) {
      console.warn('[account-parity] sync palette surfaced space mutation action after ownership split', actionIds)
    }
    if (runtimePolicy.snapshot.accountState === 'deactivated') {
      if (hasUnexpectedSpaceMutator) {
        console.warn('[account-parity] deactivated surfaced space mutation action', actionIds)
      }
    }
    if (runtimePolicy.snapshot.isIndexSpace && (runtimePolicy.lifecycle === 'sync_terminating' || runtimePolicy.lifecycle === 'sync_deactivated')) {
      if (!actionIds.includes('account-delete-all!')) {
        console.warn('[account-parity] delete-account action missing for terminating/deactivated index contract')
      }
    }
  }

  private warnPolicyOnceDev(key: string, message: string, context?: unknown): void {
    if (!import.meta.env.DEV) return
    if (this.devPolicyWarningsShown.has(key)) return
    this.devPolicyWarningsShown.add(key)
    if (typeof context === 'undefined') {
      console.warn(message)
      return
    }
    console.warn(message, context)
  }

  private openSpaceRenameFlow(): void {
    if (!this.getCapabilities().canRenameSpace || !this.canEnterIndex() || !this.activeSpaceId || this.isIndexSpace() || !this.spaceRenameDescriptor) {
      this.commands.ensurePaletteVisible()
      this.commands.setStatus('space rename unavailable', 'error')
      return
    }
    const initialName = this.storage.getActiveDeskState() === 'provisional'
      ? this.spaceIdToDefaultSlug(this.activeSpaceId)
      : this.spaceLabel
    this.commands.runDescriptor(this.spaceRenameDescriptor, {
      initialValues: { 'space-name': initialName }
    })
  }

  private openUserRenameFlow(): void {
    if (!this.getCapabilities().canRenameUser || !this.userRenameDescriptor || !this.authUsername) {
      this.commands.ensurePaletteVisible()
      this.commands.setStatus('login first', 'error')
      return
    }
    this.commands.runDescriptor(this.userRenameDescriptor, {
      initialValues: { 'user-name': this.authUsername }
    })
  }

  private applySyncUiState(): void {
    this.commands.setOrbitalVisuals(this.resolveOrbitalVisualState())
    this.commands.setSpaceKind(this.resolveSpaceKind())
    this.commands.setSyncIndicatorVisible(true)
    this.updateSyncBentoState()
    this.refreshOpenIndexPaletteConnectivity()
  }

  private refreshOpenIndexPaletteConnectivity(): void {
    if (!this.commands.isIndexSurfaceActive()) {
      this.indexPaletteReadableState = undefined
      return
    }
    const canRead = this.canAttemptIndexRead()
    if (this.indexPaletteReadableState === canRead) return
    this.indexPaletteReadableState = canRead
    void this.refreshIndexPaletteRows()
  }

  private handleSyncEngineState(state: SyncEngineState): void {
    const { status, progress } = state
    this.syncEngineStatus = status
    void progress
    if (status === 'pending' || status === 'syncing' || status === 'error') {
      if (status === 'error') {
        this.syncReachability = 'unreachable'
      }
    } else if (status === 'success') {
      this.syncReachability = 'reachable'
      this.refreshCloudDebtState()
      this.scheduleOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
      window.setTimeout(() => {
        if (this.syncEngineStatus === 'success') {
          this.setSyncEngineState('idle', 1)
        }
      }, 800)
    }
    this.applySyncUiState()
  }

  private setSyncEngineState(status: SyncStatus, progress: number): void {
    this.syncEngine.setState(status, progress)
  }

  private beginAccountCloudActivity(): () => void {
    this.accountCloudActivityDepth += 1
    this.applySyncUiState()
    let ended = false
    return () => {
      if (ended) return
      ended = true
      this.accountCloudActivityDepth = Math.max(0, this.accountCloudActivityDepth - 1)
      this.applySyncUiState()
    }
  }

  private handleLocalConsolidationActivity(activity: LocalConsolidationActivity): void {
    if (activity.active) {
      this.localMemorySettlingKey += 1
    }
    this.localMemorySettling = activity.active
    this.applySyncUiState()
  }

  private handleLocalShelfConsolidated(
    authority: LocalVolumeAuthority
  ): void {
    if (!this.storage.matchesVolumeAuthority(authority)) return
    this.localMemorySettling = false
    this.refreshCloudDebtState()
    if (this.canAttemptSyncRealtimeCatchup()) {
      this.scheduleSyncRealtimeCatchup('focus')
    }
    if (!this.cloudSyncAfterLocalConsolidationPending) {
      this.applySyncUiState()
      return
    }
    this.cloudSyncAfterLocalConsolidationPending = false
    this.startAccountCloudSync()
  }

  private saveWorkbench(
    blocks: SpaceEntry[],
    authority?: LocalVolumeAuthority
  ): void {
    const meta = this.storage.getMetaBlock()
    if (this.storage.getMountedPassport() && meta && blocks.some(block => block.ided[1] > meta.ided[1])) {
      this.seedClockFromTimeline(blocks, meta)
      this.bumpSpaceEditId()
      this.commitWorkbench(blocks, authority)
      this.markSyncDirty()
      return
    }
    if (this.searchSession) this.commands.refreshLiveInput()
    if (authority === undefined) {
      this.storage.saveWorkbench(blocks)
      return
    }
    this.storage.saveWorkbench(blocks, authority)
  }

  private commitWorkbench(
    blocks: SpaceEntry[],
    authority?: LocalVolumeAuthority
  ): void {
    // Empty-block cleanup can delete without a history checkpoint.
    const meta = this.storage.getMetaBlock()
    const hasNewDeletion = meta && blocks.some(entry => (
      isDeletionReceipt(entry) && entry.ided[1] > meta.ided[1]
    ))
    if (hasNewDeletion) {
      this.seedClockFromTimeline(blocks, meta)
      this.bumpSpaceEditId()
      this.markSyncDirty()
    }
    if (this.searchSession) this.commands.refreshLiveInput()
    if (authority === undefined) {
      this.storage.commitShelfSnapshot(blocks)
    } else {
      this.storage.commitShelfSnapshot(blocks, authority)
    }
    if (
      this.localBootReady
      && this.storage.getActiveDeskState() !== 'filed'
    ) {
      void this.fileProvisionalWelcomeAfterCommit(blocks, authority)
    }
  }

  private async fileProvisionalWelcomeAfterCommit(
    blocks: SpaceEntry[],
    capturedAuthority?: LocalVolumeAuthority
  ): Promise<void> {
    const authority = capturedAuthority === undefined
      ? this.storage.getVolumeAuthority()
      : capturedAuthority
    const committedSpaceId = this.storage.getMetaBlock()?.ided[0]
    if (!committedSpaceId) return
    const deskState = this.storage.getActiveDeskState()
    if (deskState === 'filed') return
    if (deskState === 'provisional' && !this.storage.hasProvisionalAuthorship(blocks)) return
    if (this.storage.getMetaBlock()?.ided[0] !== committedSpaceId) return
    if (!this.storage.matchesVolumeAuthority(authority)) return
    if (this.storage.getActiveDeskState() === 'filed') {
      this.storage.commitShelfSnapshot(blocks, authority)
      return
    }
    try {
      await this.storage.fileProvisionalSpace(blocks, authority)
      const adopted = await this.adoptFiledProvisionalIdentity(blocks, authority, true)
      if (!adopted) throw new Error('Filed space identity unavailable')
    } catch (error) {
      if (this.storage.getActiveDeskState() === 'filed') {
        await this.adoptFiledProvisionalIdentity(blocks, authority, false)
      }
      console.error('Provisional Welcome filing failed:', error)
      const message = error instanceof Error ? error.message : ''
      this.commands.setStatus(
        message.includes('space_missing') || message.includes('space_deleted')
          ? 'deleted elsewhere — not saved'
          : 'not saved',
        'error'
      )
    }
  }

  private async adoptFiledProvisionalIdentity(
    blocks: SpaceEntry[],
    authority: LocalVolumeAuthority,
    startCourier: boolean
  ): Promise<boolean> {
    try {
      if (!await this.storage.isVolumeAuthorityCurrent(authority)) return false
      const meta = this.storage.getMetaBlock()
      if (!meta) return false
      const identity = this.getSpaceIdentity(blocks)
      const shelf = await this.storage.loadVolumeShelfSpace(identity.spaceId, authority)
      if (!shelf || shelf.meta.deleted_at) return false
      const parsedSlug = parseLilodataText(meta.text).slug?.trim()
      const slug = parsedSlug || this.spaceIdToDefaultSlug(identity.spaceId)
      if (authority) {
        this.isSyncPaired = true
        this.syncSpaceId = identity.spaceId
        this.syncSpaceSlug = slug
        this.setSyncRuntimePendingHint(identity.spaceId, true)
      } else {
        this.isSyncPaired = false
        this.syncSpaceId = undefined
        this.syncSpaceSlug = undefined
        this.settleCloudIdentity({ syncSpaceId: null, isSyncPaired: false })
      }
      this.rememberReadyIndexSpace(identity.spaceId, slug, {
        sizeBytes: shelf.meta.size_bytes,
        spaceEd: shelf.meta.space_ed
      })
      this.setActiveSpace(identity.spaceId, slug)
      if (authority) {
        if (startCourier && this.canSyncWriteNow()) {
          this.startAccountCloudSync()
        } else {
          this.applySyncUiState()
        }
      }
      return true
    } catch {
      return false
    }
  }

  private refreshCloudDebtState(): void {
    const seq = ++this.cloudDebtRefreshSeq
    void this.computeAccountCloudDebt().then(debt => {
      if (seq !== this.cloudDebtRefreshSeq) return
      const debtCleared = this.accountCloudDebt && !debt
      this.accountCloudDebt = debt
      this.applySyncUiState()
      if (debtCleared) {
        this.scheduleOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
      }
    }).catch(() => {
      if (seq !== this.cloudDebtRefreshSeq) return
      this.applySyncUiState()
    })
  }

  private async computeAccountCloudDebt(): Promise<boolean> {
    const authority = this.storage.getVolumeAuthority()
    if (!authority || !this.isSyncPaired) return false
    const rows = await this.storage.listVolumeSpacesMeta(authority)
    if (!await this.storage.isVolumeAuthorityCurrent(authority)) {
      throw new Error('Debt read blocked: mounted volume changed')
    }
    return rows.some(row => row.dirty)
  }

  private hasActiveShelfConsolidationDebt(spaceId: string): boolean {
    if (!this.isSyncPaired || this.isIndexSpace()) return false
    const activeSpaceId = this.syncSpaceId ?? this.activeSpaceId
    if (activeSpaceId !== spaceId) return false
    return this.localMemorySettling || this.cloudSyncAfterLocalConsolidationPending
  }

  private async hasActiveSyncShelfBlob(
    spaceId: string,
    authority = this.storage.getVolumeAuthority()
  ): Promise<boolean> {
    const shelf = await this.storage.loadVolumeShelfSpace(spaceId, authority)
    return Boolean(shelf?.blob)
  }

  private flushLocalThenSyncNow(): void {
    if (this.isIndexSpace()) {
      void this.syncEngine.syncNow().catch(() => undefined)
      return
    }
    this.commitActiveEditIfNeeded()
    const authority = this.storage.getVolumeAuthority()
    const snapshot = this.document.getStateSnapshot()
    void this.storage.flushActiveDocumentForVolume(snapshot, authority)
      .then(async () => {
        if (!await this.storage.isVolumeAuthorityCurrent(authority)) return
        await this.syncEngine.syncNow()
      })
      .catch(() => undefined)
  }

  private commitActiveEditIfNeeded(): void {
    if (!this.input.isCurrentlyEditing()) return
    this.input.stopEditing()
  }

  private handleBlockedSyncWrite(): void {
    this.warnPolicyOnceDev(
      'sync-write-blocked',
      '[policy] blocked sync write while canSyncWrite=false',
      { accountState: this.authAccountState, appState: this.appState, isIndex: this.isIndexSpace() }
    )
  }

  private setAppState(state: 'edit' | 'view'): void {
    if (this.appState === state) return
    this.appState = state
    this.commands.setMode(state)
    if (state === 'view') {
      this.input.stopEditing()
      this.document.clearSelection()
      if (this.commands.isModeActive()) {
        this.commands.exitMode()
      }
      this.resetInteractionState()
    }
    this.commands.setSyncIndicatorVisible(true)
    this.applySyncUiState()
  }

  private isViewMode(): boolean {
    return this.appState === 'view'
  }

  private setAccountIdentityInFlight(isInFlight: boolean): void {
    if (this.accountIdentityInFlight === isInFlight) return
    this.accountIdentityInFlight = isInFlight
    this.updateAccountBar()
  }

  private setAccountAirlockInFlight(isInFlight: boolean): void {
    if (this.accountAirlockInFlight === isInFlight) return
    this.accountAirlockInFlight = isInFlight
  }

  private isIndexSpace(): boolean {
    return this.activeSpaceId === META_SPACES_ID
  }

  private resolveSpaceKind(): SpaceKind {
    return this.isIndexSpace() ? 'detached' : 'authored'
  }

  private resolveConnectivityState(): ReturnType<typeof resolveConnectivity> {
    const browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine !== false
    return resolveConnectivity({
      browserOnline,
      syncReachability: this.syncReachability
    })
  }

  // Only name the session dead once auth has had its say: the refresh rail is live, no
  // identity call is mid-flight, and startup's first observation has settled.
  private isFailedAuthPresentation(
    snapshot: AccountSnapshot = this.buildAccountSnapshot(),
    connectivity: ReturnType<typeof resolveConnectivity> = this.resolveConnectivityState()
  ): boolean {
    return selectFailedAuthPresentation({
      snapshot,
      browserOnline: connectivity.browserOnline,
      authObservationSettled: this.authRefreshReady
        && !this.accountIdentityInFlight
        && this.initialAuthBootstrapSettled
    })
  }

  private resolveCurrentLaneState(): LaneState {
    return resolveLaneState({
      isDetached: this.resolveSpaceKind() === 'detached',
      isViewMode: this.isViewMode()
    })
  }

  private getCapabilities(): Capabilities {
    const runtimePolicy = resolveRuntimePolicy({
      snapshot: this.buildAccountSnapshot(),
      connectivity: this.resolveConnectivityState(),
      lane: this.resolveCurrentLaneState(),
      space: this.resolveSpaceKind()
    })
    return runtimePolicy.capabilities
  }

  private buildModalFacts(): ModalFacts {
    const runtimePolicy = resolveRuntimePolicy({
      snapshot: this.buildAccountSnapshot(),
      connectivity: this.resolveConnectivityState(),
      lane: this.resolveCurrentLaneState(),
      space: this.resolveSpaceKind(),
      syncEngineStatus: this.syncEngineStatus
    })

    return selectModalFacts({
      runtimePolicy,
      activeMode: this.authModalMode,
      accountIdentityInFlight: this.accountIdentityInFlight,
      accountAirlockInFlight: this.accountAirlockInFlight,
      checkoutCancelPending: this.checkoutCancelPending,
      checkoutAttempts: this.postCheckoutAttempts,
      otpAwaitingCode: this.otpAwaitingCode,
      hasPendingOtpSendIntent: Boolean(this.pendingOtpSendIntent),
      turnstileVerificationInFlight: this.turnstileVerificationInFlight,
      accountDeleteExportReady: this.accountDeleteExportReady,
      accountDeleteInFlight: this.accountDeleteInFlight,
      syncTerminateInFlight: this.syncTerminateInFlight,
      localFoundActive: Boolean(this.localFoundResolver),
      deactivatedLocalFoundActive: Boolean(this.deactivatedLocalFoundResolver || this.deactivatedLocalFoundPrompt),
      openFileGuardActive: Boolean(this.openFileGuardResolver),
      pendingAccountIntent: this.accountMachine.pendingIntent
    })
  }

  private observeModalFacts(source: string): void {
    if (!import.meta.env.DEV) return
    const facts = this.buildModalFacts()
    if ((this.accountDeleteInFlight || this.syncTerminateInFlight) && !facts.isCloseBlocked) {
      this.warnPolicyOnceDev(
        `modal-facts-close-${source}`,
        '[modal-facts] blocking async modal can still close',
        facts
      )
    }
  }

  private dispatchModalFlow(event: ModalFlowEvent): ModalFlowState {
    this.modalFlow = reduceModalFlowEvent(this.modalFlow, event)
    return this.modalFlow
  }

  private rejectLoginWhileMounted(): boolean {
    if (!this.storage.getMountedPassport()) return false
    this.commands.setStatus('login unavailable, log out first', 'error')
    this.commands.ensurePaletteVisible()
    return true
  }

  private openAuthLoginFlow(step: AuthLoginStep = 'email'): void {
    const passport = this.storage.getMountedPassport()
    const canEditMountedUsername = Boolean(
      step === 'username'
      && passport
      && this.authSession?.user.id === passport.owner_id
    )
    if (passport && !canEditMountedUsername && this.rejectLoginWhileMounted()) return
    const flow = this.dispatchModalFlow({ type: 'START_AUTH_LOGIN', step })
    if (flow.flow !== 'auth-login') return
    const mode = authLoginStepToModalMode(flow.step)
    if (mode) this.openAuthModal(mode)
  }

  private transitionAuthLoginFlow(event: ModalFlowEvent): void {
    if (this.modalFlow.flow === 'none') {
      const currentStep = isAuthLoginModalMode(this.authModalMode)
        ? this.authModalMode as AuthLoginStep
        : 'email'
      this.dispatchModalFlow({ type: 'START_AUTH_LOGIN', step: currentStep })
    }
    const flow = this.dispatchModalFlow(event)
    if (flow.flow !== 'auth-login') return
    const mode = authLoginStepToModalMode(flow.step)
    if (mode) this.openAuthModal(mode)
  }

  private finishAuthLoginFlow(): void {
    if (this.modalFlow.flow === 'auth-login') {
      this.dispatchModalFlow({ type: 'COMPLETE' })
    }
  }

  private selectAuthLoginDescriptorForMode(mode: AuthModalMode): ModalStepDescriptor | undefined {
    const flowStep = this.modalFlow.flow === 'auth-login' && this.modalFlow.step !== 'done'
      ? this.modalFlow.step
      : undefined
    const stepFromMode = authLoginModalModeToDescriptorStep(mode)
    const step = flowStep && authLoginStepToModalMode(flowStep) === mode
      ? flowStep
      : stepFromMode
    if (!step) return undefined
    return selectAuthLoginModalDescriptor(step, {
      checkingHumanFailureReason: this.resolveCheckingHumanFailureReason()
    })
  }

  private selectUsernameDescriptorForMode(mode: AuthModalMode): ModalStepDescriptor | undefined {
    if (mode !== 'username') return undefined
    const owner = this.modalFlow.flow === 'sync-activation'
      ? 'sync-activation'
      : 'auth-login'
    return selectUsernameModalDescriptor(owner)
  }

  private selectAccountDeleteDescriptorForMode(mode: AuthModalMode): ModalStepDescriptor | undefined {
    if (mode === 'account-delete-export') {
      return selectAccountDeleteModalDescriptor('export', {
        exportReady: this.accountDeleteExportReady,
        confirmKeyword: ACCOUNT_DELETE_CONFIRM_KEYWORD
      })
    }
    if (mode === 'account-delete-confirm') {
      return selectAccountDeleteModalDescriptor('confirm', {
        exportReady: this.accountDeleteExportReady,
        confirmKeyword: ACCOUNT_DELETE_CONFIRM_KEYWORD
      })
    }
    return undefined
  }

  private selectLocalProtectionDescriptorForMode(mode: AuthModalMode): ModalStepDescriptor | undefined {
    if (mode === 'sync-exit-unsafe') {
      return selectSyncExitUnsafeModalDescriptor({
        unsafeSpaceLabels: this.syncExitUnsafeLabels
      })
    }
    if (
      mode !== 'local-found'
      && mode !== 'anonymous-login-gate'
      && mode !== 'deactivated-local-found'
      && mode !== 'protect-local-space-open-file'
      && mode !== 'protect-current-space-open-file'
      && mode !== 'local-memory-drive-recovery'
    ) {
      return undefined
    }
    return selectLocalProtectionModalDescriptor(mode as LocalProtectionStep, {
      anonymousSpaceLabels: mode === 'deactivated-local-found'
        ? this.deactivatedLocalFoundLabels
        : this.anonymousLoginGateLabels
    })
  }

  private openSubscriptionCancelFlow(): void {
    const flow = this.dispatchModalFlow({ type: 'START_SUBSCRIPTION_CANCEL' })
    if (flow.flow !== 'subscription-control') return
    this.openAuthModal(subscriptionControlStepToModalMode(flow.step))
  }

  private openSubscriptionReactivateFlow(): void {
    const flow = this.dispatchModalFlow({ type: 'START_SUBSCRIPTION_REACTIVATE' })
    if (flow.flow !== 'subscription-control') return
    this.openAuthModal(subscriptionControlStepToModalMode(flow.step))
  }

  private transitionSubscriptionControlFlow(event: ModalFlowEvent): void {
    const flow = this.dispatchModalFlow(event)
    if (flow.flow !== 'subscription-control') return
    this.openAuthModal(subscriptionControlStepToModalMode(flow.step))
  }

  private selectSubscriptionControlDescriptorForMode(
    mode: AuthModalMode
  ): ModalStepDescriptor | undefined {
    const flowStep = this.modalFlow.flow === 'subscription-control'
      ? this.modalFlow.step
      : undefined
    const stepFromMode = subscriptionControlModalModeToStep(mode)
    const step = flowStep && subscriptionControlStepToModalMode(flowStep) === mode
      ? flowStep
      : stepFromMode
    if (!step) return undefined
    return selectSubscriptionControlModalDescriptor(step, {
      periodEndLabel: this.formatPeriodEndDate(this.authStripeCurrentPeriodEnd)
    })
  }

  private selectSyncActivationDescriptorForMode(
    mode: AuthModalMode
  ): ModalStepDescriptor | undefined {
    const flowStep = this.modalFlow.flow === 'sync-activation'
      && this.modalFlow.step !== 'username'
      && this.modalFlow.step !== 'done'
      ? this.modalFlow.step
      : undefined
    const stepFromMode = syncActivationModalModeToDescriptorStep(mode)
    const step = flowStep && syncActivationStepToModalMode(flowStep) === mode
      ? flowStep
      : stepFromMode
    if (!step) return undefined
    return selectSyncActivationModalDescriptor(step, {
      checkoutEmail: step === 'login-ready' || step === 'threshold'
        ? this.authCheckoutEmail
        : undefined
    })
  }

  private selectModalDescriptorForMode(mode: AuthModalMode): ModalStepDescriptor | undefined {
    return this.selectAuthLoginDescriptorForMode(mode)
      ?? this.selectUsernameDescriptorForMode(mode)
      ?? this.selectSyncActivationDescriptorForMode(mode)
      ?? this.selectSubscriptionControlDescriptorForMode(mode)
      ?? this.selectAccountDeleteDescriptorForMode(mode)
      ?? this.selectLocalProtectionDescriptorForMode(mode)
      ?? this.selectMachineConsentDescriptorForMode(mode)
  }

  private selectMachineConsentDescriptorForMode(mode: AuthModalMode): ModalStepDescriptor | undefined {
    if (mode === 'machine-consent') return selectMachineConsentModalDescriptor(this.machineConsentAsk)
    if (mode === 'machine-consent-expired') return selectMachineConsentModalDescriptor()
    return undefined
  }

  private openSyncActivationFlow(step: SyncActivationStep = 'threshold'): void {
    const flow = this.dispatchModalFlow({ type: 'START_SYNC_ACTIVATION', step })
    if (flow.flow !== 'sync-activation') return
    const mode = syncActivationStepToModalMode(flow.step)
    if (mode) this.openAuthModal(mode)
  }

  private openDeactivatedSyncReactivationFlow(): void {
    const flow = this.dispatchModalFlow({ type: 'START_DEACTIVATED_SYNC_REACTIVATION' })
    if (flow.flow !== 'sync-activation') return
    const mode = syncActivationStepToModalMode(flow.step)
    if (mode) this.openAuthModal(mode)
  }

  private transitionSyncActivationFlow(event: ModalFlowEvent): void {
    if (this.modalFlow.flow === 'none') {
      const currentStep = this.syncActivationModalModeToStep(this.authModalMode)
      this.dispatchModalFlow({ type: 'START_SYNC_ACTIVATION', step: currentStep ?? 'threshold' })
    }
    const flow = this.dispatchModalFlow(event)
    if (flow.flow !== 'sync-activation') return
    const mode = syncActivationStepToModalMode(flow.step)
    if (mode) this.openAuthModal(mode)
  }

  private finishSyncActivationFlow(): void {
    if (this.modalFlow.flow === 'sync-activation') {
      this.dispatchModalFlow({ type: 'COMPLETE' })
    }
  }

  private syncActivationModalModeToStep(mode: AuthModalMode | undefined): SyncActivationStep | undefined {
    if (mode === 'sync-subscription-threshold') return 'threshold'
    if (mode === 'checkout-start') return 'options'
    if (mode === 'checkout-activating') return 'activating'
    if (mode === 'checkout-landing') return this.modalFlow.flow === 'sync-activation'
      ? this.modalFlow.step
      : 'landing'
    if (mode === 'checkout-timeout') return 'timeout'
    if (mode === 'sync-reactivate-from-deactivated') return 'reactivate-from-deactivated'
    if (mode === 'username') return 'username'
    return undefined
  }

  private canEnterIndex(): boolean {
    return this.getCapabilities().canOpenIndex
  }

  private canSyncReadNow(): boolean {
    return this.getCapabilities().canSyncRead
  }

  private canSyncWriteNow(): boolean {
    return this.getCapabilities().canSyncWrite
  }

  private canSyncShelfWriteNow(): boolean {
    if (!this.authSession || !this.authUsername || !this.authIsSync) return false
    if (this.authAccountState === 'deactivated') return false
    return !this.resolveConnectivityState().isOfflineLike
  }

  private getActiveShelfLane(): ShelfOwnerLane {
    return { ownerId: this.storage.getMountedPassport()?.owner_id ?? null }
  }

  private async inspectProductShelf(
    authority = this.storage.getVolumeAuthority()
  ): Promise<{ rows: LocalSpaceMeta[]; seedProofs: SeedSpaceProof[] }> {
    const rows = await this.storage.listVolumeSpacesMeta(authority)
    if (!authority) return { rows, seedProofs: [] }

    const seedBlocks = await this.storage.loadSeedBlocks()
    if (!seedBlocks) return { rows, seedProofs: [] }
    const seedComparable = serializeSeedComparableBlocks(seedBlocks)
    const seedProofs: SeedSpaceProof[] = []
    for (const row of rows) {
      if (
        row.owner_id !== authority.owner_id
        || !hasCleanSeedMetadata(row)
      ) continue
      const shelf = await this.storage.loadVolumeShelfSpace(row.space_id, authority)
      if (
        !shelf?.blob
        || shelf.meta.space_ed !== row.space_ed
        || shelf.meta.owner_id !== row.owner_id
      ) continue
      try {
        const blocksWithMeta = await decodeSpacePayload(shelf.blob.payload)
        const isSeedOnly = seedComparable === serializeSeedComparableBlocks(blocksWithMeta)
        if (!isCleanSeedSpace({
          meta: shelf.meta,
          blocks: blocksWithMeta,
          isSeedOnly
        })) continue
        seedProofs.push({
          space_id: shelf.meta.space_id,
          space_ed: shelf.meta.space_ed,
          payload: shelf.blob.payload
        })
      } catch {
        // A malformed or unreadable row is real until proven otherwise.
      }
    }
    if (!await this.storage.isVolumeAuthorityCurrent(authority)) {
      throw new Error('Shelf read blocked: mounted volume changed')
    }
    const provenIds = new Set(seedProofs.map(proof => proof.space_id))
    return {
      rows: rows.filter(row => !provenIds.has(row.space_id)),
      seedProofs
    }
  }

  private async listProductVolumeSpacesMeta(
    authority = this.storage.getVolumeAuthority()
  ): Promise<LocalSpaceMeta[]> {
    return (await this.inspectProductShelf(authority)).rows
  }

  private async resolveCleanSeedProof(
    spaceId: string,
    authority = this.storage.getVolumeAuthority()
  ): Promise<SeedSpaceProof | undefined> {
    return (await this.inspectProductShelf(authority)).seedProofs
      .find(proof => proof.space_id === spaceId)
  }

  private async pruneOrphanedCleanSeeds(
    authority = this.storage.getVolumeAuthority()
  ): Promise<void> {
    if (!authority) return
    const { seedProofs } = await this.inspectProductShelf(authority)
    for (const proof of seedProofs) {
      const deleted = await this.storage.deleteVolumeSpace(
        authority,
        proof.space_id,
        proof
      )
      if (deleted) this.indexSpaces.delete(proof.space_id)
    }
  }

  private async preflightProductVolumeShelfExport(
    authority = this.storage.getVolumeAuthority()
  ) {
    const { seedProofs } = await this.inspectProductShelf(authority)
    const preflight = await this.storage.preflightVolumeShelfExport(authority)
    if (!preflight.ok || !seedProofs.length) return preflight
    const proofBySpaceId = new Map(seedProofs.map(proof => [proof.space_id, proof]))
    return {
      ...preflight,
      spaces: preflight.spaces.filter(space => {
        const proof = proofBySpaceId.get(space.meta.space_id)
        return !proof
          || proof.space_ed !== space.meta.space_ed
          || proof.payload !== space.blob.payload
          || !hasCleanSeedMetadata(space.meta)
      })
    }
  }

  private async loadActiveMemoryShelfSpace(
    spaceId: string,
    authority = this.storage.getVolumeAuthority()
  ): Promise<LocalShelfSpace | undefined> {
    const shelf = await this.storage.loadVolumeShelfSpace(spaceId, authority)
    if (!shelf || shelf.meta.deleted_at) return undefined
    return shelf
  }

  private async resolveActiveMemoryRouteBySlug(
    slug: string,
    authority = this.storage.getVolumeAuthority()
  ): Promise<LocalSpaceMeta | undefined> {
    const ownerId = authority?.owner_id ?? null
    const rows = await this.listProductVolumeSpacesMeta(authority)
    return selectActiveMemoryShelfRowBySlug(
      rows,
      ownerId,
      slug,
      (spaceSlug, spaceId) => this.normalizeSpaceSlug(spaceSlug, spaceId)
    )
  }

  private isSyncTerminationScheduled(): boolean {
    return selectIsTerminationScheduled(this.buildAccountSnapshot())
  }

  private isAccountSyncCommandVisible(commandId: 'account-activate-sync' | 'account-cancel-sync'): boolean {
    const availability = selectAccountSyncCommandAvailability(this.buildAccountSnapshot(), commandId)
    return availability.visible
  }

  private isAccountSyncCommandEnabled(commandId: 'account-activate-sync' | 'account-cancel-sync'): boolean {
    const availability = selectAccountSyncCommandAvailability(this.buildAccountSnapshot(), commandId)
    return availability.enabled
  }

  private formatPeriodEndDate(iso?: string): string | undefined {
    if (!iso) return undefined
    const parsed = new Date(iso)
    if (Number.isNaN(parsed.getTime())) return undefined
    const year = parsed.getUTCFullYear()
    const month = String(parsed.getUTCMonth() + 1).padStart(2, '0')
    const day = String(parsed.getUTCDate()).padStart(2, '0')
    return `${year} ${month} ${day}`
  }

  private buildAccountSnapshot(): AccountSnapshot {
    // Keep account/header interpretation flowing through one inspectable read-model.
    const identity = this.getCloudIdentitySnapshot()
    const passport = this.storage.getMountedPassport()
    const mountedAccountOwnerId = passport?.owner_id ?? null
    const ambientSessionOwnerId = identity.session?.user?.id?.trim() || null
    const acceptedSessionOwnerId =
      mountedAccountOwnerId
      && ambientSessionOwnerId === mountedAccountOwnerId
        ? ambientSessionOwnerId
        : null
    const hasVerifiedProfileAuthority = Boolean(
      acceptedSessionOwnerId
      && identity.accountState !== 'none'
    )
    const localProfile = passport?.kind === 'verified' ? passport.profile : undefined
    const accountState = hasVerifiedProfileAuthority
      ? identity.accountState
      : localProfile?.account_state ?? 'none'
    const hasSyncPlan = hasVerifiedProfileAuthority
      ? identity.isSyncPlan
      : localProfile?.account_state === 'sync'
    const username = hasVerifiedProfileAuthority
      ? identity.username
      : localProfile?.username ?? null
    return {
      mountedAccountOwnerId,
      acceptedSessionOwnerId,
      hasVerifiedProfileAuthority,
      hasUsername: Boolean(username),
      isSyncPaired: identity.isSyncPaired,
      isIndexSpace: this.isIndexSpace(),
      isViewMode: this.isViewMode(),
      accountState,
      hasSyncPlan,
      stripeCancelAtPeriodEnd: hasVerifiedProfileAuthority
        ? this.authStripeCancelAtPeriodEnd
        : localProfile?.stripe_cancel_at_period_end ?? false,
      stripeCurrentPeriodEnd: hasVerifiedProfileAuthority
        ? this.authStripeCurrentPeriodEnd
        : localProfile?.stripe_current_period_end
    }
  }

  private resolveSessionAcceptanceSource(
    trigger: AuthProfileRefreshTrigger,
    explicit?: SessionAcceptanceIngress
  ): SessionAcceptanceIngress {
    if (explicit) return explicit
    if (trigger === 'startup' || trigger === 'navigation') return 'startup'
    if (trigger === 'otp-verify') return 'otp'
    if (trigger === 'auth-state-change') return 'observer'
    return 'profile-refresh'
  }

  private toLocalAccountProfile(profile: ProfileStatus): LocalAccountProfile {
    return {
      username: profile.username?.trim() || null,
      account_state: profile.accountState === 'deactivated' ? 'deactivated' : 'sync',
      stripe_cancel_at_period_end: profile.stripeCancelAtPeriodEnd,
      stripe_current_period_end: profile.stripeCurrentPeriodEnd
    }
  }

  private isResolvedAccountProfile(profile: ProfileStatus | null): boolean {
    return Boolean(
      profile
      && !profile.error
      && !profile.missing
      && (
        profile.accountState === 'sync'
        || profile.accountState === 'deactivated'
      )
    )
  }

  private async runSessionAcceptanceGateway(input: {
    source: SessionAcceptanceIngress
    session: Session | null
    profile?: ProfileStatus | null
    entryCommitted?: boolean
    mountMode?: AccountVolumeMountMode
    mountCatalogue?: RemoteSpaceMetaInput[]
    preserveUnresolvedProfileRetry?: boolean
  }): Promise<SessionAcceptanceGatewayResult> {
    const passport = this.storage.getMountedPassport()
    const sessionOwnerId = input.session?.user?.id?.trim() || null
    const profileState =
      input.profile
      && !input.profile.error
      && !input.profile.missing
        ? input.profile.accountState
        : null
    const decision = resolveSessionAcceptance({
      passportOwnerId: passport?.owner_id ?? null,
      sessionOwnerId,
      verifiedProfileAccountState: profileState,
      isCloudReachable:
        (typeof navigator === 'undefined' || navigator.onLine !== false)
        && this.syncReachability !== 'unreachable',
      entryCommitted: input.entryCommitted === true
    })

    if (decision.disposition === 'reject') {
      await this.rejectAmbientSession(input.source, sessionOwnerId)
      return { accepted: false, mounted: false, passport }
    }
    if (!input.session || !sessionOwnerId) {
      return { accepted: false, mounted: false, passport }
    }
    // Undefined means candidate inspection only. Every ingress crosses this
    // boundary before profile lookup or any runtime/cloud mutation.
    if (typeof input.profile === 'undefined') {
      return { accepted: true, mounted: false, passport }
    }

    let activePassport = passport
    let mounted = false
    let freshMountLandingSpaceId: string | undefined
    if (decision.disposition === 'mount') {
      if (
        !input.mountMode
        || !input.mountCatalogue
        || !input.profile
        || !this.isResolvedAccountProfile(input.profile)
      ) {
        return { accepted: false, mounted: false, passport }
      }
      let mount: Awaited<ReturnType<Storage['mountAccountMemory']>>
      try {
        mount = await this.storage.mountAccountMemory({
          ownerId: sessionOwnerId,
          profile: this.toLocalAccountProfile(input.profile),
          mode: input.mountMode,
          catalogue: input.mountCatalogue
        })
      } catch (error) {
        await this.failAccountMount(input.source, 'transaction_failed', error)
        return { accepted: false, mounted: false }
      }
      if (!mount.ok) {
        await this.failAccountMount(input.source, mount.reason)
        return { accepted: false, mounted: false }
      }
      activePassport = mount.passport
      mounted = mount.status === 'mounted'
      if (mounted) {
        this.resetOwnerShelfHydrationState()
      }
      const landingSpaceId = await this.applyMountedAccountWorkbench(mount.blocks, input.profile)
      if (mounted) {
        freshMountLandingSpaceId = landingSpaceId
      }
    } else if (activePassport && input.profile && this.isResolvedAccountProfile(input.profile)) {
      const verified = await this.storage.verifyMountedAccountPassport(
        sessionOwnerId,
        this.toLocalAccountProfile(input.profile)
      )
      if (!verified) {
        await this.rejectAmbientSession(input.source, sessionOwnerId)
        return { accepted: false, mounted: false, passport: activePassport }
      }
      activePassport = this.storage.getMountedPassport()
    }

    this.authSession = input.session
    this.settleCloudIdentity({ session: input.session })
    if (input.profile && !input.profile.error && !input.profile.missing) {
      this.applyProfileLifecycle(input.profile)
      if (this.isResolvedAccountProfile(input.profile)) {
        this.completeAuthLoginAtAccountLanding(sessionOwnerId, activePassport)
      }
    } else {
      this.applySessionLostState({
        preserveUnresolvedProfileRetry: input.preserveUnresolvedProfileRetry === true
      })
      this.setSyncDisabled()
    }
    this.refreshProfileRealtimeSubscription()
    this.updateAccountBar()
    return {
      accepted: true,
      mounted,
      passport: activePassport,
      freshMountLandingSpaceId
    }
  }

  private async settleSessionAtMountSeam(input: {
    source: SessionAcceptanceIngress
    session: Session
    profile: ProfileStatus
    commitAccountEntry: boolean
  }): Promise<SessionAcceptanceGatewayResult> {
    const provisional = await this.runSessionAcceptanceGateway({
      source: input.source,
      session: input.session,
      profile: input.profile
    })
    if (!provisional.accepted) return provisional
    if (
      provisional.passport
      || !input.commitAccountEntry
      || !this.isResolvedAccountProfile(input.profile)
    ) {
      return provisional
    }

    const mountMode = input.profile.accountState === 'deactivated'
      ? await this.resolveDeactivatedAccountEntryMountMode()
      : await this.resolveAnonymousAccountEntryMountMode()
    if (!mountMode) {
      return { accepted: false, mounted: false }
    }
    let mountCatalogue: RemoteSpaceMetaInput[]
    try {
      const ownerId = input.session.user.id.trim()
      const spaces = await listSyncSpacesDirect(supabase, ownerId, { includeDeleted: true })
      this.promoteReachabilityOnCloudSuccess()
      mountCatalogue = spaces.map(space => ({
        owner_id: ownerId,
        space_id: space.space_id,
        space_slug: space.space_slug ?? null,
        space_ed: space.space_ed ?? null,
        size_bytes: space.size_bytes ?? null,
        sync_at: space.sync_at ?? null,
        deleted_at: space.deleted_at ?? null
      }))
    } catch (error) {
      await this.failAccountMount(input.source, 'catalogue_unavailable', error)
      return { accepted: false, mounted: false }
    }
    const accepted = await this.runSessionAcceptanceGateway({
      source: input.source,
      session: input.session,
      profile: input.profile,
      entryCommitted: true,
      mountMode: mountMode.mode,
      mountCatalogue
    })
    if (accepted.mounted && mountMode.successStatus) {
      this.commands.setStatus(mountMode.successStatus, 'success')
    }
    return accepted
  }

  private async rejectAmbientSession(
    source: SessionAcceptanceIngress,
    sessionOwnerId: string | null
  ): Promise<void> {
    this.warnPolicyOnceDev(
      'ambient-session-owner-mismatch',
      '[policy] rejected ambient session outside mounted Account Memory',
      {
        source,
        sessionOwnerId,
        mountedOwnerId: this.storage.getMountedPassport()?.owner_id ?? null
      }
    )
    this.applyLocalSessionExit()
    await clearPersistedBrowserAuthState().catch(() => undefined)
    this.reportLoginLandingFailure('login unavailable, log out first')
    this.requestRender()
  }

  private async failAccountMount(
    source: SessionAcceptanceIngress,
    reason: string,
    error?: unknown
  ): Promise<void> {
    this.warnPolicyOnceDev(
      'account-mount-rejected',
      '[policy] Account mount transaction rejected',
      { source, reason, error }
    )
    this.applyLocalSessionExit()
    await clearPersistedBrowserAuthState().catch(() => undefined)
    this.reportLoginLandingFailure('login paused, memory airlock failed')
    this.requestRender()
  }

  private completeAuthLoginAtAccountLanding(
    sessionOwnerId: string,
    passport: LocalAccountPassport | undefined
  ): void {
    if (this.authModalMode !== 'email-sent') return
    if (passport?.kind !== 'verified' || passport.owner_id !== sessionOwnerId) return
    // Second of two deliberate clears: handleOtpVerify runs one before its refresh.
    // This landing also serves non-OTP ingress, so it cannot rely on that one.
    // Idempotent, so neither call is a duplicate to tidy away.
    this.clearOtpFlowState({ preserveCooldown: true })
    this.commands.setStatus('logged in', 'success')
    this.dispatchModalFlow({ type: 'AUTH_OTP_VERIFIED' })
    this.finishAuthLoginFlow()
    this.closeAuthModal()
  }

  private reportLoginLandingFailure(feedback: string): void {
    this.commands.setStatus(feedback, 'error')
    if (this.authModalMode === 'email-sent') {
      this.setAuthModalFeedback(feedback)
    }
  }

  private handleAuthProfileRefreshQueueError(error: unknown): void {
    console.warn('[auth] profile refresh failed', error)
    if (this.authModalMode === 'email-sent') {
      // Deliberate over-trigger: any queue failure speaks here while enter-code is
      // visible, including one raised by an unrelated caller. Not scoped to the OTP
      // caller's own work, but the message is generic and true either way.
      this.reportLoginLandingFailure('login not ready, try again')
    }
  }

  private async applyMountedAccountWorkbench(
    blocks: SpaceEntry[],
    profile: ProfileStatus
  ): Promise<string | undefined> {
    this.input.stopEditing()
    this.indexSpaces.clear()
    this.syncSpaceId = undefined
    this.syncSpaceSlug = undefined
    this.isSyncPaired = false
    this.settleCloudIdentity({
      syncSpaceId: null,
      isSyncPaired: false
    })
    await this.applyMergedBlocks(blocks, this.storage.getMetaBlock(), { persist: false })
    this.history = new History()
    this.document.clearSelection()
    const meta = this.storage.getMetaBlock()
    const bootLanding = this.storage.getBootLanding()
    const isMemoryLanding = this.isBootMemoryLanding(bootLanding)
    if (isMemoryLanding) {
      this.setActiveSpace(META_SPACES_ID, 'index')
      this.primeIndexPaletteHydrationState()
      this.commands.openIndexSurface()
    } else if (meta) {
      const parsedSlug = parseLilodataText(meta.text).slug?.trim()
      this.setActiveSpace(
        meta.ided[0],
        parsedSlug || this.spaceIdToDefaultSlug(meta.ided[0])
      )
    }
    this.setAppState(
      isMemoryLanding || profile.accountState === 'deactivated' ? 'view' : 'edit'
    )
    this.requestRender()
    // Fresh mount carries this one-shot target in the acceptance result.
    // pendingIndexRefresh remains cold-boot and deliberate-index state only.
    return bootLanding.kind === 'memory'
      ? bootLanding.intendedSpaceId
      : undefined
  }

  private settleCloudIdentity(patch: Partial<CloudIdentity>): void {
    this.cloudIdentity = {
      ...this.cloudIdentity,
      ...patch
    }
  }

  private syncCloudIdentityFromRuntime(): void {
    this.cloudIdentity = {
      session: this.authSession ?? null,
      username: this.authUsername ?? null,
      accountState: this.authAccountState,
      isSyncPlan: this.authIsSync,
      syncSpaceId: this.syncSpaceId ?? null,
      isSyncPaired: this.isSyncPaired
    }
  }

  private getCloudIdentitySnapshot(): CloudIdentity {
    this.syncCloudIdentityFromRuntime()
    return this.cloudIdentity
  }

  private dispatchAccountMachine(event: AccountMachineEvent): void {
    this.accountMachine = reduceAccountMachineEvent(this.accountMachine, event)
  }

  private syncAccountMachineFromSnapshot(): void {
    this.dispatchAccountMachine({
      type: 'PROFILE_REFRESHED',
      snapshot: this.buildAccountSnapshot()
    })
  }

  private canRunAccountIntent(intent: AccountIntent): boolean {
    return isIntentAllowed(this.accountMachine, intent)
  }

  private applySessionLostState(options: { preserveUnresolvedProfileRetry?: boolean } = {}): void {
    this.clearUnresolvedProfileRetry(options.preserveUnresolvedProfileRetry !== true)
    this.authUsername = undefined
    this.authAccountState = 'none'
    this.authIsSync = false
    this.settleCloudIdentity({
      username: null,
      accountState: 'none',
      isSyncPlan: false
    })
    this.clearVerifiedProvisioningLatch()
    this.clearStripeLifecycleProfile()
    this.dispatchAccountMachine({
      type: 'CLOUD_AUTHORITY_LOST',
      snapshot: this.buildAccountSnapshot()
    })
  }

  private applyProfileLifecycle(profile: ProfileStatus): void {
    this.clearUnresolvedProfileRetry()
    this.clearVerifiedProvisioningLatch()
    this.authUsername = profile.username ?? undefined
    this.authAccountState = profile.accountState
    this.authIsSync = profile.isSync
    this.settleCloudIdentity({
      username: profile.username ?? null,
      accountState: profile.accountState,
      isSyncPlan: profile.isSync
    })
    this.authStripeCancelAtPeriodEnd = profile.stripeCancelAtPeriodEnd
    this.authStripeCurrentPeriodEnd = profile.stripeCurrentPeriodEnd
    this.syncAccountMachineFromSnapshot()
    this.commands.refreshVisibleSurface()
  }

  private clearStripeLifecycleProfile(): void {
    this.authStripeCancelAtPeriodEnd = false
    this.authStripeCurrentPeriodEnd = undefined
  }

  private applyOptimisticSyncLifecycleFromApi(result: { cancelAtPeriodEnd?: boolean, currentPeriodEnd?: string | null }): void {
    let changed = false
    if (typeof result.cancelAtPeriodEnd === 'boolean') {
      this.authStripeCancelAtPeriodEnd = result.cancelAtPeriodEnd
      changed = true
    }
    if ('currentPeriodEnd' in result) {
      const next = typeof result.currentPeriodEnd === 'string' && result.currentPeriodEnd.trim()
        ? result.currentPeriodEnd
        : undefined
      this.authStripeCurrentPeriodEnd = next
      changed = true
    }
    if (!changed) return
    this.syncAccountMachineFromSnapshot()
    this.updateAccountBar()
    this.commands.refreshVisibleSurface()
  }

  private needsAuthenticatedCloudLaneResolution(): boolean {
    const identity = this.getCloudIdentitySnapshot()
    if (!identity.session || !identity.isSyncPlan || !identity.username) return false
    if (this.isIndexSpace()) return false
    if (!identity.isSyncPaired || !identity.syncSpaceId) return true
    if (this.activeSpaceId && this.activeSpaceId !== identity.syncSpaceId) return true
    return false
  }

  private shouldPreserveIndexContextOnProfileRefresh(): boolean {
    if (!this.isIndexSpace()) return false
    if (this.pendingRoute) return false
    if (this.pendingIndexRefresh) return false
    return true
  }

  private checkSyncOperatingContract(source: string): void {
    const isAuthenticatedSync = Boolean(this.authSession && this.authIsSync && this.authUsername)
    if (!isAuthenticatedSync) return
    if (this.isIndexSpace()) return
    const isResolving =
      this.accountIdentityInFlight
      || this.accountAirlockInFlight
      || this.authModalMode === 'local-found'
    if (isResolving) return
    const isReachable = (typeof navigator === 'undefined' || navigator.onLine !== false) && this.syncReachability !== 'unreachable'
    if (!isReachable) return
    const violations: string[] = []
    if (!this.isSyncPaired) violations.push('isSyncPaired=false')
    if (!this.syncSpaceId) violations.push('syncSpaceId=missing')
    if (this.syncSpaceId && this.activeSpaceId && this.activeSpaceId !== this.syncSpaceId) {
      violations.push(`activeSpaceId(${this.activeSpaceId})!=syncSpaceId(${this.syncSpaceId})`)
    }
    if (violations.length) {
      console.warn(`[sync-contract] ${source}: ${violations.join(' | ')}`)
    }
  }

  private async ensureAuthenticatedSyncLane(): Promise<void> {
    if (this.logoutInProgress) return
    if (!this.needsAuthenticatedCloudLaneResolution()) return
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
    if (isOffline || this.syncReachability === 'unreachable') {
      this.setSyncRuntimePendingHintForActiveSpace(true)
      this.applySyncUiState()
      return
    }
    try {
      await this.initSync()
    } finally {
      this.checkSyncOperatingContract('ensureAuthenticatedSyncLane')
    }
  }

  private async runCloudLaneInit(opts: InitSyncOptions = {}): Promise<void> {
    await this.initSync(opts)
    await this.ensureAuthenticatedSyncLane()
  }

  private promoteReachabilityOnCloudSuccess(): void {
    if (this.syncReachability === 'reachable') return
    this.syncReachability = 'reachable'
    this.applySyncUiState()
  }

  private async refreshSyncReachabilityProbe(): Promise<void> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.syncReachability = 'unreachable'
      this.applySyncUiState()
      return
    }
    if (!this.authSession || !this.authIsSync) {
      this.syncReachability = 'unknown'
      this.applySyncUiState()
      return
    }
    const reachable = await this.probeSyncReachability()
    this.syncReachability = reachable ? 'reachable' : 'unreachable'
    this.applySyncUiState()
  }

  private isOfflineLikeForSyncRuntime(): boolean {
    if (!this.authSession || !this.authIsSync) return false
    return resolveConnectivity({
      browserOnline: typeof navigator === 'undefined' || navigator.onLine !== false,
      syncReachability: this.syncReachability
    }).state === 'offline_like'
  }

  private getMountedProfileRecoverySession(): Session | undefined {
    const session = this.authSession
    const passport = this.storage.getMountedPassport()
    if (!session || !passport) return undefined
    if (session.user.id.trim() !== passport.owner_id) return undefined
    if (hasVerifiedCloudAuthority(this.buildAccountSnapshot())) return undefined
    return session
  }

  private requestSyncReachabilityReprobe(source: SyncReprobeSource, opts: { force?: boolean } = {}): void {
    const now = Date.now()
    const decision = resolveSyncReprobe(
      {
        source,
        logoutInProgress: this.logoutInProgress,
        hasAuthSession: Boolean(this.authSession),
        isSyncAuth: this.authIsSync,
        isVisible: typeof document === 'undefined' || document.visibilityState !== 'hidden',
        browserOnline: typeof navigator === 'undefined' || navigator.onLine !== false,
        isSyncOfflineLike: this.isOfflineLikeForSyncRuntime(),
        inFlight: this.syncReprobeInFlight,
        now,
        lastReprobeAt: this.syncReprobeLastAt,
        cooldownMs: SYNC_REACHABILITY_REPROBE_COOLDOWN_MS,
        force: opts.force === true
      },
      () => this.getMountedProfileRecoverySession()
    )
    if (!decision.shouldRun) return
    const mountedProfileRecoverySession = decision.mountedProfileRecovery
    this.syncReprobeLastAt = now
    this.syncReprobeInFlight = true
    void (async () => {
      try {
        if (mountedProfileRecoverySession) {
          await this.refreshAuthProfile('profile-unresolved-retry', {
            preferredSession: mountedProfileRecoverySession,
            acceptanceSource: 'recovery',
            commitAccountEntry: false
          })
          return
        }
        await this.refreshSyncReachabilityProbe()
        if (this.syncReachability !== 'reachable') return
        this.refreshProfileRealtimeSubscription()
        await this.ensureAuthenticatedSyncLane()
        this.refreshSyncRealtimeSubscription()
        void this.syncEngine.syncNow()
        this.scheduleSyncRealtimeCatchup(source === 'online' ? 'online' : 'focus')
      } catch {
        this.syncReachability = 'unreachable'
        this.applySyncUiState()
      } finally {
        this.syncReprobeInFlight = false
      }
    })()
  }

  private logAuthProfileWarning(message: string, context?: unknown): void {
    if (!import.meta.env.DEV) return
    if (typeof context === 'undefined') {
      console.warn(`[auth-profile] ${message}`)
      return
    }
    console.warn(`[auth-profile] ${message}`, context)
  }

  private clearUnresolvedProfileRetry(resetAttempts: boolean = true): void {
    if (this.unresolvedProfileRetryTimer) {
      window.clearTimeout(this.unresolvedProfileRetryTimer)
      this.unresolvedProfileRetryTimer = undefined
    }
    if (resetAttempts) {
      this.unresolvedProfileRetryAttempt = 0
    }
  }

  private scheduleUnresolvedProfileRetry(): void {
    if (!this.authSession || this.logoutInProgress) return
    if (this.unresolvedProfileRetryTimer) return
    const attemptIndex = this.unresolvedProfileRetryAttempt
    if (attemptIndex >= PROFILE_UNRESOLVED_RETRY_DELAYS_MS.length) return
    const delay = PROFILE_UNRESOLVED_RETRY_DELAYS_MS[attemptIndex]
    const userId = this.authSession.user.id
    this.unresolvedProfileRetryAttempt += 1
    this.unresolvedProfileRetryTimer = window.setTimeout(() => {
      this.unresolvedProfileRetryTimer = undefined
      if (this.logoutInProgress || !this.authSession || this.authSession.user.id !== userId) {
        this.clearUnresolvedProfileRetry()
        return
      }
      void this.refreshAuthProfile('profile-unresolved-retry')
    }, delay)
  }

  private persistOtpFlowState(startedAt?: number): void {
    if (this.otpAwaitingCode) {
      localStorage.setItem(OTP_AWAITING_CODE_KEY, '1')
      const ts = startedAt ?? Date.now()
      localStorage.setItem(OTP_FLOW_STARTED_AT_KEY, String(ts))
    } else {
      localStorage.removeItem(OTP_AWAITING_CODE_KEY)
      localStorage.removeItem(OTP_FLOW_STARTED_AT_KEY)
    }
    if (this.otpResendCooldownUntil && this.otpResendCooldownUntil > Date.now()) {
      localStorage.setItem(OTP_COOLDOWN_UNTIL_KEY, String(this.otpResendCooldownUntil))
    } else {
      localStorage.removeItem(OTP_COOLDOWN_UNTIL_KEY)
    }
  }

  private clearOtpFlowState(options: { preserveCooldown?: boolean } = {}): void {
    this.otpAwaitingCode = false
    this.stopOtpResendTicker()
    localStorage.removeItem(OTP_AWAITING_CODE_KEY)
    localStorage.removeItem(OTP_FLOW_STARTED_AT_KEY)
    if (!options.preserveCooldown) {
      this.otpResendCooldownUntil = undefined
      localStorage.removeItem(OTP_COOLDOWN_UNTIL_KEY)
    }
  }

  private restoreOtpFlowState(): void {
    const awaiting = localStorage.getItem(OTP_AWAITING_CODE_KEY) === '1'
    const cooldownUntilRaw = localStorage.getItem(OTP_COOLDOWN_UNTIL_KEY)
    const cooldownUntil = cooldownUntilRaw ? Number(cooldownUntilRaw) : NaN
    if (Number.isFinite(cooldownUntil) && cooldownUntil > Date.now()) {
      this.otpResendCooldownUntil = cooldownUntil
    } else {
      this.otpResendCooldownUntil = undefined
      localStorage.removeItem(OTP_COOLDOWN_UNTIL_KEY)
    }
    if (!awaiting || !this.authCheckoutEmail?.trim()) {
      this.otpAwaitingCode = false
      this.stopOtpResendTicker()
      localStorage.removeItem(OTP_AWAITING_CODE_KEY)
      localStorage.removeItem(OTP_FLOW_STARTED_AT_KEY)
      return
    }
    const startedAtRaw = localStorage.getItem(OTP_FLOW_STARTED_AT_KEY)
    const startedAt = startedAtRaw ? Number(startedAtRaw) : NaN
    if (!Number.isFinite(startedAt) || Date.now() - startedAt > OTP_FLOW_TTL_MS) {
      this.clearOtpFlowState({ preserveCooldown: true })
      return
    }
    this.otpAwaitingCode = true
    if (this.otpResendCooldownUntil) {
      this.resumeOtpResendTicker()
    }
  }

  private clearStorageKeysWithPrefix(
    storageLike: BrowserStorageLike | undefined,
    prefix: string
  ): void {
    if (!storageLike) return
    const keys: string[] = []
    try {
      for (let index = 0; index < storageLike.length; index += 1) {
        const key = storageLike.key(index)
        if (key?.startsWith(prefix)) {
          keys.push(key)
        }
      }
    } catch {
      return
    }
    for (const key of keys) {
      try {
        storageLike.removeItem(key)
      } catch {
        // Ignore storage capability failures.
      }
    }
  }

  private clearLocalSessionArtifacts(options: { preserveCheckoutReturnArtifacts?: boolean } = {}): void {
    if (options.preserveCheckoutReturnArtifacts) {
      this.clearOtpFlowState({ preserveCooldown: true })
    } else {
      this.clearCachedAuthEmailState()
      this.clearOtpFlowState()
    }
    this.clearVerifiedProvisioningLatch()
    this.setDetachedResumeIntent(undefined)
    this.clearStorageKeysWithPrefix(typeof window !== 'undefined' ? window.localStorage : undefined, PENDING_SYNC_KEY_PREFIX)
    this.clearStorageKeysWithPrefix(typeof window !== 'undefined' ? window.localStorage : undefined, IDENTITY_REHOME_KEY_PREFIX)
  }

  private async clearLogoutPersistenceArtifacts(): Promise<void> {
    this.clearLocalSessionArtifacts()
    await clearPersistedBrowserAuthState()
  }

  private applyLocalSessionExit(): void {
    const preserveCheckoutReturnArtifacts = this.isStripeReturnCallbackPending()
    this.authSession = null
    this.settleCloudIdentity({ session: null })
    this.applySessionLostState()
    this.clearLocalSessionArtifacts({ preserveCheckoutReturnArtifacts })
    this.setSyncDisabled()
    this.teardownProfileRealtimeSubscription()
    this.syncReachability = 'unknown'
    this.localSyncEchoSuppress.clear()
    this.pendingRoute = undefined
    this.pendingIndexRefresh = false
    this.setAccountIdentityInFlight(false)
    this.updateAccountBar()
  }

  private async handlePassiveSessionLoss(): Promise<void> {
    if (this.passiveSessionLossInFlight) {
      return this.passiveSessionLossInFlight
    }
    const inFlight = this.runPassiveSessionLoss().finally(() => {
      if (this.passiveSessionLossInFlight === inFlight) {
        this.passiveSessionLossInFlight = undefined
      }
    })
    this.passiveSessionLossInFlight = inFlight
    return inFlight
  }

  private async runPassiveSessionLoss(): Promise<void> {
    if (this.logoutInProgress) return
    if (this.isStripeReturnCallbackPending()) {
      this.authSession = null
      this.settleCloudIdentity({ session: null })
      this.updateAccountBar()
      return
    }

    const hasMountedVolume = Boolean(this.storage.getMountedPassport())
    const preserveLocal = hasMountedVolume
      ? false
      : await this.shouldPreserveLocalOnAuthExit()
    if (this.logoutInProgress) return
    this.applyLocalSessionExit()
    if (hasMountedVolume) {
      this.commands.setStatus('memory ready', 'success')
      this.requestRender()
      return
    }
    if (!preserveLocal) {
      this.applyImmediateLocalSafetyCurtain()
    }
    try {
      if (preserveLocal) {
        await this.preserveLocalAuthExitLanding()
      } else {
        await this.landInLocalBaseline({ skipAuthenticatedSyncLane: true })
      }
    } catch (error) {
      console.warn('[auth] passive logout local landing failed', error)
      this.forceRenderNow()
    }
    this.commands.setStatus('memory ready', 'success')
  }

  private setupAccountExitSignalBridge(): void {
    if (typeof window === 'undefined') return
    this.accountExitTabId = this.resolveAccountExitTabId()
    if (typeof BroadcastChannel !== 'undefined') {
      this.accountExitChannel = new BroadcastChannel(ACCOUNT_EXIT_CHANNEL_NAME)
      this.accountExitChannel.onmessage = (event) => {
        const signal = parseAccountExitSignal(event.data)
        if (signal) void this.handleAccountExitSignal(signal)
      }
    }
    window.addEventListener('storage', (event) => {
      if (event.key !== ACCOUNT_EXIT_STORAGE_KEY || !event.newValue) return
      const signal = this.parseAccountExitSignalString(event.newValue)
      if (signal) void this.handleAccountExitSignal(signal)
    })
  }

  private resolveAccountExitTabId(): string {
    try {
      const existing = window.sessionStorage.getItem(ACCOUNT_EXIT_TAB_ID_KEY)?.trim()
      if (existing) return existing
      const created = this.createAccountExitTabId()
      window.sessionStorage.setItem(ACCOUNT_EXIT_TAB_ID_KEY, created)
      return created
    } catch {
      return this.createAccountExitTabId()
    }
  }

  private createAccountExitTabId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
    return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  }

  private broadcastAccountExitSignal(
    authority: AccountUnmountAuthority,
    reason: 'logout' | 'account-delete'
  ): void {
    if (!authority.owner_id || !this.accountExitTabId) return
    const signal: AccountExitSignal = {
      type: 'account-exit',
      ownerId: authority.owner_id,
      mountId: authority.mount_id,
      sourceTabId: this.accountExitTabId,
      intent: 'explicit',
      reason,
      issuedAt: new Date().toISOString()
    }
    this.accountExitChannel?.postMessage(signal)
    try {
      window.localStorage.setItem(ACCOUNT_EXIT_STORAGE_KEY, JSON.stringify(signal))
    } catch {
      // BroadcastChannel is primary where available; storage is only a fallback.
    }
  }

  private parseAccountExitSignalString(value: string): AccountExitSignal | undefined {
    try {
      return parseAccountExitSignal(JSON.parse(value))
    } catch {
      return undefined
    }
  }

  private async handleAccountExitSignal(signal: AccountExitSignal): Promise<void> {
    if (signal.sourceTabId === this.accountExitTabId) return
    if (this.logoutInProgress) return
    if (!shouldUnmountForAccountExitSignal(signal)) return
    const authority: AccountUnmountAuthority = {
      owner_id: signal.ownerId,
      mount_id: signal.mountId
    }
    this.logoutInProgress = true
    this.setAccountAirlockInFlight(true)
    this.setAuthModalWaiting(true)
    try {
      this.closeAuthModal()
      const result = await this.unmountAccountForExit(authority, 'confirmed')
      if (!result.ok) {
        this.warnPolicyOnceDev(
          'account-exit-authority-mismatch',
          '[policy] witnessed account exit did not match mounted memory; leaving local state untouched',
          { signalOwnerId: signal.ownerId, signalMountId: signal.mountId, reason: result.reason }
        )
        this.commands.setStatus('memory exit paused, mounted volume changed', 'error')
        return
      }
      await this.finishAccountExitLocalLanding({ reuseAnonymousBaseline: true })
      this.commands.setStatus('memory ready', 'success')
      this.commands.closePalette()
      this.requestRender()
    } finally {
      this.logoutInProgress = false
      this.setAccountAirlockInFlight(false)
      this.setAuthModalWaiting(false)
    }
  }

  private stopAccountExitRuntimeMovement(): void {
    this.pendingAuthRefresh = undefined
    this.authProfileRefreshQueue.clearPending()
    this.teardownProfileRealtimeSubscription()
    this.setSyncDisabled()
    this.syncRealtimeCatchupQueued = false
    this.syncRealtimePendingSpaceIds.clear()
    this.localSyncEchoSuppress.clear()
    this.pendingRoute = undefined
    this.pendingIndexRefresh = false
  }

  private applyImmediateLocalSafetyCurtain(): void {
    const metaBlock = createLilodataBlock(this.getPaperSpec().label)
    const spaceId = metaBlock.ided[0]
    this.input.stopEditing()
    this.storage.setMetaBlock(metaBlock)
    this.seedClockFromTimeline([], metaBlock)
    this.document.hydrate([])
    this.history = new History()
    this.document.clearSelection()
    this.syncSpaceId = undefined
    this.syncSpaceSlug = undefined
    this.settleCloudIdentity({ syncSpaceId: null, isSyncPaired: false })
    this.autoSyncLocalFallbackOnEdit = false
    this.setActiveSpace(spaceId, this.spaceIdToDefaultSlug(spaceId))
    this.setAppState('edit')
    this.forceRenderNow()
  }

  private handleUserChip(): void {
    if (!authEnabled || !supabase) {
      this.commands.setStatus('login unavailable', 'error')
      this.commands.minimizePalette()
      return
    }
    const snapshot = this.buildAccountSnapshot()
    const caps = this.getCapabilities()
    if (caps.hasMountedAccount && !hasVerifiedCloudAuthority(snapshot)) {
      this.openSyncSurfaceDeterministic()
      return
    }
    if (!caps.hasMountedAccount && !caps.hasAcceptedSession) {
      this.restoreOtpFlowState()
      if (this.otpAwaitingCode && this.authCheckoutEmail?.trim()) {
        this.openAuthLoginFlow('email-sent')
      } else {
        this.openAuthLoginFlow('email')
      }
      return
    }
    if (!caps.hasSyncAccess) {
      if (snapshot.accountState !== 'deactivated') {
        this.openSyncSubscriptionThreshold()
        return
      }
      this.openSyncSurfaceDeterministic()
      return
    }
    if (!caps.hasUsername) {
      this.openAuthLoginFlow('username')
      return
    }
    this.openSyncSurfaceDeterministic()
  }

  private handleUserChipAction(action?: string): void {
    const token = (action ?? '').trim().toLowerCase()
    if (token === 'logout' || token === 'log out') {
      this.handleUserLogoutCommand()
      return
    }
    if (token === 'info') {
      const caps = this.getCapabilities()
      if (!caps.hasMountedAccount && !caps.hasAcceptedSession) {
        this.handleUserChip()
        return
      }
      if (caps.isIndexSpace) {
        this.commands.ensurePaletteVisible()
        return
      }
      this.openSyncSurfaceDeterministic()
      return
    }
    this.handleUserChip()
  }

  private buildIndexPaletteRows(): IndexPaletteRow[] {
    const currentSpaceId = this.storage.getActiveDeskState() === 'filed'
      ? this.syncSpaceId ?? this.activeSpaceId
      : undefined
    const syncTone = this.getIndexSyncTone()
    return Array.from(this.indexSpaces.entries())
      .sort(([aSpaceId, aMeta], [bSpaceId, bMeta]) => {
        const labelOrder = aMeta.slug.localeCompare(bMeta.slug, undefined, {
          numeric: true,
          sensitivity: 'base'
        })
        return labelOrder || aSpaceId.localeCompare(bSpaceId)
      })
      .map(([spaceId, meta]) => {
        const isCurrent = currentSpaceId === spaceId
        return {
          id: spaceId,
          label: meta.slug,
          description: formatMemoryRowDescription({
            availability: meta.availability,
            sizeBytes: meta.sizeBytes,
            spaceEd: meta.spaceEd
          }),
          current: isCurrent,
          statusTone: isCurrent ? syncTone : undefined
        }
      })
  }

  private primeIndexPaletteHydrationState(): void {
    const indexActions = this.resolveIndexPaletteActions()
    this.commands.setIndexPaletteState({
      status: 'blank',
      title: 'blank',
      description: '>>> memory',
      rows: [],
      actions: indexActions.actions,
      resolveActionAvailability: indexActions.resolveActionAvailability,
      onAction: indexActions.onAction
    })
  }

  private syncIndexPaletteSelection(): void {
    const rows = this.buildIndexPaletteRows()
    const indexActions = this.resolveIndexPaletteActions()
    this.commands.setIndexPaletteState(this.buildIndexPaletteState({
      status: rows.length ? 'blank' : 'empty',
      title: rows.length ? 'spaces' : 'empty',
      description: rows.length
        ? 'Tap a space to open it.'
        : 'No spaces in memory yet.',
      rows,
      actions: indexActions.actions,
      resolveActionAvailability: indexActions.resolveActionAvailability,
      onAction: indexActions.onAction,
      onOpenRow: async (rowId) => {
        await this.handleIndexPaletteRowOpen(rowId)
      }
    }))
  }

  private async handleIndexPaletteRowOpen(rowId: string): Promise<void> {
    const currentSpaceId = this.syncSpaceId ?? this.activeSpaceId
    const shouldCollapse = shouldCollapsePaletteAfterIndexNavigation(currentSpaceId, rowId)
    if (shouldCollapse) {
      this.commands.closePalette()
      return
    }
    await this.enterSpaceFromIndex(rowId)
  }

  private async refreshIndexPaletteRows(
    authority: LocalVolumeAuthority = this.storage.getVolumeAuthority()
  ): Promise<void> {
    const indexActions = this.resolveIndexPaletteActions()
    if (!this.canEnterIndex()) {
      this.commands.setIndexPaletteState(this.buildIndexPaletteState({
        status: 'blank',
        title: 'blank',
        description: '>>> memory unavailable',
        rows: [],
        actions: indexActions.actions,
        resolveActionAvailability: indexActions.resolveActionAvailability,
        onAction: indexActions.onAction
      }))
      return
    }

    let rows: LocalMemoryIndexRow[] = []
    try {
      rows = (await this.storage.listVolumeMemoryRows(authority))
        .filter(row => !row.deleted_at)
      if (!await this.storage.isVolumeAuthorityCurrent(authority)) {
        return
      }
    } catch {
      let authorityCurrent = this.storage.matchesVolumeAuthority(authority)
      if (authorityCurrent) {
        try {
          authorityCurrent = await this.storage.isVolumeAuthorityCurrent(authority)
        } catch {
          // A read failure on the current volume still belongs on the palette.
        }
      }
      if (!authorityCurrent) return
      this.commands.setIndexPaletteState(this.buildIndexPaletteState({
        status: 'unavailable',
        title: 'unavailable',
        description: 'memory unavailable',
        rows: [],
        actions: indexActions.actions,
        resolveActionAvailability: indexActions.resolveActionAvailability,
        onAction: indexActions.onAction
      }))
      return
    }

    this.indexSpaces.clear()
    rows.forEach(row => {
      if (row.space_id === META_SPACES_ID) return
      const rawSlug = row.space_slug && row.space_slug.length ? row.space_slug : undefined
      const slug = rawSlug
        ? this.normalizeSpaceSlug(rawSlug, row.space_id)
        : this.spaceIdToDefaultSlug(row.space_id)
      this.indexSpaces.set(row.space_id, {
        slug,
        availability: row.availability,
        ...(row.availability === 'ready'
          ? { sizeBytes: row.size_bytes, spaceEd: row.space_ed }
          : {})
      })
    })

    this.syncIndexPaletteSelection()
  }

  private async refreshOpenIndexPaletteAfterShelfBatch(
    refreshed: boolean,
    authority: NonNullable<LocalVolumeAuthority>
  ): Promise<void> {
    if (!refreshed || !this.commands.isIndexSurfaceActive()) return
    if (!this.storage.matchesVolumeAuthority(authority)) return
    await this.refreshIndexPaletteRows(authority)
  }

  private handleUserLogoutCommand(): void {
    const caps = this.getCapabilities()
    if (!caps.canLogout) {
      this.commands.setStatus('login first', 'error')
      this.commands.ensurePaletteVisible()
      return
    }
    void this.handleLogout()
  }

  private reportCloudUnreachable(): void {
    this.commands.ensurePaletteVisible()
    this.commands.setStatus('connection offline, changes stay local', 'error')
  }

  private handleUserRenameCommand(): void {
    const caps = this.getCapabilities()
    if (!caps.canRenameUser) {
      this.commands.setStatus('login first', 'error')
      this.commands.ensurePaletteVisible()
      return
    }
    this.openUserRenameFlow()
  }

  private handleAccountActivateSyncCommand(): void {
    if (!authEnabled || !supabase) {
      this.commands.setStatus('Account sync unavailable', 'error')
      this.commands.ensurePaletteVisible()
      return
    }
    if (!this.authSession) {
      if (this.storage.getMountedPassport()) {
        this.commands.setStatus('connection offline, Account Memory stays installed', 'error')
        this.openSyncSurfaceDeterministic()
        return
      }
      this.restoreOtpFlowState()
      if (this.otpAwaitingCode && this.authCheckoutEmail?.trim()) {
        this.openAuthLoginFlow('email-sent')
      } else {
        this.openAuthLoginFlow('email')
      }
      return
    }
    if (this.authIsSync) {
      if (this.isSyncTerminationScheduled()) {
        this.openSubscriptionReactivateFlow()
        return
      }
      this.commands.setStatus('Account sync already active', 'success')
      this.commands.ensurePaletteVisible()
      return
    }
    this.startSyncActivationFlow()
  }

  private handleAccountCancelSyncCommand(): void {
    const caps = this.getCapabilities()
    if (!caps.canTerminateSync) {
      this.commands.setStatus('Account sync required', 'error')
      this.commands.ensurePaletteVisible()
      return
    }
    this.openSubscriptionCancelFlow()
  }

  private handleAccountDeleteCommand(): void {
    if (!this.authSession) {
      this.commands.setStatus('login first', 'error')
      this.commands.ensurePaletteVisible()
      return
    }
    if (!this.canRunAccountIntent('DELETE_ACCOUNT') || !this.getCapabilities().canDeleteAccount) {
      this.commands.setStatus('deleting account and data is available after scheduling sync deactivation. use cancel Sync subscription first', 'error')
      this.commands.ensurePaletteVisible()
      return
    }
    this.accountDeleteExportReady = false
    this.dispatchAccountMachine({ type: 'INTENT', intent: 'DELETE_ACCOUNT' })
    this.openAuthModal('account-delete-export')
  }

  private startSyncActivationFlow(): void {
    if (!this.canRunAccountIntent('ACTIVATE_SYNC')) {
      this.commands.setStatus('Account sync activation unavailable', 'error')
      this.commands.ensurePaletteVisible()
      return
    }
    if (this.authSession && this.authAccountState === 'deactivated') {
      this.openDeactivatedSyncReactivationFlow()
      return
    }
    this.openSyncSubscriptionThreshold()
  }

  private syncSubscriptionEmail(): string | undefined {
    const email = this.authSession?.user?.email?.trim() ?? ''
    if (email) {
      this.authCheckoutEmail = email
      localStorage.setItem('lilo.auth.checkout-email', email)
    }
    return email || undefined
  }

  private openSyncSubscriptionThreshold(): void {
    if (this.authModalMode === 'checkout-start') return
    this.syncSubscriptionEmail()
    this.dispatchAccountMachine({ type: 'INTENT', intent: 'ACTIVATE_SYNC' })
    this.openSyncActivationFlow('threshold')
  }

  private openSyncSubscriptionOptions(): void {
    this.syncSubscriptionEmail()
    this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_OPTIONS' })
  }

  private createAuthModal(): void {
    if (this.authModalBackdrop) return
    const backdrop = document.createElement('div')
    backdrop.className = 'auth-modal-backdrop'
    backdrop.addEventListener('click', (e) => {
      if (e.target !== backdrop) return
      if (this.accountDeleteInFlight || this.syncTerminateInFlight) return
      if (
        this.authModalMode === 'username'
        || this.authModalMode === 'local-found'
        || this.authModalMode === 'local-memory-drive-recovery'
      ) return
      this.closeAuthModal()
    })
    backdrop.addEventListener('pointerdown', (event) => {
      event.stopPropagation()
    })

    const panel = document.createElement('div')
    panel.className = 'auth-modal'

    const title = document.createElement('h2')
    title.className = 'auth-modal-title'

    const copy = document.createElement('p')
    copy.className = 'auth-modal-copy'

    const custom = document.createElement('div')
    custom.className = 'auth-modal-custom'

    const menuIntro = document.createElement('p')
    menuIntro.className = 'auth-modal-note'

    const input = document.createElement('input')
    input.className = 'auth-modal-input'
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return
      event.preventDefault()
      event.stopPropagation()
      void this.handleAuthPrimaryAction()
    })

    const menuWrap = document.createElement('div')
    menuWrap.className = 'auth-modal-menu'

    const actions = document.createElement('div')
    actions.className = 'auth-modal-actions'

    const feedback = document.createElement('div')
    feedback.className = 'auth-modal-feedback'

    const primary = document.createElement('button')
    primary.type = 'button'
    primary.className = 'auth-modal-button is-primary'
    primary.addEventListener('click', () => void this.handleAuthPrimaryAction())

    actions.appendChild(primary)

    const secondary = document.createElement('button')
    secondary.type = 'button'
    secondary.className = 'auth-modal-button'
    secondary.addEventListener('click', () => void this.handleAuthSecondaryAction())

    actions.appendChild(secondary)

    const waitWrap = document.createElement('div')
    waitWrap.className = 'auth-modal-wait'
    const waitRing = document.createElement('div')
    waitRing.className = 'auth-wait-ring'
    const waitDot = document.createElement('div')
    waitDot.className = 'auth-wait-dot'
    waitRing.appendChild(waitDot)
    waitWrap.appendChild(waitRing)

    const closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.className = 'cmd-runner-close auth-modal-close'
    closeBtn.setAttribute('aria-label', 'Close')
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      if (this.accountDeleteInFlight || this.syncTerminateInFlight) return
      this.closeAuthModal()
    })
    closeBtn.appendChild(createCloseIcon())

    panel.appendChild(title)
    panel.appendChild(copy)
    panel.appendChild(custom)
    panel.appendChild(menuIntro)
    panel.appendChild(input)
    panel.appendChild(menuWrap)
    panel.appendChild(actions)
    panel.appendChild(feedback)
    panel.appendChild(waitWrap)
    panel.appendChild(menuNote)
    panel.appendChild(footerNote)
    panel.appendChild(closeBtn)
    backdrop.appendChild(panel)
    document.body.appendChild(backdrop)

    this.authModalBackdrop = backdrop
    this.authModalPanel = panel
    this.authModalTitle = title
    this.authModalCopy = copy
    this.authModalCustom = custom
    this.authModalInput = input
    this.authModalPrimary = primary
    this.authModalSecondary = secondary
    this.authModalFeedback = feedback
    this.authModalClose = closeBtn
    this.authModalMenuIntro = menuIntro
    this.authModalMenuWrap = menuWrap
    this.authModalMenuNote = menuNote
    this.authModalFooterNote = footerNote
    this.authModalActions = actions
    this.authModalWait = waitWrap
  }

  private openAuthModal(mode: AuthModalMode): void {
    const passport = this.storage.getMountedPassport()
    const canEditMountedUsername = Boolean(
      mode === 'username'
      && passport
      && this.authSession?.user.id === passport.owner_id
    )
    if (passport && isAuthLoginModalMode(mode) && !canEditMountedUsername) {
      this.rejectLoginWhileMounted()
      return
    }
    this.createAuthModal()
    if (!this.authModalBackdrop || !this.authModalPanel || !this.authModalTitle || !this.authModalCopy || !this.authModalCustom || !this.authModalInput || !this.authModalPrimary || !this.authModalSecondary || !this.authModalClose || !this.authModalMenuIntro || !this.authModalMenuWrap || !this.authModalMenuNote || !this.authModalFooterNote) return
    this.authModalMode = mode
    this.authModalBackdrop.classList.add('is-visible')
    this.attachAuthModalKeyGuard()
    this.authModalInput.value = ''
    this.authModalInput.style.display = AUTH_MODAL_HIDE_INPUT_MODES.has(mode) ? 'none' : 'block'
    this.authModalInput.inputMode = 'text'
    this.authModalInput.autocomplete = 'off'
    this.authModalInput.autocapitalize = 'none'
    this.authModalInput.spellcheck = false
    this.authModalInput.maxLength = 1024
    this.authModalClose.style.display = AUTH_MODAL_HIDE_CLOSE_MODES.has(mode) ? 'none' : 'inline-flex'
    this.authModalMenuIntro.classList.remove('is-checkout-terms')
    this.authModalMenuIntro.style.display = 'none'
    this.authModalMenuWrap.style.display = 'none'
    this.authModalMenuNote.style.display = 'none'
    this.authModalFooterNote.style.display = 'none'
    this.authModalCustom.style.display = 'none'
    this.authModalCustom.textContent = ''
    this.authModalCopy.style.display = 'block'
    this.authModalPrimary.style.display = 'inline-flex'
    this.authModalSecondary.style.display = 'none'
    this.authModalPrimaryAction = undefined
    this.authModalSecondaryAction = undefined
    this.authModalInlineOtpResend = undefined
    this.setAuthButtonEnabled(this.authModalPrimary, true)
    this.setAuthButtonEnabled(this.authModalSecondary, true)
    this.authModalPrimary.classList.remove('is-sync', 'is-danger')
    this.authModalPrimary.classList.add('is-primary')
    this.authModalSecondary.classList.remove('is-primary', 'is-sync', 'is-danger')
    this.authModalMenuWrap.innerHTML = ''
    this.authModalPrimary.parentElement?.classList.remove('is-full')
    this.authModalActions?.classList.remove('is-stack')
    this.setAuthModalWaiting(false)
    this.setAuthModalFeedback()

    const descriptor = this.selectModalDescriptorForMode(mode)
    if (descriptor) {
      this.applyAuthModalDescriptor(descriptor, {
        renderActions: mode !== 'checkout-start'
      })
    }
    if (mode === 'email' && this.authCheckoutEmail) {
      this.authModalInput.value = this.authCheckoutEmail
    }
    if (mode === 'email-sent') {
      this.refreshOtpResendUi()
      this.resumeOtpResendTicker()
      this.authModalFooterNote.textContent = ''
      this.authModalFooterNote.style.display = 'none'
    }
    if (mode === 'checkout-start') {
      this.renderCheckoutTermsNote(this.authModalMenuIntro)
      this.authModalMenuNote.textContent = ''
      this.authModalMenuNote.style.display = 'none'
      this.authModalMenuWrap.style.display = 'flex'
      this.authModalPrimary.style.display = 'none'
      if (descriptor) this.renderDescriptorActionMenu(descriptor)
    }

    if (!AUTH_MODAL_NO_AUTOFOCUS_MODES.has(mode)) {
      setTimeout(() => this.authModalInput?.focus(), 0)
    }
    this.observeModalFacts('openAuthModal')
  }

  private applyAuthModalDescriptor(
    descriptor: ModalStepDescriptor,
    options: { renderActions?: boolean } = {}
  ): void {
    if (!this.authModalTitle || !this.authModalCopy || !this.authModalPrimary || !this.authModalSecondary || !this.authModalActions || !this.authModalInput || !this.authModalCustom || !this.authModalClose) return
    this.authModalTitle.textContent = descriptor.title
    if (descriptor.body.kind === 'otp-email') {
      this.renderEmailSentCopy(descriptor)
    } else if (descriptor.body.kind === 'rich-copy') {
      this.renderRichModalCopy(descriptor)
    } else if (descriptor.body.kind === 'memory-list') {
      this.renderMemoryListModalCopy(descriptor)
    } else {
      this.authModalCopy.textContent = descriptor.copy
    }

    if (descriptor.input.kind === 'none') {
      this.authModalInput.style.display = 'none'
    } else {
      this.authModalInput.style.display = 'block'
      this.authModalInput.type = descriptor.input.kind === 'email' ? 'email' : 'text'
      this.authModalInput.inputMode = descriptor.input.inputMode
      this.authModalInput.setAttribute('autocomplete', descriptor.input.autocomplete)
      this.authModalInput.placeholder = descriptor.input.placeholder
      this.authModalInput.maxLength = descriptor.input.maxLength ?? 1024
    }

    if (descriptor.custom === 'turnstile') {
      this.authModalCustom.style.display = 'block'
    }

    this.authModalClose.style.display = descriptor.close === 'blocked' ? 'none' : 'inline-flex'

    const shouldRenderActions = options.renderActions ?? true
    if (shouldRenderActions) {
      const buttonActions = descriptor.actions.filter(action => action.placement !== 'inline' && action.placement !== 'menu')
      const primaryAction = buttonActions.find(action => action.placement === 'primary') ?? buttonActions[0]
      const secondaryAction = buttonActions.find(action => action.placement === 'secondary')
        ?? buttonActions.find(action => action !== primaryAction)
      if (primaryAction) {
        this.authModalPrimaryAction = primaryAction
        this.setAuthButtonLabel(this.authModalPrimary, primaryAction.label)
        this.applyAuthButtonTone(this.authModalPrimary, primaryAction)
        this.authModalPrimary.classList.toggle('is-primary', descriptor.mode !== 'deactivated-local-found')
      } else {
        this.authModalPrimaryAction = undefined
        this.authModalPrimary.style.display = 'none'
      }

      if (secondaryAction) {
        this.authModalSecondaryAction = secondaryAction
        this.setAuthButtonLabel(this.authModalSecondary, secondaryAction.label)
        this.applyAuthButtonTone(this.authModalSecondary, secondaryAction)
        this.authModalSecondary.classList.toggle('is-primary', descriptor.mode === 'deactivated-local-found')
        this.authModalSecondary.style.display = 'inline-flex'
      } else {
        this.authModalSecondaryAction = undefined
      }
    }

    if (descriptor.layout === 'stack') {
      this.authModalActions.classList.add('is-stack')
    }
    if (descriptor.layout === 'full') {
      this.authModalActions.classList.add('is-full')
    }
    if (descriptor.layout === 'stack' || descriptor.layout === 'full') {
      this.authModalPrimary.parentElement?.classList.add('is-full')
    }
    if (descriptor.isWaiting) {
      this.setAuthModalWaiting(true)
    }
  }

  private renderDescriptorActionMenu(descriptor: ModalStepDescriptor): void {
    if (!this.authModalMenuWrap) return
    for (const action of descriptor.actions.filter(action => action.placement === 'menu')) {
      this.authModalMenuWrap.appendChild(this.createDescriptorActionButton(action))
    }
  }

  private createDescriptorActionButton(action: ModalDescriptorAction): HTMLButtonElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'auth-modal-button is-primary'
    this.applyAuthButtonTone(button, action)
    this.setAuthButtonLabel(button, action.label)
    button.addEventListener('click', () => void this.handleModalDescriptorAction(action))
    return button
  }

  private applyAuthButtonTone(button: HTMLButtonElement, action: ModalDescriptorAction): void {
    button.classList.remove('is-sync', 'is-danger')
    if (action.tone === 'sync') button.classList.add('is-sync')
    if (action.tone === 'danger') button.classList.add('is-danger')
  }

  private async handleModalDescriptorAction(action: ModalDescriptorAction): Promise<void> {
    switch (action.event) {
      case 'AUTH_EMAIL_SUBMIT': {
        const email = this.authModalInput?.value?.trim() ?? ''
        await this.handleEmailSubmit(email)
        return
      }
      case 'AUTH_HUMAN_CHECK_RETRY': {
        const intent = this.pendingOtpSendIntent
        if (!intent) return
        await this.beginHumanCheckedOtpSend(intent.email, intent.source)
        return
      }
      case 'AUTH_OTP_VERIFY':
        await this.handleOtpVerify()
        return
      case 'AUTH_CHANGE_EMAIL':
        this.clearOtpFlowState()
        this.transitionAuthLoginFlow({ type: 'AUTH_CHANGE_EMAIL' })
        return
      case 'AUTH_OTP_RESEND':
        await this.handleOtpResend()
        return
      case 'AUTH_USERNAME_SUBMIT': {
        const username = this.authModalInput?.value?.trim() ?? ''
        await this.handleUsernameSubmit(username)
        return
      }
      case 'SUBSCRIPTION_CANCEL_CONFIRM':
        await this.handleSyncTerminateConfirmStep()
        return
      case 'SUBSCRIPTION_REACTIVATE_CONFIRM':
        await this.handleSyncReactivateConfirmStep()
        return
      case 'SYNC_ACTIVATION_OPTIONS':
      case 'SYNC_REACTIVATE_FROM_DEACTIVATED':
        this.openSyncSubscriptionOptions()
        return
      case 'SYNC_CHECKOUT_MONTHLY':
        await this.handleCheckoutChoice('monthly')
        return
      case 'SYNC_CHECKOUT_YEARLY':
        await this.handleCheckoutChoice('yearly')
        return
      case 'SYNC_POST_CHECKOUT_FRESH_LOGIN':
        await this.handlePostCheckoutFreshLogin()
        return
      case 'SYNC_POST_CHECKOUT_CONTINUE':
        await this.handlePostCheckoutContinue()
        return
      case 'SYNC_ACTIVATION_RETRY':
        this.postCheckoutAttempts = 0
        this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_RETRY' })
        await this.handlePostCheckout()
        return
      case 'ACCOUNT_DELETE_EXPORT':
        if (this.accountDeleteExportReady) {
          this.openAuthModal('account-delete-confirm')
          return
        }
        await this.handleAccountDeleteExportStep()
        return
      case 'ACCOUNT_DELETE_CONFIRM':
        await this.handleAccountDeleteConfirmStep()
        return
      case 'LOCAL_FOUND_SYNC':
        this.closeAuthModal()
        this.resolveLocalFound('sync')
        return
      case 'LOCAL_FOUND_DELETE':
        this.closeAuthModal()
        this.resolveLocalFound('delete')
        return
      case 'ANONYMOUS_LOGIN_IMPORT':
        this.resolveAnonymousLoginGate('import')
        this.closeAuthModal()
        return
      case 'ANONYMOUS_LOGIN_DELETE':
        this.resolveAnonymousLoginGate('delete')
        this.closeAuthModal()
        return
      case 'DEACTIVATED_LOCAL_EXPORT_AND_CONTINUE':
        this.resolveDeactivatedLocalFound('export-and-continue')
        this.closeAuthModal()
        return
      case 'DEACTIVATED_LOCAL_DISCARD_AND_CONTINUE':
        this.resolveDeactivatedLocalFound('delete-and-continue')
        this.closeAuthModal()
        return
      case 'OPEN_FILE_REPLACE':
        this.resolveOpenFileGuardChoice(true)
        this.closeAuthModal()
        return
      case 'SYNC_EXIT_STAY':
        this.pendingSyncExitAuthority = undefined
        this.closeAuthModal()
        this.commands.ensurePaletteVisible()
        this.commands.setStatus('logout paused, memory not in Account sync yet', 'error')
        return
      case 'SYNC_EXIT_WIPE':
        {
          const authority = this.pendingSyncExitAuthority
          this.pendingSyncExitAuthority = undefined
          this.closeAuthModal()
          await this.handleLogout({
            forceLocalBaseline: true,
            authority
          })
        }
        return
      case 'LOCAL_MEMORY_RESCUE_EXPORT':
        await this.downloadLocalMemoryRescueCopy()
        return
      case 'CONTACT_SUPPORT':
        this.openSupportEmail()
        return
      case 'MACHINE_ALLOW':
      case 'MACHINE_DENY':
        await this.answerMachineConsent(action.event === 'MACHINE_ALLOW')
        return
      default: {
        const exhaustive: never = action.event
        return exhaustive
      }
    }
  }

  private async handleAuthPrimaryAction(): Promise<void> {
    if (!this.authModalPrimaryAction) return
    await this.handleModalDescriptorAction(this.authModalPrimaryAction)
  }

  private async handleAuthSecondaryAction(): Promise<void> {
    if (!this.authModalSecondaryAction) return
    await this.handleModalDescriptorAction(this.authModalSecondaryAction)
  }

  private renderCheckoutTermsNote(target: HTMLParagraphElement): void {
    target.classList.add('is-checkout-terms')
    target.textContent = 'Read '
    const link = document.createElement('a')
    link.href = '/terms.html'
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.textContent = 'terms'
    link.addEventListener('click', (event) => {
      event.stopPropagation()
    })
    target.appendChild(link)
    target.appendChild(document.createTextNode(' first.'))
    target.style.display = 'block'
  }

  private setAuthButtonLabel(button: HTMLButtonElement, label: string): void {
    button.textContent = ''
    const action = label.trim()
    const countdownMatch = action.match(/^(.*?)(\s+\(\d+s\))$/)
    const actionText = countdownMatch ? countdownMatch[1] : action
    const countdownText = countdownMatch ? countdownMatch[2] : ''

    const actionSpan = document.createElement('span')
    actionSpan.className = 'auth-modal-action'
    actionSpan.textContent = actionText
    button.appendChild(actionSpan)

    if (countdownText) {
      const countdownSpan = document.createElement('span')
      countdownSpan.className = 'auth-modal-countdown'
      countdownSpan.textContent = countdownText
      button.appendChild(countdownSpan)
    }
  }

  private setAuthButtonEnabled(button: HTMLButtonElement, enabled: boolean): void {
    button.disabled = !enabled
    button.style.opacity = enabled ? '1' : '0.45'
    button.style.cursor = enabled ? 'pointer' : 'default'
  }

  private setAuthActionButtonsEnabled(enabled: boolean): void {
    if (this.authModalPrimary) this.setAuthButtonEnabled(this.authModalPrimary, enabled)
    if (this.authModalSecondary) this.setAuthButtonEnabled(this.authModalSecondary, enabled)
  }

  private setAuthInputEnabled(enabled: boolean): void {
    if (!this.authModalInput) return
    this.authModalInput.disabled = !enabled
    this.authModalInput.style.opacity = enabled ? '1' : '0.45'
    this.authModalInput.style.cursor = enabled ? 'text' : 'default'
  }

  private parseOtpCooldownSeconds(message?: string): number | undefined {
    const source = (message ?? '').trim()
    if (!source) return undefined
    const patterns = [
      /every\s+(\d+)\s*seconds?/i,
      /after\s+(\d+)\s*seconds?/i,
      /wait\s+(\d+)\s*seconds?/i,
      /\b(\d+)\s*seconds?\b/i
    ]
    for (const pattern of patterns) {
      const match = source.match(pattern)
      if (!match) continue
      const value = Number(match[1])
      if (Number.isFinite(value) && value > 0) {
        return Math.max(1, Math.min(600, Math.floor(value)))
      }
    }
    return undefined
  }

  private getOtpResendRemainingSeconds(): number {
    if (!this.otpResendCooldownUntil) return 0
    const ms = this.otpResendCooldownUntil - Date.now()
    if (ms <= 0) return 0
    return Math.ceil(ms / 1000)
  }

  private stopOtpResendTicker(): void {
    if (!this.otpResendTicker) return
    window.clearInterval(this.otpResendTicker)
    this.otpResendTicker = undefined
  }

  private startOtpResendCooldown(seconds: number): void {
    this.otpResendCooldownUntil = Date.now() + Math.max(1, seconds) * 1000
    this.persistOtpFlowState()
    this.refreshOtpResendUi()
    this.resumeOtpResendTicker()
  }

  private resumeOtpResendTicker(): void {
    if (this.getOtpResendRemainingSeconds() === 0) {
      this.stopOtpResendTicker()
      return
    }
    if (this.otpResendTicker) return
    this.otpResendTicker = window.setInterval(() => {
      this.refreshOtpResendUi()
      if (this.getOtpResendRemainingSeconds() === 0) {
        this.stopOtpResendTicker()
      }
    }, 250)
  }

  private refreshOtpResendUi(): void {
    if (this.authModalMode !== 'email-sent') return
    if (!this.authModalInlineOtpResend) return
    const remaining = this.getOtpResendRemainingSeconds()
    if (remaining > 0) {
      this.authModalInlineOtpResend.textContent = `resend code (${remaining}s)`
      this.authModalInlineOtpResend.disabled = true
      this.authModalInlineOtpResend.classList.add('is-disabled')
      return
    }
    this.authModalInlineOtpResend.textContent = 'resend code'
    this.authModalInlineOtpResend.disabled = false
    this.authModalInlineOtpResend.classList.remove('is-disabled')
  }

  private renderEmailSentCopy(descriptor: ModalStepDescriptor): void {
    if (!this.authModalCopy) return
    const email = this.authCheckoutEmail?.trim() ?? ''
    const changeEmailActionDescriptor = descriptor.actions.find(action => action.event === 'AUTH_CHANGE_EMAIL')
      ?? { label: 'change email', event: 'AUTH_CHANGE_EMAIL' as const }
    const resendActionDescriptor = descriptor.actions.find(action => action.event === 'AUTH_OTP_RESEND')
      ?? { label: 'resend code', event: 'AUTH_OTP_RESEND' as const }
    this.authModalInlineOtpResend = undefined
    this.authModalCopy.textContent = ''
    this.authModalCopy.appendChild(document.createTextNode(`${descriptor.copy} `))
    if (email) {
      const emailSpan = document.createElement('span')
      emailSpan.className = 'auth-modal-email'
      emailSpan.textContent = email
      this.authModalCopy.appendChild(emailSpan)
    }
    this.authModalCopy.appendChild(document.createTextNode('.\n'))
    const changeEmailAction = document.createElement('button')
    changeEmailAction.type = 'button'
    changeEmailAction.className = 'auth-modal-inline-action auth-modal-action'
    changeEmailAction.textContent = changeEmailActionDescriptor.label
    changeEmailAction.addEventListener('click', () => {
      void this.handleModalDescriptorAction(changeEmailActionDescriptor)
    })
    this.authModalCopy.appendChild(changeEmailAction)
    this.authModalCopy.appendChild(document.createTextNode(' / '))
    const resendAction = document.createElement('button')
    resendAction.type = 'button'
    resendAction.className = 'auth-modal-inline-action auth-modal-action'
    resendAction.textContent = resendActionDescriptor.label
    resendAction.addEventListener('click', () => {
      void this.handleModalDescriptorAction(resendActionDescriptor)
    })
    this.authModalInlineOtpResend = resendAction
    this.authModalCopy.appendChild(resendAction)
  }

  private renderRichModalCopy(descriptor: ModalStepDescriptor): void {
    if (!this.authModalCopy || descriptor.body.kind !== 'rich-copy') return
    this.authModalCopy.textContent = ''
    for (const part of descriptor.body.parts) {
      if (part.kind === 'cloud-value') {
        const valueSpan = document.createElement('span')
        valueSpan.className = 'auth-modal-cloud-value'
        valueSpan.textContent = part.text
        this.authModalCopy.appendChild(valueSpan)
      } else {
        this.authModalCopy.appendChild(document.createTextNode(part.text))
      }
    }
  }

  private renderMemoryListModalCopy(descriptor: ModalStepDescriptor): void {
    if (!this.authModalCopy || descriptor.body.kind !== 'memory-list') return
    this.authModalCopy.textContent = ''
    this.authModalCopy.appendChild(document.createTextNode(`${descriptor.body.before}\n`))
    const list = document.createElement('div')
    list.className = 'auth-modal-memory-list'
    descriptor.body.items.forEach((item) => {
      const row = document.createElement('div')
      row.className = 'auth-modal-memory-list-item'
      row.textContent = item
      list.appendChild(row)
    })
    this.authModalCopy.appendChild(list)
    this.authModalCopy.appendChild(document.createTextNode(`\n${descriptor.body.after}`))
  }

  private async beginHumanCheckedOtpSend(email: string, source: 'initial' | 'resend'): Promise<void> {
    if (this.rejectLoginWhileMounted()) return
    const normalizedEmail = email.trim()
    if (!normalizedEmail) {
      this.commands.setStatus('email required', 'error')
      return
    }
    this.authCheckoutEmail = normalizedEmail
    localStorage.setItem('lilo.auth.checkout-email', normalizedEmail)
    if (!TURNSTILE_SITE_KEY) {
      await this.submitOtpWithoutHumanCheck({ email: normalizedEmail, source })
      return
    }
    this.pendingOtpSendIntent = { email: normalizedEmail, source }
    this.turnstileVerificationInFlight = false
    this.checkingHumanFailureReason = 'failed'
    this.transitionAuthLoginFlow({ type: 'AUTH_HUMAN_CHECK_REQUIRED' })
    try {
      await this.renderCheckingHumanChallenge()
    } catch {
      this.openCheckingHumanFailed('unavailable')
    }
  }

  private async renderCheckingHumanChallenge(): Promise<void> {
    if (!this.authModalCustom || this.authModalMode !== 'checking-human' || !this.pendingOtpSendIntent) return
    this.clearTurnstileWidget()
    this.setAuthModalFeedback()
    this.setAuthModalWaiting(true)
    await this.ensureTurnstileScriptLoaded()
    if (!window.turnstile || this.authModalMode !== 'checking-human' || !this.pendingOtpSendIntent) {
      throw new Error('turnstile_unavailable')
    }
    const target = document.createElement('div')
    target.className = 'auth-turnstile-slot'
    this.authModalCustom.textContent = ''
    this.authModalCustom.appendChild(target)
    this.turnstileWidgetId = window.turnstile.render(target, {
      sitekey: TURNSTILE_SITE_KEY as string,
      theme: resolveThemeMode(readThemeMode()),
      size: 'flexible',
      retry: 'never',
      callback: (token: string) => {
        void this.completeHumanCheckedOtpSend(token)
      },
      'error-callback': () => {
        this.openCheckingHumanFailed('failed')
      },
      'expired-callback': () => {
        this.openCheckingHumanFailed('expired')
      },
      'timeout-callback': () => {
        this.openCheckingHumanFailed('timeout')
      }
    })
  }

  private async ensureTurnstileScriptLoaded(): Promise<void> {
    if (window.turnstile) return
    if (this.turnstileScriptPromise) {
      await this.turnstileScriptPromise
      return
    }
    this.turnstileScriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile-script="true"]')
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true })
        existing.addEventListener('error', () => reject(new Error('turnstile_script_failed')), { once: true })
        return
      }
      const script = document.createElement('script')
      script.src = TURNSTILE_SCRIPT_URL
      script.async = true
      script.defer = true
      script.dataset.turnstileScript = 'true'
      script.addEventListener('load', () => resolve(), { once: true })
      script.addEventListener('error', () => reject(new Error('turnstile_script_failed')), { once: true })
      document.head.appendChild(script)
    })
    try {
      await this.turnstileScriptPromise
    } catch (error) {
      this.turnstileScriptPromise = undefined
      throw error
    }
  }

  private async completeHumanCheckedOtpSend(turnstileToken: string): Promise<void> {
    if (this.turnstileVerificationInFlight) return
    const intent = this.pendingOtpSendIntent
    if (!intent || this.authModalMode !== 'checking-human') return
    this.turnstileVerificationInFlight = true
    this.setAuthModalFeedback()
    this.setAuthModalWaiting(true)
    try {
      const result = await this.sendOtpEmail(intent.email, turnstileToken)
      if (this.handleOtpSendSuccess(intent, result)) {
        return
      }
      if (result.throttled) {
        this.handleOtpSendThrottle(result)
        return
      }
      if (result.error === 'human_check_failed' || result.error === 'human_check_required') {
        this.openCheckingHumanFailed('failed')
        return
      }
      if (result.error === 'human_check_unavailable') {
        this.openCheckingHumanFailed('unavailable')
        return
      }
      this.openCheckingHumanFailed('otp')
    } finally {
      this.turnstileVerificationInFlight = false
    }
  }

  private async submitOtpWithoutHumanCheck(intent: PendingOtpSendIntent): Promise<void> {
    this.setAuthModalFeedback()
    this.setAuthModalWaiting(true)
    try {
      const result = await this.sendOtpEmail(intent.email, '')
      if (this.handleOtpSendSuccess(intent, result)) {
        return
      }
      if (result.throttled) {
        this.handleOtpSendThrottle(result)
        return
      }
      if (result.error === 'human_check_failed' || result.error === 'human_check_required' || result.error === 'human_check_unavailable') {
        this.commands.setStatus('human check unavailable', 'error')
        this.setAuthModalFeedback('human check unavailable')
        return
      }
      this.commands.setStatus('code send failed', 'error')
      this.setAuthModalFeedback('code send failed')
    } finally {
      this.setAuthModalWaiting(false)
    }
  }

  private handleOtpSendSuccess(intent: PendingOtpSendIntent, result: OtpSendResult): boolean {
    if (!result.ok) return false
    this.clearCheckingHumanState()
    this.otpAwaitingCode = true
    this.persistOtpFlowState()
    this.startOtpResendCooldown(result.cooldownSeconds ?? 60)
    this.commands.setStatus(intent.source === 'resend' ? 'code resent' : 'check your inbox', 'success')
    this.commands.ensurePaletteVisible()
    this.transitionAuthLoginFlow({ type: 'AUTH_OTP_SENT' })
    return true
  }

  private handleOtpSendThrottle(result: OtpSendResult): void {
    this.clearCheckingHumanState()
    this.otpAwaitingCode = true
    this.persistOtpFlowState()
    this.startOtpResendCooldown(result.cooldownSeconds ?? 60)
    this.commands.setStatus('resend available soon', 'error')
    this.commands.ensurePaletteVisible()
    this.transitionAuthLoginFlow({ type: 'AUTH_OTP_SENT' })
  }

  private openCheckingHumanFailed(reason: CheckingHumanFailureReason): void {
    if (this.authModalMode !== 'checking-human') return
    this.clearTurnstileWidget()
    this.checkingHumanFailureReason = reason
    this.transitionAuthLoginFlow({ type: 'AUTH_HUMAN_CHECK_FAILED' })
  }

  private resolveCheckingHumanFailureReason(): CheckingHumanFailureReason {
    return this.checkingHumanFailureReason
  }

  private clearTurnstileWidget(): void {
    if (window.turnstile && this.turnstileWidgetId) {
      if (typeof window.turnstile.remove === 'function') {
        window.turnstile.remove(this.turnstileWidgetId)
      } else {
        window.turnstile.reset(this.turnstileWidgetId)
      }
    }
    this.turnstileWidgetId = undefined
    if (this.authModalCustom) {
      this.authModalCustom.textContent = ''
    }
  }

  private clearCheckingHumanState(): void {
    this.clearTurnstileWidget()
    this.pendingOtpSendIntent = undefined
    this.turnstileVerificationInFlight = false
    this.checkingHumanFailureReason = 'failed'
  }

  private closeAuthModal(): void {
    if (!this.authModalBackdrop) return
    if (this.accountDeleteInFlight || this.syncTerminateInFlight) return
    this.authModalBackdrop.classList.remove('is-visible')
    const previousMode = this.authModalMode
    this.authModalMode = undefined
    if (isAuthLoginModalMode(previousMode) && this.modalFlow.flow === 'auth-login') {
      this.dispatchModalFlow({ type: 'CANCEL' })
    }
    if (isSubscriptionControlModalMode(previousMode) && this.modalFlow.flow === 'subscription-control') {
      this.dispatchModalFlow({ type: 'CANCEL' })
    }
    if (isSyncActivationModalMode(previousMode) && this.modalFlow.flow === 'sync-activation') {
      this.dispatchModalFlow({ type: 'CANCEL' })
    }
    if (previousMode === 'checking-human' || previousMode === 'checking-human-failed') {
      this.clearCheckingHumanState()
    }
    if (previousMode === 'account-delete-export' || previousMode === 'account-delete-confirm') {
      this.accountDeleteExportReady = false
    }
    if (previousMode === 'deactivated-local-found') {
      const resolve = this.deactivatedLocalFoundResolver
      this.deactivatedLocalFoundResolver = undefined
      this.deactivatedLocalFoundPrompt = undefined
      this.deactivatedLocalFoundLabels = []
      if (resolve) resolve('cancel')
    }
    if (previousMode === 'anonymous-login-gate') {
      const resolve = this.anonymousLoginGateResolver
      this.anonymousLoginGateResolver = undefined
      this.anonymousLoginGatePrompt = undefined
      this.anonymousLoginGateLabels = []
      if (resolve) resolve('cancel')
    }
    if (previousMode === 'sync-exit-unsafe') {
      this.syncExitUnsafeLabels = []
    }
    if (previousMode === 'protect-local-space-open-file' || previousMode === 'protect-current-space-open-file') {
      const resolve = this.openFileGuardResolver
      this.openFileGuardResolver = undefined
      if (resolve) resolve(false)
    }
    this.stopOtpResendTicker()
    this.setAuthModalWaiting(false)
    this.detachAuthModalKeyGuard()
    if (isVerifiedProvisioningExitModalMode(previousMode) && this.shouldStayInVerifiedProvisioningLane()) {
      void this.abandonVerifiedProvisioningSession()
    }
    // A waiting allow request gets its turn once the modal before it is gone.
    if (this.machineConsent) setTimeout(() => void this.offerMachineConsent(), 0)
  }

  notifyAppUpdateReady(apply: () => Promise<void>): void {
    this.pendingAppUpdate = {
      apply,
      dismissed: false,
      shown: false
    }
    this.requestRender()
  }

  private isQuietOccasion(): boolean {
    if (this.input.isCurrentlyEditing()) return false
    if (this.document.getSelectedBlockIds().length > 0) return false
    return true
  }

  private canShowUpdateRunner(): boolean {
    if (!this.pendingAppUpdate) return false
    if (this.pendingAppUpdate.dismissed || this.pendingAppUpdate.shown) return false
    if (!this.isQuietOccasion()) return false
    if (this.authModalMode) return false
    if (this.commands.isEngineActive()) return false
    return true
  }

  private maybeShowUpdateRunner(): void {
    if (!this.canShowUpdateRunner() || !this.pendingAppUpdate) return
    this.pendingAppUpdate.shown = true
    this.commands.setNoticeState({
      feedback: 'fresh version of Lilo Write available',
      copy: 'Refresh takes a moment. Your text stays.',
      choices: [
        {
          label: 'refresh',
          tone: 'armed',
          onPick: async () => {
            this.commands.setStatus('refreshing', 'success')
            try {
              await this.pendingAppUpdate?.apply()
            } catch {
              window.location.reload()
            }
          }
        },
        {
          label: 'later',
          tone: 'quiet',
          onPick: () => {
            if (this.pendingAppUpdate) {
              this.pendingAppUpdate.dismissed = true
            }
            this.commands.closePalette()
          }
        }
      ]
    })
    this.commands.openNoticeSurface()
  }

  private setAuthModalWaiting(isWaiting: boolean): void {
    if (!this.authModalActions || !this.authModalWait) return
    const suppressWaitVisual = this.authModalMode === 'checking-human'
    if (isWaiting) {
      this.authModalActions.style.display = 'none'
      this.authModalWait.style.display = suppressWaitVisual ? 'none' : 'flex'
      if (this.authModalFeedback) {
        this.authModalFeedback.style.display = 'none'
      }
      if (this.authModalMode === 'checkout-start' && this.authModalMenuWrap) {
        this.authModalMenuWrap.style.display = 'none'
      }
      return
    }
    this.authModalActions.style.display = 'flex'
    this.authModalWait.style.display = 'none'
    if (this.authModalFeedback && this.authModalFeedback.textContent) {
      this.authModalFeedback.style.display = 'flex'
    }
    if (this.authModalMode === 'checkout-start' && this.authModalMenuWrap) {
      this.authModalMenuWrap.style.display = 'flex'
    }
  }

  private setAuthModalFeedback(message?: string, tone: 'success' | 'error' = 'error'): void {
    if (!this.authModalFeedback) return
    const text = message?.trim() ?? ''
    this.authModalFeedback.classList.toggle('is-success', text !== '' && tone === 'success')
    this.authModalFeedback.classList.toggle('is-error', text !== '' && tone === 'error')
    const prefixed = text ? (text.startsWith('>') ? text : `> ${text}`) : ''
    this.authModalFeedback.textContent = prefixed
    this.authModalFeedback.style.display = text ? 'flex' : 'none'
  }

  private attachAuthModalKeyGuard(): void {
    if (this.authModalKeydownHandler) return
    this.authModalKeydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        if (this.canCloseAuthModalWithEscape()) {
          this.closeAuthModal()
        }
        return
      }
      const target = event.target as HTMLElement | null
      if (target && target.closest('.auth-modal')) return
      event.preventDefault()
      event.stopPropagation()
    }
    document.addEventListener('keydown', this.authModalKeydownHandler, true)
  }

  private canCloseAuthModalWithEscape(): boolean {
    const facts = this.buildModalFacts()
    return canCloseModalWithEscape({
      isVisible: Boolean(this.authModalBackdrop?.classList.contains('is-visible')),
      hasActiveMode: Boolean(this.authModalMode),
      hasCloseControl: Boolean(this.authModalClose),
      isCloseControlVisible: this.authModalClose?.style.display !== 'none',
      isCloseBlocked: facts.isCloseBlocked,
      accountDeleteInFlight: this.accountDeleteInFlight,
      syncTerminateInFlight: this.syncTerminateInFlight
    })
  }

  private detachAuthModalKeyGuard(): void {
    if (!this.authModalKeydownHandler) return
    document.removeEventListener('keydown', this.authModalKeydownHandler, true)
    this.authModalKeydownHandler = undefined
  }

  private openSupportEmail(): void {
    if (SUPPORT_EMAIL) {
      window.location.href = `mailto:${SUPPORT_EMAIL}`
      this.commands.setStatus('contact-devs opened', 'success')
    } else {
      this.commands.setStatus('contact-devs unavailable', 'error')
    }
  }

  private resolveLocalFound(choice: 'sync' | 'delete'): void {
    const resolve = this.localFoundResolver
    this.localFoundResolver = undefined
    if (resolve) resolve(choice)
  }

  private resolveAnonymousLoginGate(choice: 'import' | 'delete' | 'cancel'): void {
    const resolve = this.anonymousLoginGateResolver
    this.anonymousLoginGateResolver = undefined
    this.anonymousLoginGatePrompt = undefined
    this.anonymousLoginGateLabels = []
    if (resolve) resolve(choice)
  }

  private resolveDeactivatedLocalFound(choice: 'export-and-continue' | 'delete-and-continue' | 'cancel'): void {
    const resolve = this.deactivatedLocalFoundResolver
    this.deactivatedLocalFoundResolver = undefined
    this.deactivatedLocalFoundPrompt = undefined
    this.deactivatedLocalFoundLabels = []
    if (resolve) resolve(choice)
  }

  private resolveOpenFileGuardChoice(confirmed: boolean): void {
    const resolve = this.openFileGuardResolver
    this.openFileGuardResolver = undefined
    if (resolve) resolve(confirmed)
  }

  private promptAnonymousLoginGate(labels: string[]): Promise<'import' | 'delete' | 'cancel'> {
    if (this.anonymousLoginGatePrompt) return this.anonymousLoginGatePrompt
    this.anonymousLoginGateLabels = labels
    this.anonymousLoginGatePrompt = new Promise((resolve) => {
      this.anonymousLoginGateResolver = (choice) => {
        this.anonymousLoginGateResolver = undefined
        this.anonymousLoginGatePrompt = undefined
        this.anonymousLoginGateLabels = []
        resolve(choice)
      }
      this.openAuthModal('anonymous-login-gate')
    })
    return this.anonymousLoginGatePrompt
  }

  private async resolveAnonymousAccountEntryMountMode(): Promise<AccountEntryMountPlan | undefined> {
    const session = this.authSession
    const authority = this.storage.getVolumeAuthority()
    if (authority !== null) return undefined
    let mode: AccountVolumeMountMode = 'replace-anonymous'
    let successStatus: 'memory imported' | 'memory deleted' | undefined
    const shouldContinue = await runAnonymousLoginGateBeforeAccountEntry({
      acceptedSessionOwnerId: session?.user?.id?.trim() || null,
      shouldFlushAnonymousEditor: !this.isSyncPaired && !this.isIndexSpace(),
      flushAnonymousEditor: async () => {
        await this.storage.flushActiveDocumentForVolume(
          this.document.getStateSnapshot(),
          authority
        )
      },
      listMeaningfulAnonymousRows: () => (
        this.listMeaningfulAnonymousShelfRows(authority)
      ),
      labelRow: (row) => {
        const slug = row.space_slug?.trim()
        return slug && slug.length ? this.normalizeSpaceSlug(slug, row.space_id) : this.spaceIdToDefaultSlug(row.space_id)
      },
      prompt: (labels) => this.promptAnonymousLoginGate(labels),
      importAnonymousRows: async () => {
        mode = 'import-anonymous'
        successStatus = 'memory imported'
      },
      clearAnonymousRows: async () => {},
      deleteAnonymousRows: async () => {
        mode = 'replace-anonymous'
        successStatus = 'memory deleted'
      },
      resetAfterDelete: async () => {},
      cancelAccountEntry: () => this.cancelAccountEntryAfterAnonymousGate()
    })
    if (!shouldContinue) return undefined
    return { mode, successStatus }
  }

  private async resolveDeactivatedAccountEntryMountMode(): Promise<AccountEntryMountPlan | undefined> {
    const authority = this.storage.getVolumeAuthority()
    if (authority !== null) return undefined
    let successStatus: 'Local Memory discarded' | undefined
    const shouldContinue = await runDeactivatedLoginLocalMemoryGate({
      acceptedSessionOwnerId: this.authSession?.user?.id?.trim() || null,
      shouldFlushAnonymousEditor: !this.isSyncPaired && !this.isIndexSpace(),
      flushAnonymousEditor: async () => {
        await this.storage.flushActiveDocumentForVolume(
          this.document.getStateSnapshot(),
          authority
        )
      },
      listMeaningfulAnonymousRows: () => (
        this.listMeaningfulAnonymousShelfRows(authority)
      ),
      labelRow: (row) => {
        const slug = row.space_slug?.trim()
        return slug && slug.length ? this.normalizeSpaceSlug(slug, row.space_id) : this.spaceIdToDefaultSlug(row.space_id)
      },
      prompt: (labels) => this.promptDeactivatedLocalFound(labels),
      exportAnonymousRows: async () => this.exportLocalShelfZip({
        lane: { ownerId: null },
        successStatus: 'Local Memory zip started',
        failureStatus: 'Local Memory export failed'
      }),
      clearAnonymousRows: async () => {},
      deleteAnonymousRows: async () => {
        successStatus = 'Local Memory discarded'
      },
      resetAfterDelete: async () => {},
      cancelAccountEntry: () => this.cancelAccountEntryAfterAnonymousGate()
    })
    if (!shouldContinue) return undefined
    return { mode: 'replace-anonymous', successStatus }
  }

  private async listMeaningfulAnonymousShelfRows(
    authority = this.storage.getVolumeAuthority()
  ): Promise<LocalSpaceMeta[]> {
    if (authority !== null) {
      throw new Error('Anonymous shelf read blocked: Account memory is mounted')
    }
    const rows = (await this.storage.listVolumeSpacesMeta(authority))
      .filter(row => !row.deleted_at)
    const meaningful: LocalSpaceMeta[] = []
    for (const row of rows) {
      const blob = (await this.storage.loadVolumeShelfSpace(
        row.space_id,
        authority
      ))?.blob
      if (!blob) {
        meaningful.push(row)
        continue
      }
      try {
        const blocksWithMeta = await decodeSpacePayload(blob.payload)
        const { blocks } = extractLilodataBlock(blocksWithMeta)
        const isSeedOnly = await this.isLocalSeedOnly(blocks)
        if (hasMeaningfulLocalDraft({ blocks, isSeedOnly })) {
          meaningful.push(row)
        }
      } catch {
        meaningful.push(row)
      }
    }
    if (!await this.storage.isVolumeAuthorityCurrent(authority)) {
      throw new Error('Anonymous shelf read blocked: mounted volume changed')
    }
    return meaningful
  }

  private async cancelAccountEntryAfterAnonymousGate(): Promise<void> {
    this.applyLocalSessionExit()
    await clearPersistedBrowserAuthState()
    this.commands.ensurePaletteVisible()
    this.commands.setStatus('login cancelled', 'error')
    this.requestRender()
  }

  private promptDeactivatedLocalFound(labels: string[]): Promise<'export-and-continue' | 'delete-and-continue' | 'cancel'> {
    if (this.deactivatedLocalFoundPrompt) {
      return this.deactivatedLocalFoundPrompt
    }
    this.deactivatedLocalFoundLabels = labels
    this.deactivatedLocalFoundPrompt = new Promise((resolve) => {
      this.deactivatedLocalFoundResolver = (choice) => {
        this.deactivatedLocalFoundResolver = undefined
        this.deactivatedLocalFoundPrompt = undefined
        this.deactivatedLocalFoundLabels = []
        resolve(choice)
      }
      this.openAuthModal('deactivated-local-found')
    })
    return this.deactivatedLocalFoundPrompt
  }

  private markVerifiedProvisioningPending(): void {
    const userId = this.authSession?.user?.id?.trim()
    this.verifiedProvisioningUserId = userId || undefined
    if (userId) {
      localStorage.setItem(VERIFIED_PROVISIONING_USER_KEY, userId)
      return
    }
    localStorage.removeItem(VERIFIED_PROVISIONING_USER_KEY)
  }

  private shouldStayInVerifiedProvisioningLane(): boolean {
    const userId = this.authSession?.user?.id?.trim()
    if (!userId) return false
    const persistedUserId = (localStorage.getItem(VERIFIED_PROVISIONING_USER_KEY) ?? '').trim() || undefined
    const expectedUserId = this.verifiedProvisioningUserId ?? persistedUserId
    return expectedUserId === userId
  }

  private clearVerifiedProvisioningLatch(): void {
    this.verifiedProvisioningUserId = undefined
    this.recoveredVerifiedProvisioningUserId = undefined
    localStorage.removeItem(VERIFIED_PROVISIONING_USER_KEY)
  }

  private async shouldPreserveLocalOnAuthExit(): Promise<boolean> {
    if (this.isIndexSpace()) return false
    const localBlocks = this.document.getStateSnapshot()
    const localIdentity = this.getSpaceIdentity(localBlocks)
    if (this.shouldTrustActiveSyncLane(localIdentity)) return false
    const localIsSeedOnly = await this.isLocalSeedOnly(localBlocks)
    return hasMeaningfulLocalDraft({
      blocks: localBlocks,
      isSeedOnly: localIsSeedOnly
    })
  }

  private async resolveAccountUnmountAuthority(): Promise<AccountUnmountAuthority | undefined> {
    return this.storage.getDatabaseMemory().resolveAccountUnmountAuthority()
  }

  private unresolvedAccountMemoryDebt(): UnsafeOwnerShelfRow[] {
    return [{
      meta: {
        owner_id: null,
        space_id: 'Account Memory',
        space_slug: 'Account Memory',
        space_ed: '',
        size_bytes: 0,
        published: false,
        sync_at: null,
        deleted_at: null,
        dirty: true,
        dirty_since: null,
        payload_state: 'missing',
        payload_error_at: null,
        last_opened_at: null,
        last_local_save_at: null,
        last_sync_ok_at: null,
        in_flight_tab_id: null,
        in_flight_started_at: null
      },
      reason: 'dirty'
    }]
  }

  private async listUnsafeSyncExitRows(
    authority: AccountUnmountAuthority
  ): Promise<UnsafeOwnerShelfRow[]> {
    try {
      await this.flushPendingActiveSyncExitIfNeeded(authority)
      const { seedProofs } = await this.inspectProductShelf(authority)
      const proofBySpaceId = new Map(seedProofs.map(proof => [proof.space_id, proof]))
      const unsafe = await this.storage.listUnsafeVolumeRows(authority)
      return unsafe.filter(row => {
        const proof = proofBySpaceId.get(row.meta.space_id)
        return !proof
          || proof.space_ed !== row.meta.space_ed
          || !hasCleanSeedMetadata(row.meta)
      })
    } catch {
      return this.unresolvedAccountMemoryDebt()
    }
  }

  private labelUnsafeSyncExitRow(row: UnsafeOwnerShelfRow): string {
    const slug = row.meta.space_slug?.trim()
    if (slug) return this.normalizeSpaceSlug(slug, row.meta.space_id)
    if (row.meta.space_id === 'Account Memory') return row.meta.space_id
    return this.spaceIdToDefaultSlug(row.meta.space_id)
  }

  private async blockUnsafeSyncExitIfNeeded(
    surface: 'logout' | 'account-delete',
    authority: AccountUnmountAuthority
  ): Promise<boolean> {
    const unsafe = await this.listUnsafeSyncExitRows(authority)
    if (!unsafe.length) {
      if (surface === 'logout') this.pendingSyncExitAuthority = undefined
      return false
    }
    if (surface === 'logout') {
      this.pendingSyncExitAuthority = authority
      this.syncExitUnsafeLabels = unsafe.map(row => this.labelUnsafeSyncExitRow(row))
      this.openAuthModal('sync-exit-unsafe')
      this.commands.ensurePaletteVisible()
      this.commands.setStatus('logout paused, memory not in Account sync yet', 'error')
      return true
    }
    const message = surface === 'account-delete'
      ? 'sync/export pending local changes before account delete'
      : 'sync/export pending local changes before logout'
    this.commands.setStatus(message, 'error')
    this.commands.ensurePaletteVisible()
    if (this.authModalMode) {
      this.setAuthModalFeedback(message, 'error')
    }
    return true
  }

  private async preserveLocalAuthExitLanding(): Promise<void> {
    this.input.stopEditing()
    this.exitSelectConnectedMode({ preserveSelection: true })
    this.exitCopyMode({ preserveSelection: true })
    this.exitPasteMode()
    this.exitDeleteMode({ preserveSelection: true })
    this.exitMultiSelectMode({ preserveNextClick: true })
    this.resetInteractionState()
    this.history = new History()
    this.document.clearSelection()
    const localBlocks = this.document.getStateSnapshot()
    const localIdentity = this.getSpaceIdentity(localBlocks)
    this.seedClockFromTimeline(localBlocks, this.storage.getMetaBlock())
    this.setActiveSpace(localIdentity.spaceId, this.spaceIdToDefaultSlug(localIdentity.spaceId))
    this.setAppState('edit')
    this.commitWorkbench(localBlocks)
    this.forceRenderNow()
  }

  private async abandonVerifiedProvisioningSession(): Promise<void> {
    if (this.logoutInProgress) return
    const preserveLocal = await this.shouldPreserveLocalOnAuthExit()
    this.clearVerifiedProvisioningLatch()
    this.authSession = null
    this.applySessionLostState()
    this.clearCachedAuthEmailState()
    this.clearOtpFlowState()
    this.setSyncDisabled()
    this.syncReachability = 'unknown'
    this.localSyncEchoSuppress.clear()
    this.pendingRoute = undefined
    this.pendingIndexRefresh = false
    if (preserveLocal) {
      await this.preserveLocalAuthExitLanding()
    } else {
      await this.landInLocalBaseline()
    }
    this.syncCloudIdentityFromRuntime()
    this.updateAccountBar()
    this.commands.setStatus('memory ready', 'success')
    this.requestRender()
    if (!supabase) return
    // Fire-and-forget: app state is already reset above; signOut may wait for
    // a cross-tab Web Lock, so not awaited here to avoid a hung promise.
    void supabase.auth.signOut({ scope: 'local' }).catch(() => {})
  }

  private promptOpenFileGuard(mode: OpenFileGuardMode): Promise<boolean> {
    return new Promise((resolve) => {
      this.openFileGuardResolver = resolve
      this.openAuthModal(mode === 'protect-local-space' ? 'protect-local-space-open-file' : 'protect-current-space-open-file')
    })
  }

  private async resolveOpenFileGuardMode(): Promise<OpenFileGuardMode | undefined> {
    if (this.isIndexSpace()) return undefined
    const localBlocks = this.document.getStateSnapshot()
    const localIsSeedOnly = await this.isLocalSeedOnly(localBlocks)
    const hasReplaceRisk = hasMeaningfulLocalDraft({
      blocks: localBlocks,
      isSeedOnly: localIsSeedOnly
    })
    const snapshot = this.buildAccountSnapshot()
    const runtimePolicy = resolveRuntimePolicy({
      snapshot,
      connectivity: this.resolveConnectivityState(),
      lane: this.resolveCurrentLaneState(),
      space: this.resolveSpaceKind()
    })
    return resolveOpenFileGuard({
      hasLocalReplaceRisk: hasReplaceRisk,
      hasSyncReplaceRisk: hasReplaceRisk,
      hasMountedAccount: hasMountedAccount(snapshot),
      isSyncPaired: this.isSyncPaired,
      cloudPosture: runtimePolicy.cloudPosture
    })
  }

  private async isLocalSeedOnly(localBlocks: SpaceEntry[]): Promise<boolean> {
    const seedBlocks = await this.storage.loadSeedBlocks()
    if (!seedBlocks) {
      return localBlocks.length === 0
    }
    return serializeSeedComparableSpace(seedBlocks)
      === serializeSeedComparableSpace(localBlocks, this.storage.getMetaBlock())
  }

  private async handleEmailSubmit(email: string): Promise<void> {
    if (!email) {
      const feedback = 'enter your email'
      this.commands.setStatus(feedback, 'error')
      this.setAuthModalFeedback(feedback)
      return
    }
    await this.beginHumanCheckedOtpSend(email, 'initial')
  }

  private async handleOtpResend(): Promise<void> {
    const email = this.authCheckoutEmail?.trim() ?? ''
    if (!email) {
      this.commands.setStatus('email required', 'error')
      this.transitionAuthLoginFlow({ type: 'AUTH_CHANGE_EMAIL' })
      return
    }
    if (this.getOtpResendRemainingSeconds() > 0) {
      this.refreshOtpResendUi()
      return
    }
    await this.beginHumanCheckedOtpSend(email, 'resend')
  }

  private async handleOtpVerify(): Promise<void> {
    if (!supabase) return
    if (!this.authModalInput) return
    const token = this.authModalInput.value.trim()
    if (!token) {
      const feedback = 'enter the code'
      this.commands.setStatus(feedback, 'error')
      this.setAuthModalFeedback(feedback)
      this.authModalInput.focus()
      return
    }
    const email = this.authCheckoutEmail?.trim() ?? ''
    if (!email) {
      this.commands.setStatus('email required', 'error')
      this.transitionAuthLoginFlow({ type: 'AUTH_CHANGE_EMAIL' })
      return
    }
    this.setAuthModalWaiting(true)
    try {
      const { data, error } = await supabase.auth.verifyOtp({ email, token, type: 'email' })
      if (error) {
        const feedback = 'code did not work, try again'
        this.commands.setStatus(feedback, 'error')
        this.setAuthModalFeedback(feedback)
        return
      }
      const settledSession = await this.confirmVerifiedAuthSession(data.session ?? null)
      if (!settledSession) {
        this.commands.setStatus('login not ready, try again', 'error')
        this.setAuthModalFeedback('login not ready, try again')
        return
      }
      // First of two deliberate clears: completeAuthLoginAtAccountLanding runs the
      // other. Idempotent, so neither call is a duplicate to tidy away.
      this.clearOtpFlowState({ preserveCooldown: true })
      this.authCheckoutEmail = settledSession.user.email?.trim() || email
      await this.refreshAuthProfile('otp-verify', {
        preferredSession: settledSession,
        acceptanceSource: 'otp',
        commitAccountEntry: true
      })
    } catch (error) {
      console.warn('[auth] OTP verification failed', error)
      const feedback = 'login not ready, try again'
      this.commands.setStatus(feedback, 'error')
      this.setAuthModalFeedback(feedback)
    } finally {
      this.setAuthModalWaiting(false)
    }
  }

  private async confirmVerifiedAuthSession(initialSession: Session | null): Promise<Session | null> {
    if (!supabase) return null
    if (initialSession?.user?.id) return initialSession

    // Give Supabase one small settle beat before trusting the first post-OTP read.
    await this.wait(120)
    const { data, error } = await supabase.auth.getSession()
    if (error) return null
    return data.session ?? null
  }

  private async handleCheckoutChoice(interval: 'yearly' | 'monthly'): Promise<void> {
    const email = this.authCheckoutEmail?.trim() ?? ''
    if (!email) {
      this.commands.setStatus('email required', 'error')
      return
    }
    this.setAuthModalWaiting(true)
    try {
      localStorage.setItem('lilo.auth.checkout-email', email)
      localStorage.setItem('lilo.auth.checkout-pending', '1')
      const url = await this.createStripeCheckout(email, interval)
      if (url) {
        this.dispatchAccountMachine({ type: 'REQUEST_OK', intent: 'ACTIVATE_SYNC' })
        window.location.href = url
        return
      }
      this.dispatchAccountMachine({ type: 'REQUEST_FAIL', intent: 'ACTIVATE_SYNC', error: 'checkout_failed' })
      this.commands.setStatus('stripe checkout failed', 'error')
    } finally {
      this.setAuthModalWaiting(false)
    }
  }

  private async handleUsernameSubmit(username: string): Promise<void> {
    const normalized = normalizeHandle(username)
    if (!normalized) {
      this.setAuthModalFeedback('username required')
      return
    }
    const verdict = validateUsernameHandle(normalized)
    if (!verdict.valid) {
      this.setAuthModalFeedback(verdict.error)
      return
    }
    if (!this.authSession) {
      this.setAuthModalFeedback('login required')
      return
    }
    this.setAuthModalWaiting(true)
    try {
      const result = await this.saveUsername(normalized)
      if (result.ok) {
        this.authUsername = result.username
        const passport = this.storage.getMountedPassport()
        if (
          passport
          && this.authSession?.user.id === passport.owner_id
          && (
            this.authAccountState === 'sync'
            || this.authAccountState === 'deactivated'
          )
        ) {
          await this.storage.verifyMountedAccountPassport(
            passport.owner_id,
            this.toLocalAccountProfile({
              username: result.username,
              accountState: this.authAccountState,
              isSync: this.authIsSync,
              stripeCancelAtPeriodEnd: this.authStripeCancelAtPeriodEnd,
              stripeCurrentPeriodEnd: this.authStripeCurrentPeriodEnd
            })
          )
        }
        this.updateAccountBar()
        this.commands.setStatus('username saved', 'success')
        this.setAuthModalFeedback()
        if (this.modalFlow.flow === 'auth-login') {
          this.dispatchModalFlow({ type: 'AUTH_USERNAME_SAVED' })
          this.finishAuthLoginFlow()
        } else if (this.modalFlow.flow === 'sync-activation') {
          this.dispatchModalFlow({ type: 'SYNC_ACTIVATION_USERNAME_SAVED' })
          this.finishSyncActivationFlow()
        }
        this.closeAuthModal()
        await this.initSync()
      } else {
        this.setAuthModalFeedback(this.mapUsernameSaveErrorToFeedback(result.error))
      }
    } finally {
      this.setAuthModalWaiting(false)
    }
  }

  private clearPostCheckoutTimer(): void {
    if (this.postCheckoutTimer) {
      clearTimeout(this.postCheckoutTimer)
      this.postCheckoutTimer = undefined
    }
  }

  private openPostCheckoutLandingIfStillActivating(): void {
    this.checkoutActivatingShownAt = undefined
    if (this.authModalMode !== 'checkout-activating') return
    this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_PROFILE_ACTIVE' })
  }

  private schedulePostCheckoutFreshLoginLanding(): void {
    this.clearPostCheckoutTimer()
    this.setAuthModalWaiting(true)
    const cooldownMs = this.getOtpResendRemainingSeconds() * 1000
    const shownAt = this.checkoutActivatingShownAt ?? Date.now()
    const minBeatMs = Math.max(0, CHECKOUT_ACTIVATING_MIN_MS - (Date.now() - shownAt))
    const delayMs = Math.max(cooldownMs, minBeatMs)
    if (delayMs > 0) {
      this.postCheckoutTimer = window.setTimeout(() => {
        this.openPostCheckoutFreshLoginLandingIfStillActivating()
      }, delayMs)
      return
    }
    this.openPostCheckoutFreshLoginLandingIfStillActivating()
  }

  private openPostCheckoutFreshLoginLandingIfStillActivating(): void {
    this.checkoutActivatingShownAt = undefined
    if (this.authModalMode !== 'checkout-activating') return
    this.setAuthModalWaiting(false)
    this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_READY_FOR_FRESH_LOGIN' })
  }

  private async handlePostCheckoutFreshLogin(): Promise<void> {
    if (this.rejectLoginWhileMounted()) return
    const email = this.authCheckoutEmail?.trim()
      || (localStorage.getItem('lilo.auth.checkout-email') ?? '').trim()
    if (!email) {
      this.commands.setStatus('email required', 'error')
      this.openAuthLoginFlow('email')
      return
    }
    if (this.getOtpResendRemainingSeconds() > 0) {
      this.openSyncActivationFlow('activating')
      this.checkoutActivatingShownAt = Date.now()
      this.schedulePostCheckoutFreshLoginLanding()
      return
    }
    this.clearPostCheckoutTimer()
    this.authCheckoutEmail = email
    this.finishSyncActivationFlow()
    await this.beginHumanCheckedOtpSend(email, 'initial')
  }

  private async handlePostCheckout(): Promise<void> {
    if (!supabase) return
    this.clearPostCheckoutTimer()
    this.setAuthModalWaiting(true)
    this.setAccountIdentityInFlight(true)
    const { data, error } = await supabase.auth.getSession()
    if (error || !data.session) {
      this.authSession = null
      this.applySessionLostState()
      this.setAccountIdentityInFlight(false)
      this.setSyncDisabled()
      this.updateAccountBar()
      this.commands.setStatus('login required', 'error')
      this.setAuthModalWaiting(false)
      this.openAuthModal('email')
      this.checkoutActivatingShownAt = undefined
      return
    }

    const session = data.session
    const candidate = await this.runSessionAcceptanceGateway({
      source: 'stripe-poll',
      session
    })
    if (!candidate.accepted) {
      this.setAccountIdentityInFlight(false)
      this.setAuthModalWaiting(false)
      this.checkoutActivatingShownAt = undefined
      return
    }
    let profile: ProfileStatus = { accountState: 'none', isSync: false, stripeCancelAtPeriodEnd: false, error: true }
    try {
      profile = await this.retryFetch(() => this.fetchProfileStatusOrThrow(session.user.id))
    } catch {
      profile = { accountState: 'none', isSync: false, stripeCancelAtPeriodEnd: false, error: true }
    }
    if (profile.error) {
      await this.runSessionAcceptanceGateway({
        source: 'stripe-poll',
        session,
        profile: null
      })
      this.setAccountIdentityInFlight(false)
      this.postCheckoutAttempts += 1
      if (this.postCheckoutAttempts <= POST_CHECKOUT_MAX_ATTEMPTS) {
        this.commands.setStatus('sync activating...', 'success')
        this.postCheckoutTimer = window.setTimeout(() => {
          void this.handlePostCheckout()
        }, 1500)
        return
      }
      this.postCheckoutAttempts = 0
      this.setAuthModalWaiting(false)
      this.commands.ensurePaletteVisible()
      this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_TIMEOUT' })
      this.checkoutActivatingShownAt = undefined
      return
    }

    if (profile.missing) {
      await this.runSessionAcceptanceGateway({
        source: 'stripe-poll',
        session,
        profile: null
      })
      this.setAccountIdentityInFlight(false)
      this.postCheckoutAttempts += 1
      if (this.postCheckoutAttempts <= POST_CHECKOUT_MAX_ATTEMPTS) {
        this.commands.setStatus('sync activating...', 'success')
        this.postCheckoutTimer = window.setTimeout(() => {
          void this.handlePostCheckout()
        }, 1500)
        return
      }
      this.postCheckoutAttempts = 0
      this.setAuthModalWaiting(false)
      this.commands.ensurePaletteVisible()
      this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_TIMEOUT' })
      this.checkoutActivatingShownAt = undefined
      return
    }

    if (!profile?.isSync) {
      await this.settleSessionAtMountSeam({
        source: 'stripe-poll',
        session,
        profile,
        commitAccountEntry: false
      })
      this.setAccountIdentityInFlight(false)
      this.postCheckoutAttempts += 1
      if (this.postCheckoutAttempts <= POST_CHECKOUT_MAX_ATTEMPTS) {
        this.commands.setStatus('sync activating...', 'success')
        this.postCheckoutTimer = window.setTimeout(() => {
          void this.handlePostCheckout()
        }, 1500)
        return
      }
      this.postCheckoutAttempts = 0
      this.setAuthModalWaiting(false)
      this.commands.ensurePaletteVisible()
      this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_TIMEOUT' })
      this.checkoutActivatingShownAt = undefined
      return
    }

    this.postCheckoutAttempts = 0
    const acceptance = await this.settleSessionAtMountSeam({
      source: 'stripe-poll',
      session,
      profile,
      commitAccountEntry: false
    })
    if (!acceptance.accepted) {
      this.setAccountIdentityInFlight(false)
      this.setAuthModalWaiting(false)
      this.checkoutActivatingShownAt = undefined
      return
    }
    this.setAccountIdentityInFlight(false)
    this.updateAccountBar()
    this.setAuthModalWaiting(false)
    const shownAt = this.checkoutActivatingShownAt
    if (shownAt) {
      const elapsed = Date.now() - shownAt
      if (elapsed < CHECKOUT_ACTIVATING_MIN_MS) {
        this.postCheckoutTimer = window.setTimeout(() => {
          this.openPostCheckoutLandingIfStillActivating()
        }, CHECKOUT_ACTIVATING_MIN_MS - elapsed)
        return
      }
    }
    this.openPostCheckoutLandingIfStillActivating()
  }

  private async handlePostCheckoutContinue(): Promise<void> {
    if (!supabase) return
    this.setAuthModalWaiting(true)
    this.setAccountIdentityInFlight(true)
    const { data, error } = await supabase.auth.getSession()
    if (error || !data.session) {
      this.authSession = null
      this.applySessionLostState()
      this.setAccountIdentityInFlight(false)
      this.setSyncDisabled()
      this.updateAccountBar()
      this.commands.setStatus('login required', 'error')
      this.setAuthModalWaiting(false)
      this.openAuthModal('email')
      return
    }

    const session = data.session
    const candidate = await this.runSessionAcceptanceGateway({
      source: 'stripe-continue',
      session
    })
    if (!candidate.accepted) {
      this.setAccountIdentityInFlight(false)
      this.setAuthModalWaiting(false)
      return
    }
    let profile: ProfileStatus = { accountState: 'none', isSync: false, stripeCancelAtPeriodEnd: false, error: true }
    try {
      profile = await this.retryFetch(() => this.fetchProfileStatusOrThrow(session.user.id))
    } catch {
      profile = { accountState: 'none', isSync: false, stripeCancelAtPeriodEnd: false, error: true }
    }
    if (profile.error) {
      await this.runSessionAcceptanceGateway({
        source: 'stripe-continue',
        session,
        profile: null
      })
      this.setAccountIdentityInFlight(false)
      this.setAuthModalWaiting(false)
      this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_RETRY' })
      void this.handlePostCheckout()
      return
    }

    if (profile.missing) {
      await this.runSessionAcceptanceGateway({
        source: 'stripe-continue',
        session,
        profile: null
      })
      this.setAccountIdentityInFlight(false)
      this.setAuthModalWaiting(false)
      this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_RETRY' })
      void this.handlePostCheckout()
      return
    }

    if (!profile?.isSync) {
      await this.settleSessionAtMountSeam({
        source: 'stripe-continue',
        session,
        profile,
        commitAccountEntry: false
      })
      this.setAccountIdentityInFlight(false)
      this.setAuthModalWaiting(false)
      this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_RETRY' })
      void this.handlePostCheckout()
      return
    }

    const acceptance = await this.settleSessionAtMountSeam({
      source: 'stripe-continue',
      session,
      profile,
      commitAccountEntry: true
    })
    if (!acceptance.accepted || !acceptance.passport) {
      this.setAccountIdentityInFlight(false)
      this.setAuthModalWaiting(false)
      return
    }
    this.setAccountIdentityInFlight(false)
    this.updateAccountBar()
    if (!this.authUsername) {
      this.setSyncDisabled()
      this.setAuthModalWaiting(false)
      this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_MISSING_USERNAME' })
      return
    }
    this.setAuthModalWaiting(false)
    await this.initSync()
    await this.ensureAuthenticatedSyncLane()
    if (this.authModalMode === 'checkout-landing') {
      this.finishSyncActivationFlow()
      this.closeAuthModal()
    }
  }

  private async handleLogout(opts?: {
    forceLocalBaseline?: boolean
    authority?: AccountUnmountAuthority
  }): Promise<void> {
    if (this.logoutInProgress) return
    let authority = opts?.authority
    try {
      authority ??= await this.resolveAccountUnmountAuthority()
    } catch {
      this.commands.setStatus('logout paused, memory authority unavailable', 'error')
      return
    }
    if (opts?.forceLocalBaseline && !authority) {
      this.commands.setStatus('logout paused, mounted memory changed', 'error')
      return
    }
    if (
      !opts?.forceLocalBaseline
      && authority
      && await this.blockUnsafeSyncExitIfNeeded('logout', authority)
    ) {
      return
    }
    this.dispatchAccountMachine({ type: 'INTENT', intent: 'LOGOUT' })
    this.logoutInProgress = true
    this.setAccountAirlockInFlight(true)
    this.setAuthModalWaiting(true)
    if (this.localFoundResolver) {
      const resolve = this.localFoundResolver
      this.localFoundResolver = undefined
      resolve('delete')
    }
    const preserveLocal =
      !authority
      && !opts?.forceLocalBaseline
      && await this.shouldPreserveLocalOnAuthExit()
    try {
      if (authority) {
        let unmountResult: AccountUnmountResult
        try {
          unmountResult = await this.unmountAccountForExit(
            authority,
            opts?.forceLocalBaseline ? 'confirmed' : 'guarded'
          )
        } catch {
          this.dispatchAccountMachine({
            type: 'REQUEST_FAIL',
            intent: 'LOGOUT',
            error: 'unmount_failed'
          })
          this.commands.setStatus('logout paused, memory airlock failed', 'error')
          return
        }
        if (!unmountResult.ok) {
          this.dispatchAccountMachine({
            type: 'REQUEST_FAIL',
            intent: 'LOGOUT',
            error: unmountResult.reason
          })
          if (unmountResult.reason === 'unsafe') {
            this.pendingSyncExitAuthority = authority
            this.syncExitUnsafeLabels = unmountResult.unsafe_rows.map(row => (
              this.labelUnsafeSyncExitRow(row)
            ))
            this.openAuthModal('sync-exit-unsafe')
            this.commands.ensurePaletteVisible()
            this.commands.setStatus('logout paused, memory not in Account sync yet', 'error')
          } else {
            this.commands.setStatus('logout paused, mounted memory changed', 'error')
          }
          return
        }
        this.pendingSyncExitAuthority = undefined
        try {
          await this.finishAccountExitLocalLanding({
            beforeAuthClear: () => this.broadcastAccountExitSignal(authority, 'logout')
          })
        } catch (error) {
          console.warn('[auth] Account unmounted but Anonymous reset did not finish', error)
        }
      } else {
        try {
          if (preserveLocal) {
            this.applyLocalSessionExit()
            await this.clearLogoutPersistenceArtifacts()
            await this.preserveLocalAuthExitLanding()
          } else {
            this.applyLocalSessionExit()
            await this.clearLogoutPersistenceArtifacts()
            await this.landInLocalBaseline({ skipAuthenticatedSyncLane: true })
          }
        } catch {
          // Auth is already clean; cold boot can finish the provisional exit landing.
        }
      }
      this.dispatchAccountMachine({ type: 'REQUEST_OK', intent: 'LOGOUT' })
      this.updateAccountBar()
      this.commands.setStatus('logged out', 'success')
      this.commands.closePalette()
      this.requestRender()
    } finally {
      this.logoutInProgress = false
      this.setAccountAirlockInFlight(false)
      this.setAuthModalWaiting(false)
    }
  }

  private refreshAuthProfile(
    trigger: AuthProfileRefreshTrigger = 'auth-state-change',
    options: AuthProfileRefreshOptions = {}
  ): Promise<void> {
    // A queued caller still waits for the shared drain. OTP verification and
    // the auth observer may request the same landing in either order.
    return this.authProfileRefreshQueue.enqueue({ trigger, options })
  }

  private async runAuthProfileRefresh(
    trigger: AuthProfileRefreshTrigger,
    options: AuthProfileRefreshOptions
  ): Promise<void> {
    if (this.logoutInProgress) return
    if (!supabase) return
    const shouldDriveAccountAirlock = Boolean(options.preferAccountAirlock && !this.isInitialSync)
    if (shouldDriveAccountAirlock) {
      this.setAccountAirlockInFlight(true)
    }
    this.setAccountIdentityInFlight(true)
    const acceptanceSource = this.resolveSessionAcceptanceSource(
      trigger,
      options.acceptanceSource
    )
    const sessionResult = await this.resolveAuthProfileSession(options)
    const error = sessionResult.error
    const session = sessionResult.session
    if (error || !session) {
      await this.handleAuthRefreshSessionLost(shouldDriveAccountAirlock)
      return
    }
    const candidate = await this.runSessionAcceptanceGateway({
      source: acceptanceSource,
      session
    })
    if (!candidate.accepted) {
      this.setAccountIdentityInFlight(false)
      this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
      return
    }
    const profile = await this.fetchProfileStatusForTrigger(session.user.id, trigger)
    if (profile.error || profile.missing) {
      const unresolved = await this.runSessionAcceptanceGateway({
        source: acceptanceSource,
        session,
        profile: null,
        preserveUnresolvedProfileRetry: true
      })
      if (!unresolved.accepted) {
        this.setAccountIdentityInFlight(false)
        this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
        return
      }
      // The mounted passport remains local identity while cloud authority is
      // unresolved. Only provisional entry below owns the bounded retry UI.
      if (unresolved.passport?.owner_id === session.user.id.trim()) {
        this.clearUnresolvedProfileRetry()
        this.setAccountIdentityInFlight(false)
        this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
        this.requestRender()
        return
      }
      if (this.handleVerifiedProvisioningProfileGap(profile, shouldDriveAccountAirlock)) {
        return
      }
      if (this.isStripeReturnCallbackPending()) {
        this.setAccountIdentityInFlight(false)
        this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
        return
      }
      this.applyUnresolvedProfileState(shouldDriveAccountAirlock)
      this.commands.setStatus('memory ready', 'success')
      this.commands.ensurePaletteVisible()
      this.closeAuthModal()
      this.requestRender()
      return
    }
    const acceptance = await this.settleSessionAtMountSeam({
      source: acceptanceSource,
      session,
      profile,
      commitAccountEntry: options.commitAccountEntry !== false
    })
    if (!acceptance.accepted) {
      this.setAccountIdentityInFlight(false)
      this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
      return
    }

    if (!profile.isSync) {
      this.authCheckoutEmail = session.user.email ?? undefined
      this.setAccountIdentityInFlight(false)
      this.updateAccountBar()
      if (profile.accountState === 'deactivated') {
        this.setSyncDisabled()
        const enteredShelf = await this.enterDeactivatedShelfFromLocalMemory()
        if (!enteredShelf) {
          this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
          this.reportCloudUnreachable()
          return
        }
      } else if (acceptance.passport) {
        // A verified-none response revokes cloud authority, never the mounted
        // passport or its local lifecycle.
        this.setSyncDisabled()
        this.commands.setStatus('memory ready', 'success')
      } else {
        this.setSyncDisabled()
        this.commands.ensurePaletteVisible()
        if (this.checkoutCancelPending) {
          this.checkoutCancelPending = false
          this.openSyncSubscriptionOptions()
        } else {
          this.openSyncSubscriptionThreshold()
        }
      }
      if (shouldDriveAccountAirlock) {
        this.setAccountAirlockInFlight(false)
      }
      return
    }
    this.setAccountIdentityInFlight(false)
    this.updateAccountBar()
    if (!acceptance.passport) {
      this.setSyncDisabled()
      this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
      return
    }
    if (this.handleSyncProfileMissingUsername(shouldDriveAccountAirlock)) {
      return
    }
    if (acceptance.freshMountLandingSpaceId) {
      await this.finishFreshAccountLanding(acceptance.freshMountLandingSpaceId)
      this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
      return
    }
    if (this.pendingIndexRefresh) {
      this.isSyncPaired = true
      this.setSyncEngineState('idle', 1)
      this.applySyncUiState()
      this.commands.setSyncIndicatorVisible(true)
      this.openIndexSurfaceDeterministic()
      this.pendingIndexRefresh = false
      this.scheduleOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
      if (shouldDriveAccountAirlock) {
        this.setAccountAirlockInFlight(false)
      }
      return
    }
    if (this.shouldPreserveIndexContextOnProfileRefresh()) {
      this.isSyncPaired = true
      this.syncSpaceId = undefined
      this.syncSpaceSlug = undefined
      this.setSyncEngineState('idle', 1)
      this.applySyncUiState()
      this.commands.setSyncIndicatorVisible(true)
      this.scheduleOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
      if (shouldDriveAccountAirlock) {
        this.setAccountAirlockInFlight(false)
      }
      return
    }
    await this.runCloudLaneInit({
      startupAccountWorkbenchResolution: trigger === 'startup'
    })
    if (this.pendingRoute) {
      await this.handlePendingRoute()
    }
    if (shouldDriveAccountAirlock) {
      this.setAccountAirlockInFlight(false)
    }
  }

  private async finishFreshAccountLanding(spaceId: string): Promise<boolean> {
    this.isSyncPaired = true
    this.setSyncEngineState('idle', 1)
    this.applySyncUiState()
    this.commands.setSyncIndicatorVisible(true)
    const authority = this.storage.getVolumeAuthority()
    const opened = Boolean(
      authority
      && await this.enterSpaceFromIndex(spaceId, {
        skipFlush: true,
        authority
      })
    )
    if (opened) {
      this.commands.closePalette()
    } else {
      this.commands.ensurePaletteVisible()
    }
    this.scheduleOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
    return opened
  }

  private finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock: boolean): void {
    if (shouldDriveAccountAirlock) {
      this.setAccountAirlockInFlight(false)
    }
  }

  private async handleAuthRefreshSessionLost(shouldDriveAccountAirlock: boolean): Promise<void> {
    const hadPendingRoute = Boolean(this.pendingRoute)
    await this.handlePassiveSessionLoss()
    this.setAccountIdentityInFlight(false)
    if (hadPendingRoute && !this.storage.getMountedPassport()) {
      this.commands.ensurePaletteVisible()
      this.openAuthModal('email')
    }
    this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
  }

  private handleVerifiedProvisioningProfileGap(
    profile: ProfileStatus,
    shouldDriveAccountAirlock: boolean
  ): boolean {
    if (!profile.missing || !this.authSession) {
      return false
    }
    if (this.isStripeReturnCallbackPending()) {
      this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
      return true
    }
    this.markVerifiedProvisioningPending()
    this.authCheckoutEmail = this.authSession.user.email ?? this.authCheckoutEmail
    this.authUsername = undefined
    this.authAccountState = 'none'
    this.authIsSync = false
    this.settleCloudIdentity({
      username: null,
      accountState: 'none',
      isSyncPlan: false
    })
    this.clearStripeLifecycleProfile()
    this.setAccountIdentityInFlight(false)
    this.setSyncDisabled()
    this.syncAccountMachineFromSnapshot()
    this.updateAccountBar()
    this.commands.ensurePaletteVisible()
    if (this.checkoutCancelPending) {
      this.checkoutCancelPending = false
      this.openSyncSubscriptionOptions()
    } else {
      this.openSyncSubscriptionThreshold()
    }
    this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
    return true
  }

  private handleSyncProfileMissingUsername(shouldDriveAccountAirlock: boolean): boolean {
    if (this.authUsername) return false
    this.setSyncDisabled()
    if (this.isStripeReturnCallbackPending()) {
      this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
      return true
    }
    if (this.modalFlow.flow === 'auth-login') {
      this.transitionAuthLoginFlow({ type: 'AUTH_PROFILE_MISSING_USERNAME' })
    } else if (this.modalFlow.flow === 'sync-activation') {
      this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_MISSING_USERNAME' })
    } else {
      this.openAuthModal('username')
    }
    this.finishAuthProfileRefreshAirlock(shouldDriveAccountAirlock)
    return true
  }

  private applyUnresolvedProfileState(clearAccountAirlock: boolean): void {
    this.pendingIndexRefresh = false
    this.authUsername = undefined
    this.authAccountState = 'none'
    this.authIsSync = false
    this.settleCloudIdentity({
      username: null,
      accountState: 'none',
      isSyncPlan: false
    })
    this.clearStripeLifecycleProfile()
    this.setAccountIdentityInFlight(false)
    this.setSyncDisabled()
    this.syncAccountMachineFromSnapshot()
    this.syncReachability = 'unknown'
    this.localSyncEchoSuppress.clear()
    this.refreshProfileRealtimeSubscription()
    this.scheduleUnresolvedProfileRetry()
    this.updateAccountBar()
    if (clearAccountAirlock) {
      this.setAccountAirlockInFlight(false)
    }
  }

  private async resolveAuthProfileSession(
    options: AuthProfileRefreshOptions
  ): Promise<{ session: Session | null, error: unknown }> {
    if (!supabase) return { session: null, error: new Error('supabase unavailable') }
    const preferredSession = typeof options.preferredSession === 'undefined'
      ? undefined
      : options.preferredSession
    if (preferredSession && !options.settleSession) {
      return { session: preferredSession, error: null }
    }
    const { data, error } = await supabase.auth.getSession()
    if (!error && data.session) {
      return { session: data.session, error: null }
    }
    if (preferredSession) {
      return { session: preferredSession, error: null }
    }
    return { session: null, error }
  }

  private async fetchProfileStatus(userId: string): Promise<ProfileStatus> {
    if (!supabase) return { accountState: 'none', isSync: false, stripeCancelAtPeriodEnd: false, error: true }
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('username, plan, stripe_current_period_end, stripe_cancel_at_period_end')
        .eq('id', userId)
        .maybeSingle()
      if (error) {
        if (!this.isMissingProfileLifecycleColumnError(error)) {
          this.logAuthProfileWarning('profiles select failed', error)
          return { accountState: 'none', isSync: false, stripeCancelAtPeriodEnd: false, error: true }
        }
        const fallback = await supabase
          .from('profiles')
          .select('username, plan')
          .eq('id', userId)
          .maybeSingle()
        if (fallback.error) {
          this.logAuthProfileWarning('profiles fallback select failed', fallback.error)
          return { accountState: 'none', isSync: false, stripeCancelAtPeriodEnd: false, error: true }
        }
        return this.parseProfileStatusRow(fallback.data)
      }
      return this.parseProfileStatusRow(data)
    } catch (error) {
      this.logAuthProfileWarning('profiles select threw', error)
      return { accountState: 'none', isSync: false, stripeCancelAtPeriodEnd: false, error: true }
    }
  }

  private async fetchProfileStatusOrThrow(userId: string): Promise<ProfileStatus> {
    if (!supabase) throw new Error('supabase unavailable')
    const { data, error } = await supabase
      .from('profiles')
      .select('username, plan, stripe_current_period_end, stripe_cancel_at_period_end')
      .eq('id', userId)
      .maybeSingle()
    if (error) {
      if (!this.isMissingProfileLifecycleColumnError(error)) throw error
      const fallback = await supabase
        .from('profiles')
        .select('username, plan')
        .eq('id', userId)
        .maybeSingle()
      if (fallback.error) throw fallback.error
      return this.parseProfileStatusRow(fallback.data)
    }
    return this.parseProfileStatusRow(data)
  }

  private parseProfileStatusRow(data: any): ProfileStatus {
    if (!data || typeof data !== 'object') {
      this.logAuthProfileWarning('profiles row missing or invalid', data)
      return {
        accountState: 'none',
        isSync: false,
        stripeCancelAtPeriodEnd: false,
        missing: true
      }
    }
    const username = typeof data?.username === 'string' ? data.username : undefined
    const plan = typeof data?.plan === 'string' ? data.plan : undefined
    const accountState = resolveAccountAccessState(plan)
    return {
      username: username && username.length ? username : undefined,
      accountState,
      isSync: hasSyncAccessFromPlan(plan),
      stripeCancelAtPeriodEnd: data?.stripe_cancel_at_period_end === true,
      stripeCurrentPeriodEnd: typeof data?.stripe_current_period_end === 'string'
        ? data.stripe_current_period_end
        : undefined,
      missing: false
    }
  }

  private async fetchProfileStatusForTrigger(
    userId: string,
    trigger: AuthProfileRefreshTrigger
  ): Promise<ProfileStatus> {
    let profile = await this.fetchProfileStatus(userId)
    const retryDelays = this.getColdStartProfileRetryDelays(trigger, profile)
    for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
      if (!profile.error && !profile.missing) {
        return profile
      }
      this.logAuthProfileWarning('retrying cold-start profile resolution', {
        trigger,
        attempt: attempt + 1,
        delayMs: retryDelays[attempt],
        profile
      })
      await this.wait(retryDelays[attempt])
      profile = await this.fetchProfileStatus(userId)
    }
    return profile
  }

  private getColdStartProfileRetryDelays(
    trigger: AuthProfileRefreshTrigger,
    profile: ProfileStatus
  ): readonly number[] {
    const isColdStartTrigger =
      trigger === 'startup'
      || trigger === 'navigation'
      || trigger === 'auth-state-change'
    if (!isColdStartTrigger) return []
    if (!profile.error && !profile.missing) return []
    return PROFILE_COLD_START_RETRY_DELAYS_MS
  }

  private isMissingProfileLifecycleColumnError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false
    const code = 'code' in error && typeof (error as { code?: unknown }).code === 'string'
      ? (error as { code: string }).code
      : ''
    const message = 'message' in error && typeof (error as { message?: unknown }).message === 'string'
      ? (error as { message: string }).message.toLowerCase()
      : ''
    return code === '42703'
      || message.includes('stripe_current_period_end')
      || message.includes('stripe_cancel_at_period_end')
  }

  private async handleStripeReturn(): Promise<void> {
    if (!authEnabled) return
    const url = new URL(window.location.href)
    const hashParams = new URLSearchParams(url.hash.replace('#', ''))
    const authError = hashParams.get('error')
    if (authError) {
      this.commands.ensurePaletteVisible()
      this.commands.setStatus('login required', 'error')
      this.openAuthModal('email')
      hashParams.delete('error')
      hashParams.delete('error_description')
      url.hash = hashParams.toString() ? `#${hashParams.toString()}` : ''
      window.history.replaceState({}, '', url.toString())
      return
    }
    const stripeStatus = url.searchParams.get('stripe')
    const sessionId = url.searchParams.get('session_id')?.trim() ?? ''
    if (stripeStatus === 'cancel') {
      this.clearPendingCheckoutState()
      this.checkoutCancelPending = true
      this.commands.ensurePaletteVisible()
      this.commands.setStatus('checkout cancelled', 'success')
      if (this.authSession) {
        this.openSyncSubscriptionOptions()
        this.checkoutCancelPending = false
      }
      url.searchParams.delete('stripe')
      url.searchParams.delete('session_id')
      window.history.replaceState({}, '', url.toString())
      return
    }
    const isStripeReturn =
      stripeStatus === 'success'
      && url.pathname === '/auth/callback'
      && sessionId.length > 0
    if (!isStripeReturn) return
    const verification = await this.verifyStripeCheckoutReturn(sessionId)
    if (!verification.ok) {
      this.clearCachedAuthEmailState()
      this.commands.ensurePaletteVisible()
      this.commands.setStatus('checkout return could not be verified', 'error')
      this.syncSubscriptionEmail()
      this.dispatchAccountMachine({ type: 'INTENT', intent: 'ACTIVATE_SYNC' })
      this.transitionSyncActivationFlow({ type: 'SYNC_ACTIVATION_VERIFICATION_FAILED' })
      url.searchParams.delete('stripe')
      url.searchParams.delete('session_id')
      window.history.replaceState({}, '', url.toString())
      return
    }
    const cachedCheckoutEmail = (localStorage.getItem('lilo.auth.checkout-email') ?? '').trim()
    const checkoutEmail = [
      verification.loginEmail,
      this.authCheckoutEmail,
      cachedCheckoutEmail
    ].map(value => value?.trim()).find(Boolean)
    this.authCheckoutEmail = checkoutEmail || undefined
    if (checkoutEmail) {
      localStorage.setItem('lilo.auth.checkout-email', checkoutEmail)
    }
    localStorage.removeItem('lilo.auth.checkout-pending')
    this.commands.ensurePaletteVisible()
    this.openSyncActivationFlow('activating')
    this.checkoutActivatingShownAt = Date.now()
    this.schedulePostCheckoutFreshLoginLanding()
    url.searchParams.delete('stripe')
    url.searchParams.delete('session_id')
    window.history.replaceState({}, '', url.toString())
  }

  private isStripeReturnCallbackPending(): boolean {
    if (this.authModalMode === 'checkout-activating' || this.authModalMode === 'checkout-landing') {
      return true
    }
    if (typeof window === 'undefined') return false
    const url = new URL(window.location.href)
    const stripeStatus = url.searchParams.get('stripe')
    const sessionId = url.searchParams.get('session_id')?.trim() ?? ''
    return stripeStatus === 'success'
      && url.pathname === '/auth/callback'
      && sessionId.length > 0
  }

  private clearCachedAuthEmailState(): void {
    this.authCheckoutEmail = undefined
    this.checkoutCancelPending = false
    this.clearPendingCheckoutState()
    localStorage.removeItem('lilo.auth.email')
  }

  private clearPendingCheckoutState(): void {
    localStorage.removeItem('lilo.auth.checkout-email')
    localStorage.removeItem('lilo.auth.checkout-pending')
  }

  // Note: flow originated as MagicLink; converted to OTP for PWA reliability.
  // Any legacy “magic link” naming is historical and can be ignored.
  private async sendOtpEmail(email: string, turnstileToken: string): Promise<OtpSendResult> {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, turnstileToken })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      if (data?.error === 'otp_throttled') {
        const retryAfterSeconds = typeof data?.retry_after_seconds === 'number'
          ? data.retry_after_seconds
          : this.parseOtpCooldownSeconds(res.headers.get('Retry-After') ?? undefined) ?? 60
        return { ok: false, throttled: true, cooldownSeconds: retryAfterSeconds }
      }
      if (data?.error === 'human_check_required') {
        return { ok: false, error: 'human_check_required' }
      }
      if (data?.error === 'human_check_failed') {
        return { ok: false, error: 'human_check_failed' }
      }
      if (data?.error === 'human_check_unavailable') {
        return { ok: false, error: 'human_check_unavailable' }
      }
      const message = typeof data?.message === 'string' ? data.message : undefined
      const cooldown = this.parseOtpCooldownSeconds(message)
      const lowered = message?.toLowerCase() ?? ''
      const throttled = lowered.includes('rate limit') || lowered.includes('security purposes') || lowered.includes('once every')
      if (throttled) {
        return { ok: false, throttled: true, cooldownSeconds: cooldown ?? 60 }
      }
      return { ok: false, error: 'otp_failed' }
    }
    return { ok: true, cooldownSeconds: 60 }
  }

  private async createStripeCheckout(email: string, interval: 'yearly' | 'monthly'): Promise<string | undefined> {
    if (!this.authSession) return undefined
    const res = await fetch('/api/stripe/checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.authSession.access_token}`
      },
      body: JSON.stringify({ email, interval })
    })
    if (!res.ok) return undefined
    const data = await res.json()
    return typeof data?.url === 'string' ? data.url : undefined
  }

  private async verifyStripeCheckoutReturn(sessionId: string): Promise<StripeCheckoutReturnResult> {
    let session = this.authSession
    if (!session && supabase) {
      const { data, error } = await supabase.auth.getSession()
      if (!error && data.session) {
        session = data.session
      }
    }
    if (session) {
      const candidate = await this.runSessionAcceptanceGateway({
        source: 'stripe-poll',
        session
      })
      if (!candidate.accepted) {
        return { ok: false }
      }
    }
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`
    }
    const res = await fetch('/api/stripe/checkout-session', {
      method: 'POST',
      headers,
      body: JSON.stringify({ sessionId })
    })
    if (!res.ok) return { ok: false }
    const data = await res.json().catch(() => ({}))
    return {
      ok: data?.ok === true,
      loginEmail: typeof data?.login_email === 'string' && data.login_email.trim()
        ? data.login_email.trim()
        : undefined
    }
  }

  private async saveUsername(
    username: string
  ): Promise<{ ok: true, username: string } | { ok: false, error: 'username_taken' | 'invalid_username' | 'server_unreachable' }> {
    if (!this.authSession) return { ok: false, error: 'server_unreachable' }
    const res = await fetch('/api/auth/set-username', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.authSession.access_token}`
      },
      body: JSON.stringify({ username })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      if (data?.error === 'username_taken') {
        return { ok: false, error: 'username_taken' }
      }
      if (data?.error === 'invalid_username') {
        return { ok: false, error: 'invalid_username' }
      }
      return { ok: false, error: 'server_unreachable' }
    }
    return { ok: true, username: typeof data?.username === 'string' ? data.username : username }
  }

  private mapUsernameSaveErrorToFeedback(
    error: 'username_taken' | 'invalid_username' | 'server_unreachable'
  ): string {
    if (error === 'username_taken') return 'username taken'
    if (error === 'invalid_username') return 'username format invalid'
    return 'server unreachable'
  }

  private setSyncDisabled(): void {
    this.pauseOwnerShelfMirror()
    this.isSyncPaired = false
    this.syncSpaceId = undefined
    this.syncSpaceSlug = undefined
    this.settleCloudIdentity({
      syncSpaceId: null,
      isSyncPaired: false
    })
    this.autoSyncLocalFallbackOnEdit = false
    this.teardownSyncRealtimeSubscription()
    this.syncEngine.stop()
    this.applySyncUiState()
    this.commands.setSyncIndicatorVisible(true)
  }

  private setupSyncLifecycle(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.syncForegroundPointerIds.clear()
        this.pauseOwnerShelfMirror()
        this.flushLocalThenSyncNow()
      } else if (document.visibilityState === 'visible') {
        if (this.hasPendingSyncForActiveSpace()) {
          void this.syncEngine.syncNow()
        }
        this.scheduleSyncRealtimeCatchup('focus')
        this.requestSyncReachabilityReprobe('focus')
        this.scheduleOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
      }
    })
    document.addEventListener('pointerdown', (event) => {
      this.syncForegroundPointerIds.add(event.pointerId)
      this.deferOwnerShelfMirrorForForeground()
      this.requestSyncReachabilityReprobe('interaction')
    }, { passive: true })
    document.addEventListener('keydown', () => {
      this.deferOwnerShelfMirrorForForeground()
      this.requestSyncReachabilityReprobe('interaction')
    })
    document.addEventListener('input', () => {
      this.deferOwnerShelfMirrorForForeground()
    })
    document.addEventListener('wheel', () => {
      this.deferOwnerShelfMirrorForForeground()
    }, { passive: true })
    const releaseForegroundPointer = (event: PointerEvent): void => {
      this.syncForegroundPointerIds.delete(event.pointerId)
    }
    window.addEventListener('pointerup', releaseForegroundPointer, { passive: true })
    window.addEventListener('pointercancel', releaseForegroundPointer, { passive: true })
    window.addEventListener('pagehide', () => {
      this.syncForegroundPointerIds.clear()
      this.pauseOwnerShelfMirror()
      this.flushLocalThenSyncNow()
    })
    window.addEventListener('beforeunload', () => {
      this.flushLocalThenSyncNow()
    })
    window.addEventListener('online', () => {
      this.applySyncUiState()
      this.requestSyncReachabilityReprobe('online', { force: true })
      void this.maybePromoteLocalFallbackToSync()
    })
    window.addEventListener('offline', () => {
      this.pauseOwnerShelfMirror()
      this.syncReachability = 'unreachable'
      this.teardownProfileRealtimeSubscription()
      this.teardownSyncRealtimeSubscription()
      this.applySyncUiState()
    })
  }

  private teardownProfileRealtimeSubscription(): void {
    if (this.profileRealtimeRefreshTimer) {
      window.clearTimeout(this.profileRealtimeRefreshTimer)
      this.profileRealtimeRefreshTimer = undefined
    }
    const channel = this.profileRealtimeChannel
    this.profileRealtimeChannel = undefined
    this.profileRealtimeUserId = undefined
    if (channel) {
      void channel.unsubscribe().catch(() => undefined)
    }
  }

  private refreshProfileRealtimeSubscription(): void {
    if (
      this.logoutInProgress ||
      !supabase ||
      !this.authSession ||
      typeof navigator !== 'undefined' && navigator.onLine === false ||
      this.syncReachability === 'unreachable'
    ) {
      this.teardownProfileRealtimeSubscription()
      return
    }

    const userId = this.authSession.user.id
    if (this.profileRealtimeChannel && this.profileRealtimeUserId === userId) return
    this.teardownProfileRealtimeSubscription()
    const channel = supabase.channel(`profile:${userId}`)
    channel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${userId}`
      },
      (payload: any) => this.handleProfileRealtimeEvent(payload)
    )
    channel.subscribe()
    this.profileRealtimeChannel = channel as { unsubscribe: () => Promise<'ok' | 'timed out' | 'error'> }
    this.profileRealtimeUserId = userId
  }

  private handleProfileRealtimeEvent(payload: any): void {
    if (this.logoutInProgress || !this.authSession) return
    const row = payload?.new ?? payload?.old ?? payload?.data?.record ?? payload?.data?.old_record
    const eventUserId = row && typeof row.id === 'string' ? row.id : ''
    if (eventUserId && eventUserId !== this.authSession.user.id) return
    if (this.profileRealtimeRefreshTimer) {
      window.clearTimeout(this.profileRealtimeRefreshTimer)
    }
    this.profileRealtimeRefreshTimer = window.setTimeout(() => {
      this.profileRealtimeRefreshTimer = undefined
      if (!this.authRefreshReady) {
        this.pendingAuthRefresh = {
          trigger: 'profile-realtime',
          options: {}
        }
        return
      }
      void this.refreshAuthProfile('profile-realtime')
    }, PROFILE_REALTIME_REFRESH_DEBOUNCE_MS)
  }

  private teardownSyncRealtimeSubscription(): void {
    if (this.syncRealtimeNudgeTimer) {
      window.clearTimeout(this.syncRealtimeNudgeTimer)
      this.syncRealtimeNudgeTimer = undefined
    }
    this.syncRealtimeCatchupQueued = false
    this.syncRealtimePendingSpaceIds.clear()
    const channel = this.syncRealtimeChannel
    this.syncRealtimeChannel = undefined
    this.syncRealtimeChannelKey = undefined
    if (channel) {
      void channel.unsubscribe().catch(() => undefined)
    }
  }

  private refreshSyncRealtimeSubscription(): void {
    if (this.logoutInProgress) {
      this.teardownSyncRealtimeSubscription()
      return
    }
    if (!supabase || !this.authSession || !this.isSyncPaired) {
      this.teardownSyncRealtimeSubscription()
      return
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.teardownSyncRealtimeSubscription()
      return
    }
    if (this.syncReachability === 'unreachable') {
      this.teardownSyncRealtimeSubscription()
      return
    }
    let ownerId = ''
    try {
      ownerId = getSyncOwnerId(this.authSession)
    } catch {
      this.teardownSyncRealtimeSubscription()
      return
    }
    const channelKey = ownerId
    if (this.syncRealtimeChannel && this.syncRealtimeChannelKey === channelKey) return
    this.teardownSyncRealtimeSubscription()
    const channel = supabase.channel(`sync-spaces:${channelKey}`)
    channel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'spaces',
        filter: `owner_id=eq.${ownerId}`
      },
      (payload: any) => this.handleSyncRealtimeMetadataEvent(payload)
    )
    channel.subscribe()
    this.syncRealtimeChannel = channel as { unsubscribe: () => Promise<'ok' | 'timed out' | 'error'> }
    this.syncRealtimeChannelKey = channelKey
  }

  private handleSyncRealtimeMetadataEvent(payload: any): void {
    if (this.logoutInProgress) return
    if (!this.authSession || !this.isSyncPaired) return
    const row = payload?.new ?? payload?.old ?? payload?.data?.record ?? payload?.data?.old_record
    if (!row || typeof row !== 'object') return
    const eventSpaceId = typeof row.space_id === 'string' ? row.space_id : ''
    if (!eventSpaceId) return
    const ownerId = typeof row.owner_id === 'string' ? row.owner_id : ''
    if (ownerId && ownerId !== this.authSession.user.id) return
    const eventSyncAt = typeof row.sync_at === 'string' ? row.sync_at : undefined
    if (this.shouldSuppressLocalSyncEcho(eventSpaceId, eventSyncAt)) return
    this.syncRealtimePendingSpaceIds.add(eventSpaceId)
    void incrementUsageOdometer({ realtimeEventCountDelta: 1 }, supabase).catch(() => undefined)
    this.scheduleSyncRealtimeCatchup('realtime')
  }

  private shouldSuppressLocalSyncEcho(spaceId: string, eventSyncAt?: string): boolean {
    const entry = this.localSyncEchoSuppress.get(spaceId)
    if (!entry) return false
    if (Date.now() > entry.expiresAt) {
      this.localSyncEchoSuppress.delete(spaceId)
      return false
    }
    if (!eventSyncAt) return false
    return eventSyncAt.localeCompare(entry.syncAt) <= 0
  }

  private rememberLocalSyncEcho(spaceId: string, syncAt: string): void {
    if (!spaceId || !syncAt) return
    const now = Date.now()
    this.localSyncEchoSuppress.set(spaceId, {
      syncAt,
      expiresAt: now + SYNC_DEBOUNCE_MS
    })
    for (const [id, entry] of this.localSyncEchoSuppress.entries()) {
      if (entry.expiresAt <= now) {
        this.localSyncEchoSuppress.delete(id)
      }
    }
  }

  private scheduleSyncRealtimeCatchup(_source: 'realtime' | 'online' | 'focus'): void {
    if (!this.canAttemptSyncRealtimeCatchup()) return
    if (this.syncRealtimeNudgeTimer) {
      window.clearTimeout(this.syncRealtimeNudgeTimer)
    }
    this.syncRealtimeNudgeTimer = window.setTimeout(() => {
      this.syncRealtimeNudgeTimer = undefined
      void this.runSyncRealtimeCatchup()
    }, SYNC_REALTIME_NUDGE_DEBOUNCE_MS)
  }

  private canAttemptSyncRealtimeCatchup(): boolean {
    if (this.logoutInProgress) return false
    if (!this.authSession || !this.isSyncPaired) return false
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false
    if (this.syncReachability === 'unreachable') return false
    return true
  }

  private async runSyncRealtimeCatchup(): Promise<void> {
    if (this.logoutInProgress) return
    if (this.syncRealtimeCatchupInFlight) {
      this.syncRealtimeCatchupQueued = true
      return
    }
    if (!this.canAttemptSyncRealtimeCatchup()) return
    this.syncRealtimeCatchupInFlight = true
    const endAccountActivity = this.beginAccountCloudActivity()
    try {
      await this.ensureAuthenticatedSyncLane()
      const ok = await this.reconcileSyncSpacesFromRemoteNudge()
      if (ok) {
        this.syncReachability = 'reachable'
        this.scheduleOwnerShelfMirror()
      } else if (this.syncEngine.isDirty()) {
        const synced = await this.syncEngine.syncNow()
        if (synced) {
          this.syncReachability = 'reachable'
          this.scheduleOwnerShelfMirror()
        }
      }
    } catch {
      this.syncReachability = 'unreachable'
      this.applySyncUiState()
    } finally {
      endAccountActivity()
      this.syncRealtimeCatchupInFlight = false
      if (this.syncRealtimeCatchupQueued) {
        this.syncRealtimeCatchupQueued = false
        this.scheduleSyncRealtimeCatchup('realtime')
      }
    }
  }

  private setAccountShelfReadiness(readiness: AccountShelfReadiness): void {
    if (this.accountShelfReadiness === readiness) return
    this.accountShelfReadiness = readiness
    this.applySyncUiState()
  }

  private clearOwnerShelfMirrorTimer(): void {
    if (this.syncOwnerMirrorTimer !== undefined) {
      window.clearTimeout(this.syncOwnerMirrorTimer)
    }
    this.syncOwnerMirrorTimer = undefined
    this.syncOwnerMirrorTimerDueAt = undefined
  }

  private pauseOwnerShelfMirror(): void {
    this.clearOwnerShelfMirrorTimer()
    this.syncOwnerMirrorQueued = false
    this.syncOwnerMirrorEpoch += 1
  }

  private resetOwnerShelfHydrationState(): void {
    this.pauseOwnerShelfMirror()
    if (this.accountShelfReadiness === 'unknown') return
    this.accountShelfReadiness = 'unknown'
    this.applySyncUiState()
  }

  private isOwnerShelfMirrorForegroundQuiet(): boolean {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false
    if (this.syncForegroundPointerIds.size > 0) return false
    if (this.accountIdentityInFlight || this.accountAirlockInFlight) return false
    if (this.syncForegroundShelfDepth > 0) return false
    if (this.localMemorySettling || this.accountCloudActivityDepth > 0) return false
    if (this.cloudSyncAfterLocalConsolidationPending) return false
    if (this.syncEngineStatus === 'pending' || this.syncEngineStatus === 'syncing') return false
    if (this.dragArmed || this.isDragging || this.resizeArmed || this.isResizing) return false
    if (this.touchPoints.size > 0 || this.areaSelectDragActive || this.touchZoomScrubActive) return false
    if (this.pendingRoute || this.pendingIndexRefresh) return false
    return true
  }

  private resolveOwnerShelfMirrorLane(): {
    ownerId: string
    authority: NonNullable<LocalVolumeAuthority>
  } | undefined {
    if (!this.authSession || !this.isSyncPaired) return undefined
    if (!this.canSyncReadNow() || this.logoutInProgress) return undefined
    const ownerId = getSyncOwnerId(this.authSession)
    const authority = this.storage.getVolumeAuthority()
    if (!authority || authority.owner_id !== ownerId) return undefined
    return { ownerId, authority }
  }

  private queueOwnerShelfMirror(delayMs: number): void {
    const boundedDelay = Number.isFinite(delayMs)
      ? Math.max(SYNC_SHELF_HYDRATION_QUIET_GAP_MS, Math.ceil(delayMs))
      : SYNC_SHELF_HYDRATION_QUIET_GAP_MS
    const dueAt = Date.now() + boundedDelay
    if (
      this.syncOwnerMirrorTimer !== undefined
      && this.syncOwnerMirrorTimerDueAt !== undefined
      && this.syncOwnerMirrorTimerDueAt <= dueAt
    ) return
    this.clearOwnerShelfMirrorTimer()
    this.syncOwnerMirrorTimerDueAt = dueAt
    this.syncOwnerMirrorTimer = window.setTimeout(() => {
      this.syncOwnerMirrorTimer = undefined
      this.syncOwnerMirrorTimerDueAt = undefined
      this.scheduleOwnerShelfMirror()
    }, boundedDelay)
  }

  private deferOwnerShelfMirrorForForeground(): void {
    if (this.syncOwnerMirrorTimer === undefined && !this.syncOwnerMirrorQueued) return
    const remainingDelay = this.syncOwnerMirrorTimerDueAt === undefined
      ? SYNC_SHELF_HYDRATION_QUIET_GAP_MS
      : Math.max(
          SYNC_SHELF_HYDRATION_QUIET_GAP_MS,
          this.syncOwnerMirrorTimerDueAt - Date.now()
        )
    this.clearOwnerShelfMirrorTimer()
    this.syncOwnerMirrorQueued = false
    this.scheduleOwnerShelfMirror(remainingDelay)
  }

  private preemptOwnerShelfMirrorQueue(): number | undefined {
    const hadWork =
      this.syncOwnerMirrorTimer !== undefined
      || this.syncOwnerMirrorQueued
      || this.syncOwnerMirrorInFlight
    if (!hadWork) return undefined
    const remainingDelay = this.syncOwnerMirrorTimerDueAt === undefined
      ? SYNC_SHELF_HYDRATION_QUIET_GAP_MS
      : Math.max(
          SYNC_SHELF_HYDRATION_QUIET_GAP_MS,
          this.syncOwnerMirrorTimerDueAt - Date.now()
        )
    this.clearOwnerShelfMirrorTimer()
    this.syncOwnerMirrorQueued = false
    this.syncOwnerMirrorEpoch += 1
    return remainingDelay
  }

  private scheduleOwnerShelfMirror(delayMs?: number): void {
    const lane = this.resolveOwnerShelfMirrorLane()
    if (!lane) return
    if (this.accountShelfReadiness === 'unknown') {
      this.setAccountShelfReadiness('pending')
    }
    if (delayMs !== undefined) {
      this.queueOwnerShelfMirror(delayMs)
      return
    }
    if (this.syncOwnerMirrorInFlight) {
      this.syncOwnerMirrorQueued = true
      return
    }
    if (!this.isOwnerShelfMirrorForegroundQuiet()) {
      this.queueOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
      return
    }
    this.clearOwnerShelfMirrorTimer()
    const runEpoch = this.syncOwnerMirrorEpoch
    let resumeAfterMs: number | undefined
    this.syncOwnerMirrorInFlight = true
    void this.runOwnerShelfMirror(lane.ownerId, lane.authority)
      .then(delay => {
        resumeAfterMs = delay
      })
      .catch(() => {
        const sessionOwnerId = this.authSession
          ? getSyncOwnerId(this.authSession)
          : undefined
        if (
          this.storage.matchesVolumeAuthority(lane.authority)
          && this.isSyncPaired
          && sessionOwnerId === lane.ownerId
        ) {
          this.syncReachability = 'unreachable'
          this.applySyncUiState()
        }
      })
      .finally(() => {
        this.syncOwnerMirrorInFlight = false
        const queueRequested = this.syncOwnerMirrorQueued
        this.syncOwnerMirrorQueued = false
        if (queueRequested) {
          this.scheduleOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
        } else if (runEpoch === this.syncOwnerMirrorEpoch && resumeAfterMs !== undefined) {
          this.scheduleOwnerShelfMirror(resumeAfterMs)
        }
      })
  }

  private async runOwnerShelfMirror(
    ownerId: string,
    authority: NonNullable<LocalVolumeAuthority>
  ): Promise<number | undefined> {
    const endAccountActivity = this.beginAccountCloudActivity()
    try {
      const result = await this.syncEngine.mirrorOwnerShelf({
        ownerId,
        volumeAuthority: authority
      })
      if (
        !this.storage.matchesVolumeAuthority(authority)
        || !await this.storage.isVolumeAuthorityCurrent(authority)
        || !this.authSession
        || getSyncOwnerId(this.authSession) !== ownerId
        || !this.isSyncPaired
      ) return undefined

      for (const item of result.mirrored) {
        if (item.intent === 'skip') continue
        this.setSyncRuntimePendingHint(item.spaceId, !item.ok || item.pending === true)
        if (item.spaceId === this.syncSpaceId && item.ok && item.action === 'deleted') {
          await this.handleActiveSpaceDeletedByShelfFeed(
            item.spaceId,
            authority
          )
        }
      }
      if (!await this.storage.isVolumeAuthorityCurrent(authority)) return undefined
      if (result.refreshed && this.canSyncReadNow()) {
        this.syncReachability = 'reachable'
        this.applySyncUiState()
      }
      this.refreshCloudDebtState()
      await this.refreshOpenIndexPaletteAfterShelfBatch(result.refreshed, authority)
      if (!result.refreshed) {
        this.setAccountShelfReadiness('pending')
        return undefined
      }

      const hydration = result.hydration
      if (hydration.terminal > 0 || hydration.quotaBlocked) {
        this.setAccountShelfReadiness('blocked')
        return undefined
      }
      if (hydration.blockedByDebt) {
        this.setAccountShelfReadiness('pending')
        return SYNC_SHELF_HYDRATION_RETRY_BASE_MS
      }
      if (hydration.remaining > 0) {
        this.setAccountShelfReadiness('pending')
        return hydration.retryAfterMs ?? SYNC_SHELF_HYDRATION_QUIET_GAP_MS
      }
      const preflight = await this.preflightProductVolumeShelfExport(authority)
      if (
        !this.storage.matchesVolumeAuthority(authority)
        || !await this.storage.isVolumeAuthorityCurrent(authority)
      ) return undefined
      if (preflight.ok) {
        this.setAccountShelfReadiness('complete')
      } else if (preflight.reason !== 'authority_mismatch') {
        this.setAccountShelfReadiness('blocked')
      }
      return undefined
    } finally {
      try { await this.cleanupOwnerShelfFiles(ownerId, authority) }
      finally { endAccountActivity() }
    }
  }

  private async cleanupOwnerShelfFiles(
    ownerId: string,
    authority: NonNullable<LocalVolumeAuthority>
  ): Promise<void> {
    const lane = this.resolveOwnerShelfMirrorLane()
    if (!lane || lane.ownerId !== ownerId || !this.storage.matchesVolumeAuthority(authority)) return
    let retryAfterMs = SYNC_SHELF_HYDRATION_RETRY_MAX_MS
    try {
      const current = await this.requireCurrentSyncCloudSession(authority)
      const result = await cleanupSyncSpaceFiles(createSessionDataClient, ownerId, {
        session: current.session,
        assertCurrent: async () => {
          if (!this.resolveOwnerShelfMirrorLane()) throw new Error('cleanup_paused')
          await this.requireCurrentSyncCloudSession(authority)
        }
      })
      if (!result.pending) return
      retryAfterMs = result.failed ? SYNC_SHELF_HYDRATION_RETRY_MAX_MS : SYNC_SHELF_HYDRATION_QUIET_GAP_MS
    } catch { /* Cleanup never changes the save result or its sync feedback. */ }
    if (this.storage.matchesVolumeAuthority(authority)) this.scheduleOwnerShelfMirror(retryAfterMs)
  }

  private async reconcileSyncSpacesFromRemoteNudge(): Promise<boolean> {
    if (!this.canSyncReadNow()) return false
    if (!this.authSession || !this.isSyncPaired) return false
    const authority = this.storage.getVolumeAuthority()
    const ownerId = getSyncOwnerId(this.authSession)
    if (!authority || authority.owner_id !== ownerId) return false
    const pending = [...this.syncRealtimePendingSpaceIds]
    this.syncRealtimePendingSpaceIds.clear()
    const spaceIds = pending.length
      ? pending
      : (this.syncSpaceId && !this.isIndexSpace() ? [this.syncSpaceId] : [])
    const activeSpaceId = this.syncSpaceId && !this.isIndexSpace() ? this.syncSpaceId : undefined
    if (activeSpaceId && spaceIds.includes(activeSpaceId)) {
      const ready = await this.prepareActiveSpaceForShelfFeed(
        activeSpaceId,
        authority
      )
      if (!ready) {
        this.syncRealtimePendingSpaceIds.add(activeSpaceId)
        return false
      }
    }

    const result = await this.syncEngine.feedShelf({
      spaceIds,
      hydrateMissing: spaceIds.length > 0,
      reconcileDirtyTombstonesForOwnerId: this.canSyncShelfWriteNow() ? ownerId : undefined,
      volumeAuthority: authority
    })

    for (const reconciled of result.reconciled) {
      this.setSyncRuntimePendingHint(reconciled.spaceId, !reconciled.ok)
      if (reconciled.spaceId === activeSpaceId && reconciled.ok && reconciled.action === 'deleted') {
        await this.handleActiveSpaceDeletedByShelfFeed(
          reconciled.spaceId,
          authority
        )
      }
    }
    await this.refreshOpenIndexPaletteAfterShelfBatch(result.refreshed, authority)
    return result.ok
  }

  private async prepareActiveSpaceForShelfFeed(
    spaceId: string,
    authority = this.storage.getVolumeAuthority()
  ): Promise<boolean> {
    const localBlocks = this.document.getStateSnapshot()
    const localIdentity = this.getSpaceIdentity(localBlocks)
    if (!this.isLocalIdentityAligned(spaceId, localIdentity)) {
      if (!this.canSyncWriteNow()) return true
      return this.attemptIdentityRehome(localBlocks, localIdentity, authority)
    }
    return !this.hasActiveShelfConsolidationDebt(spaceId)
  }

  private async handleActiveSpaceDeletedByShelfFeed(
    spaceId: string,
    authority = this.storage.getVolumeAuthority()
  ): Promise<void> {
    this.indexSpaces.delete(spaceId)
    this.setSyncRuntimePendingHint(spaceId, false)
    this.localSyncEchoSuppress.delete(spaceId)
    const rows = await this.listProductVolumeSpacesMeta(authority)
    const replacement = await this.openLiveShelfReplacement(
      rows,
      spaceId,
      authority
    )
    if (!replacement.opened && !replacement.hadCandidates) {
      await this.createLocalBlankSpacePreservingShelf({
        authority
      })
    }
    this.openIndexSurfaceDeterministic()
    this.commands.ensurePaletteVisible()
    this.commands.setStatus('space deleted', 'success')
  }

  private async probeSyncReachability(): Promise<boolean> {
    if (!this.authSession || !this.isSyncPaired) {
      return typeof navigator !== 'undefined' ? navigator.onLine !== false : true
    }
    try {
      const ownerId = getSyncOwnerId(this.requireVerifiedCloudSession())
      await listSyncSpacesDirect(supabase, ownerId)
      return true
    } catch {
      return false
    }
  }

  private markSyncDirty(): void {
    if (this.storage.getActiveDeskState() !== 'filed') {
      this.autoSyncLocalFallbackOnEdit = false
      return
    }
    this.markActiveSyncSpaceDirtyForLocalChange()
    if (this.autoSyncLocalFallbackOnEdit) {
      this.setSyncRuntimePendingHintForActiveSpace(true)
      this.setSyncEngineState('pending', 0)
      void this.promoteLocalFallbackAndMarkDirty()
      return
    }
    if (this.shouldDeferCloudSyncUntilLocalConsolidation()) {
      if (this.syncEngine.getState().status === 'syncing') this.syncEngine.markDirty()
      this.cloudSyncAfterLocalConsolidationPending = true
      this.applySyncUiState()
      return
    }
    this.startAccountCloudSync()
  }

  private shouldDeferCloudSyncUntilLocalConsolidation(): boolean {
    return this.isSyncPaired && !this.isIndexSpace()
  }

  private startAccountCloudSync(): void {
    this.accountCloudDebt = true
    this.syncEngine.markDirty()
    this.applySyncUiState()
  }

  private markActiveSyncSpaceDirtyForLocalChange(): void {
    if ((!this.storage.getMountedPassport() && !this.isSyncPaired) || this.isIndexSpace()) return
    const authority = this.storage.getVolumeAuthority()
    const activeSpaceId = this.syncSpaceId ?? this.activeSpaceId
    if (!activeSpaceId) return
    const localIdentity = this.getSpaceIdentity(this.document.getStateSnapshot())
    if (!this.isLocalIdentityAligned(activeSpaceId, localIdentity)) return
    void this.markActiveAccountSpaceDirtyAfterMeaningfulChange(
      activeSpaceId,
      authority
    )
  }

  private async markActiveAccountSpaceDirtyAfterMeaningfulChange(
    activeSpaceId: string,
    authority: LocalVolumeAuthority
  ): Promise<void> {
    const meta = await this.storage.getActiveSpaceMeta(authority)
    if (!meta || meta.space_id !== activeSpaceId) return
    if (
      !meta.dirty
      && !meta.sync_at
      && !meta.last_sync_ok_at
    ) {
      const localBlocks = this.document.getStateSnapshot()
      const localIsSeedOnly = await this.isLocalSeedOnly(localBlocks)
      if (!hasMeaningfulLocalDraft({ blocks: localBlocks, isSeedOnly: localIsSeedOnly })) {
        return
      }
    }
    if ((this.syncSpaceId ?? this.activeSpaceId) !== activeSpaceId) return
    this.setSyncRuntimePendingHint(activeSpaceId, true)
    await this.storage.markVolumeSpaceDirty(
      authority,
      activeSpaceId,
      true
    ).catch(() => {
      this.warnPolicyOnceDev(
        'sync-dirty-mark-failed',
        '[policy] failed to mark active Sync space dirty after local change',
        { activeSpaceId }
      )
    })
  }

  private async flushPendingActiveSyncExitIfNeeded(
    authority: LocalVolumeAuthority = this.storage.getVolumeAuthority()
  ): Promise<void> {
    if (this.isIndexSpace()) return
    const activeSpaceId = this.syncSpaceId ?? this.activeSpaceId
    if (!this.isSyncPaired && authority) {
      if (!activeSpaceId) return
      this.commitActiveEditIfNeeded()
      const localBlocks = this.document.getStateSnapshot()
      const localIdentity = this.getSpaceIdentity(localBlocks)
      if (!this.isLocalIdentityAligned(activeSpaceId, localIdentity)) return
      await this.storage.flushActiveDocumentForVolume(localBlocks, authority)
      await this.markActiveAccountSpaceDirtyAfterMeaningfulChange(
        activeSpaceId,
        authority
      )
      return
    }
    if (!this.isSyncPaired) return
    if (!activeSpaceId || !this.hasSyncRuntimePendingHint(activeSpaceId)) return
    this.commitActiveEditIfNeeded()
    const localBlocks = this.document.getStateSnapshot()
    const localIdentity = this.getSpaceIdentity(localBlocks)
    if (!this.isLocalIdentityAligned(activeSpaceId, localIdentity)) return
    await this.storage.flushActiveDocumentForVolume(localBlocks, authority)
    await this.storage.markVolumeSpaceDirty(
      authority,
      activeSpaceId,
      true
    )
  }

  private async promoteLocalFallbackAndMarkDirty(): Promise<void> {
    if (this.localFallbackPromotionInFlight) return
    this.localFallbackPromotionInFlight = true
    try {
      const promoted = await this.maybePromoteLocalFallbackToSync()
      if (!promoted || !this.canSyncWriteNow()) return
      this.setSyncRuntimePendingHintForActiveSpace(true)
      this.setSyncEngineState('pending', 0)
      this.syncEngine.markDirty()
    } finally {
      this.localFallbackPromotionInFlight = false
    }
  }

  private async maybePromoteLocalFallbackToSync(): Promise<boolean> {
    if (!this.autoSyncLocalFallbackOnEdit) return false
    if (this.storage.getActiveDeskState() !== 'filed') return false
    if (!this.authSession || !this.authUsername || !this.authIsSync) return false
    if (this.isIndexSpace()) return false

    const authority = this.storage.getVolumeAuthority()
    const localBlocks = this.document.getStateSnapshot()
    const localIdentity = this.getSpaceIdentity(localBlocks)
    const localIsSeedOnly = await this.isLocalSeedOnly(localBlocks)
    const shouldPromote = hasMeaningfulLocalDraft({
      blocks: localBlocks,
      isSeedOnly: localIsSeedOnly
    })
    if (!shouldPromote) return false
    if (this.resolveConnectivityState().isOfflineLike) return false
    const claimed = await this.claimCurrentLocalSpaceIntoSync(
      localBlocks,
      localIdentity,
      authority
    )
    if (!claimed) return false
    this.autoSyncLocalFallbackOnEdit = false
    return true
  }

  private seedClockFromTimeline(blocks: SpaceEntry[], metaBlock?: Block): void {
    seedClock(metaBlock ? [...blocks, metaBlock] : blocks)
  }

  private getSpaceIdentity(blocks: SpaceEntry[]): { spaceId: string, spaceEd: string } {
    const meta = this.storage.getMetaBlock()
    if (meta) {
      return { spaceId: meta.ided[0], spaceEd: meta.ided[1] }
    }
    const derived = deriveSpaceIded(blocks)
    return { spaceId: derived.createdId, spaceEd: derived.editedId }
  }

  private parseSpaceIdParts(value: string): { year?: string, mmdd?: string, hhmm?: string, sscc?: string } {
    const trimmed = value.trim()
    if (!trimmed) return {}
    if (trimmed.includes('-')) {
      const parts = trimmed.split('-').filter(Boolean)
      if (parts.length === 4 && parts.every(part => part.length === 4)) {
        return {
          year: parts[0],
          mmdd: parts[1],
          hhmm: parts[2],
          sscc: parts[3]
        }
      }
      return {}
    }
    const parts = trimmed.split(/\s+/).filter(Boolean)
    if (parts.length >= 4 && parts.slice(0, 4).every(part => part.length === 4)) {
      return { year: parts[0], mmdd: parts[1], hhmm: parts[2], sscc: parts[3] }
    }
    return {}
  }

  private hasUserEditsSinceCreate(): boolean {
    const meta = this.storage.getMetaBlock()
    const lastEdited = this.document.getLastEditedBlock()
    if (!meta || !lastEdited) return false
    const created = this.parseSpaceIdParts(meta.ided[0])
    const edited = this.parseSpaceIdParts(lastEdited.ided[1])
    if (!created.year || !created.mmdd || !created.hhmm || !created.sscc) return false
    if (!edited.year || !edited.mmdd || !edited.hhmm || !edited.sscc) return false
    const createdStamp = `${created.year}${created.mmdd}${created.hhmm}${created.sscc}`
    const editedStamp = `${edited.year}${edited.mmdd}${edited.hhmm}${edited.sscc}`
    return editedStamp > createdStamp
  }

  private focusBlockInView(block: Block): void {
    const widthPx = block.xyzw[3] * GRID_SIZE
    const metrics = this.canvas.measureText(block.text, widthPx)
    const centerX = block.xyzw[0] * GRID_SIZE + widthPx / 2
    const centerY = block.xyzw[1] * GRID_SIZE + metrics.height / 2
    const viewport = this.getViewportCssSize()
    this.camera.tx = (viewport.width / 2) - centerX * this.camera.scale
    this.camera.ty = (viewport.height / 2) - centerY * this.camera.scale
  }

  private focusLastEditedBlock(opts?: {
    requireMeaningfulEdit?: boolean
    selectInEditMode?: boolean
  }): boolean {
    if (opts?.requireMeaningfulEdit && !this.hasUserEditsSinceCreate()) {
      return false
    }
    const target = this.document.getLastEditedBlock()
    if (!target) return false
    this.focusBlockInView(target)
    if (opts?.selectInEditMode && !this.isViewMode()) {
      this.document.selectBlock(target.ided[0])
    }
    return true
  }

  private spaceIdToDefaultSlug(spaceId: string): string {
    return spaceIdToDefaultSlug(spaceId)
  }

  private handleSearchQueryInput(query: string, ctx: EngineContext): void {
    const previous = this.searchSession
    const active = previous?.query === query ? previous.hits[previous.index] : undefined
    const hits = query.length ? collectHits(this.getActiveBlocks().map(block => ({
      id: block.ided[0], editedId: block.ided[1], createdId: block.ided[0],
      layout: this.canvas.getSearchLayout(block.text, block.xyzw[3] * GRID_SIZE)
    })), query) : []
    // Raw text must be unchanged: matching offsets alone cannot identify a hit
    // after arbitrary edits. Reordering other blocks is safe to follow.
    const index = active && this.document.getBlock(active.blockId)?.text === previous?.activeText
      ? hits.findIndex(hit => hit.blockId === active.blockId && hit.range.start === active.range.start && hit.range.end === active.range.end)
      : -1
    this.searchSession = { query, hits, index, activeText: index >= 0 ? previous?.activeText : undefined }
    this.publishSearchSession(ctx)
  }

  private navigateSearchHit(delta: number, ctx: EngineContext): boolean {
    const session = this.searchSession
    if (!session?.hits.length) return false
    const index = stepHitIndex(session.index, delta, session.hits.length)
    const hit = session.hits[index]
    const block = this.document.getBlock(hit.blockId)
    if (!block) {
      this.commands.refreshLiveInput()
      return false
    }
    session.index = index
    session.activeText = block.text
    this.focusBlockInView(block)
    if (!this.isViewMode()) this.document.selectBlock(block.ided[0])
    this.publishSearchSession(ctx)
    return true
  }

  private publishSearchSession(ctx: EngineContext): void {
    const session = this.searchSession
    if (!session) return
    this.canvas.setSearchMarks(session.hits.map((hit, index) => ({ ...hit, active: index === session.index })))
    this.requestRender()
    ctx.setFeedback(!session.query.length ? undefined
      : !session.hits.length ? 'match: none'
      : session.index < 0 ? `match: ${session.hits.length}`
      : `match: ${session.index + 1}/${session.hits.length}`)
  }

  private clearSearchSession(): void {
    if (!this.searchSession) return
    this.searchSession = undefined
    this.canvas.setSearchMarks([])
    this.requestRender()
  }

  private isDbName(value: string): boolean {
    return /^\d{4}-\d{4}-\d{4}-\d{4}$/.test(value.trim())
  }

  private isDefaultSlug(value: string): boolean {
    return isDefaultSpaceSlug(value)
  }

  private normalizeSpaceSlug(spaceSlug: string, spaceId?: string): string {
    const trimmed = spaceSlug.trim()
    if (this.isDefaultSlug(trimmed)) return trimmed
    if (this.isDbName(trimmed)) return `${trimmed}-lilo-data`
    if (trimmed.includes(' ')) {
      const parts = this.parseSpaceIdParts(trimmed)
      if (parts.year && parts.mmdd && parts.hhmm && parts.sscc) {
        return this.spaceIdToDefaultSlug(trimmed)
      }
    }
    if (spaceId && !trimmed.length) return this.spaceIdToDefaultSlug(spaceId)
    return trimmed
  }

  private normalizeSpaceRenameInput(value: string): string {
    return normalizeHandle(value)
  }

  private normalizeUserRenameInput(value: string): string {
    return normalizeHandle(value)
  }

  private stampDefaultSlugIfMissing(blocks: SpaceEntry[]): boolean {
    const meta = this.storage.getMetaBlock()
    if (!meta) return false
    const parsedSlug = parseLilodataText(meta.text).slug?.trim()
    if (parsedSlug) return false
    const identity = this.getSpaceIdentity(blocks)
    if (identity.spaceId === META_SPACES_ID) return false
    const slug = this.spaceIdToDefaultSlug(identity.spaceId)
    this.storage.setMetaBlock({
      ...meta,
      text: updateLilodataText(meta.text, { slug })
    })
    this.setActiveSpace(identity.spaceId, slug)
    return true
  }

  private isRemoteNewer(remoteEd: string | null | undefined, localEd: string): boolean {
    const normalizedRemote = typeof remoteEd === 'string' ? remoteEd.trim() : ''
    if (!normalizedRemote) return false
    return normalizedRemote.localeCompare(localEd) > 0
  }

  private isLocalIdentityAligned(expectedSpaceId: string, localIdentity: { spaceId: string, spaceEd: string }): boolean {
    if (localIdentity.spaceId !== expectedSpaceId) return false
    const meta = this.storage.getMetaBlock()
    if (meta && meta.ided[0] !== expectedSpaceId) return false
    return true
  }

  private async attemptIdentityRehome(
    localBlocks: SpaceEntry[],
    localIdentity: { spaceId: string, spaceEd: string },
    authority = this.storage.getVolumeAuthority()
  ): Promise<boolean> {
    if (!this.authSession) return false
    if (this.hasIdentityRehomeAttempt(localIdentity.spaceId)) return false
    this.markIdentityRehomeAttempt(localIdentity.spaceId)
    const slug = this.spaceIdToDefaultSlug(localIdentity.spaceId)
    const json = this.storage.exportToJSON(localBlocks)
    await this.storage.flushActiveDocumentForVolume(localBlocks, authority)
    const accepted = await this.saveSyncSpaceWithShelfProof({
      spaceId: localIdentity.spaceId,
      spaceSlug: slug,
      spaceEd: localIdentity.spaceEd,
      json
    }, 'claim', authority)
    await this.refreshActiveSpaceLabelFromLocalShelf(
      authority,
      localIdentity.spaceId
    )
    if (!accepted) return false
    this.syncSpaceId = localIdentity.spaceId
    this.syncSpaceSlug = accepted.space_slug ?? slug
    this.rememberReadyIndexSpace(localIdentity.spaceId, this.syncSpaceSlug, {
      sizeBytes: accepted.size_bytes,
      spaceEd: accepted.space_ed
    })
    this.setActiveSpace(localIdentity.spaceId, this.syncSpaceSlug)
    this.setSyncRuntimePendingHint(localIdentity.spaceId, false)
    return true
  }

  private validateSpaceRenameInput(value: string): { valid: boolean, error?: string } {
    const normalized = normalizeHandle(value)
    const verdict = validateSpaceSlugHandle(normalized)
    if (!verdict.valid) return { valid: false, error: verdict.error }
    return { valid: true }
  }

  private validateUserRenameInput(value: string): { valid: boolean, error?: string } {
    const normalized = normalizeHandle(value)
    const verdict = validateUsernameHandle(normalized)
    if (!verdict.valid) return { valid: false, error: verdict.error }
    return { valid: true }
  }

  private resolveActiveSpaceLabel(spaceId: string, spaceSlug?: string): string {
    if (spaceId === META_SPACES_ID) {
      return 'index'
    }
    if (this.storage.getActiveDeskState() !== 'filed') return 'unsaved'
    return spaceSlug?.trim()
      ? this.normalizeSpaceSlug(spaceSlug, spaceId)
      : this.spaceIdToDefaultSlug(spaceId)
  }

  private restoreBootSpaceIdentity(spaceId: string): void {
    const meta = this.storage.getMetaBlock()
    const storedSlug = meta?.ided[0] === spaceId
      ? parseLilodataText(meta.text).slug?.trim()
      : undefined
    this.activeSpaceId = spaceId
    this.spaceLabel = this.resolveActiveSpaceLabel(spaceId, storedSlug)
  }

  private setActiveSpace(spaceId: string, spaceSlug?: string): void {
    if (this.searchSession && this.activeSpaceId !== spaceId) this.commands.exitEngine()
    this.activeSpaceId = spaceId
    this.spaceLabel = this.resolveActiveSpaceLabel(spaceId, spaceSlug)
    if (spaceId !== META_SPACES_ID) {
      this.setDetachedResumeIntent(undefined)
    }
    if (this.hasSyncRuntimePendingHint(spaceId) && this.syncEngineStatus === 'idle') {
      this.setSyncEngineState('pending', 0)
    }
    this.updateAccountBar()
    if (this.indexSpaces.size) {
      this.syncIndexPaletteSelection()
    } else {
      this.commands.refreshVisibleSurface()
    }
    this.applySyncUiState()
    this.refreshCloudDebtState()
    this.refreshSyncRealtimeSubscription()
  }

  private updateIndexSpaceAvailability(
    spaceId: string,
    availability: LocalSpaceAvailability
  ): void {
    const indexed = this.indexSpaces.get(spaceId)
    if (!indexed || indexed.availability === availability) return
    this.indexSpaces.set(spaceId, { ...indexed, availability })
    this.syncIndexPaletteSelection()
  }

  private rememberReadyIndexSpace(
    spaceId: string,
    slug: string,
    facts?: { sizeBytes?: number | null, spaceEd?: string | null }
  ): void {
    const previous = this.indexSpaces.get(spaceId)
    const sizeBytes = typeof facts?.sizeBytes === 'number'
      ? facts.sizeBytes
      : previous?.sizeBytes
    const spaceEd = facts?.spaceEd?.trim() || previous?.spaceEd
    this.indexSpaces.set(spaceId, {
      slug,
      availability: 'ready',
      ...(sizeBytes !== undefined ? { sizeBytes } : {}),
      ...(spaceEd ? { spaceEd } : {})
    })
  }

  private async openShelfSpaceInEditor(
    spaceId: string,
    authority = this.storage.getVolumeAuthority(),
    opts?: { consumeSeed?: SeedSpaceProof }
  ): Promise<boolean> {
    const blocks = await this.storage.loadSpaceFromShelf(spaceId, authority, opts)
    if (!blocks) return false
    if (!await this.storage.isVolumeAuthorityCurrent(authority)) return false
    await this.applyMergedBlocks(blocks, this.storage.getMetaBlock(), { persist: false })
    return true
  }

  private async openActiveMemoryShelfSpace(
    spaceId: string,
    authority = this.storage.getVolumeAuthority(),
    opts?: { consumeSeed?: SeedSpaceProof }
  ): Promise<ActiveMemoryShelfOpenResult> {
    const shelf = await this.storage.loadVolumeShelfSpace(spaceId, authority)
    if (shelf?.meta.deleted_at) return { ok: false, reason: 'missing' }
    if (!shelf) return { ok: false, reason: 'missing' }
    const availability = deriveLocalSpaceAvailability(shelf.meta, Boolean(shelf.blob))
    this.updateIndexSpaceAvailability(spaceId, availability)
    if (availability === 'unavailable') {
      return { ok: false, reason: 'missing-payload' }
    }
    if (availability === 'damaged' || !shelf.blob) {
      return { ok: false, reason: 'invalid-payload' }
    }
    try {
      const opened = await this.openShelfSpaceInEditor(spaceId, authority, opts)
      if (!opened) return { ok: false, reason: 'missing-payload' }
      return { ok: true, meta: shelf.meta }
    } catch {
      const latest = await this.storage.loadVolumeShelfSpace(spaceId, authority)
        .catch(() => undefined)
      if (
        latest
        && deriveLocalSpaceAvailability(latest.meta, Boolean(latest.blob)) === 'damaged'
      ) {
        this.updateIndexSpaceAvailability(spaceId, 'damaged')
      }
      return { ok: false, reason: 'invalid-payload' }
    }
  }

  private async openLiveShelfReplacement(
    rows: LocalSpaceMeta[],
    currentSpaceId: string,
    authority = this.storage.getVolumeAuthority()
  ): Promise<{ opened: boolean; hadCandidates: boolean }> {
    const remaining = [...rows]
    let hadCandidates = false
    while (true) {
      const candidate = selectLandingSpace(remaining, { excludeSpaceId: currentSpaceId })
      if (!candidate) return { opened: false, hadCandidates }
      hadCandidates = true
      const opened = await this.enterSpaceFromIndex(candidate.space_id, {
        skipFlush: true,
        authority
      })
      if (opened) return { opened: true, hadCandidates }
      const index = remaining.indexOf(candidate)
      if (index >= 0) remaining.splice(index, 1)
    }
  }

  private async runForegroundShelfHydration<T>(operation: () => Promise<T>): Promise<T> {
    this.preemptOwnerShelfMirrorQueue()
    this.syncForegroundShelfDepth += 1
    if (this.accountShelfReadiness !== 'blocked') {
      this.setAccountShelfReadiness('pending')
    }
    try {
      return await operation()
    } finally {
      this.syncForegroundShelfDepth = Math.max(0, this.syncForegroundShelfDepth - 1)
      this.scheduleOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
    }
  }

  private async ensureShelfSpaceHydrated(
    spaceId: string,
    authority = this.storage.getVolumeAuthority()
  ): Promise<boolean> {
    if (!this.authSession) return false
    const ownerId = getSyncOwnerId(this.authSession)
    if (authority?.owner_id !== ownerId) return false
    const before = await this.storage.loadVolumeShelfSpace(spaceId, authority)
    if (this.resolveConnectivityState().isOfflineLike) {
      return Boolean(before?.blob)
    }
    try {
      const result = await this.runForegroundShelfHydration(() => (
        this.retryFetch(() => this.syncEngine.hydrateShelfSpace({
          ownerId,
          spaceId,
          volumeAuthority: authority
        }))
      ))
      if (!result.ok && !before?.blob) return false
    } catch {
      if (!before?.blob) return false
    }
    const after = await this.storage.loadVolumeShelfSpace(spaceId, authority)
    return Boolean(after?.blob)
  }

  private async hydrateAndOpenSyncShelfSpace(
    space: SyncSpaceMeta,
    authority = this.storage.getVolumeAuthority(),
    opts?: { consumeSeedSpaceId?: string }
  ): Promise<boolean> {
    if (!this.authSession) return false
    const ownerId = getSyncOwnerId(this.authSession)
    if (authority?.owner_id !== ownerId) return false
    const hydrated = await this.runForegroundShelfHydration(() => (
      this.syncEngine.hydrateShelfSpace({
        ownerId,
        spaceId: space.space_id,
        volumeAuthority: authority
      })
    ))
    if (!hydrated.ok) return false
    const consumeSeed = opts?.consumeSeedSpaceId
      ? await this.resolveCleanSeedProof(opts.consumeSeedSpaceId, authority)
      : undefined
    if (opts?.consumeSeedSpaceId && !consumeSeed) return false
    const opened = await this.openActiveMemoryShelfSpace(
      space.space_id,
      authority,
      consumeSeed ? { consumeSeed } : undefined
    )
    if (!opened.ok) return false
    if (!await this.storage.isVolumeAuthorityCurrent(authority)) return false
    if (consumeSeed) {
      this.indexSpaces.delete(consumeSeed.space_id)
      this.setSyncRuntimePendingHint(consumeSeed.space_id, false)
    }

    const rawSlug = opened.meta.space_slug ?? space.space_slug
    const slug = (rawSlug && rawSlug.length)
      ? this.normalizeSpaceSlug(rawSlug, space.space_id)
      : this.spaceIdToDefaultSlug(space.space_id)
    this.syncSpaceId = space.space_id
    this.syncSpaceSlug = slug
    this.rememberReadyIndexSpace(space.space_id, slug, {
      sizeBytes: opened.meta.size_bytes,
      spaceEd: opened.meta.space_ed
    })
    this.settleCloudIdentity({ syncSpaceId: space.space_id })
    this.setActiveSpace(space.space_id, slug)
    this.setSyncRuntimePendingHint(space.space_id, false)
    this.history = new History()
    this.document.clearSelection()
    this.setAppState('edit')
    return true
  }

  private async refreshSyncSpacesFromBridge(
    authority = this.storage.getVolumeAuthority()
  ): Promise<SyncSpaceMeta[]> {
    if (!this.authSession) throw new Error('missing_session')
    const ownerId = getSyncOwnerId(this.authSession)
    const result = await this.syncEngine.feedShelf({
      reconcileDirtyTombstonesForOwnerId: this.canSyncShelfWriteNow() ? ownerId : undefined,
      volumeAuthority: authority
    })
    for (const reconciled of result.reconciled) {
      this.setSyncRuntimePendingHint(reconciled.spaceId, !reconciled.ok)
    }
    const rows = await this.listProductVolumeSpacesMeta(authority)
    return this.projectLiveSyncSpaces(rows)
  }

  private async listSyncShelfSpaces(
    authority = this.storage.getVolumeAuthority()
  ): Promise<SyncSpaceMeta[]> {
    if (!this.authSession) throw new Error('missing_session')
    const ownerId = getSyncOwnerId(this.authSession)
    const rows = await this.listProductVolumeSpacesMeta(authority)
    if (rows.some(row => row.owner_id !== ownerId)) {
      throw new Error('Sync shelf read blocked: owner is outside the mounted volume')
    }
    return this.projectLiveSyncSpaces(rows)
  }

  async listLocalShelfSpaces(): Promise<SyncSpaceMeta[]> {
    const rows = await this.storage.listVolumeSpacesMeta()
    if (rows.some(row => row.owner_id !== null)) {
      throw new Error('Anonymous shelf read blocked: Account memory is mounted')
    }
    return this.projectLiveSyncSpaces(rows)
  }

  private projectLiveSyncSpaces(rows: LocalSpaceMeta[]): SyncSpaceMeta[] {
    return selectLiveShelfRows(rows.map(projectSyncSpaceMeta))
  }

  private setDetachedResumeIntent(intent?: 'index'): void {
    try {
      if (typeof sessionStorage === 'undefined') return
      if (intent) {
        sessionStorage.setItem(DETACHED_RESUME_INTENT_KEY, intent)
      } else {
        sessionStorage.removeItem(DETACHED_RESUME_INTENT_KEY)
      }
    } catch {
      // Ignore storage capability failures (private mode / quota / disabled storage).
    }
  }

  private shouldResumeDetachedIndex(): boolean {
    try {
      if (typeof sessionStorage === 'undefined') return false
      return sessionStorage.getItem(DETACHED_RESUME_INTENT_KEY) === 'index'
    } catch {
      return false
    }
  }

  private async enterSpaceFromIndex(
    spaceId: string,
    opts?: {
      skipFlush?: boolean
      authority?: LocalVolumeAuthority
    }
  ): Promise<boolean> {
    const authority = opts?.authority !== undefined
      ? opts.authority
      : this.storage.getVolumeAuthority()
    const lane: ShelfOwnerLane = { ownerId: authority?.owner_id ?? null }
    const ownerShelfResumeDelay = lane.ownerId
      ? this.preemptOwnerShelfMirrorQueue()
      : undefined
    if (lane.ownerId) {
      this.syncForegroundShelfDepth += 1
    }
    try {
      if (!opts?.skipFlush && !this.isIndexSpace()) {
        this.commitActiveEditIfNeeded()
        await this.storage.flushActiveDocumentForVolume(
          this.document.getStateSnapshot(),
          authority
        )
      }
      let opened = await this.openActiveMemoryShelfSpace(spaceId, authority)
      const runtimePolicy = resolveRuntimePolicy({
        snapshot: this.buildAccountSnapshot(),
        connectivity: this.resolveConnectivityState(),
        lane: this.resolveCurrentLaneState(),
        space: this.resolveSpaceKind()
      })
      if (shouldHydrateMissingShelfPayload(lane, opened, {
        mountedAccountOwnerId: runtimePolicy.authority.mountedAccountOwnerId,
        cloudPosture: runtimePolicy.cloudPosture
      })) {
        const ownerId = lane.ownerId
        if (!ownerId) return false
        try {
          await this.runForegroundShelfHydration(() => (
            this.syncEngine.hydrateShelfSpace({
              ownerId,
              spaceId,
              volumeAuthority: authority
            })
          ))
          opened = await this.openActiveMemoryShelfSpace(spaceId, authority)
        } catch {
          // Fall through to the local-memory error below.
        }
      }
      if (!opened.ok) {
        const message = selectMemoryOpenFailureMessage(opened.reason)
        this.commands.setStatus(message, 'error')
        this.commands.ensurePaletteVisible()
        return false
      }
      this.history = new History()
      this.document.clearSelection()
      this.focusLastEditedBlock({ requireMeaningfulEdit: true })
      this.applyCssGridTransform()
      const rawSlug = opened.meta.space_slug?.trim()
      const slug = rawSlug
        ? this.normalizeSpaceSlug(rawSlug, spaceId)
        : this.indexSpaces.get(spaceId)?.slug
      const resolvedSlug = slug ?? this.spaceIdToDefaultSlug(spaceId)
      this.rememberReadyIndexSpace(spaceId, resolvedSlug, {
        sizeBytes: opened.meta.size_bytes,
        spaceEd: opened.meta.space_ed
      })
      if (lane.ownerId) {
        this.syncSpaceId = spaceId
        this.syncSpaceSlug = resolvedSlug
        this.isSyncPaired = true
      } else {
        this.syncSpaceId = undefined
        this.syncSpaceSlug = undefined
        this.isSyncPaired = false
        this.settleCloudIdentity({ syncSpaceId: null, isSyncPaired: false })
      }
      this.setActiveSpace(spaceId, resolvedSlug)
      this.setAppState(lane.ownerId && this.authAccountState === 'deactivated' ? 'view' : 'edit')
      if (lane.ownerId) {
        this.syncEngine.stop()
        this.setSyncEngineState('idle', 1)
      }
      this.commands.setSyncIndicatorVisible(true)
      this.checkSyncOperatingContract('enterSpaceFromIndex')
      this.requestRender()
      return true
    } finally {
      if (lane.ownerId) {
        this.syncForegroundShelfDepth = Math.max(0, this.syncForegroundShelfDepth - 1)
      }
      if (ownerShelfResumeDelay !== undefined) {
        this.scheduleOwnerShelfMirror(ownerShelfResumeDelay)
      }
    }
  }

  private async enterDeactivatedShelfFromLocalMemory(): Promise<boolean> {
    if (!this.authSession) return false
    const authority = this.storage.getVolumeAuthority()
    if (
      !authority
      || authority.owner_id !== getSyncOwnerId(this.authSession)
    ) return false

    let spaces: SyncSpaceMeta[] = []
    try {
      await this.retryFetch(() => this.refreshSyncSpacesFromBridge(authority))
      spaces = await this.listSyncShelfSpaces(authority)
    } catch {
      spaces = []
    }

    this.indexSpaces.clear()
    spaces.forEach((space) => {
      const slug = (space.space_slug && space.space_slug.length)
        ? this.normalizeSpaceSlug(space.space_slug, space.space_id)
        : this.spaceIdToDefaultSlug(space.space_id)
      this.rememberReadyIndexSpace(space.space_id, slug, {
        sizeBytes: space.size_bytes,
        spaceEd: space.space_ed
      })
    })

    const landingSpace = selectLandingSpace(spaces)
    if (!landingSpace) {
      this.isSyncPaired = true
      this.syncSpaceId = undefined
      this.syncSpaceSlug = undefined
      this.syncEngine.stop()
      this.setSyncEngineState('idle', 1)
      this.setAppState('view')
      this.commands.setSyncIndicatorVisible(true)
      this.openIndexSurfaceDeterministic()
      this.commands.ensurePaletteVisible()
      return true
    }

    await this.enterSpaceFromIndex(landingSpace.space_id, { authority })
    if (this.syncSpaceId !== landingSpace.space_id) {
      return false
    }

    this.setAppState('view')
    this.openIndexSurfaceDeterministic()
    this.commands.ensurePaletteVisible()
    return true
  }

  private async createNewSpace(): Promise<void> {
    if (!this.canEnterIndex()) return
    if (
      this.storage.getActiveDeskState() === 'filed'
      && this.storage.getMetaBlock()
    ) {
      const authority = this.storage.getVolumeAuthority()
      this.commitActiveEditIfNeeded()
      await this.storage.flushActiveDocumentForVolume(
        this.document.getStateSnapshot(),
        authority
      )
    }
    await this.createLocalBlankSpacePreservingShelf()
    this.setSyncEngineState('idle', 0)
    this.commands.setSyncIndicatorVisible(true)
    this.commands.setStatus('unsaved space ready', 'success')
    this.checkSyncOperatingContract('createNewSpace')
    this.requestRender()
  }

  private async claimCurrentLocalSpaceIntoSync(
    localBlocks = this.document.getStateSnapshot(),
    localIdentity = this.getSpaceIdentity(localBlocks),
    authority = this.storage.getVolumeAuthority()
  ): Promise<boolean> {
    if (this.storage.getActiveDeskState() !== 'filed') return false
    if (!this.authSession || !this.authUsername || !this.authIsSync) return false
    if (this.resolveConnectivityState().isOfflineLike) return false
    if (
      !authority
      || authority.owner_id !== getSyncOwnerId(this.authSession)
    ) return false

    const meta = this.storage.getMetaBlock()
    const parsedSlug = meta ? parseLilodataText(meta.text).slug?.trim() : undefined
    const slug = parsedSlug && parsedSlug.length
      ? parsedSlug
      : this.spaceIdToDefaultSlug(localIdentity.spaceId)
    const json = this.storage.exportToJSON(localBlocks)
    await this.storage.flushActiveDocumentForVolume(localBlocks, authority)
    const accepted = await this.saveSyncSpaceWithShelfProof({
      spaceId: localIdentity.spaceId,
      spaceSlug: slug,
      spaceEd: localIdentity.spaceEd,
      json
    }, 'claim', authority)
    await this.refreshActiveSpaceLabelFromLocalShelf(
      authority,
      localIdentity.spaceId
    )
    if (!accepted) return false

    this.isSyncPaired = true
    this.syncSpaceId = localIdentity.spaceId
    this.syncSpaceSlug = accepted.space_slug ?? slug
    this.rememberReadyIndexSpace(localIdentity.spaceId, this.syncSpaceSlug, {
      sizeBytes: accepted.size_bytes,
      spaceEd: accepted.space_ed
    })
    this.setActiveSpace(localIdentity.spaceId, this.syncSpaceSlug)
    this.setSyncRuntimePendingHint(localIdentity.spaceId, false)
    this.applySyncUiState()
    return true
  }

  private async resolveStartupAccountWorkbench(input: {
    enabled?: boolean
    localBlocks: SpaceEntry[]
    localIdentity: { spaceId: string, spaceEd: string }
    localHasMeaningfulDraft: boolean
    authority: NonNullable<LocalVolumeAuthority>
  }): Promise<{ handled: boolean, pulledRemote: boolean }> {
    if (!input.enabled) return { handled: false, pulledRemote: false }
    if (!this.authSession || !this.authUsername || !this.authIsSync) return { handled: false, pulledRemote: false }
    if (this.isIndexSpace()) return { handled: false, pulledRemote: false }
    if (!input.localHasMeaningfulDraft) return { handled: false, pulledRemote: false }

    const ownerId = getSyncOwnerId(this.authSession)
    if (input.authority.owner_id !== ownerId) {
      return { handled: false, pulledRemote: false }
    }
    const activeAccountShelf = await this.storage.loadVolumeShelfSpace(
      input.localIdentity.spaceId,
      input.authority
    )
    if (activeAccountShelf?.blob && !activeAccountShelf.meta.deleted_at) {
      return { handled: false, pulledRemote: false }
    }

    const fallbackSlug = this.spaceIdToDefaultSlug(input.localIdentity.spaceId)
    let remoteSpaces: SyncSpaceMeta[]
    try {
      remoteSpaces = await this.listSyncSpacesForBridge(input.authority)
    } catch {
      return { handled: false, pulledRemote: false }
    }
    const plan = resolveStartupAccountWorkbenchPlan({
      enabled: input.enabled,
      isAuthenticatedSync: true,
      isIndexSpace: false,
      localHasMeaningfulDraft: input.localHasMeaningfulDraft,
      hasActiveAccountShelfBlob: false,
      localSpaceId: input.localIdentity.spaceId,
      remoteSpaces,
      defaultSlug: fallbackSlug,
      normalizeSlug: (spaceSlug, spaceId) => this.normalizeSpaceSlug(spaceSlug, spaceId)
    })
    if (plan.action !== 'resolve') return { handled: false, pulledRemote: false }

    await this.storage.flushActiveDocumentForOwner(input.localBlocks, ownerId, {
      spaceSlug: plan.slug,
      dirty: true,
      authority: input.authority
    })

    this.isSyncPaired = true
    this.syncSpaceId = input.localIdentity.spaceId
    this.syncSpaceSlug = plan.slug
    this.rememberReadyIndexSpace(input.localIdentity.spaceId, plan.slug, {
      spaceEd: input.localIdentity.spaceEd
    })
    this.settleCloudIdentity({
      syncSpaceId: input.localIdentity.spaceId,
      isSyncPaired: true
    })
    this.setActiveSpace(input.localIdentity.spaceId, plan.slug)
    this.setSyncRuntimePendingHint(input.localIdentity.spaceId, true)

    const result = await this.syncEngine.reconcileShelfSpace({
      ownerId,
      spaceId: input.localIdentity.spaceId,
      volumeAuthority: input.authority
    })
    if (result.ok) {
      this.setSyncRuntimePendingHint(input.localIdentity.spaceId, false)
      await this.refreshActiveSpaceLabelFromLocalShelf(input.authority)
    } else {
      this.setSyncRuntimePendingHint(input.localIdentity.spaceId, true)
    }

    return {
      handled: true,
      pulledRemote: result.ok && (result.action === 'pulled' || result.action === 'merged')
    }
  }

  private async adoptActiveWorkbenchFromLocalShelf(
    localIdentity: { spaceId: string, spaceEd: string },
    authority = this.storage.getVolumeAuthority()
  ): Promise<boolean> {
    if (!this.authSession || !this.authUsername || !this.authIsSync) return false
    const shelf = await this.storage.loadVolumeShelfSpace(
      localIdentity.spaceId,
      authority
    )
    if (!shelf?.blob || shelf.meta.deleted_at) return false

    const slug = shelf.meta.space_slug?.trim() || this.spaceIdToDefaultSlug(localIdentity.spaceId)
    this.isSyncPaired = true
    this.syncSpaceId = localIdentity.spaceId
    this.syncSpaceSlug = slug
    this.rememberReadyIndexSpace(localIdentity.spaceId, slug, {
      sizeBytes: shelf.meta.size_bytes,
      spaceEd: shelf.meta.space_ed
    })
    this.settleCloudIdentity({
      syncSpaceId: localIdentity.spaceId,
      isSyncPaired: true
    })
    this.setActiveSpace(localIdentity.spaceId, slug)
    this.setSyncRuntimePendingHint(localIdentity.spaceId, shelf.meta.dirty)
    this.applySyncUiState()
    return true
  }

  private shouldTrustActiveSyncLane(localIdentity: { spaceId: string, spaceEd: string }): boolean {
    if (!this.authSession || !this.authUsername || !this.authIsSync) return false
    if (!this.isSyncPaired) return false
    if (!this.syncSpaceId || this.syncSpaceId !== localIdentity.spaceId) return false
    if (!this.activeSpaceId || this.activeSpaceId !== localIdentity.spaceId) return false
    return true
  }

  private async reconcileTrustedActiveLaneWithoutShelfMatch(
    localBlocks: SpaceEntry[],
    localIdentity: { spaceId: string, spaceEd: string },
    authority = this.storage.getVolumeAuthority()
  ): Promise<{ ok: boolean, pulledRemote: boolean }> {
    if (!this.syncSpaceId || this.syncSpaceId !== localIdentity.spaceId) {
      return { ok: false, pulledRemote: false }
    }

    const trustedSpaceId = this.syncSpaceId
    const trustedSlug = this.syncSpaceSlug ?? this.spaceIdToDefaultSlug(trustedSpaceId)
    this.setActiveSpace(trustedSpaceId, trustedSlug)
    await this.storage.flushActiveDocumentForVolume(localBlocks, authority)
    await this.storage.markVolumeSpaceDirty(
      authority,
      trustedSpaceId,
      true
    )
    if (!this.authSession) return { ok: false, pulledRemote: false }
    const result = await this.syncEngine.recoverActiveShelfSpace({
      ownerId: getSyncOwnerId(this.authSession),
      spaceId: trustedSpaceId,
      volumeAuthority: authority
    })
    if (!result.ok) {
      const claimed = await this.claimCurrentLocalSpaceIntoSync(
        localBlocks,
        localIdentity,
        authority
      )
      return { ok: claimed, pulledRemote: false }
    }

    return { ok: true, pulledRemote: result.action === 'pulled' || result.action === 'merged' }
  }

  private async listSyncSpaces(_token: string, opts?: { strict?: boolean }): Promise<SyncSpaceMeta[]> {
    try {
      const ownerId = getSyncOwnerId(this.requireVerifiedCloudSession())
      const spaces = await listSyncSpacesDirect(supabase, ownerId)
      this.promoteReachabilityOnCloudSuccess()
      return spaces
    } catch (error) {
      if (opts?.strict) throw error
      return []
    }
  }

  private async loadSyncSpace(_token: string, spaceId: string, opts?: { strict?: boolean }): Promise<string | undefined> {
    try {
      const ownerId = getSyncOwnerId(this.requireVerifiedCloudSession())
      const outcome = await loadSyncSpaceJson(supabase, ownerId, spaceId)
      if (outcome.status === 'retryable-failure') {
        throw new Error('sync_space_payload_retryable')
      }
      this.promoteReachabilityOnCloudSuccess()
      if (outcome.status !== 'payload') return undefined
      const json = outcome.payload
      void incrementUsageOdometer({
        syncPullCountDelta: 1,
        syncPullBytesDelta: measureUtf8Bytes(json)
      }, supabase).catch(() => undefined)
      return json
    } catch (error) {
      if (opts?.strict) throw error
      return undefined
    }
  }

  private requireVerifiedCloudSession(): Session {
    const session = this.authSession
    const passport = this.storage.getMountedPassport()
    if (
      !session
      || !passport
      || session.user.id !== passport.owner_id
      || !hasVerifiedCloudAuthority(this.buildAccountSnapshot())
    ) {
      throw new Error('missing_verified_cloud_authority')
    }
    return session
  }

  private requireSyncVolumeAuthority(
    authority: LocalVolumeAuthority | undefined
  ): NonNullable<LocalVolumeAuthority> {
    if (!authority) {
      throw new Error('missing_mounted_volume_authority')
    }
    return authority
  }

  private async requireCurrentSyncCloudSession(
    authority?: LocalVolumeAuthority
  ): Promise<{
    authority: NonNullable<LocalVolumeAuthority>
    session: Session
  }> {
    const resolved = this.requireSyncVolumeAuthority(
      authority !== undefined
        ? authority
        : this.storage.getVolumeAuthority()
    )
    if (!await this.storage.isVolumeAuthorityCurrent(resolved)) {
      throw new Error('stale_mounted_volume_authority')
    }
    const session = this.requireVerifiedCloudSession()
    if (!this.storage.matchesVolumeAuthority(resolved) || session.user.id !== resolved.owner_id) {
      throw new Error('stale_mounted_volume_authority')
    }
    return { authority: resolved, session }
  }

  private async listSyncSpacesForBridge(
    authority?: LocalVolumeAuthority
  ): Promise<RemoteSpaceMetaInput[]> {
    const { session } = await this.requireCurrentSyncCloudSession(authority)
    const ownerId = getSyncOwnerId(session)
    const spaces = await listSyncSpacesDirect(supabase, ownerId, { includeDeleted: true })
    this.promoteReachabilityOnCloudSuccess()
    return spaces.map(space => ({
      owner_id: ownerId,
      space_id: space.space_id,
      space_slug: space.space_slug ?? null,
      space_ed: space.space_ed ?? null,
      sync_at: space.sync_at ?? null,
      size_bytes: space.size_bytes ?? null,
      path: space.path ?? null,
      deleted_at: space.deleted_at ?? null
    }))
  }

  private async loadSyncSpaceForBridge(
    spaceId: string,
    authority?: LocalVolumeAuthority
  ): Promise<SyncSpacePayloadLoadOutcome> {
    const current = await this.requireCurrentSyncCloudSession(authority)
    const ownerId = getSyncOwnerId(current.session)
    const outcome = await loadSyncSpaceJson(supabase, ownerId, spaceId)
    if (outcome.status === 'payload') {
      void incrementUsageOdometer({
        syncPullCountDelta: 1,
        syncPullBytesDelta: measureUtf8Bytes(outcome.payload)
      }, { rpc: (name, args) => supabase!.rpc(name, args)
        .setHeader('Authorization', `Bearer ${current.session.access_token}`) }).catch(() => undefined)
    }
    await this.requireCurrentSyncCloudSession(current.authority)
    if (outcome.status !== 'retryable-failure') this.promoteReachabilityOnCloudSuccess()
    return outcome
  }

  private async saveSyncSpaceForBridge(
    input: SyncShelfSaveInput,
    authority?: LocalVolumeAuthority
  ): Promise<SyncShelfSaveOutcome> {
    try {
      const current = await this.requireCurrentSyncCloudSession(authority)
      const ownerId = getSyncOwnerId(current.session)
      const result = await saveSyncSpaceSnapshot(supabase, ownerId, {
        spaceId: input.spaceId,
        spaceSlug: input.spaceSlug,
        spaceEd: input.spaceEd,
        expectedPath: input.expectedPath,
        json: input.payload
      }, {
        session: current.session,
        assertCurrent: async () => { await this.requireCurrentSyncCloudSession(current.authority) }
      })
      if ('ok' in result && result.ok === false) return result
      this.promoteReachabilityOnCloudSuccess()
      if (result.syncAt) this.rememberLocalSyncEcho(input.spaceId, result.syncAt)
      this.scheduleOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
      return result
    } catch (error) {
      return {
        ok: false,
        reason: isSyncSpaceSlugConflictError(error) ? 'slug-conflict' : 'failed'
      }
    }
  }

  private async deleteSyncSpaceForBridge(
    input: { spaceId: string, deletedAt: string },
    authority?: LocalVolumeAuthority
  ): Promise<SyncShelfSaveResult> {
    const current = await this.requireCurrentSyncCloudSession(authority)
    const ownerId = getSyncOwnerId(current.session)
    const result = await deleteSyncSpaceDirect(supabase, ownerId, input.spaceId, input.deletedAt, {
      session: current.session,
      assertCurrent: async () => { await this.requireCurrentSyncCloudSession(current.authority) }
    })
    this.promoteReachabilityOnCloudSuccess()
    this.scheduleOwnerShelfMirror(SYNC_SHELF_HYDRATION_QUIET_GAP_MS)
    return result
  }

  private async retryFetch<T>(task: () => Promise<T>, attempts = 4, baseDelayMs = 400): Promise<T> {
    let lastError: unknown
    for (let i = 0; i < attempts; i += 1) {
      try {
        return await task()
      } catch (error) {
        lastError = error
        if (i >= attempts - 1) break
        const delay = baseDelayMs * Math.pow(2, i)
        await this.wait(delay)
      }
    }
    throw lastError ?? new Error('retry failed')
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(() => resolve(), ms)
    })
  }

  private async saveSyncSpaceWithShelfProof(
    payload: { spaceId: string, spaceSlug: string, spaceEd: string, json: string },
    _proof: 'claim' | 'sync-success' | 'remote-meta-only',
    authority = this.storage.getVolumeAuthority()
  ): Promise<LocalSpaceMeta | undefined> {
    if (!authority) return undefined
    await this.requireCurrentSyncCloudSession(authority)
    const local = await this.storage.loadVolumeShelfSpace(payload.spaceId, authority)
    if (!local?.blob || local.meta.deleted_at) return undefined
    await this.storage.markVolumeSpaceDirty(authority, payload.spaceId, true)
    const result = await this.syncEngine.reconcileShelfSpace({
      ownerId: authority.owner_id,
      spaceId: payload.spaceId,
      volumeAuthority: authority
    })
    if (!result.ok || result.pending) return undefined
    const accepted = (await this.storage.loadVolumeShelfSpace(payload.spaceId, authority))?.meta
    if (!this.storage.matchesVolumeAuthority(authority) || this.storage.getMetaBlock()?.ided[0] !== payload.spaceId) return undefined
    return accepted
  }

  private async createLocalBlankSpacePreservingShelf(opts?: {
    authority?: LocalVolumeAuthority
  }): Promise<void> {
    const authority = opts?.authority !== undefined
      ? opts.authority
      : this.storage.getVolumeAuthority()
    const seedBlocks = await this.storage.loadSeedBlocks()
    const seeded = seedBlocks
      ? instantiateSeedSpace(seedBlocks, { fallbackPaperLabel: this.getPaperSpec().label })
      : { blocks: [] as SpaceEntry[], metaBlock: createLilodataBlock(this.getPaperSpec().label) }
    const nextMeta = seeded.metaBlock
    if (!nextMeta) return
    const spaceId = nextMeta.ided[0]
    await this.storage.setProvisionalWelcome(seeded.blocks, nextMeta, authority)
    this.seedClockFromTimeline(seeded.blocks, nextMeta)
    this.document.hydrate(seeded.blocks)
    this.history = new History()
    this.document.clearSelection()
    this.syncSpaceId = undefined
    this.syncSpaceSlug = undefined
    this.isSyncPaired = false
    this.settleCloudIdentity({
      syncSpaceId: null,
      isSyncPaired: false
    })
    this.autoSyncLocalFallbackOnEdit = false
    this.setActiveSpace(spaceId, this.spaceIdToDefaultSlug(spaceId))
    this.setAppState('edit')
    this.requestRender()
  }

  private async unmountAccountForExit(
    authority: AccountUnmountAuthority,
    mode: AccountUnmountMode
  ): Promise<AccountUnmountResult> {
    await this.storage.waitForActiveFiling()
    if (mode === 'guarded') {
      try {
        await this.flushPendingActiveSyncExitIfNeeded(authority)
      } catch {
        return {
          ok: false,
          reason: 'unsafe',
          unsafe_rows: this.unresolvedAccountMemoryDebt()
        }
      }
    }
    const safeSeedProofs = mode === 'guarded'
      ? (await this.inspectProductShelf(authority)).seedProofs
      : []
    const result = await this.storage.unmountAccountMemory(
      authority,
      mode,
      safeSeedProofs
    )
    if (result.ok) {
      this.resetOwnerShelfHydrationState()
    }
    return result
  }

  private async finishAccountExitLocalLanding(options: {
    reuseAnonymousBaseline?: boolean
    beforeAuthClear?: () => void
  } = {}): Promise<void> {
    this.stopAccountExitRuntimeMovement()
    this.applyLocalSessionExit()
    let landingError: unknown
    try {
      if (options.reuseAnonymousBaseline) {
        await this.landInExistingAnonymousBaseline()
      } else {
        await this.landInResetAnonymousBaseline()
      }
    } catch (error) {
      landingError = error
    }
    options.beforeAuthClear?.()
    await this.clearLogoutPersistenceArtifacts()
    if (landingError) throw landingError
  }

  private prepareLocalBaselineLanding(): void {
    this.input.stopEditing()
    this.exitSelectConnectedMode({ preserveSelection: true })
    this.exitCopyMode({ preserveSelection: true })
    this.exitPasteMode()
    this.exitDeleteMode({ preserveSelection: true })
    this.exitMultiSelectMode({ preserveNextClick: true })
    this.resetInteractionState()
  }

  private async landInLocalBaseline(opts?: { skipAuthenticatedSyncLane?: boolean }): Promise<void> {
    void opts
    this.prepareLocalBaselineLanding()
    await this.createLocalBlankSpacePreservingShelf()
  }

  private applyAnonymousBaselineToRuntime(blocks: SpaceEntry[], metaBlock: Block): void {
    const spaceId = metaBlock.ided[0]
    this.seedClockFromTimeline(blocks, metaBlock)
    this.document.hydrate(blocks)
    this.history = new History()
    this.document.clearSelection()
    this.syncSpaceId = undefined
    this.syncSpaceSlug = undefined
    this.settleCloudIdentity({ syncSpaceId: null, isSyncPaired: false })
    this.autoSyncLocalFallbackOnEdit = false
    this.setActiveSpace(spaceId, this.spaceIdToDefaultSlug(spaceId))
    this.setAppState('edit')
    this.requestRender()
  }

  private async landInExistingAnonymousBaseline(): Promise<void> {
    this.prepareLocalBaselineLanding()
    const blocks = await this.storage.loadBlocks()
    const metaBlock = this.storage.getMetaBlock()
    const shelfMeta = metaBlock
      ? (await this.storage.loadVolumeShelfSpace(metaBlock.ided[0]))?.meta
      : undefined
    if (!metaBlock || shelfMeta?.owner_id !== null) {
      await this.landInResetAnonymousBaseline()
      return
    }
    this.applyAnonymousBaselineToRuntime(blocks, metaBlock)
  }

  private async landInResetAnonymousBaseline(): Promise<void> {
    this.prepareLocalBaselineLanding()
    await this.createLocalBlankSpacePreservingShelf({ authority: null })
  }

  private applyMergedBlocks(
    blocks: SpaceEntry[],
    metaBlock?: Block,
    opts?: {
      persist?: boolean
      authority?: LocalVolumeAuthority
    }
  ): void {
    const persist = opts?.persist !== false
    // Installing the merge in memory is independent of scheduling persistence.
    // The guarded sync flush must capture this passport together with its blocks.
    if (metaBlock) this.storage.setMetaBlock(metaBlock)
    const shouldPersistMeta = this.applyPaperMeta(metaBlock, blocks, { persist })
    this.seedClockFromTimeline(blocks, this.storage.getMetaBlock())
    this.document.hydrate(blocks)
    if (persist) {
      this.commitWorkbench(this.document.getStateSnapshot(), opts?.authority)
      if (shouldPersistMeta) {
        this.commitWorkbench(this.document.getStateSnapshot(), opts?.authority)
      }
    }
    if (!persist && this.searchSession) this.commands.refreshLiveInput()
    this.requestRender()
  }

  private async prepareActiveSpaceForSync(spaceId: string, authority?: LocalVolumeAuthority): Promise<void> {
    const mounted = this.requireSyncVolumeAuthority(authority)
    const isCurrent = () => !this.isIndexSpace()
      && this.storage.getMetaBlock()?.ided[0] === spaceId
      && this.storage.matchesVolumeAuthority(mounted)
    if (!isCurrent()) return
    if (!await this.storage.isVolumeAuthorityCurrent(mounted)) throw new Error('stale_mounted_volume_authority')
    const shelf = await this.storage.loadVolumeShelfSpace(spaceId, mounted)
    if (!isCurrent() || !shelf?.blob || shelf.meta.deleted_at) return
    const saved = parseBlocksFromJson(shelf.blob.payload)
    const blocks = this.document.getStateSnapshot()
    let meta = this.storage.getMetaBlock()!
    if (blocks.some(block => block.ided[1] > meta.ided[1])) {
      this.seedClockFromTimeline(blocks, meta)
      this.bumpSpaceEditId()
      meta = this.storage.getMetaBlock()!
    }
    const live = [...blocks, meta]
    const merged = mergeSpaceSnapshots(saved, live)
    const changedLive = serializeBlocks(merged.mergedAll) !== serializeBlocks(live)
    const changedShelf = serializeBlocks(merged.mergedAll) !== serializeBlocks(saved)
    if (!changedLive && !changedShelf) return
    const selected = this.document.getSelectedBlockIds()
    const connections = this.document.getSelectedConnections()
    // Applying is synchronous: no space switch can occur between checking and hydration.
    if (changedLive) {
      this.applyMergedBlocks(merged.blocks, merged.metaBlock, { persist: false })
      this.document.setSelection(selected, connections)
    } else {
      this.storage.setMetaBlock(merged.metaBlock)
    }
    // This flush cancels queued pre-merge snapshots and orders itself after running saves.
    await this.storage.flushActiveDocumentForVolume(merged.blocks, mounted, shelf.blob.payload)
    if (changedShelf) await this.storage.markVolumeSpaceDirty(mounted, spaceId, true)
  }

  private async performCloudSync(): Promise<boolean> {
    if (!this.canSyncWriteNow()) {
      this.setSyncRuntimePendingHintForActiveSpace(false)
      this.warnPolicyOnceDev(
        'sync-write-perform-blocked',
        '[policy] blocked performCloudSync while canSyncWrite=false',
        { accountState: this.authAccountState, appState: this.appState, isIndex: this.isIndexSpace() }
      )
      return true
    }
    if (!this.authSession || !this.syncSpaceId || this.isIndexSpace()) return false
    const spaceId = this.syncSpaceId
    const authority = this.storage.getVolumeAuthority()
    if (
      !authority
      || authority.owner_id !== getSyncOwnerId(this.authSession)
    ) return false
    const localBlocks = this.document.getStateSnapshot()
    const localIdentity = this.getSpaceIdentity(localBlocks)
    if (!this.isLocalIdentityAligned(spaceId, localIdentity)) {
      return this.attemptIdentityRehome(localBlocks, localIdentity, authority)
    }
    if (this.hasActiveShelfConsolidationDebt(spaceId)) return false
    if (!await this.hasActiveSyncShelfBlob(spaceId, authority)) return false
    await this.storage.markVolumeSpaceDirty(authority, spaceId, true)
    const result = await this.syncEngine.syncActiveShelfSpace({
      ownerId: authority.owner_id,
      spaceId,
      volumeAuthority: authority
    })
    await this.refreshActiveSpaceLabelFromLocalShelf(authority, spaceId)
    if (result.ok) {
      if (this.syncSpaceId === spaceId && this.storage.matchesVolumeAuthority(authority)
        && (result.pending || this.hasActiveShelfConsolidationDebt(spaceId))) this.syncEngine.markDirty()
      return true
    }
    if (result.reason === 'remote_replace_blocked') return true
    return false
  }

  private async refreshActiveSpaceLabelFromLocalShelf(
    authority = this.storage.getVolumeAuthority(),
    targetSpaceId?: string
  ): Promise<void> {
    const spaceId = targetSpaceId ?? this.syncSpaceId
    if (!spaceId) return
    const shelf = await this.storage.loadVolumeShelfSpace(spaceId, authority)
    const slug = shelf?.meta.space_slug?.trim()
    if (!shelf || !slug) return
    const meta = this.storage.getMetaBlock()
    if (meta?.ided[0] === spaceId) {
      this.storage.setMetaBlock({
        ...meta,
        text: updateLilodataText(meta.text, { slug }),
        ided: [meta.ided[0], shelf.meta.space_ed]
      })
    }
    this.rememberReadyIndexSpace(spaceId, slug, {
      sizeBytes: shelf.meta.size_bytes,
      spaceEd: shelf.meta.space_ed
    })
    if (this.syncSpaceId === spaceId) {
      this.syncSpaceSlug = slug
    }
    if (this.activeSpaceId === spaceId) {
      this.setActiveSpace(spaceId, slug)
    }
  }

  private async initSync(opts: InitSyncOptions = {}): Promise<void> {
    if (this.initSyncInFlight) return this.initSyncInFlight
    this.initSyncInFlight = this.runInitSync(opts).finally(() => {
      this.initSyncInFlight = undefined
    })
    return this.initSyncInFlight
  }

  private async runInitSync(opts: InitSyncOptions): Promise<void> {
    if (
      !this.authSession
      || !this.authUsername
      || !hasVerifiedCloudAuthority(this.buildAccountSnapshot())
    ) return
    const authority = this.storage.getVolumeAuthority()
    if (
      !authority
      || authority.owner_id !== getSyncOwnerId(this.authSession)
    ) return
    let pulledRemote = false
    {
      await this.pruneOrphanedCleanSeeds(authority)
      const liveSpaces = await this.refreshSyncSpacesFromBridge(authority)
      const localBlocks = this.document.getStateSnapshot()
      const localIdentity = this.getSpaceIdentity(localBlocks)
      const localIsSeedOnly = await this.isLocalSeedOnly(localBlocks)
      const localHasMeaningfulDraft = hasMeaningfulLocalDraft({
        blocks: localBlocks,
        isSeedOnly: localIsSeedOnly
      })
      const startupWorkbench = await this.resolveStartupAccountWorkbench({
        enabled: opts.startupAccountWorkbenchResolution,
        localBlocks,
        localIdentity,
        localHasMeaningfulDraft,
        authority
      })
      if (startupWorkbench.handled) {
        pulledRemote = startupWorkbench.pulledRemote
      } else if (!liveSpaces.length) {
        if (!localHasMeaningfulDraft) {
          this.syncSpaceId = undefined
          this.syncSpaceSlug = undefined
          this.isSyncPaired = false
          this.autoSyncLocalFallbackOnEdit = this.storage.getActiveDeskState() === 'filed'
          this.indexSpaces.delete(localIdentity.spaceId)
          this.settleCloudIdentity({ syncSpaceId: null, isSyncPaired: false })
          this.setActiveSpace(
            localIdentity.spaceId,
            this.spaceIdToDefaultSlug(localIdentity.spaceId)
          )
          this.accountCloudDebt = false
          this.syncEngine.stop()
          this.setSyncEngineState('idle', 1)
          this.setAccountShelfReadiness('complete')
          this.commands.setSyncIndicatorVisible(true)
          this.applySyncUiState()
          this.requestRender()
          return
        }
        const json = this.storage.exportToJSON(localBlocks)
        const slug = this.spaceIdToDefaultSlug(localIdentity.spaceId)
        await this.storage.flushActiveDocumentForVolume(localBlocks, authority)
        const accepted = await this.saveSyncSpaceWithShelfProof({
          spaceId: localIdentity.spaceId,
          spaceSlug: slug,
          spaceEd: localIdentity.spaceEd,
          json
        }, 'claim', authority)
        if (!accepted) return
        this.syncSpaceId = localIdentity.spaceId
        this.syncSpaceSlug = accepted.space_slug ?? slug
        this.rememberReadyIndexSpace(localIdentity.spaceId, this.syncSpaceSlug, {
          sizeBytes: accepted.size_bytes,
          spaceEd: accepted.space_ed
        })
        this.settleCloudIdentity({ syncSpaceId: localIdentity.spaceId })
        this.setActiveSpace(localIdentity.spaceId, this.syncSpaceSlug)
        this.setSyncRuntimePendingHint(localIdentity.spaceId, false)
      } else {
        const landingSpace = selectLandingSpace(liveSpaces)
        if (!landingSpace) return
        const matching = liveSpaces.find(space => space.space_id === localIdentity.spaceId)
        const cleanSeedProof = !localHasMeaningfulDraft
          ? await this.resolveCleanSeedProof(localIdentity.spaceId, authority)
          : undefined
        if (cleanSeedProof) {
          const opened = await this.hydrateAndOpenSyncShelfSpace(
            landingSpace,
            authority,
            { consumeSeedSpaceId: cleanSeedProof.space_id }
          )
          if (!opened) {
            this.reportCloudUnreachable()
            return
          }
          pulledRemote = true
        } else if (matching && this.isLocalIdentityAligned(matching.space_id, localIdentity)) {
          this.syncSpaceId = matching.space_id
          this.syncSpaceSlug = (matching.space_slug && matching.space_slug.length)
            ? this.normalizeSpaceSlug(matching.space_slug, matching.space_id)
            : this.spaceIdToDefaultSlug(matching.space_id)
          this.settleCloudIdentity({ syncSpaceId: matching.space_id })
          this.setActiveSpace(matching.space_id, this.syncSpaceSlug)
          if (this.isRemoteNewer(matching.space_ed, localIdentity.spaceEd)) {
            const hydrated = await this.ensureShelfSpaceHydrated(
              this.syncSpaceId,
              authority
            )
            if (
              hydrated
              && await this.openShelfSpaceInEditor(this.syncSpaceId, authority)
            ) {
              pulledRemote = true
            } else {
              const json = this.storage.exportToJSON(localBlocks)
              await this.storage.flushActiveDocumentForVolume(
                localBlocks,
                authority
              )
              await this.storage.markVolumeSpaceDirty(
                authority,
                this.syncSpaceId,
                true
              )
              await this.saveSyncSpaceWithShelfProof({
                spaceId: this.syncSpaceId,
                spaceSlug: this.syncSpaceSlug ?? this.spaceIdToDefaultSlug(this.syncSpaceId),
                spaceEd: localIdentity.spaceEd,
                json
              }, 'sync-success', authority)
            }
          } else if ((matching.space_ed ?? '').trim().localeCompare(localIdentity.spaceEd) < 0) {
            const json = this.storage.exportToJSON(localBlocks)
            await this.storage.flushActiveDocumentForVolume(
              localBlocks,
              authority
            )
            await this.storage.markVolumeSpaceDirty(
              authority,
              this.syncSpaceId,
              true
            )
            await this.saveSyncSpaceWithShelfProof({
              spaceId: this.syncSpaceId,
              spaceSlug: this.syncSpaceSlug ?? this.spaceIdToDefaultSlug(this.syncSpaceId),
              spaceEd: localIdentity.spaceEd,
              json
            }, 'sync-success', authority)
          }
        } else {
          if (this.shouldTrustActiveSyncLane(localIdentity)) {
            const trusted = await this.reconcileTrustedActiveLaneWithoutShelfMatch(
              localBlocks,
              localIdentity,
              authority
            )
            if (!trusted.ok) {
              this.reportCloudUnreachable()
              return
            }
            pulledRemote = pulledRemote || trusted.pulledRemote
          } else {
            const adopted = await this.adoptActiveWorkbenchFromLocalShelf(
              localIdentity,
              authority
            )
            if (!adopted) {
              this.setSyncRuntimePendingHintForActiveSpace(true)
            }
          }
        }
      }

      if (!await this.storage.isVolumeAuthorityCurrent(authority)) return
      this.syncEngine.stop()
      this.isSyncPaired = true
      this.settleCloudIdentity({
        syncSpaceId: this.syncSpaceId ?? null,
        isSyncPaired: true
      })
      if (pulledRemote && this.syncSpaceId) {
        this.setActiveSpace(this.syncSpaceId, this.syncSpaceSlug)
      }
      this.setSyncEngineState(pulledRemote ? 'success' : 'idle', pulledRemote ? 1 : 0)
      this.commands.setSyncIndicatorVisible(true)
      const active = this.syncSpaceId ?? this.activeSpaceId
      if (active && this.hasSyncRuntimePendingHint(active) && this.canSyncWriteNow()) {
        this.syncEngine.markDirty()
        void this.syncEngine.syncNow()
      } else if (active && this.hasSyncRuntimePendingHint(active) && !this.canSyncWriteNow()) {
        this.warnPolicyOnceDev(
          'sync-write-pending-suppressed-init-sync',
          '[policy] pending sync suppressed during init while canSyncWrite=false',
          { accountState: this.authAccountState, appState: this.appState }
        )
      }
      this.checkSyncOperatingContract('initSync')
      this.scheduleOwnerShelfMirror()
    }
  }

  private async handlePendingRoute(): Promise<void> {
    if (!this.pendingRoute || !this.authSession || !this.authUsername) return
    const authority = this.storage.getVolumeAuthority()
    const ownerId = getSyncOwnerId(this.authSession)
    if (!authority || authority.owner_id !== ownerId) return
    const { username, slug } = this.pendingRoute
    if (this.authUsername !== username) {
      this.commands.ensurePaletteVisible()
      this.commands.setStatus('requested Account space is unavailable', 'error')
      this.pendingRoute = undefined
      return
    }
    let localMatch: LocalSpaceMeta | undefined
    if (await this.openPendingRouteLocalMatch(slug, authority)) {
      this.pendingRoute = undefined
      return
    }
    try {
      await this.syncEngine.feedShelf({
        reconcileDirtyTombstonesForOwnerId: this.canSyncShelfWriteNow() ? ownerId : undefined,
        volumeAuthority: authority
      })
    } catch {
      this.commands.ensurePaletteVisible()
      this.commands.setStatus(selectMemoryOpenFailureMessage('read-failed'), 'error')
      this.pendingRoute = undefined
      return
    }

    try {
      localMatch = await this.resolveActiveMemoryRouteBySlug(slug, authority)
    } catch {
      localMatch = undefined
    }
    if (!localMatch) {
      this.commands.ensurePaletteVisible()
      this.commands.setStatus(selectMemoryOpenFailureMessage('missing'), 'error')
      this.pendingRoute = undefined
      return
    }
    const routeSpaceId = localMatch.space_id

    const shelf = await this.loadActiveMemoryShelfSpace(
      routeSpaceId,
      authority
    )
    if (!shelf?.blob) {
      try {
        await this.runForegroundShelfHydration(() => (
          this.syncEngine.hydrateShelfSpace({
            ownerId,
            spaceId: routeSpaceId,
            volumeAuthority: authority
          })
        ))
      } catch {
        this.commands.ensurePaletteVisible()
        this.commands.setStatus(selectMemoryOpenFailureMessage('missing-payload'), 'error')
        this.pendingRoute = undefined
        return
      }
    }

    await this.enterSpaceFromIndex(routeSpaceId, { authority })
    this.pendingRoute = undefined
  }

  private async openPendingRouteLocalMatch(
    slug: string,
    authority = this.storage.getVolumeAuthority()
  ): Promise<boolean> {
    let localMatch: LocalSpaceMeta | undefined
    try {
      localMatch = await this.resolveActiveMemoryRouteBySlug(slug, authority)
    } catch {
      return false
    }
    if (!localMatch) return false
    const shelf = await this.loadActiveMemoryShelfSpace(
      localMatch.space_id,
      authority
    )
    if (!shelf?.blob) return false
    return this.enterSpaceFromIndex(localMatch.space_id, { authority })
  }

  private getActiveBlocks(): Block[] {
    return this.document.getBlocks()
  }

  private bumpSpaceEditId(): void {
    const meta = this.storage.getMetaBlock()
    if (!meta) return
    this.storage.setMetaBlock({ ...meta, ided: [meta.ided[0], generateId()] })
  }

  private recordHistorySnapshot(snapshot: SpaceEntry[]): void {
    const pushed = this.history.push(this.filteredSnapshot(snapshot))
    if (pushed) {
      this.bumpSpaceEditId()
      this.markSyncDirty()
    }
  }

  private setPaperCssVars(app: HTMLElement, paper: PaperSpec): void {
    const toPx = (units: number) => `${units * GRID_SIZE}px`
    app.style.setProperty('--paperw', toPx(paper.nominal.w))
    app.style.setProperty('--paperh', toPx(paper.nominal.h))
    app.style.setProperty('--columnw', toPx(paper.quarter.w))
    app.style.setProperty('--columnh', toPx(paper.quarter.h))
  }

  private syncPaperSpec(): void {
    const paper = this.getPaperSpec()
    this.lastCreateWidthUnits = paper.quarter.w
    this.canvas.setPaperSpec(paper)
    this.input.setPaperSpec(paper)
    if (this.appRoot) {
      this.setPaperCssVars(this.appRoot, paper)
    }
  }

  private updateLilodataPaperLabel(): void {
    const label = this.getPaperSpec().label
    const meta = this.storage.getMetaBlock()
    const derived = meta ? undefined : deriveSpaceIded(this.document.getStateSnapshot())
    const nextMeta = meta
      ? {
          ...meta,
          text: updateLilodataText(meta.text, { paperLabel: label }),
          ided: [
            meta.ided[0],
            parseLilodataText(meta.text).paperLabel === label
              ? meta.ided[1]
              : generateId()
          ] as Block['ided']
        }
      : createLilodataBlock(label, derived)
    this.storage.setMetaBlock(nextMeta)
  }

  private getDeoverlapSelectionIds(): string[] {
    const activeIds = new Set(this.getActiveBlocks().map(block => block.ided[0]))
    return this.document.getSelectedBlockIds().filter(id => activeIds.has(id))
  }

  private buildDeoverlapSelectionFeedback(): string {
    const count = this.getDeoverlapSelectionIds().length
    return count === 1 ? 'selected 1 block' : `selected ${count} blocks`
  }

  private syncDeoverlapTargetMode(scope: string, ctx?: EngineContext): void {
    this.deoverlapTargetModeActive = scope === 'selected'
    if (scope === 'all') {
      ctx?.setFeedback('all blocks', 'success')
      this.commands.setStatus('all blocks', 'success')
      return
    }
    const feedback = this.buildDeoverlapSelectionFeedback()
    ctx?.setFeedback(feedback, 'success')
    this.commands.setStatus(feedback, 'success')
  }

  private handleDeoverlapTargetPick(value: string, ctx: EngineContext): StepSelectResult {
    if (!this.deoverlapTargetModeActive) {
      return { ok: false, message: 'selected targeting is not active' }
    }
    const changed = this.addBlockToSelection(value)
    if (!changed) {
      this.document.toggleBlockSelection(value)
    }
    const feedback = this.buildDeoverlapSelectionFeedback()
    ctx.setFeedback(feedback, 'success')
    this.commands.setStatus(feedback, 'success')
    this.requestRender()
    return { ok: true }
  }

  private openMoveTextSpaceoutFlow(descriptor: CommandDescriptor): void {
    const selectedCount = this.getDeoverlapSelectionIds().length
    if (selectedCount >= 2) {
      this.commands.runDescriptor(descriptor, {
        initialValues: { scope: 'selected' },
        stepIndex: 1
      })
      return
    }
    this.commands.runDescriptor(descriptor)
  }

  private openMoveTextDeoverlapFlow(descriptor: CommandDescriptor): void {
    this.commands.runDescriptor(descriptor)
  }

  private resolveDeoverlapTargets(scope: 'all' | 'selected'): { scope: 'all' | 'selected', targetIds: string[] } {
    if (scope === 'selected') {
      return { scope: 'selected', targetIds: this.getDeoverlapSelectionIds() }
    }
    return {
      scope: 'all',
      targetIds: this.getActiveBlocks().map(block => block.ided[0])
    }
  }

  private resolveOverlaps(opts: { targetIds?: string[], spacingUnits?: number, mode?: 'soft' | 'spaceout' } = {}): boolean {
    const blocks = this.getActiveBlocks()
    if (!blocks.length) return false
    const targetIds = opts.targetIds ? new Set(opts.targetIds) : new Set(blocks.map(block => block.ided[0]))
    const spacingUnits = Math.max(0, opts.spacingUnits ?? 0)
    const mode = opts.mode ?? 'soft'

    let changed = false
    let pushed = false

    const applyUpdate = (id: string, nextY: number): boolean => {
      const block = this.document.getBlock(id)
      if (!block || !targetIds.has(id) || nextY === block.xyzw[1]) return false
      if (!pushed) {
        this.pushHistorySnapshot()
        pushed = true
      }
      this.document.updateBlockPosition(id, block.xyzw[0], nextY)
      changed = true
      return true
    }

    const applyUpdates = (updates: Map<string, number>): boolean => {
      let applied = false
      updates.forEach((nextTop, id) => {
        if (applyUpdate(id, nextTop)) {
          applied = true
        }
      })
      return applied
    }

    if (mode === 'soft') {
      for (let guard = 0; guard < blocks.length; guard++) {
        const snapshot = createSpaceGeometrySnapshot(this.getActiveBlocks(), this.createTextLayoutOracle())
        const groups = snapshot.deoverlapCollisionGroups(targetIds, {
          snapY: value => snapYToEvenNearest(value)
        })
        let passChanged = false
        groups.forEach(list => {
          if (!list.length) return
          if (applyUpdates(resolveClusteredSoftDeoverlapPositions(list))) {
            passChanged = true
          }
        })
        if (!passChanged) break
      }
      return changed
    }

    const snapshot = createSpaceGeometrySnapshot(blocks, this.createTextLayoutOracle())
    const stacks = snapshot.deoverlapStacks(targetIds, {
      snapY: value => snapYToEvenNearest(value)
    })
    stacks.forEach(list => {
      applyUpdates(resolveStackSpacingPositions(list, spacingUnits))
    })

    return changed
  }

  private alignBlocksToCurrentColumns(): boolean {
    const blocks = this.getActiveBlocks()
    if (!blocks.length) return false
    const columnWidth = this.getColumnWidthUnits()
    let changed = false
    blocks.forEach(block => {
      const nextX = snapXToColumn(block.xyzw[0], columnWidth)
      const nextW = snapToPaperWidth(block.xyzw[3] * GRID_SIZE, columnWidth)
      if (nextX !== block.xyzw[0]) {
        this.document.updateBlockPosition(block.ided[0], nextX, block.xyzw[1])
        changed = true
      }
      if (nextW !== block.xyzw[3]) {
        this.document.updateBlockWidth(block.ided[0], nextW)
        changed = true
      }
    })
    return changed
  }

  private resnapBlocksToColumnWidth(oldColumnWidth: number, newColumnWidth: number): boolean {
    if (oldColumnWidth === newColumnWidth) return false
    const blocks = this.getActiveBlocks()
    let changed = false
    let pushed = false
    blocks.forEach(block => {
      const columnIndex = Math.round(block.xyzw[0] / oldColumnWidth)
      const newX = columnIndex * newColumnWidth
      const widthColumns = Math.max(1, Math.round(block.xyzw[3] / oldColumnWidth))
      const newW = widthColumns * newColumnWidth
      const needsX = newX !== block.xyzw[0]
      const needsW = newW !== block.xyzw[3]
      if (!needsX && !needsW) return
      if (!pushed) {
        this.pushHistorySnapshot()
        pushed = true
      }
      if (needsX) {
        this.document.updateBlockPosition(block.ided[0], newX, block.xyzw[1])
      }
      if (needsW) {
        this.document.updateBlockWidth(block.ided[0], newW)
      }
      changed = true
    })
    return changed
  }

  private setPaperType(paperTypeId: PaperTypeId, opts: { resnap?: boolean, persistMeta?: boolean, refreshView?: boolean } = {}): void {
    const prevColumn = this.getColumnWidthUnits()
    this.paperTypeId = paperTypeId
    this.syncPaperSpec()
    const nextColumn = this.getColumnWidthUnits()
    const policy = opts.resnap ? resolveLayoutNormalizationPolicy('paper-resnap') : undefined
    const didResnap = policy?.alignColumns ? this.resnapBlocksToColumnWidth(prevColumn, nextColumn) : false
    const didResolve = policy && policy.overlapMode !== 'none'
      ? this.resolveOverlaps({ mode: policy.overlapMode })
      : false
    if (opts.persistMeta) {
      this.updateLilodataPaperLabel()
    }
    if (opts.refreshView !== false) {
      this.computeFitScales()
      this.applyCssGridTransform()
      this.requestRender()
    }
    if (opts.persistMeta || didResnap || didResolve) {
      this.commitWorkbench(this.document.getStateSnapshot())
    }
  }
  // Touch detection kept strict to avoid misclassifying macOS trackpads as touch
  private isTouchDevice = (navigator.maxTouchPoints || 0) > 0
  private multiSelectMode = false
  // Touch gesture state
  private touchPoints = new Map<number, { x: number, y: number }>()
  private singleTouchLast?: { x: number, y: number }
  private touchTapCandidateId?: number
  private touchTapStartTime = 0
  private touchTapStartScreen?: { x: number, y: number }
  private touchTapMoved = false
  private touchTapMultiTouch = false
  private pinHoldTimer?: number
  private pinHoldPointerId?: number
  private pinHoldBlockId?: string
  private pinHoldStart?: { sx: number, sy: number }
  private pinHoldKind?: 'start' | 'end'
  private pinchStart?: {
    scale: number
    tx: number
    ty: number
    dist: number
    anchorX: number
    anchorY: number
  }
  private clipboardSelection?: ClipboardPayload
  private copyModeActive = false
  private pasteModeActive = false
  private pasteBlocks?: ClipboardBlock[]
  private pastePrimaryId?: string
  private selectConnectedModeActive = false
  private deoverlapTargetModeActive = false
  private silentMultiSelectMode = false
  private deleteModeActive = false
  private areaSelectModeActive = false
  private areaSelectDragActive = false
  private areaSelectPointerId?: number
  private areaSelectOriginWorld?: { x: number, y: number }
  private areaSelectCurrentWorld?: { x: number, y: number }
  private areaSelectRect?: { x: number, y: number, width: number, height: number }
  private areaSelectAnchorId?: string
  private areaSelectTouchedIds = new Set<string>()
  private areaSelectShiftPending = false
  private areaSelectShiftStart?: { sx: number, sy: number, x: number, y: number }
  private areaSelectLongPressTimer?: number
  private areaSelectLongPressPointerId?: number
  private areaSelectLongPressStart?: { sx: number, sy: number }
  private areaSelectLongPressBlockId?: string
  private areaSelectLongPressSelectPointerId?: number
  private areaSelectLongPressSelectBlockId?: string
  private areaSelectLongPressSelectMoved = false
  private touchZoomScrubTimer?: number
  private touchZoomScrubPointerId?: number
  private touchZoomScrubStart?: { sx: number, sy: number }
  private touchZoomScrubActive = false
  private touchZoomScrubAnchor?: {
    sx: number
    sy: number
    worldX: number
    worldY: number
    scale: number
  }
  private touchZoomScrubIndicator?: HTMLDivElement
  private touchZoomScrubIndicatorLabel?: HTMLSpanElement
  private touchZoomScrubIndicatorSide: 'left' | 'right' = 'left'
  private deferredInstallPrompt?: BeforeInstallPromptEvent

  constructor() {
    this.document = new Document()
    this.storage = new Storage()
    this.input = new Input()
    this.commands = new Commands()
    this.storage.setConsolidationActivityListener(activity => this.handleLocalConsolidationActivity(activity))
    this.storage.setConsolidatedListener(
      authority => this.handleLocalShelfConsolidated(authority)
    )
    this.storage.setWriteRefusedListener(() => {
      this.commands.setStatus('deleted elsewhere — not saved', 'error')
    })
    this.history = new History()
    this.syncEngine = new SyncEngine({
      debounceMs: SYNC_DEBOUNCE_MS,
      onSync: () => this.performCloudSync(),
      onState: (state) => this.handleSyncEngineState(state),
      canSyncWrite: () => this.canSyncWriteNow(),
      canSyncShelfWrite: () => this.canSyncShelfWriteNow(),
      getActiveOwnerId: () => this.authSession ? getSyncOwnerId(this.authSession) : undefined,
      getActiveSpaceId: () => this.syncSpaceId ?? this.activeSpaceId,
      getVolumeAuthority: () => this.storage.getVolumeAuthority(),
      getDefaultSpaceSlug: (spaceId) => this.spaceIdToDefaultSlug(spaceId),
      setPendingSync: (spaceId, pending) => this.setSyncRuntimePendingHint(spaceId, pending),
      isPendingSync: (spaceId) => this.hasSyncRuntimePendingHint(spaceId),
      onBlockedSync: () => this.handleBlockedSyncWrite(),
      localVolume: {
        listVolumeSpacesMeta: (authority) => this.listProductVolumeSpacesMeta(
          this.requireSyncVolumeAuthority(authority)
        ),
        loadVolumeShelfSpace: (spaceId, authority) => this.storage.loadVolumeShelfSpace(
          spaceId,
          this.requireSyncVolumeAuthority(authority)
        ),
        upsertRemoteSpacesMetaForVolume: (input, now, authority) => (
          this.storage.upsertRemoteSpacesMetaForVolume(
            this.requireSyncVolumeAuthority(authority),
            input,
            now
          )
        ),
        replaceVolumeBlobFromRemote: (input, authority) => (
          this.storage.replaceVolumeBlobFromRemote(
            this.requireSyncVolumeAuthority(authority),
            input
          )
        ),
        writeMergedVolumeSpace: (input, authority) => {
          const mounted = this.requireSyncVolumeAuthority(authority)
          if (input.ownerId !== mounted.owner_id) {
            throw new Error('Merged shelf write blocked: owner mismatch')
          }
          return this.storage.writeMergedVolumeSpace(mounted, {
            expectedPayload: input.expectedPayload,
            blocksWithMeta: input.blocksWithMeta,
            spaceSlug: input.spaceSlug,
            dirty: input.dirty,
            payloadState: input.payloadState,
            now: input.now
          })
        },
        markVolumeSpaceDeleted: (spaceId, deletedAt, now, authority) => (
          this.storage.markVolumeSpaceDeleted(
            this.requireSyncVolumeAuthority(authority),
            spaceId,
            deletedAt,
            now
          )
        ),
        purgeDeletedVolumePayload: (spaceId, now, authority) => (
          this.storage.purgeDeletedVolumePayload(
            this.requireSyncVolumeAuthority(authority),
            spaceId,
            now
          )
        ),
        setVolumePayloadState: (spaceId, payloadState, opts, authority) => (
          this.storage.setVolumePayloadState(
            this.requireSyncVolumeAuthority(authority),
            spaceId,
            payloadState,
            opts
          )
        ),
        recordVolumeSyncSuccess: (spaceId, syncAt, now, canonical, authority) => (
          this.storage.recordVolumeSyncSuccess(
            this.requireSyncVolumeAuthority(authority),
            spaceId,
            syncAt,
            now,
            canonical
          )
        ),
        renameVolumeSpaceForConflict: (
          ownerId,
          spaceId,
          spaceSlug,
          spaceEd,
          expectedPayload,
          authority
        ) => {
          const mounted = this.requireSyncVolumeAuthority(authority)
          if (ownerId !== mounted.owner_id) return Promise.resolve(undefined)
          return this.storage.renameVolumeSpace(
            mounted,
            spaceId,
            spaceSlug,
            spaceEd,
            undefined,
            expectedPayload
          )
        }
      },
      remoteShelf: {
        listSpaces: (authority) => this.listSyncSpacesForBridge(authority),
        loadSpacePayload: (spaceId, authority) => (
          this.loadSyncSpaceForBridge(spaceId, authority)
        ),
        saveSpacePayload: (input, authority) => (
          this.saveSyncSpaceForBridge(input, authority)
        ),
        deleteSpace: (input, authority) => (
          this.deleteSyncSpaceForBridge(input, authority)
        )
      },
      activeEditor: {
        getActiveSpaceId: () => this.syncSpaceId ?? this.activeSpaceId,
        prepareActiveSpace: (spaceId, authority) => this.prepareActiveSpaceForSync(spaceId, authority),
        applyActiveBlocks: async (_blocks, metaBlock, authority) => {
          if (metaBlock) await this.prepareActiveSpaceForSync(metaBlock.ided[0], authority)
        }
      }
    })
    this.commands.setOnHideCallback((reason) => {
      this.suppressNextClick = reason === 'outside-tap'
      this.revealBootProvisionalSheet()
    })
    this.commands.setOnSurfaceChangeCallback(() => {
      this.handlePaletteSurfaceChange()
    })
    this.commands.setModeExitHandler((modeName) => {
      if (modeName === 'select') {
        this.exitMultiSelectMode({ preserveNextClick: true })
      }
    })
    this.commands.setEngineExitHandler(() => {
      this.clearSearchSession()
      if (this.areaSelectModeActive) {
        this.exitAreaSelectMode()
      }
      if (this.deoverlapTargetModeActive) {
        this.deoverlapTargetModeActive = false
        this.suppressNextClick = false
      }
      if (this.multiSelectMode) {
        this.multiSelectMode = false
        this.suppressNextClick = false
      }
      if (this.copyModeActive) {
        this.copyModeActive = false
        this.suppressNextClick = false
      }
      if (this.pasteModeActive) {
        this.pasteModeActive = false
        this.suppressNextClick = false
      }
      if (this.selectConnectedModeActive) {
        this.selectConnectedModeActive = false
        this.suppressNextClick = false
      }
      if (this.deleteModeActive) {
        this.deleteModeActive = false
        this.suppressNextClick = false
      }
    })
    
    // Create canvas
    this.canvasElement = this.createCanvas()
    this.canvas = new Canvas(this.canvasElement)
    this.syncPaperSpec()
    
    this.setupEventListeners()
    this.registerCommands()
    this.setupAccountExitSignalBridge()
    this.setupAuthUI()
    this.capturePendingRoute()
    if (typeof window !== 'undefined') this.machineConsent = captureMachineConsentRequest(window)

    // Kick an immediate first paint so label sizing/position logic is applied on load
    this.applyCssGridTransform()
    this.render()
    this.requestRender()
  }

  private capturePendingRoute(): void {
    if (typeof window === 'undefined') return
    const path = window.location.pathname || ''
    if (!path.startsWith('/@')) return
    const cleaned = path.replace(/\/+$/, '')
    const parts = cleaned.split('/').filter(Boolean)
    if (parts.length < 2) return
    const username = parts[0].startsWith('@') ? parts[0].slice(1) : parts[0]
    const slug = parts[1] ?? ''
    if (!username || !slug) return
    this.pendingRoute = { username: decodeURIComponent(username), slug: decodeURIComponent(slug) }
  }

  // Runs after every login refresh and every closed modal until the request is
  // answered. Login and subscribing stay the app's own; this only asks at the end.
  private async offerMachineConsent(): Promise<void> {
    const request = this.machineConsent
    if (!request || !supabase || typeof window === 'undefined') return
    const next = selectMachineConsentNext({
      hasRequest: true,
      isModalOpen: Boolean(this.authModalMode),
      isInFlight: this.machineConsentInFlight,
      hasSyncAccess: this.getCapabilities().hasSyncAccess,
      loginOffered: request.loginOffered
    })
    if (next === 'login') {
      this.machineConsent = keepMachineConsentRequest(window, { ...request, loginOffered: true })
      this.handleUserChip()
      return
    }
    if (next !== 'fetch') return
    this.machineConsentInFlight = true
    const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(request.authorizationId)
    this.machineConsentInFlight = false
    const step = selectMachineConsentStep(error ? undefined : data)
    if (step.kind === 'redirect') {
      releaseMachineConsentRequest(window)
      window.location.assign(step.url)
      return
    }
    // Another modal opened while we asked: its close brings us back.
    if (this.authModalMode) return
    if (step.kind === 'expired') {
      this.machineConsent = undefined
      releaseMachineConsentRequest(window)
      this.openAuthModal('machine-consent-expired')
      return
    }
    this.machineConsentAsk = step
    this.openAuthModal('machine-consent')
  }

  private async answerMachineConsent(allow: boolean): Promise<void> {
    const authorizationId = this.machineConsent?.authorizationId
    if (!authorizationId || !supabase || typeof window === 'undefined') return
    this.setAuthModalWaiting(true)
    const options = { skipBrowserRedirect: true }
    const { data, error } = allow
      ? await supabase.auth.oauth.approveAuthorization(authorizationId, options)
      : await supabase.auth.oauth.denyAuthorization(authorizationId, options)
    this.machineConsent = undefined
    this.machineConsentAsk = undefined
    releaseMachineConsentRequest(window)
    const step = selectMachineConsentStep(error ? undefined : data)
    if (step.kind === 'redirect') {
      window.location.assign(step.url)
      return
    }
    this.setAuthModalWaiting(false)
    this.openAuthModal('machine-consent-expired')
  }

  private createCanvas(): HTMLCanvasElement {
    const canvas = document.createElement('canvas')
    canvas.style.position = 'absolute'
    canvas.style.top = '0'
    canvas.style.left = '0'
    const viewport = this.getViewportCssSize()
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`
    canvas.style.cursor = this.getBaseCanvasCursor()
    canvas.style.background = 'transparent'
    ;(canvas.style as any).touchAction = 'none' // Prevent browser panning/zooming on touch
    
    const app = document.getElementById('app')
    if (app) {
      this.appRoot = app as HTMLDivElement
      app.classList.add('grid-layered')
      // Expose grid and paper metrics as CSS variables so CSS can render
      // the paper grid purely with layered backgrounds.
      app.style.setProperty('--grid', `${GRID_SIZE}px`)
      this.setPaperCssVars(app, this.getPaperSpec())
      // Initialize transform CSS vars with breathing room from origin.
      // Device-specific: mobile gets 2×, desktop 4× on both axes.
      const { xPx: startOffsetPxX, yPx: startOffsetPxY } = this.computeStartOffsets()
      app.style.setProperty('--scale', '1')
      app.style.setProperty('--tx', `${startOffsetPxX}px`) // X axis: horizontal
      app.style.setProperty('--ty', `${startOffsetPxY}px`) // Y axis: vertical
      // Keep camera model in sync from the first paint
      this.camera.scale = 1
      this.camera.tx = startOffsetPxX
      this.camera.ty = startOffsetPxY
      app.appendChild(canvas)
      this.ensureTouchZoomScrubIndicator(app)
      this.lastCanvasCssSize = { w: viewport.width, h: viewport.height }
    }
    
    return canvas
  }

  private getBaseCanvasCursor(): string {
    return 'crosshair'
  }

  private resolveCanvasCursorAtScreenPosition(sx: number, sy: number): string {
    const { x, y } = this.screenToWorld(sx, sy)
    const hoveredBlock = this.findBlockAtPosition(x, y)
    if (hoveredBlock) {
      const href = this.canvas.hitTestLink(hoveredBlock, x, y)
      if (href) return 'pointer'
    }
    return this.getBaseCanvasCursor()
  }

  private applyCanvasCursor(cursor: string): void {
    if (this.canvasElement.style.cursor === cursor) return
    this.canvasElement.style.cursor = cursor
  }

  private updateCanvasCursorFromMouseEvent(e: MouseEvent): void {
    const rect = this.canvasElement.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top
    this.applyCanvasCursor(this.resolveCanvasCursorAtScreenPosition(sx, sy))
  }

  private updateTouchSelectionGuard(selectedBlockIds: string[], editingId?: string): void {
    if (!this.appRoot) return
    if (!this.isTouchDevice) {
      this.appRoot.classList.remove('touch-selection-guard')
      return
    }
    const shouldGuard = selectedBlockIds.length > 0 && !editingId
    this.appRoot.classList.toggle('touch-selection-guard', shouldGuard)
  }

  private registerCommands(): void {
    const spaceRenameCopy = 'URL-safe name, 4-64 chars. Spaces, dots, and underscores become dashes.'
    const userRenameCopy = 'Public handle only, spaces become dashes, lowercase only'
    const spaceExportDescriptor: CommandDescriptor = {
      id: 'space-export-file',
      label: 'space-export-file',
      description: 'Export all your writings and space as a portable [.json] file',
      modes: ['edit', 'view'],
      steps: [],
      execute: () => {
        void this.handleExportCommand()
      }
    }

    const spaceRenameDescriptor: CommandDescriptor = {
      id: 'space-rename',
      label: 'space-rename',
      description: spaceRenameCopy,
      modes: ['edit', 'view'],
      steps: [
        {
          id: 'space-name',
          label: '',
          type: 'text-input',
          prompt: spaceRenameCopy,
          required: true,
          normalize: (value) => this.normalizeSpaceRenameInput(value),
          validate: (value) => {
            if (typeof value !== 'string') {
              return { valid: false, error: 'space-rename needs 4–64 chars' }
            }
            return this.validateSpaceRenameInput(value)
          }
        }
      ],
      execute: (ctx) => this.handleSpaceRenameExecute(ctx),
      completionUi: 'runner',
      completeOnExecute: false
    }
    this.spaceRenameDescriptor = spaceRenameDescriptor

    const userRenameDescriptor: CommandDescriptor = {
      id: 'user-rename',
      label: 'user-rename',
      description: userRenameCopy,
      modes: ['edit', 'view'],
      completionUi: 'runner',
      steps: [
        {
          id: 'user-name',
          label: '',
          type: 'text-input',
          prompt: userRenameCopy,
          required: true,
          normalize: (value) => this.normalizeUserRenameInput(value),
          validate: (value) => {
            if (typeof value !== 'string') {
              return { valid: false, error: 'username needs 4–16 chars' }
            }
            return this.validateUserRenameInput(value)
          }
        }
      ],
      execute: (ctx) => this.handleUserRenameExecute(ctx),
      completeOnExecute: false
    }
    this.userRenameDescriptor = userRenameDescriptor

    const spaceDeleteCopy = 'This permanently deletes the current space. Type delete to confirm.'
    const spaceDeleteDescriptor: CommandDescriptor = {
      id: 'space-delete-!',
      label: 'space-delete-!',
      description: spaceDeleteCopy,
      modes: ['edit', 'view'],
      steps: [
        {
          id: 'space-delete-confirm',
          label: '',
          type: 'text-input',
          prompt: spaceDeleteCopy,
          required: true,
          normalize: (value) => value.trim(),
          validate: (value) => {
            if (typeof value !== 'string' || value !== 'delete') {
              return { valid: false, error: 'type "delete" to confirm' }
            }
            return { valid: true }
          }
        }
      ],
      execute: (ctx) => this.handleSpaceDeleteExecute(ctx),
      completeOnExecute: true
    }

    const spaceImportDescriptor: CommandDescriptor = {
      id: 'space-import-file',
      label: 'space-import-file',
      description: 'Import a local JSON space file and merge into current space',
      modes: ['edit', 'view'],
      completionUi: 'close',
      steps: [],
      execute: () => this.handleImportFileCommand(),
      completeOnExecute: true
    }

    const viewLastEditDescriptor: CommandDescriptor = {
      id: 'view-last-edit',
      label: 'view-last-edit',
      description: 'Move view to the last edited block',
      modes: ['edit', 'view'],
      steps: [],
      execute: () => this.handleGoToLastCommand(),
      completeOnExecute: true
    }

    const viewOriginDescriptor: CommandDescriptor = {
      id: 'view-origin',
      label: 'view-origin',
      description: 'Reset view to the origin with breathing room',
      modes: ['edit', 'view'],
      steps: [],
      execute: () => this.handleGoToHomeCommand(),
      completeOnExecute: true
    }

    const searchDescriptor: CommandDescriptor = {
      id: 'search', label: 'search',
      description: 'Find text across this space, newest match first',
      modes: ['edit', 'view'], completionUi: 'close',
      steps: [{
        id: 'search-query', label: '', type: 'text-input', prompt: '', required: false,
        normalize: value => value,
        onInput: (value, ctx) => this.handleSearchQueryInput(value, ctx),
        live: {
          actions: [
            { id: 'search-prev', label: 'prev', enabled: () => Boolean(this.searchSession?.hits.length) },
            { id: 'search-next', label: 'next', enabled: () => Boolean(this.searchSession?.hits.length) }
          ],
          enterAction: 'search-next',
          onAction: (id, ctx) => this.navigateSearchHit(id === 'search-prev' ? -1 : 1, ctx)
        }
      }],
      execute: () => {
        const hit = this.searchSession?.hits[this.searchSession.index]
        if (hit && !this.isViewMode() && this.document.getBlock(hit.blockId)) this.document.selectBlock(hit.blockId)
        this.clearSearchSession()
      }
    }
    this.commands.registerCommand({
      name: 'search', description: searchDescriptor.description,
      execute: () => this.commands.runDescriptor(searchDescriptor),
      type: 'oneshot', ready: true, visible: () => !this.isIndexSpace(),
      modes: ['edit', 'view'], autoClose: false
    })

    const refreshDescriptor: CommandDescriptor = {
      id: 'refresh',
      label: 'refresh',
      description: 'Reload the app',
      modes: ['edit', 'view'],
      completionUi: 'close',
      steps: [],
      execute: () => this.handleRefreshCommand(),
      completeOnExecute: true
    }

    const lilowriteInstallDescriptor: CommandDescriptor = {
      id: 'settings-lilowrite-install',
      label: 'settings-lilowrite-install',
      description: 'Install Lilo Write as an app. On iOS: Share → Add to Home Screen.',
      modes: ['edit', 'view'],
      completionUi: 'close',
      steps: [],
      execute: () => this.handleInstallCommand(),
      completeOnExecute: true
    }

    const spaceNewDescriptor: CommandDescriptor = {
      id: 'space-new',
      label: 'space-new',
      description: 'Create a new space and enter it immediately',
      modes: ['edit', 'view'],
      completionUi: 'runner',
      steps: [],
      execute: (ctx) => this.handleSpaceNewCommand(ctx),
      completeOnExecute: false
    }

    const paperChangeDescriptor: CommandDescriptor = {
      id: 'settings-paper-change',
      label: 'settings-paper-change',
      description: 'Switch paper format (ISO or ANSI, vertical or horizontal)',
      modes: ['edit', 'view'],
      completionUi: 'close',
      steps: [
        {
          id: 'paper',
          label: 'paper',
          type: 'picker-single',
          prompt: 'Choose paper size to fit',
          required: true,
          options: [
            { label: getPaperType('ISO_V').label, value: getPaperType('ISO_V').label },
            { label: getPaperType('ISO_H').label, value: getPaperType('ISO_H').label },
            { label: getPaperType('ANSI_V').label, value: getPaperType('ANSI_V').label },
            { label: getPaperType('ANSI_H').label, value: getPaperType('ANSI_H').label },
          ],
          onHighlight: (value, ctx) => {
            const next = findPaperTypeIdByLabel(value)
            if (!next) return
            ctx.setValue('paper', getPaperType(next).label)
            ctx.setFeedback(`paper: ${getPaperType(next).label}`, 'success')
          }
        }
      ],
      execute: (ctx) => this.handlePaperChangeExecute(ctx),
      completeOnExecute: true
    }

    const settingsThemeDescriptor: CommandDescriptor = {
      id: 'settings-ui-darklight',
      label: 'settings-ui-darklight',
      description: 'Choose the app theme for this browser',
      modes: ['edit', 'view'],
      completionUi: 'close',
      steps: [
        {
          id: 'mode',
          label: 'mode',
          type: 'picker-single',
          prompt: 'Choose the app theme for this browser',
          required: true,
          options: [
            { label: 'auto', value: 'auto', description: 'Follow the device light or dark setting.' },
            { label: 'light', value: 'light', description: 'Use light mode on this browser.' },
            { label: 'dark', value: 'dark', description: 'Use dark mode on this browser.' }
          ]
        }
      ],
      execute: (ctx) => this.handleThemeModeExecute(ctx),
      completeOnExecute: true
    }

    const moveTextDeoverlapDescriptor: CommandDescriptor = {
      id: 'move-text-deoverlap',
      label: 'move-text-deoverlap',
      description: 'Nudge overlapping text blocks just enough to separate them',
      modes: ['edit', 'view'],
      completionUi: 'close',
      steps: [
        {
          id: 'scope',
          label: 'select',
          type: 'picker-single',
          prompt: 'Select blocks or choose all',
          required: true,
          defaultValue: 'selected',
          keepFeedback: true,
          allowCanvasSelection: true,
          options: [
            { label: 'selected', value: 'selected', description: 'Use the current or tapped block selection.' },
            { label: 'all', value: 'all', description: 'Use all blocks in the canvas.' }
          ],
          onHighlight: (value, ctx) => {
            this.syncDeoverlapTargetMode(value, ctx)
          },
          onSelect: (value) => {
            if (value === 'all') return { ok: true }
            if (this.getDeoverlapSelectionIds().length <= 0) {
              return { ok: false, message: 'select at least one block' }
            }
            return { ok: true }
          },
          onCanvasSelect: (value, ctx) => this.handleDeoverlapTargetPick(value, ctx),
          showDoneWhen: (ctx) => ctx.getValue('scope') === 'all'
            || (ctx.getValue('scope') === 'selected' && this.getDeoverlapSelectionIds().length > 0)
        }
      ],
      execute: (ctx) => this.handleMoveTextDeoverlapExecute(ctx),
      completeOnExecute: true
    }

    const moveTextSpaceoutDescriptor: CommandDescriptor = {
      id: 'move-text-spaceout',
      label: 'move-text-spaceout',
      description: 'Normalize vertical spacing between stacked text blocks',
      modes: ['edit', 'view'],
      completionUi: 'close',
      steps: [
        {
          id: 'scope',
          label: 'select',
          type: 'picker-single',
          prompt: 'Select blocks or choose all',
          required: true,
          defaultValue: 'selected',
          keepFeedback: true,
          allowCanvasSelection: true,
          options: [
            { label: 'selected', value: 'selected', description: 'Use the current or tapped block selection.' },
            { label: 'all', value: 'all', description: 'Use all blocks in the canvas.' }
          ],
          onHighlight: (value, ctx) => {
            this.syncDeoverlapTargetMode(value, ctx)
          },
          onSelect: (value) => {
            if (value === 'all') return { ok: true }
            if (this.getDeoverlapSelectionIds().length <= 0) {
              return { ok: false, message: 'select at least one block' }
            }
            return { ok: true }
          },
          onCanvasSelect: (value, ctx) => this.handleDeoverlapTargetPick(value, ctx),
          advanceOnSelect: (value) => value === 'all',
          showDoneWhen: (ctx) => ctx.getValue('scope') === 'selected' && this.getDeoverlapSelectionIds().length > 0
        },
        {
          id: 'space',
          label: 'spacing',
          type: 'picker-single',
          prompt: 'Choose the writing-line gap between stacked blocks',
          required: true,
          defaultValue: '1',
          advanceOnSelect: () => true,
          showDoneWhen: (ctx) => typeof ctx.getValue('space') === 'string',
          options: [
            { label: '0', value: '0', description: 'Pack blocks with no extra writing-line gap.' },
            { label: '1', value: '1', description: 'Leave one writing line between blocks.' },
            { label: '2', value: '2', description: 'Leave two writing lines between blocks.' },
            { label: '4', value: '4', description: 'Leave four writing lines between blocks.' }
          ]
        }
      ],
      execute: (ctx) => this.handleMoveTextSpaceoutExecute(ctx),
      completeOnExecute: true
    }

    const selectAllDescriptor: CommandDescriptor = {
      id: 'select-all',
      label: 'select-all',
      description: 'Select every block and connection',
      modes: ['edit'],
      completionUi: 'close',
      steps: [],
      execute: () => this.handleSelectAllCommand(),
      completeOnExecute: true
    }

    const connectDescriptor: CommandDescriptor = {
      id: 'connect',
      label: 'connect',
      description: 'Connect one block to many targets',
      modes: ['edit'],
      steps: [
        {
          id: 'pick-source',
          label: 'pick-source',
          type: 'canvas-select-single',
          prompt: 'Tap a block to connect from',
          required: true,
          onSelect: (value) => {
            const block = this.document.getBlock(value)
            if (!block) {
              return { ok: false, message: 'tap a block' }
            }
            this.document.selectBlock(value)
            this.document.clearConnectionSelection()
            this.requestRender()
            return { ok: true }
          }
        },
        {
          id: 'pick-target',
          label: 'pick-target',
          type: 'canvas-select-multi',
          prompt: 'Tap blocks to link or tap done',
          required: true,
          cardinality: 'multi',
          display: 'none',
          onSelect: (value, ctx) => {
            const sourceId = ctx.getValue('pick-source')
            if (typeof sourceId !== 'string') {
              return { ok: false, message: 'pick a source' }
            }
            if (value === sourceId) {
              return { ok: false, message: 'cannot connect block to itself' }
            }
            const sourceBlock = this.document.getBlock(sourceId)
            if (!sourceBlock) {
              return { ok: false, message: 'source missing' }
            }
            if (sourceBlock.link.includes(value)) {
              return { ok: false, message: 'already connected' }
            }
            this.pushHistorySnapshot()
            const added = this.document.addLink(sourceId, value)
            if (!added) {
              return { ok: false, message: 'connection failed' }
            }
            this.commitWorkbench(this.document.getStateSnapshot())
            this.requestRender()
            return { ok: true }
          }
        }
      ],
      execute: () => {}
    }
    this.connectDescriptor = connectDescriptor

    const disconnectDescriptor: CommandDescriptor = {
      id: 'disconnect',
      label: 'disconnect',
      description: 'Clear all connections touching tapped blocks',
      modes: ['edit'],
      steps: [
        {
          id: 'pick-blocks',
          label: 'pick-blocks',
          type: 'canvas-select-multi',
          prompt: 'Tap blocks to clear their connections, then tap done',
          required: true,
          cardinality: 'multi',
          display: 'none',
          keepFeedback: true,
          onSelect: (value, ctx) => this.handleDisconnectDescriptorPick(value, ctx)
        }
      ],
      execute: () => {}
    }
    this.disconnectDescriptor = disconnectDescriptor

    const mergeTextDescriptor: CommandDescriptor = {
      id: 'merge-text',
      label: 'merge-text',
      description: 'Merge selected text blocks into the first block',
      modes: ['edit'],
      completionUi: 'close',
      steps: [
        {
          id: 'pick-blocks',
          label: 'pick-blocks',
          type: 'canvas-select-multi',
          prompt: 'Tap blocks to merge into the first, then tap done',
          required: true,
          cardinality: 'multi',
          display: 'none',
          keepFeedback: true,
          onSelect: (value, ctx) => this.handleMergeTextDescriptorPick(value, ctx),
          showDoneWhen: (ctx) => {
            const value = ctx.getValue('pick-blocks')
            return Array.isArray(value) && value.length >= 2
          }
        }
      ],
      execute: (ctx) => this.handleMergeTextDescriptorExecute(ctx),
      completeOnExecute: true
    }
    this.mergeTextDescriptor = mergeTextDescriptor

    const createSelectAxisBaseStep = (
      kind: 'horizontal' | 'vertical',
      section: 'all' | 'left' | 'right' | 'above' | 'below'
    ): CommandDescriptor['steps'][number] => ({
      id: 'pick-base',
      label: 'pick-base',
      type: 'canvas-select-multi',
      prompt: 'Tap a base block',
      required: true,
      cardinality: 'multi',
      display: 'none',
      keepFeedback: true,
      onSelect: (value, ctx) => {
        const block = this.document.getBlock(value)
        if (!block) {
          return { ok: false, message: 'tap a block' }
        }
        const nextSelection = this.selectAxisFromBase(value, kind, section, {
          preserveExisting: true,
          setFeedback: (message, tone) => {
            ctx.setFeedback(message, tone)
          }
        })
        if (nextSelection.length > 1) {
          this.commands.minimizePalette()
        }
        return { ok: true, value: nextSelection }
      }
    })

    const selectDirectionAboveDescriptor: CommandDescriptor = {
      id: 'select-direction-above',
      label: 'select-direction-above',
      description: 'Select blocks above the base in the same column',
      modes: ['edit'],
      steps: [createSelectAxisBaseStep('vertical', 'above')],
      execute: () => {}
    }
    this.selectDirectionAboveDescriptor = selectDirectionAboveDescriptor

    const selectDirectionBelowDescriptor: CommandDescriptor = {
      id: 'select-direction-below',
      label: 'select-direction-below',
      description: 'Select blocks below the base in the same column',
      modes: ['edit'],
      steps: [createSelectAxisBaseStep('vertical', 'below')],
      execute: () => {}
    }
    this.selectDirectionBelowDescriptor = selectDirectionBelowDescriptor

    const selectAreaDescriptor: CommandDescriptor = {
      id: 'select-area',
      label: 'select-area',
      description: 'Drag to draw a selection box and tap done to finish',
      modes: ['edit'],
      steps: [
        {
          id: 'pick-area',
          label: 'pick-area',
          type: 'canvas-select-multi',
          prompt: 'Drag to draw a selection box, tap done to finish',
          required: true,
          cardinality: 'multi',
          display: 'none',
          keepFeedback: true,
          onSelect: (value, ctx) => {
            this.addBlockToSelection(value)
            const count = this.document.getSelectedBlockIds().length
            if (count > 0) {
              const label = count === 1 ? 'selected 1 block' : `selected ${count} blocks`
              ctx.setFeedback(label, 'success')
            }
            return { ok: true }
          }
        }
      ],
      execute: () => {}
    }

    const selectDescriptor: CommandDescriptor = {
      id: 'select',
      label: 'select',
      description: 'Select blocks or links, then tap done',
      modes: ['edit'],
      steps: [
        {
          id: 'pick-items',
          label: 'pick-items',
          type: 'canvas-select-multi',
          prompt: this.buildMultiSelectMessage(),
          required: true,
          cardinality: 'multi',
          display: 'none',
          keepFeedback: true,
          allowConnectionHits: true,
          onSelect: (value, ctx) => this.handleSelectDescriptorPick(value, ctx)
        }
      ],
      execute: () => {}
    }
    this.selectDescriptor = selectDescriptor

    const deleteDescriptor: CommandDescriptor = {
      id: 'delete!',
      label: 'delete!',
      description: 'Delete selected blocks or connections',
      modes: ['edit'],
      steps: [
        {
          id: 'pick-items',
          label: 'pick-items',
          type: 'canvas-select-multi',
          prompt: 'Tap blocks or connections to select, then tap done to delete',
          required: true,
          cardinality: 'multi',
          display: 'none',
          keepFeedback: true,
          allowConnectionHits: true,
          onSelect: (value, ctx) => this.handleDeleteDescriptorPick(value, ctx)
        }
      ],
      execute: () => {
        this.performDeleteSelection()
        this.deleteModeActive = false
      }
    }
    this.deleteDescriptor = deleteDescriptor

    const copyDescriptor: CommandDescriptor = {
      id: 'edit-copy',
      label: 'edit-copy',
      description: 'Copy selected blocks',
      modes: ['edit'],
      steps: [
        {
          id: 'pick-items',
          label: 'pick-items',
          type: 'canvas-select-multi',
          prompt: 'Tap blocks to select then tap done to copy',
          required: true,
          cardinality: 'multi',
          display: 'none',
          keepFeedback: true,
          onSelect: (value, ctx) => this.handleCopyDescriptorPick(value, ctx)
        }
      ],
      execute: async () => {
        await this.performCopy()
        this.copyModeActive = false
      }
    }
    this.copyDescriptor = copyDescriptor

    const pasteDescriptor: CommandDescriptor = {
      id: 'edit-paste',
      label: 'edit-paste',
      description: 'Paste copied blocks onto the canvas',
      modes: ['edit'],
      steps: [
        {
          id: 'pick-place',
          label: 'pick-place',
          type: 'canvas-point-multi',
          prompt: 'Tap to paste selection then tap done to stop',
          required: true,
          cardinality: 'multi',
          display: 'none',
          keepFeedback: true,
          onPoint: (point, ctx) => this.handlePasteDescriptorPoint(point, ctx)
        }
      ],
      execute: () => {
        this.pasteModeActive = false
      }
    }
    this.pasteDescriptor = pasteDescriptor

    const selectConnectedDescriptor: CommandDescriptor = {
      id: 'select-connected',
      label: 'select-connected',
      description: 'Select downstream blocks from tapped source blocks',
      modes: ['edit'],
      steps: [
        {
          id: 'pick-source',
          label: 'pick-source',
          type: 'canvas-select-multi',
          prompt: 'Tap a block to select downstream connections',
          required: true,
          cardinality: 'multi',
          display: 'none',
          keepFeedback: true,
          onSelect: (value, ctx) => this.handleSelectConnectedDescriptorPick(value, ctx)
        }
      ],
      execute: () => {
        this.selectConnectedModeActive = false
      }
    }
    this.selectConnectedDescriptor = selectConnectedDescriptor

    this.commands.registerCommand({
      name: 'space-export-file',
      description: 'Export all your writings and space as a portable [.json] file',
      execute: () => this.commands.runDescriptor(spaceExportDescriptor),
      type: 'oneshot',
      ready: true,
      visible: () => !this.isIndexSpace(),
      modes: ['edit', 'view']
    })

    // Compatibility alias while the command copy migrates.
    this.commands.registerCommand({
      name: 'space-export',
      description: 'Export all your writings and space as a portable [.json] file',
      execute: () => this.commands.runDescriptor(spaceExportDescriptor),
      type: 'oneshot',
      ready: true,
      visible: false
    })

    this.commands.registerCommand({
      name: 'space-delete-!',
      description: spaceDeleteCopy,
      execute: () => {
        this.commands.runDescriptor(spaceDeleteDescriptor)
      },
      type: 'oneshot',
      ready: () => this.getCapabilities().canDeleteSpace,
      visible: () => !this.isIndexSpace() && this.getCapabilities().canDeleteSpace,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'space-new',
      description: 'Create a new space and enter it immediately',
      execute: () => this.commands.runDescriptor(spaceNewDescriptor),
      type: 'oneshot',
      ready: () => this.getCapabilities().canCreateSpace,
      visible: () => this.commands.isIndexSurfaceActive() && this.getCapabilities().canCreateSpace,
      autoClose: false,
      modes: ['edit', 'view']
    })

    this.commands.registerCommand({
      name: 'index-export-all',
      description: 'Export spaces in memory as standard JSON files in one zip',
      execute: () => this.handleExportAllCommand(),
      type: 'oneshot',
      availability: () => {
        const lane = this.getActiveShelfLane()
        if (selectShelfExportSource(lane) === 'local') return 'armed'
        const snapshot = this.buildAccountSnapshot()
        if (!hasAcceptedSession(snapshot)) return 'disabled'
        if (!snapshot.hasUsername || !snapshot.isSyncPaired || snapshot.isViewMode) return 'disabled'
        const state = resolveAccountMachineState(snapshot)
        if (state === 'local_only') return 'disabled'
        return 'armed'
      },
      gatedReason: () => {
        return 'Export spaces in memory as standard JSON files in one zip.'
      },
      visible: false,
      autoClose: false,
      modes: ['edit', 'view']
    })

    this.commands.registerCommand({
      name: 'space-rename',
      description: spaceRenameCopy,
      execute: () => this.openSpaceRenameFlow(),
      type: 'oneshot',
      ready: () => this.getCapabilities().canRenameSpace,
      visible: false,
      autoClose: false,
      modes: ['edit', 'view']
    })

    this.commands.registerCommand({
      name: 'space-open-file',
      description: 'Open a local JSON space file and overwrite current space. Logged-in sync users spaces stay saved.',
      execute: () => void this.handleOpenFileCommand(),
      type: 'oneshot',
      ready: true,
      autoClose: false,
      visible: () => !this.isIndexSpace()
    })

    this.commands.registerCommand({
      name: 'space-import-file',
      description: 'Import a local JSON space file and merge into current space',
      execute: () => this.commands.runDescriptor(spaceImportDescriptor),
      type: 'oneshot',
      ready: true,
      autoClose: false,
      visible: () => !this.isIndexSpace()
    })

    this.commands.registerCommand({
      name: 'delete!',
      description: 'Delete selected blocks or connections (prompts to select if none)',
      execute: () => this.handleDeleteSelectedCommand(),
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'edit-copy',
      description: 'Copy selected blocks',
      execute: () => this.handleCopyCommand(),
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'edit-paste',
      description: 'Paste blocks from clipboard',
      execute: () => this.handlePasteCommand(),
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'merge-text',
      description: 'Merge selected text blocks into the first block',
      execute: () => this.handleMergeTextCommand(),
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'select',
      description: 'Toggle multi-block selection mode',
      execute: () => this.handleSelectMultipleCommand(),
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'connect',
      description: 'Connect one block to many targets',
      execute: () => this.handleConnectCommand(),
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'disconnect',
      description: 'Clear all connections touching tapped blocks',
      execute: () => this.handleDisconnectCommand(),
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'select-direction-above',
      description: 'Select blocks above the base in the same column',
      execute: () => {
        if (!this.selectDirectionAboveDescriptor) return
        this.runSelectAxisQuickCommand(this.selectDirectionAboveDescriptor)
      },
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'select-direction-below',
      description: 'Select blocks below the base in the same column',
      execute: () => {
        if (!this.selectDirectionBelowDescriptor) return
        this.runSelectAxisQuickCommand(this.selectDirectionBelowDescriptor)
      },
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'select-area',
      description: 'Drag to draw a selection box and tap done to finish',
      execute: () => this.startSelectAreaCommand(selectAreaDescriptor),
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'select-connected',
      description: 'Select downstream blocks from the selected one',
      execute: () => this.handleSelectConnectedCommand(),
      type: 'oneshot',
      ready: true,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'select-all',
      description: 'Select every block and connection',
      execute: () => this.commands.runDescriptor(selectAllDescriptor),
      type: 'oneshot',
      ready: () => this.getActiveBlocks().length > 0
    })

    this.commands.registerCommand({
      name: 'edit-undo',
      description: 'Undo last change (⌘/Ctrl+Z)',
      execute: () => this.handleUndo(),
      type: 'oneshot',
      ready: () => this.history.canUndo(),
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'edit-redo',
      description: 'Redo last change (⇧⌘/Ctrl+Y)',
      execute: () => this.handleRedo(),
      type: 'oneshot',
      ready: () => this.history.canRedo(),
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'view-last-edit',
      description: 'Move view to the last edited block',
      execute: () => this.commands.runDescriptor(viewLastEditDescriptor),
      type: 'oneshot',
      ready: true,
      visible: () => this.resolveSpaceKind() !== 'detached' && !this.commands.isIndexSurfaceActive(),
      autoClose: false,
      modes: ['edit', 'view']
    })

    this.commands.registerCommand({
      name: 'view-origin',
      description: 'Reset view to the origin with breathing room',
      execute: () => this.commands.runDescriptor(viewOriginDescriptor),
      type: 'oneshot',
      ready: true,
      visible: () => this.resolveSpaceKind() !== 'detached' && !this.commands.isIndexSurfaceActive(),
      autoClose: false,
      modes: ['edit', 'view']
    })

    this.commands.registerCommand({
      name: 'account-activate-sync',
      description: 'Activate sync on this device (login + checkout if needed)',
      execute: () => this.handleAccountActivateSyncCommand(),
      type: 'oneshot',
      ready: () => this.getCapabilities().canActivateSync && this.isAccountSyncCommandEnabled('account-activate-sync'),
      visible: () => this.isAccountSyncCommandVisible('account-activate-sync'),
      autoClose: false,
      modes: ['edit', 'view']
    })

    this.commands.registerCommand({
      name: 'user-rename',
      description: 'Rename your public user handle',
      execute: () => this.handleUserRenameCommand(),
      type: 'oneshot',
      ready: () => this.getCapabilities().canRenameUser,
      visible: () => this.commands.isSyncSurfaceActive() && this.getCapabilities().canRenameUser,
      autoClose: false,
      modes: ['edit', 'view']
    })

    this.commands.registerCommand({
      name: 'account-cancel-sync',
      description: 'Schedule sync cancellation at period end (reversible)',
      execute: () => this.handleAccountCancelSyncCommand(),
      type: 'oneshot',
      ready: () => this.getCapabilities().canTerminateSync && this.isAccountSyncCommandEnabled('account-cancel-sync'),
      visible: () => this.isAccountSyncCommandVisible('account-cancel-sync'),
      autoClose: false,
      modes: ['edit', 'view']
    })

    this.commands.registerCommand({
      name: 'account-delete-all!',
      description: 'Permanently delete your sync account data (irreversible)',
      gatedReason: () => selectSyncSurfaceActionDescription(this.buildAccountSnapshot(), 'account-delete-all!'),
      availability: () => {
        const snapshot = this.buildAccountSnapshot()
        if (!hasVerifiedCloudAuthority(snapshot)) return 'disabled'
        if (this.canRunAccountIntent('DELETE_ACCOUNT') && this.getCapabilities().canDeleteAccount) {
          return 'armed'
        }
        return snapshot.accountState === 'sync' && snapshot.hasSyncPlan && !snapshot.stripeCancelAtPeriodEnd
          ? 'gated'
          : 'disabled'
      },
      execute: () => this.handleAccountDeleteCommand(),
      type: 'oneshot',
      visible: () => this.commands.isSyncSurfaceActive(),
      autoClose: false,
      modes: ['edit', 'view']
    })

    this.commands.registerCommand({
      name: 'settings-lilowrite-install',
      description: 'Install Lilo Write as an app. On iOS: Share → Add to Home Screen.',
      execute: () => this.commands.runDescriptor(lilowriteInstallDescriptor),
      type: 'oneshot',
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'refresh',
      description: 'Reload the app',
      execute: () => this.commands.runDescriptor(refreshDescriptor),
      type: 'oneshot',
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'settings-paper-change',
      description: 'Switch paper format (ISO or ANSI, vertical or horizontal)',
      execute: () => this.commands.runDescriptor(paperChangeDescriptor, {
        initialValues: { paper: this.getPaperSpec().label }
      }),
      type: 'oneshot',
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'paper-change',
      description: 'Legacy alias for settings-paper-change',
      execute: () => this.commands.runDescriptor(paperChangeDescriptor, {
        initialValues: { paper: this.getPaperSpec().label }
      }),
      type: 'oneshot',
      visible: false,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'settings-ui-darklight',
      description: 'Choose the app theme for this browser',
      execute: () => this.commands.runDescriptor(settingsThemeDescriptor, {
        initialValues: { mode: readThemeMode() }
      }),
      type: 'oneshot',
      autoClose: false,
      modes: ['edit', 'view']
    })

    this.commands.registerCommand({
      name: 'move-text-deoverlap',
      description: 'Nudge overlapping text blocks just enough to separate them',
      execute: () => this.openMoveTextDeoverlapFlow(moveTextDeoverlapDescriptor),
      type: 'oneshot',
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'move-text-spaceout',
      description: 'Normalize vertical spacing between stacked text blocks',
      execute: () => this.openMoveTextSpaceoutFlow(moveTextSpaceoutDescriptor),
      type: 'oneshot',
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'deoverlap',
      description: 'Legacy alias for move-text-deoverlap',
      execute: () => this.openMoveTextDeoverlapFlow(moveTextDeoverlapDescriptor),
      type: 'oneshot',
      visible: false,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'deoverlap-spacing',
      description: 'Legacy alias for move-text-spaceout',
      execute: () => this.openMoveTextSpaceoutFlow(moveTextSpaceoutDescriptor),
      type: 'oneshot',
      visible: false,
      autoClose: false
    })

    this.commands.registerCommand({
      name: 'de-overlap-all',
      description: 'Legacy alias for move-text-spaceout',
      execute: () => this.openMoveTextSpaceoutFlow(moveTextSpaceoutDescriptor),
      type: 'oneshot',
      visible: false,
      autoClose: false
    })
  }

  private setupEventListeners(): void {
    // Canvas click handling (desktop only; touch taps handled via pointer events)
    if (!this.isTouchDevice) {
      this.canvasElement.addEventListener('click', (e) => {
        this.handleCanvasClick(e)
      })
      this.canvasElement.addEventListener('mouseleave', () => {
        this.applyCanvasCursor(this.getBaseCanvasCursor())
      })
    }

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault()
      this.deferredInstallPrompt = event as BeforeInstallPromptEvent
    })

    window.addEventListener('appinstalled', () => {
      this.deferredInstallPrompt = undefined
    })

    // Window resize (layout viewport)
    window.addEventListener('resize', () => {
      this.handleResize()
    })

    // Visual viewport resize (iOS address bar/keyboard changes) — touch devices only
    if (this.isTouchDevice) {
      const vv: any = (window as any).visualViewport
      if (vv && typeof vv.addEventListener === 'function') {
        vv.addEventListener('resize', () => {
          this.handleResize()
        }, { passive: true } as any)
      }
    }

    // Desktop deletion via Backspace/Delete when not editing
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      this.handleKeyDown(e)
    })

    // Double-click editing removed (accessibility): single click selects; second click edits

    // Desktop pan (left-drag) or block drag (when selected and pressed on it)
    let isPanning = false
    let panMoved = false
    let lastX = 0
    let lastY = 0
    let panStartX = 0
    let panStartY = 0
    this.canvasElement.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return
      this.input.clearPointerStopFlag()
      const editing = this.input.isCurrentlyEditing()
      const rect = this.canvasElement.getBoundingClientRect()
      const sx = e.clientX - rect.left
      const sy = e.clientY - rect.top
      const world = this.screenToWorld(sx, sy)
      const hit = this.findBlockAtPosition(world.x, world.y)
      if (this.areaSelectModeActive && !editing) {
        this.beginAreaSelectDrag(-1, world.x, world.y)
        return
      }
      if (e.shiftKey && !editing && this.canStartAreaSelectGesture()) {
        this.areaSelectShiftPending = true
        this.areaSelectShiftStart = { sx, sy, x: world.x, y: world.y }
        return
      }
      if (!editing) {
        const startPinBlock = this.findStartPinAtScreenPosition(sx, sy)
        if (startPinBlock) {
          this.startPinHold(-1, startPinBlock.ided[0], 'start', sx, sy)
          return
        }
        const endPinBlock = this.findEndPinAtScreenPosition(sx, sy)
        if (endPinBlock) {
          this.startPinHold(-1, endPinBlock.ided[0], 'end', sx, sy)
          return
        }
      }
      if (hit && !editing && this.tryStartBlockDrag(-1, world, { x: sx, y: sy }, hit.ided[0])) {
        return
      }
      // Pan canvas
      isPanning = true
      panMoved = false
      lastX = e.clientX
      lastY = e.clientY
      panStartX = e.clientX
      panStartY = e.clientY
    })
    window.addEventListener('mousemove', (e) => {
      if (this.areaSelectShiftPending && this.areaSelectShiftStart) {
        const rect = this.canvasElement.getBoundingClientRect()
        const sx = e.clientX - rect.left
        const sy = e.clientY - rect.top
        const dx = sx - this.areaSelectShiftStart.sx
        const dy = sy - this.areaSelectShiftStart.sy
        if (Math.hypot(dx, dy) > AREA_SELECT_SHIFT_DRAG_THRESHOLD_PX) {
          this.areaSelectShiftPending = false
          const { x, y } = this.screenToWorld(sx, sy)
          this.beginAreaSelectDrag(-1, this.areaSelectShiftStart.x, this.areaSelectShiftStart.y)
          this.updateAreaSelectDrag(x, y)
          return
        }
      }
      if (this.areaSelectDragActive && this.areaSelectPointerId === -1) {
        const rect = this.canvasElement.getBoundingClientRect()
        const sx = e.clientX - rect.left
        const sy = e.clientY - rect.top
        const { x, y } = this.screenToWorld(sx, sy)
        this.updateAreaSelectDrag(x, y)
        return
      }
      if (this.resizeArmed) {
        const rect = this.canvasElement.getBoundingClientRect()
        const sx = e.clientX - rect.left
        const sy = e.clientY - rect.top
        const { x, y } = this.screenToWorld(sx, sy)
        this.resizeCurrentWorld = { x, y }
        const target = this.findConnectionTargetAtScreenPosition(sx, sy, this.resizeBlockId)
        this.resizeHoverTargetId = target ? target.ided[0] : undefined
        this.isResizing = !this.resizeHoverTargetId
        this.requestRender()
        return
      }
      if (this.dragArmed) {
        const rect = this.canvasElement.getBoundingClientRect()
        const sx = e.clientX - rect.left
        const sy = e.clientY - rect.top
        // Sub-threshold jitter stays a click: keep the gesture armed (no pan
        // underneath) but never latch it into a drag that would eat the click.
        if (!this.isDragging && this.dragStartScreen && !exceedsDragIntent({
          startX: this.dragStartScreen.x,
          startY: this.dragStartScreen.y,
          currentX: sx,
          currentY: sy,
          thresholdPx: DRAG_INTENT_THRESHOLD_PX
        })) {
          return
        }
        this.isDragging = true
        const { x, y } = this.screenToWorld(sx, sy)
        this.dragCurrentWorld = { x, y }
        this.requestRender()
        return
      }
      if (this.pinHoldPointerId === -1 && this.pinHoldStart) {
        const rect = this.canvasElement.getBoundingClientRect()
        const sx = e.clientX - rect.left
        const sy = e.clientY - rect.top
        if (this.handlePinHoldMove(-1, sx, sy)) {
          return
        }
      }
      if (!isPanning) {
        this.updateCanvasCursorFromMouseEvent(e)
        return
      }
      // Camera follows from pixel one for feel; only the click-suppression
      // flag waits for real pan intent so a jittery click still lands.
      if (!panMoved && exceedsDragIntent({
        startX: panStartX,
        startY: panStartY,
        currentX: e.clientX,
        currentY: e.clientY,
        thresholdPx: DRAG_INTENT_THRESHOLD_PX
      })) {
        panMoved = true
      }
      const dx = e.clientX - lastX
      const dy = e.clientY - lastY
      lastX = e.clientX
      lastY = e.clientY
      this.camera.tx += dx
      this.camera.ty += dy
      this.requestRender()
    })
    window.addEventListener('mouseup', () => {
      this.cancelPinHold()
      if (this.areaSelectDragActive && this.areaSelectPointerId === -1) {
        this.endAreaSelectDrag()
        this.suppressNextClick = true
        return
      }
      if (this.areaSelectShiftPending) {
        this.areaSelectShiftPending = false
        this.areaSelectShiftStart = undefined
      }
      if (this.commitEndPinRelease()) {
        this.suppressNextClick = true
      }
      if (this.isDragging && this.dragBlockIds.length && this.dragStartWorld && this.dragCurrentWorld && this.dragOrigUnitsMap.size) {
        const dxUnits = (this.dragCurrentWorld.x - this.dragStartWorld.x) / GRID_SIZE
        const dyUnits = (this.dragCurrentWorld.y - this.dragStartWorld.y) / GRID_SIZE
        let updated = false
        let pushed = false
        const columnWidth = this.getColumnWidthUnits()
        this.dragBlockIds.forEach(id => {
          const origin = this.dragOrigUnitsMap.get(id)
          if (!origin) return
          const newXUnits = origin.x + dxUnits
          const newYUnits = origin.y + dyUnits
          const snappedX = snapXToColumn(newXUnits, columnWidth)
          const snappedY = snapYToEvenNearest(newYUnits)
          if (!pushed && (snappedX !== origin.x || snappedY !== origin.y)) {
            this.pushHistorySnapshot()
            pushed = true
          }
          const changed = this.document.updateBlockPosition(id, snappedX, snappedY)
          updated = updated || changed
        })
        if (updated) {
          this.commitWorkbench(this.document.getStateSnapshot())
        }
        this.suppressNextClick = true // consume the click after a drag
        this.requestRender()
      }
      // Reset drag & pan states
      this.resetDragState()
      isPanning = false
      if (panMoved) {
        this.suppressNextClick = true
      }
      panMoved = false
    })
    // Crossing UI chrome must not kill an in-flight drag: move/up live on
    // window, so only pan/resize/pin state resets here; drag cancel belongs
    // to the true window-escape handler below.
    this.canvasElement.addEventListener('mouseleave', () => {
      this.cancelPinHold()
      isPanning = false
      panMoved = false
      this.resizeArmed = false
      this.isResizing = false
      this.resizeHoverTargetId = undefined
      if (this.areaSelectDragActive && this.areaSelectPointerId === -1) {
        this.endAreaSelectDrag()
      }
    })

    // Cursor out of the window (or cmd-tab away) cancels every in-flight
    // pointer interaction: the dragged block snaps back to origin immediately.
    const cancelPointerInteractionsOnWindowEscape = () => {
      const hadPreview = this.isDragging || this.isResizing || this.dragArmed || this.resizeArmed
      this.cancelPinHold()
      if (this.areaSelectDragActive && this.areaSelectPointerId === -1) {
        this.endAreaSelectDrag()
      }
      this.areaSelectShiftPending = false
      this.areaSelectShiftStart = undefined
      this.resizeArmed = false
      this.isResizing = false
      this.resizeBlockId = undefined
      this.resizeOrig = undefined
      this.resizeCurrentWorld = undefined
      this.resizePointerId = undefined
      this.resizeHoverTargetId = undefined
      this.resetDragState()
      isPanning = false
      panMoved = false
      if (hadPreview) {
        this.requestRender()
      }
    }
    document.documentElement.addEventListener('mouseleave', cancelPointerInteractionsOnWindowEscape)
    window.addEventListener('blur', cancelPointerInteractionsOnWindowEscape)

    // Scroll to pan; ctrl+scroll OR trackpad pinch (ctrlKey=true) to zoom.
    // The editing overlay forwards its wheel events here so pan/zoom stay
    // alive over the block being edited (the overlay otherwise swallows them).
    this.canvasElement.addEventListener('wheel', (e: WheelEvent) => {
      this.handleCanvasWheel(e)
    }, { passive: false })
    this.input.onWheel((e: WheelEvent) => {
      this.handleCanvasWheel(e)
    })

    // Touch gestures via Pointer Events (single-finger pan, two-finger pinch+pan)
    this.canvasElement.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return
      e.preventDefault()
      this.input.clearPointerStopFlag()
      const editing = this.input.isCurrentlyEditing()
      const rect = this.canvasElement.getBoundingClientRect()
      const sx = e.clientX - rect.left
      const sy = e.clientY - rect.top
      this.touchPoints.set(e.pointerId, { x: sx, y: sy })
      if (this.touchPoints.size >= 2) {
        if (this.touchZoomScrubActive) {
          this.endTouchZoomScrub()
        }
        this.cancelPinHold()
        this.cancelAreaSelectLongPress()
        this.cancelTouchZoomScrubLongPress()
      }
      if (this.touchPoints.size === 1) {
        this.singleTouchLast = { x: sx, y: sy }
        this.touchTapCandidateId = e.pointerId
        this.touchTapStartTime = performance.now()
        this.touchTapStartScreen = { x: sx, y: sy }
        this.touchTapMoved = false
        this.touchTapMultiTouch = false
        if (this.areaSelectModeActive) {
          if (!editing) {
            const world = this.screenToWorld(sx, sy)
            this.beginAreaSelectDrag(e.pointerId, world.x, world.y)
          }
          this.touchTapCandidateId = undefined
          this.touchTapStartScreen = undefined
          return
        }
        if (!editing) {
          // Consider drag on selected block for single touch when not editing
          const world = this.screenToWorld(sx, sy)
          const hit = this.findBlockAtPosition(world.x, world.y)
          const startPinBlock = this.findStartPinAtScreenPosition(sx, sy)
          const endPinBlock = this.findEndPinAtScreenPosition(sx, sy)
          if (startPinBlock) {
            this.startPinHold(e.pointerId, startPinBlock.ided[0], 'start', sx, sy)
          } else if (endPinBlock) {
            this.startPinHold(e.pointerId, endPinBlock.ided[0], 'end', sx, sy)
          } else if (hit) {
            if (this.canStartAreaSelectGesture() && !this.isOnEndHandle(hit, sx, sy)) {
              this.startAreaSelectLongPress(e.pointerId, sx, sy, hit.ided[0])
            }
            this.tryStartBlockDrag(e.pointerId, world, { x: sx, y: sy }, hit.ided[0])
          } else {
            this.startTouchZoomScrubLongPress(e.pointerId, sx, sy)
          }
        }
      }
      if (this.touchPoints.size >= 2) {
        this.touchTapMultiTouch = true
        this.cancelPinHold()
        this.cancelAreaSelectLongPress()
        this.cancelTouchZoomScrubLongPress()
      }
      if (this.touchPoints.size === 2) {
        // Initialize pinch
        const pts = Array.from(this.touchPoints.values())
        const cx = (pts[0].x + pts[1].x) / 2
        const cy = (pts[0].y + pts[1].y) / 2
        const dx = pts[1].x - pts[0].x
        const dy = pts[1].y - pts[0].y
        const dist = Math.hypot(dx, dy)
        const anchor = this.screenToWorld(cx, cy)
        this.pinchStart = {
          scale: this.camera.scale,
          tx: this.camera.tx,
          ty: this.camera.ty,
          dist,
          anchorX: anchor.x,
          anchorY: anchor.y,
        }
      }
    }, { passive: false })

    this.canvasElement.addEventListener('pointermove', (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return
      // Prevent page scroll/zoom while interacting
      e.preventDefault()
      const editing = this.input.isCurrentlyEditing()
      const rect = this.canvasElement.getBoundingClientRect()
      const sx = e.clientX - rect.left
      const sy = e.clientY - rect.top
      if (!this.touchPoints.has(e.pointerId)) return
      this.touchPoints.set(e.pointerId, { x: sx, y: sy })
      if (this.pinHoldPointerId === e.pointerId) {
        if (this.handlePinHoldMove(e.pointerId, sx, sy)) {
          return
        }
      }
      if (this.areaSelectDragActive && this.areaSelectPointerId === e.pointerId) {
        if (this.areaSelectLongPressSelectPointerId === e.pointerId) {
          this.areaSelectLongPressSelectMoved = true
        }
        const { x, y } = this.screenToWorld(sx, sy)
        this.updateAreaSelectDrag(x, y)
        this.touchTapMoved = true
        return
      }
      if (this.areaSelectLongPressPointerId === e.pointerId && this.areaSelectLongPressStart) {
        const dx = sx - this.areaSelectLongPressStart.sx
        const dy = sy - this.areaSelectLongPressStart.sy
        if (Math.hypot(dx, dy) > AREA_SELECT_LONG_PRESS_MOVE_TOLERANCE_PX) {
          this.cancelAreaSelectLongPress()
        }
      }
      if (this.touchZoomScrubActive && this.touchZoomScrubPointerId === e.pointerId && this.touchZoomScrubAnchor) {
        this.updateTouchZoomScrub(sy)
        this.touchTapMoved = true
        return
      }
      if (this.touchZoomScrubPointerId === e.pointerId && this.touchZoomScrubStart) {
        const dx = sx - this.touchZoomScrubStart.sx
        const dy = sy - this.touchZoomScrubStart.sy
        if (Math.hypot(dx, dy) > AREA_SELECT_LONG_PRESS_MOVE_TOLERANCE_PX) {
          this.cancelTouchZoomScrubLongPress()
        }
      }
      if (this.touchTapCandidateId === e.pointerId && this.touchTapStartScreen) {
        const dx = sx - this.touchTapStartScreen.x
        const dy = sy - this.touchTapStartScreen.y
        if (!this.touchTapMoved && Math.hypot(dx, dy) > TOUCH_TAP_MOVE_TOLERANCE_PX) {
          this.touchTapMoved = true
        }
      }

      if (this.touchPoints.size === 1 && this.singleTouchLast) {
        // If armed for drag with this pointer, move block instead of panning
        if (!editing && this.resizeArmed && this.resizePointerId === e.pointerId) {
          const { x, y } = this.screenToWorld(sx, sy)
          this.resizeCurrentWorld = { x, y }
          const target = this.findConnectionTargetAtScreenPosition(sx, sy, this.resizeBlockId)
          this.resizeHoverTargetId = target ? target.ided[0] : undefined
          this.isResizing = !this.resizeHoverTargetId
          this.requestRender()
          return
        }
        if (!editing && this.dragArmed && this.dragPointerId === e.pointerId) {
          this.isDragging = true
          const { x, y } = this.screenToWorld(sx, sy)
          this.dragCurrentWorld = { x, y }
          this.requestRender()
          return
        }
        // Single-finger pan
        const last = this.singleTouchLast
        const dx = sx - last.x
        const dy = sy - last.y
        this.singleTouchLast = { x: sx, y: sy }
        this.camera.tx += dx
        this.camera.ty += dy
        this.requestRender()
      } else if (this.touchPoints.size >= 2 && this.pinchStart) {
        this.touchTapMultiTouch = true
        // Two-finger pinch + pan
        const pts = Array.from(this.touchPoints.values()).slice(0, 2)
        const cx = (pts[0].x + pts[1].x) / 2
        const cy = (pts[0].y + pts[1].y) / 2
        const dx = pts[1].x - pts[0].x
        const dy = pts[1].y - pts[0].y
        const dist = Math.max(0.0001, Math.hypot(dx, dy))
        const sf = dist / this.pinchStart.dist
        const unclamped = this.pinchStart.scale * sf
        const nextScale = Math.min(Math.max(unclamped, this.scaleMin), this.scaleMax)
        this.camera.scale = nextScale
        // Anchor: keep world point under centroid fixed
        this.camera.tx = cx - this.pinchStart.anchorX * nextScale
        this.camera.ty = cy - this.pinchStart.anchorY * nextScale
        this.requestRender()
      }
    }, { passive: false })

    const endTouch = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return
      const editing = this.input.isCurrentlyEditing()
      const rect = this.canvasElement.getBoundingClientRect()
      const sx = e.clientX - rect.left
      const sy = e.clientY - rect.top
      if (this.pinHoldPointerId === e.pointerId) {
        this.cancelPinHold()
      }
      if (this.areaSelectLongPressPointerId === e.pointerId) {
        this.cancelAreaSelectLongPress()
      }
      const endedTouchZoomScrub = this.touchZoomScrubPointerId === e.pointerId
      if (endedTouchZoomScrub && !this.touchZoomScrubActive) {
        this.cancelTouchZoomScrubLongPress()
      }
      this.touchPoints.delete(e.pointerId)
      if (this.touchZoomScrubActive && endedTouchZoomScrub) {
        this.endTouchZoomScrub()
        return
      }
      if (this.areaSelectDragActive && this.areaSelectPointerId === e.pointerId) {
        const shouldEnterSilentMultiSelect =
          this.areaSelectLongPressSelectPointerId === e.pointerId
          && !this.areaSelectLongPressSelectMoved
          && Boolean(this.areaSelectLongPressSelectBlockId)
        const selectionBlockId = this.areaSelectLongPressSelectBlockId
        this.endAreaSelectDrag()
        if (shouldEnterSilentMultiSelect && selectionBlockId) {
          this.enterSilentMultiSelectMode(selectionBlockId)
        }
        return
      }
      // Commit drag on touch end if active
      if (!editing && this.resizeArmed && this.resizePointerId === e.pointerId) {
        this.commitEndPinRelease()
      }
      if (!editing && this.dragArmed && this.dragPointerId === e.pointerId) {
        if (this.isDragging && this.dragBlockIds.length && this.dragStartWorld && this.dragCurrentWorld && this.dragOrigUnitsMap.size) {
          const dxUnits = (this.dragCurrentWorld.x - this.dragStartWorld.x) / GRID_SIZE
          const dyUnits = (this.dragCurrentWorld.y - this.dragStartWorld.y) / GRID_SIZE
          let updated = false
          let pushed = false
          const columnWidth = this.getColumnWidthUnits()
          this.dragBlockIds.forEach(id => {
            const origin = this.dragOrigUnitsMap.get(id)
            if (!origin) return
            const newXUnits = origin.x + dxUnits
            const newYUnits = origin.y + dyUnits
            const snappedX = snapXToColumn(newXUnits, columnWidth)
            const snappedY = snapYToEvenNearest(newYUnits)
            if (!pushed && (snappedX !== origin.x || snappedY !== origin.y)) {
              this.pushHistorySnapshot()
              pushed = true
            }
            const changed = this.document.updateBlockPosition(id, snappedX, snappedY)
            updated = updated || changed
          })
          if (updated) {
            this.commitWorkbench(this.document.getStateSnapshot())
            this.requestRender() // reflect snap immediately on touch end
          } else {
            this.requestRender()
          }
        }
        this.resetDragState()
      }
      const endedCandidate = this.touchTapCandidateId === e.pointerId
      if (endedCandidate) {
        const duration = performance.now() - this.touchTapStartTime
        const shouldTap = !this.touchTapMoved && !this.touchTapMultiTouch && duration <= TOUCH_TAP_MAX_DURATION_MS
        if (shouldTap) {
          this.handleTapAtScreenPosition(sx, sy)
        }
      }
      if (endedCandidate || this.touchPoints.size === 0) {
        this.touchTapCandidateId = undefined
        this.touchTapStartScreen = undefined
        this.touchTapMoved = false
        this.touchTapMultiTouch = false
        this.touchTapStartTime = 0
      }
      if (this.touchPoints.size === 1) {
        // Seed single finger pan continuation
        const remaining = Array.from(this.touchPoints.values())[0]
        this.singleTouchLast = { x: remaining.x, y: remaining.y }
        // Reset pinch state
        this.pinchStart = undefined
      } else if (this.touchPoints.size === 0) {
        this.singleTouchLast = undefined
        this.pinchStart = undefined
        this.touchTapCandidateId = undefined
        this.touchTapStartScreen = undefined
        this.touchTapMoved = false
        this.touchTapMultiTouch = false
        this.touchTapStartTime = 0
      } else if (this.touchPoints.size >= 2) {
        // If a finger lifted but still 2+ remain, reinitialize pinch baseline
        const pts = Array.from(this.touchPoints.values()).slice(0, 2)
        const cx = (pts[0].x + pts[1].x) / 2
        const cy = (pts[0].y + pts[1].y) / 2
        const dx = pts[1].x - pts[0].x
        const dy = pts[1].y - pts[0].y
        const dist = Math.hypot(dx, dy)
        const anchor = this.screenToWorld(cx, cy)
        this.pinchStart = {
          scale: this.camera.scale,
          tx: this.camera.tx,
          ty: this.camera.ty,
          dist,
          anchorX: anchor.x,
          anchorY: anchor.y,
        }
      }
    }
    this.canvasElement.addEventListener('pointerup', endTouch)
    this.canvasElement.addEventListener('pointercancel', endTouch)
    this.canvasElement.addEventListener('pointerout', endTouch)
  }

  private handleCanvasClick(e: MouseEvent): void {
    const rect = this.canvasElement.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top
    this.handleTapAtScreenPosition(sx, sy, { shiftKey: e.shiftKey })
  }

  private handleTapAtScreenPosition(sx: number, sy: number, opts: { shiftKey?: boolean } = {}): void {
    if (this.input.consumePointerStopFlag()) {
      return
    }
    // If we just ended editing due to a blur/tap, suppress this click
    if (this.suppressNextClick) {
      this.suppressNextClick = false
      return
    }
    // If editing, a tap in empty space should end editing and return to neutral (no new block)
    if (this.input.isCurrentlyEditing()) {
      this.input.stopEditing() // onStop callback will set justStoppedEditing
      return
    }

    const { x, y } = this.screenToWorld(sx, sy)
    const caretPoint = this.canvasPointToClient(sx, sy)

    if (this.areaSelectModeActive) {
      const target = this.findBlockAtPosition(x, y)
      if (!target) {
        this.commands.exitEngine()
      }
      return
    }

    if (this.isViewMode()) {
      const clickedBlock = this.findBlockAtPosition(x, y)
      if (clickedBlock) {
        const href = this.canvas.hitTestLink(clickedBlock, x, y)
        if (href) {
          if (typeof window !== 'undefined') {
            window.open(href, '_blank', 'noopener')
          }
          return
        }
        if (this.isIndexSpace()) {
          void this.enterSpaceFromIndex(clickedBlock.ided[0])
          return
        }
      }
      return
    }

    if (this.commands.isEngineCanvasMode()) {
      if (this.commands.isEnginePointCanvasMode()) {
        this.commands.handleEngineCanvasPoint({ x, y })
        this.suppressNextClick = false
        return
      }
      const target = this.findBlockAtPosition(x, y)
      if (target) {
        this.commands.handleEngineCanvasSelect(target.ided[0])
      } else if (this.commands.isEngineConnectionCanvasMode()) {
        const hitConnection = this.findConnectionAtPosition(x, y)
        if (hitConnection) {
          this.commands.handleEngineCanvasSelect(`conn:${hitConnection.sourceId}->${hitConnection.targetId}`)
        }
      } else {
        this.commands.setStatus('tap a block', 'error')
      }
      this.suppressNextClick = false
      return
    }

    // Check if clicking on existing block
    const clickedBlock = this.findBlockAtPosition(x, y)
    const selectedIds = this.document.getSelectedBlockIds()
    const selectedConnections = this.document.getSelectedConnections()
    let handled = false
    const additive = Boolean(opts.shiftKey) || this.multiSelectMode || this.silentMultiSelectMode || this.copyModeActive || this.pasteModeActive || this.deleteModeActive

    if (clickedBlock) {
      const href = this.canvas.hitTestLink(clickedBlock, x, y)
      if (href) {
        this.document.selectBlock(clickedBlock.ided[0])
        this.requestRender()
        if (typeof window !== 'undefined') {
          window.open(href, '_blank', 'noopener')
        }
        return
      }
      if (additive) {
        this.document.toggleBlockSelection(clickedBlock.ided[0])
        if (this.multiSelectMode) {
          this.updateMultiSelectMessage()
        }
      } else {
        const isSelected = selectedIds.includes(clickedBlock.ided[0])
        if (isSelected && selectedIds.length === 1 && !this.deleteModeActive) {
          // Second tap enters editing (no double-tap timing)
          this.startEditing(clickedBlock, caretPoint)
        } else {
          this.document.selectBlock(clickedBlock.ided[0])
        }
      }
      handled = true
    } else {
      const hitConnection = this.findConnectionAtPosition(x, y)
      if (hitConnection) {
        if (additive) {
          this.document.toggleConnectionSelection(hitConnection.sourceId, hitConnection.targetId)
          if (this.multiSelectMode) {
            this.updateMultiSelectMessage()
          }
        } else {
          this.document.selectConnection(hitConnection.sourceId, hitConnection.targetId)
        }
        handled = true
      } else if (selectedIds.length) {
        if (this.silentMultiSelectMode) {
          this.exitMultiSelectMode({ preserveNextClick: true })
          handled = true
        } else if (this.multiSelectMode || this.deleteModeActive) {
          // Stay in mode; do not clear selection on blank tap
          handled = true
        } else if (this.copyModeActive) {
          this.exitCopyMode()
          handled = true
        } else {
          // Tap empty space while a block is selected: clear selection (and prune empty blocks)
          this.pruneEmptyBlocks(selectedIds)
          this.document.clearSelection()
          handled = true
        }
      } else if (selectedConnections.length) {
        if (this.silentMultiSelectMode) {
          this.exitMultiSelectMode({ preserveNextClick: true })
          handled = true
        } else if (this.multiSelectMode || this.deleteModeActive) {
          // Stay in mode; do nothing on blank tap
          handled = true
        } else {
          // Blank tap clears active connection without creating a block
          this.document.clearSelection()
          handled = true
        }
      } else {
        if (this.silentMultiSelectMode) {
          this.exitMultiSelectMode({ preserveNextClick: true })
          handled = true
        } else if (this.multiSelectMode || this.deleteModeActive) {
          // In multi-select, blank tap does nothing (stay in mode)
          handled = true
        } else if (this.copyModeActive) {
          this.exitCopyMode()
          handled = true
        } else {
          // Neutral state: tap creates a new block
          const snapshotBeforeCreate = this.document.getStateSnapshot()
          const gridPos = snapToGrid(x, y)
          const columnWidth = this.getColumnWidthUnits()
          const blockId = this.document.createBlock('', snapXToColumnBiased(gridPos.x, columnWidth), snapYToEven(gridPos.y), this.lastCreateWidthUnits)
          this.document.selectBlock(blockId)
          this.pendingCreateHistory = { blockId, snapshot: snapshotBeforeCreate }
          this.pendingEphemeralCreate = { blockId, snapshot: snapshotBeforeCreate }

          // Start editing immediately
          const block = this.document.getBlock(blockId)
          if (block) {
            this.startEditing(block, caretPoint)
          }
          handled = true
        }
      }
    }

    if (handled) {
      this.requestRender()
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.defaultPrevented || e.isComposing) return
    // While editing, let the browser handle text edits
    if (this.input.isCurrentlyEditing()) return
    const isView = this.isViewMode()
    const meta = e.metaKey || e.ctrlKey
    const key = e.key.toLowerCase()
    const target = e.target as HTMLElement | null
    const inPalette = target ? Boolean(target.closest('.cmd-palette')) : false
    const inInput = target ? (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) : false
    // Query editing owns Backspace/undo; a live search owns keyboard navigation
    // even after its input blurs. Neither may fall through to canvas mutations.
    if (inInput || this.searchSession) return
    const paletteCapturesKeyboard = this.commands.isKeyboardCapturing()
    if (isView && meta && (key === 'z' || key === 'y' || key === 'c' || key === 'v')) {
      return
    }
    if (meta && key === 'z' && !e.shiftKey) {
      e.preventDefault()
      this.handleUndo()
      return
    }
    if (meta && (key === 'y' || (key === 'z' && e.shiftKey))) {
      e.preventDefault()
      this.handleRedo()
      return
    }
    if (meta && key === 'a' && !isView && !inPalette && !inInput) {
      e.preventDefault()
      this.handleSelectAllCommand()
      return
    }
    if (meta && key === 'c' && !inPalette && !inInput) {
      e.preventDefault()
      void this.handleCopyCommand()
      return
    }
    if (meta && key === 'v' && !inPalette && !inInput) {
      e.preventDefault()
      void this.handlePasteCommand()
      return
    }
    // Delete selected block on Backspace/Delete
    if (!isView && (e.key === 'Backspace' || e.key === 'Delete')) {
      const hasDeletable = Boolean(this.document.getSelectedConnections().length || this.document.getSelectedBlockIds().length)
      if (hasDeletable) {
        this.pushHistorySnapshot()
      }
      const deleted = this.document.deleteSelection()
      if (deleted.deleted) {
        e.preventDefault()
        this.commitWorkbench(this.document.getStateSnapshot())
        this.requestRender()
      } else {
        // Fallback to legacy block-only path for safety (should rarely run)
        const selectedId = this.document.getSelectedBlockId()
        if (selectedId) {
          e.preventDefault()
          this.document.deleteBlock(selectedId)
          this.document.clearSelection()
          this.commitWorkbench(this.document.getStateSnapshot())
          this.requestRender()
        }
      }
      return
    }
    // ESC clears selection (and removes empty blocks on exit from selected state)
    if (!isView && e.key === 'Escape') {
      if (this.pasteModeActive) {
        this.exitPasteMode()
        this.requestRender()
        return
      }
      if (this.copyModeActive) {
        this.exitCopyMode()
        this.requestRender()
        return
      }
      if (this.deleteModeActive) {
        this.exitDeleteMode({ preserveSelection: true })
        this.requestRender()
        return
      }
      if (this.selectConnectedModeActive) {
        this.exitSelectConnectedMode({ preserveSelection: true })
        this.requestRender()
        return
      }
      if (this.multiSelectMode) {
        this.exitMultiSelectMode()
        this.requestRender()
        return
      }
      const selectedIds = this.document.getSelectedBlockIds()
      const selectedConnections = this.document.getSelectedConnections()
      if (selectedIds.length) {
        this.pruneEmptyBlocks(selectedIds)
        this.document.clearSelection()
        if (this.multiSelectMode) {
          this.exitMultiSelectMode({ preserveNextClick: true })
        }
        this.requestRender()
      } else if (selectedConnections.length) {
        this.document.clearSelection()
        this.requestRender()
      }
      return
    }
    // Enter creates a new block below the selected one (if any)
    if (!isView && e.key === 'Enter') {
      const selectedIds = this.document.getSelectedBlockIds()
      if (selectedIds.length === 1) {
        e.preventDefault()
        const anchor = this.document.getBlock(selectedIds[0])
        if (anchor) {
          const newId = this.createBlockBelow(anchor)
          // Delete anchor if empty
          if (!anchor.text || anchor.text.trim().length === 0) {
            this.document.deleteBlock(anchor.ided[0])
            this.commitWorkbench(this.document.getStateSnapshot())
          }
          const newBlock = this.document.getBlock(newId)
          if (newBlock) this.startEditing(newBlock, undefined, true)
          this.requestRender()
        }
      }
    }
    // Arrow-key panning with fixed paper steps (skip when palette/input focused)
    if (!meta && !e.altKey && !inPalette && !inInput && !paletteCapturesKeyboard) {
      const useLarge = this.camera.scale < 0.25
      const paper = this.getPaperSpec()
      const stepXWorldPx = (useLarge ? paper.nominal.w : paper.quarter.w) * GRID_SIZE
      const stepYWorldPx = (useLarge ? paper.nominal.h : paper.quarter.h) * GRID_SIZE
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        this.panByWorld(-stepXWorldPx, 0)
        return
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        this.panByWorld(stepXWorldPx, 0)
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.panByWorld(0, -stepYWorldPx)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.panByWorld(0, stepYWorldPx)
        return
      }
    }
  }

  // Double-click handler removed per accessibility decision

  private startEditing(block: Block, caretScreenPosition?: { clientX: number, clientY: number }, mobileCaretAtStart?: boolean): void {
    if (this.isViewMode()) return
    this.pendingEditSnapshot = { blockId: block.ided[0], snapshot: this.document.getStateSnapshot() }
    this.input.startEditing(block, (id, text) => {
      if (!this.document.updateBlockText(id, text)) {
        const snapshot = this.document.getStateSnapshot()
        if (!snapshot.some(entry => entry.ided[0] === id && isDeletionReceipt(entry))) return
        // Typing after an incoming deletion is a newer edit of the same atomic block.
        this.document.restoreSnapshot([
          ...snapshot.filter(entry => entry.ided[0] !== id),
          { ...block, text }
        ])
      }
      this.maybePromoteEphemeralCreate(id)
      this.requestRender()
      this.saveWorkbench(this.document.getStateSnapshot())
    }, () => {
      // Stop editing callback: return to neutral state
      // If the edited block is empty, delete it; otherwise just clear selection
      const b = this.document.getBlock(block.ided[0])
      if (b) {
        const normalizedText = normalizeEditorCommitText(b.text)
        if (normalizedText !== b.text) {
          this.document.updateBlockText(block.ided[0], normalizedText)
          this.commitWorkbench(this.document.getStateSnapshot())
        }
      }
      if (b && (!b.text || b.text.trim().length === 0)) {
        if (this.pendingCreateHistory && this.pendingCreateHistory.blockId === block.ided[0]) {
          this.pendingCreateHistory = undefined
        }
        if (this.pendingEphemeralCreate && this.pendingEphemeralCreate.blockId === block.ided[0]) {
          this.pendingEphemeralCreate = undefined
        } else {
          this.pushHistorySnapshot()
        }
        this.document.deleteBlock(block.ided[0])
        this.commitWorkbench(this.document.getStateSnapshot())
      }
      this.applyPendingEphemeralMakeRoom(block.ided[0])
      const committedEdit = this.commitPendingEditSnapshot(block.ided[0])
      if (committedEdit) {
        this.commitWorkbench(this.document.getStateSnapshot())
      }
      this.document.clearSelection()
      this.requestRender()
    }, (split) => {
      // Enter key while editing: split at caret and start editing new block
      const anchor = this.document.getBlock(block.ided[0])
      if (!anchor) return
      this.handleSplitEnter(anchor, split)
    }, caretScreenPosition, mobileCaretAtStart)
  }

  private createBlockBelow(anchor: Block): string {
    const placement = this.resolveBlockBelowPlacement(anchor, '')
    const newX = placement.x
    const newY = placement.y
    const newW = placement.width
    const newId = this.document.createBlock('', newX, newY, newW)
    this.document.selectBlock(newId)
    return newId
  }

  private handleSplitEnter(anchor: Block, split?: { before: string, after: string, dropTrailingBreak?: boolean }): void {
    let beforeText = split ? split.before : anchor.text
    const afterText = split ? split.after : ''
    const trimmed = split?.dropTrailingBreak && beforeText.endsWith('\n')
    if (trimmed) {
      beforeText = beforeText.slice(0, -1)
    }
    const mergedText = beforeText + afterText
    // Record history with a merged, unsplit block so undo restores the paragraph cleanly
    this.document.updateBlockText(anchor.ided[0], mergedText)
    this.pushHistorySnapshot()
    // Apply the split (anchor first half, new block gets second half)
    this.document.updateBlockText(anchor.ided[0], beforeText)

    const placement = this.resolveBlockBelowPlacement(anchor, afterText)
    const newX = placement.x
    const newY = placement.y
    const newW = placement.width
    const newId = this.document.createBlock(afterText, newX, newY, newW)
    this.document.selectBlock(newId)
    this.commitWorkbench(this.document.getStateSnapshot())
    const newBlock = this.document.getBlock(newId)
    if (newBlock) {
      const caret = this.computeBlockStartClientPoint(newBlock)
      this.startEditing(newBlock, caret, true)
    }
    this.requestRender()
  }

  private resolveBlockBelowPlacement(anchor: Block, text: string): { x: number, y: number, width: number } {
    const widthUnits = Math.round(anchor.xyzw[3])
    const snapshot = createSpaceGeometrySnapshot(this.getActiveBlocks(), this.createTextLayoutOracle())
    const slot = snapshot.openSlotBelow(anchor.ided[0], { text, widthUnits })
    if (slot) {
      return {
        x: slot.xUnits,
        y: slot.yUnits,
        width: slot.widthUnits
      }
    }

    const widthPx = anchor.xyzw[3] * GRID_SIZE
    const metrics = this.canvas.measureText(anchor.text, widthPx)
    const minUnits = Math.max(1, Math.round(LINE_HEIGHT / GRID_SIZE))
    const heightUnits = Math.max(minUnits, Math.ceil(metrics.height / GRID_SIZE))
    const newX = Math.round(anchor.xyzw[0])
    const candidateY = Math.round(anchor.xyzw[1] + heightUnits)
    return {
      x: newX,
      y: snapYToEven(candidateY),
      width: widthUnits
    }
  }

  private createTextLayoutOracle(): TextLayoutOracle {
    return {
      measureBlock: (text: string, widthPx: number) => {
        const metrics = this.canvas.measureText(text, widthPx)
        const lineCount = Math.max(1, Math.round(metrics.height / LINE_HEIGHT))
        return {
          heightPx: metrics.height,
          lineCount,
          maxLineWidthPx: metrics.width
        }
      }
    }
  }

  private applyPendingEphemeralMakeRoom(blockId: string): boolean {
    if (!this.pendingEphemeralCreate || this.pendingEphemeralCreate.blockId !== blockId) return false
    const source = this.document.getBlock(blockId)
    if (!source || !source.text || source.text.trim().length === 0) return false

    const snapshot = createSpaceGeometrySnapshot(this.getActiveBlocks(), this.createTextLayoutOracle())
    const updates = snapshot.makeRoomBelow(blockId)
    let changed = false
    updates.forEach((nextY, targetId) => {
      const target = this.document.getBlock(targetId)
      if (!target || target.xyzw[1] === nextY) return
      this.document.updateBlockPosition(targetId, target.xyzw[0], nextY)
      changed = true
    })
    return changed
  }

  private computeBlockStartClientPoint(block: Block): { clientX: number, clientY: number } {
    const baseX = block.xyzw[0] * GRID_SIZE
    const baseY = block.xyzw[1] * GRID_SIZE
    const padX = TEXT_PADDING_X_UNITS * GRID_SIZE
    const halfLeading = (LINE_HEIGHT - FONT_SIZE) / 2
    const innerLeft = (baseX + padX) * this.camera.scale + this.camera.tx
    const innerTop = (baseY + TEXT_PADDING_TOP_PX + TEXT_BASELINE_SHIFT_PX - (halfLeading + CSS_BASELINE_ADJUST_PX)) * this.camera.scale + this.camera.ty
    return this.canvasPointToClient(innerLeft, innerTop)
  }

  private pushHistorySnapshot(): void {
    this.recordHistorySnapshot(this.document.getStateSnapshot())
  }


  private commitPendingEditSnapshot(blockId: string): boolean {
    if (this.pendingCreateHistory && this.pendingCreateHistory.blockId === blockId) {
      const current = this.document.getStateSnapshot()
      const changed = this.hasBlockChanged(this.pendingCreateHistory.snapshot, current, blockId)
      if (changed) {
        this.recordHistorySnapshot(this.pendingCreateHistory.snapshot)
      }
      this.pendingCreateHistory = undefined
      this.pendingEphemeralCreate = undefined
      this.pendingEditSnapshot = undefined
      return changed
    }
    if (this.pendingEditSnapshot && this.pendingEditSnapshot.blockId === blockId) {
      const original = this.pendingEditSnapshot.snapshot
      const current = this.document.getStateSnapshot()
      const changed = this.hasBlockChanged(original, current, blockId)
      if (changed) {
        this.recordHistorySnapshot(original)
      }
      this.pendingEditSnapshot = undefined
      this.pendingEphemeralCreate = undefined
      return changed
    }
    return false
  }

  private hasBlockChanged(before: SpaceEntry[], after: SpaceEntry[], blockId: string): boolean {
    const prev = before.filter(isLiveBlock).find(b => b.ided[0] === blockId)
    const next = after.filter(isLiveBlock).find(b => b.ided[0] === blockId)
    if (!prev && next) return true
    if (prev && !next) return true
    if (!prev || !next) return false
    if (prev.text !== next.text) return true
    if (prev.link.length !== next.link.length) return true
    const [px, py, , pw] = prev.xyzw
    const [nx, ny, , nw] = next.xyzw
    return px !== nx || py !== ny || pw !== nw
  }

  private maybePromoteEphemeralCreate(blockId: string): void {
    if (!this.pendingEphemeralCreate || this.pendingEphemeralCreate.blockId !== blockId) return
    if (this.pendingEphemeralCreate.promoted) return
    const current = this.document.getStateSnapshot()
    const changed = this.hasBlockChanged(this.pendingEphemeralCreate.snapshot, current, blockId)
    const block = this.document.getBlock(blockId)
    const hasText = Boolean(block && block.text && block.text.trim().length > 0)
    if (changed || hasText) {
      this.recordHistorySnapshot(this.pendingEphemeralCreate.snapshot)
      this.pendingEphemeralCreate.promoted = true
    }
  }

  private filteredSnapshot(blocks: SpaceEntry[]): SpaceEntry[] {
    return blocks.filter(b => b.text && b.text.trim().length > 0)
  }

  private pruneEmptyBlocks(blockIds: string[]): void {
    let removed = false
    let pushed = false
    blockIds.forEach(id => {
      const block = this.document.getBlock(id)
      if (block && (!block.text || block.text.trim().length === 0)) {
        if (this.pendingEphemeralCreate && this.pendingEphemeralCreate.blockId === id) {
          this.pendingEphemeralCreate = undefined
          this.pendingCreateHistory = undefined
        } else if (!pushed) {
          this.pushHistorySnapshot()
          pushed = true
        }
        if (this.document.deleteBlock(id)) {
          removed = true
        }
      }
    })
    if (removed) {
      this.commitWorkbench(this.document.getStateSnapshot())
    }
  }

  private handleCanvasWheel(e: WheelEvent): void {
    // Prevent page scroll; use for pan/zoom
    e.preventDefault()
    if (e.ctrlKey) {
      const rect = this.canvasElement.getBoundingClientRect()
      const sx = e.clientX - rect.left
      const sy = e.clientY - rect.top
      this.zoomAtScreenPoint(sx, sy, e.deltaY)
    } else {
      // Natural pan with two-finger scroll
      this.camera.tx -= e.deltaX
      this.camera.ty -= e.deltaY
      this.requestRender()
    }
  }

  // Same absolute width broadcasts to the whole selection only when the
  // grabbed block belongs to a 2+ selection; an outsider grab stays single.
  private getResizeTargets(grabbedId: string): string[] {
    const selectedIds = this.document.getSelectedBlockIds()
    return selectedIds.length >= 2 && selectedIds.includes(grabbedId) ? selectedIds : [grabbedId]
  }

  // Shared End+ release commit for mouse and touch: connect wins over resize,
  // then all resize state resets. Returns true when either commit branch ran.
  private commitEndPinRelease(): boolean {
    let acted = false
    const sourceId = this.resizeBlockId
    const targetId = this.resizeHoverTargetId
    if (sourceId && targetId) {
      if (this.document.getBlock(sourceId) && this.document.getBlock(targetId)) {
        this.pushHistorySnapshot()
      }
      if (this.document.addLink(sourceId, targetId)) {
        this.commitWorkbench(this.document.getStateSnapshot())
      }
      acted = true
      this.requestRender()
    } else if (this.isResizing && this.resizeBlockId && this.resizeOrig && this.resizeCurrentWorld) {
      const origXUnits = this.resizeOrig.xUnits
      // Compute new width in units from current world X
      const newRightPx = this.resizeCurrentWorld.x
      const leftPx = origXUnits * GRID_SIZE
      const widthPx = Math.max(GRID_SIZE, newRightPx - leftPx)
      const snappedUnits = snapToPaperWidth(widthPx, this.getColumnWidthUnits())
      const targets = this.getResizeTargets(this.resizeBlockId)
      const needsSnapshot = targets.some(id => {
        const block = this.document.getBlock(id)
        return block ? block.xyzw[3] !== snappedUnits : false
      })
      if (needsSnapshot) {
        this.pushHistorySnapshot()
      }
      if (this.document.updateBlocksWidth(targets, snappedUnits)) {
        this.commitWorkbench(this.document.getStateSnapshot())
      }
      this.lastCreateWidthUnits = snappedUnits
      acted = true
      this.requestRender()
    }
    this.resizeArmed = false
    this.isResizing = false
    this.resizeBlockId = undefined
    this.resizeOrig = undefined
    this.resizeCurrentWorld = undefined
    this.resizePointerId = undefined
    this.resizeHoverTargetId = undefined
    return acted
  }

  private tryStartBlockDrag(pointerId: number, startWorld: { x: number, y: number }, startScreen: { x: number, y: number }, hitBlockId: string): boolean {
    const selectedIds = this.document.getSelectedBlockIds()
    if (!selectedIds.includes(hitBlockId)) return false
    this.document.setPrimarySelection(hitBlockId)
    const dragIds = this.document.getSelectedBlockIds()
    const origins = new Map<string, { x: number, y: number }>()
    dragIds.forEach(id => {
      const block = this.document.getBlock(id)
      if (block) {
        origins.set(id, { x: block.xyzw[0], y: block.xyzw[1] })
      }
    })
    if (!origins.size) return false
    this.dragArmed = true
    this.dragPointerId = pointerId
    this.dragStartWorld = { ...startWorld }
    this.dragStartScreen = { ...startScreen }
    this.dragCurrentWorld = { ...startWorld }
    this.dragBlockIds = [...dragIds]
    this.dragOrigUnitsMap = origins
    this.isDragging = false
    return true
  }

  private resetDragState(): void {
    this.dragArmed = false
    this.isDragging = false
    this.dragBlockIds = []
    this.dragStartWorld = undefined
    this.dragStartScreen = undefined
    this.dragCurrentWorld = undefined
    this.dragPointerId = undefined
    this.dragOrigUnitsMap.clear()
  }

  private findBlockAtPosition(x: number, y: number): Block | undefined {
    const snapshot = createSpaceGeometrySnapshot(this.getActiveBlocks(), this.createTextLayoutOracle())
    return snapshot.blockAt({ x, y })?.block
  }

  private findConnectionAtPosition(x: number, y: number): ConnectionSelection | undefined {
    const blocks = this.getActiveBlocks()
    if (!blocks.length) return undefined
    const blockMap = new Map(blocks.map(b => [b.ided[0], b]))
    const tolerance = CONNECTION_HIT_TOLERANCE_PX
    let closest: { selection: ConnectionSelection, distance: number } | undefined
    for (const source of blocks) {
      if (!source.link.length) continue
      for (const targetId of source.link) {
        const target = blockMap.get(targetId)
        if (!target) continue
        const path = this.canvas.getConnectionPath(source, target)
        if (path.length < 2) continue
        const distance = this.distanceToPolyline(x, y, path)
        if (distance <= tolerance && (!closest || distance < closest.distance)) {
          closest = {
            selection: { sourceId: source.ided[0], targetId },
            distance
          }
        }
      }
    }
    return closest?.selection
  }

  private distanceToPolyline(x: number, y: number, points: { x: number, y: number }[]): number {
    if (points.length < 2) return Number.POSITIVE_INFINITY
    let min = Number.POSITIVE_INFINITY
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i]
      const b = points[i + 1]
      const dist = this.distanceToSegment(x, y, a.x, a.y, b.x, b.y)
      if (dist < min) min = dist
    }
    return min
  }

  private distanceToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
    const vx = bx - ax
    const vy = by - ay
    const wx = px - ax
    const wy = py - ay
    const segLenSq = vx * vx + vy * vy
    if (segLenSq === 0) {
      return Math.hypot(px - ax, py - ay)
    }
    const t = Math.max(0, Math.min(1, (wx * vx + wy * vy) / segLenSq))
    const projX = ax + t * vx
    const projY = ay + t * vy
    return Math.hypot(px - projX, py - projY)
  }

  private getViewportCssSize(): { width: number, height: number } {
    if (this.isTouchDevice) {
      const vv = (window as any).visualViewport
      if (vv && typeof vv.width === 'number' && typeof vv.height === 'number') {
        return { width: Math.round(vv.width), height: Math.round(vv.height) }
      }
    }
    const width = Math.round(window.innerWidth || document.documentElement.clientWidth)
    const height = Math.round(window.innerHeight || document.documentElement.clientHeight)
    return { width, height }
  }

  private handleResize(): void {
    const viewport = this.getViewportCssSize()
    const w = viewport.width
    const h = viewport.height
    const last = this.lastCanvasCssSize
    if (last && last.w === w && last.h === h) {
      // No size change; just repaint
      this.requestRender()
      return
    }
    // Ensure canvas CSS size matches the visual viewport before rebuilding
    this.canvasElement.style.width = `${w}px`
    this.canvasElement.style.height = `${h}px`
    // Recreate canvas backing store for new size
    this.canvas = new Canvas(this.canvasElement)
    this.lastCanvasCssSize = { w, h }
    // Recompute fit scales and clamp camera
    this.computeFitScales()
    this.clampCamera()
    this.requestRender()
  }

  private requestRender(): void {
    if (this.animationFrame) return
    
    this.animationFrame = requestAnimationFrame(() => {
      // Coalesce CSS background updates to once per frame
      this.applyCssGridTransform()
      this.render()
      this.animationFrame = undefined
    })
  }

  private forceRenderNow(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = undefined
    }
    this.applyCssGridTransform()
    this.render()
  }

  private render(): void {
    this.maybeShowUpdateRunner()
    const blocks = this.getActiveBlocks()
    let renderBlocks = blocks
    // If dragging, render a temporary position for the dragged block (unsnapped)
    let connectionBlocks: Block[] = blocks
    if (this.isDragging && this.dragBlockIds.length && this.dragStartWorld && this.dragCurrentWorld && this.dragOrigUnitsMap.size) {
      const dxUnits = (this.dragCurrentWorld.x - this.dragStartWorld.x) / GRID_SIZE
      const dyUnits = (this.dragCurrentWorld.y - this.dragStartWorld.y) / GRID_SIZE
      renderBlocks = blocks.map(block => {
        const origin = this.dragOrigUnitsMap.get(block.ided[0])
        if (!origin) return block
        return { ...block, xyzw: [origin.x + dxUnits, origin.y + dyUnits, block.xyzw[2], block.xyzw[3]] as [number, number, number, number] }
      })
      connectionBlocks = blocks.map(block => {
        const origin = this.dragOrigUnitsMap.get(block.ided[0])
        if (!origin) return block
        const snappedX = snapXToColumn(origin.x + dxUnits, this.getColumnWidthUnits())
        const snappedY = snapYToEvenNearest(origin.y + dyUnits)
        return { ...block, xyzw: [snappedX, snappedY, block.xyzw[2], block.xyzw[3]] as [number, number, number, number] }
      })
    } else {
      connectionBlocks = renderBlocks
    }
    const selectedIds = this.document.getSelectedBlockIds()
    const editingId = this.input.getCurrentBlockId()
    this.updateTouchSelectionGuard(selectedIds, editingId)
    const realOutgoingSet = new Set<string>()
    const realIncomingSet = new Set<string>()
    blocks.forEach(b => {
      if (b.link.length) realOutgoingSet.add(b.ided[0])
      b.link.forEach(targetId => realIncomingSet.add(targetId))
    })
    const connectedSet = new Set<string>()
    realOutgoingSet.forEach(id => connectedSet.add(id))
    realIncomingSet.forEach(id => connectedSet.add(id))
    const outgoingSet = new Set(realOutgoingSet)
    const incomingSet = new Set(realIncomingSet)
    if (this.resizeBlockId && this.resizeHoverTargetId) {
      outgoingSet.add(this.resizeBlockId)
      incomingSet.add(this.resizeHoverTargetId)
    }
    this.outgoingConnectionIds = outgoingSet
    this.incomingConnectionIds = incomingSet
    this.canvas.setConnectionContext({
      outgoing: outgoingSet,
      incoming: incomingSet,
      connected: connectedSet,
      draftSourceId: this.resizeBlockId && this.resizeHoverTargetId ? this.resizeBlockId : undefined,
      draftTargetId: this.resizeHoverTargetId,
    })
    const selectedConnections = this.document.getSelectedConnections()
    this.canvas.setSelectedConnections(selectedConnections)
    this.canvas.setAreaSelectionRect(this.areaSelectRect)
    this.canvas.setAreaSelectAnchor(this.areaSelectAnchorId)
    this.canvas.setCamera(this.camera)
    this.canvas.setMobileEditing(this.input.isMobileEditing())
    let resizePreview: { id: string, x: number, y: number, width: number, height: number } | undefined
    let resizeFollowerPreviews: Array<{ x: number, y: number, width: number, height: number }> | undefined
    if (this.isResizing && this.resizeBlockId && this.resizeCurrentWorld) {
      const block = this.document.getBlock(this.resizeBlockId)
      if (block) {
        const xPx = block.xyzw[0] * GRID_SIZE
        const yPx = block.xyzw[1] * GRID_SIZE
        const widthPx = this.resizeCurrentWorld.x - xPx
        const rawHeight = this.resizeCurrentWorld.y - yPx
        const heightPx = rawHeight
        resizePreview = {
          id: block.ided[0],
          x: xPx,
          y: yPx,
          width: widthPx,
          height: heightPx,
        }
        // Followers preview the same absolute width at their own origin with
        // their current (cached) height; wrap settles only at release.
        const followerIds = this.getResizeTargets(block.ided[0]).filter(id => id !== block.ided[0])
        if (followerIds.length) {
          resizeFollowerPreviews = []
          followerIds.forEach(id => {
            const follower = this.document.getBlock(id)
            if (!follower) return
            const fx = follower.xyzw[0] * GRID_SIZE
            const fy = follower.xyzw[1] * GRID_SIZE
            const currentHeight = this.canvas.measureText(follower.text, follower.xyzw[3] * GRID_SIZE).height
            resizeFollowerPreviews!.push({ x: fx, y: fy, width: widthPx, height: currentHeight })
          })
        }
      }
    }
    if (resizePreview && !this.resizeHoverTargetId) {
      this.canvas.setResizePreview(resizePreview, resizeFollowerPreviews)
    } else {
      this.canvas.setResizePreview(undefined)
    }
    if (this.resizeBlockId && this.resizeHoverTargetId) {
      const source = this.document.getBlock(this.resizeBlockId)
      const target = this.document.getBlock(this.resizeHoverTargetId)
      const draftGeometry = source && target ? this.canvas.computeDraftGeometry(source, target, resizePreview) : undefined
      this.canvas.setConnectionDraft(draftGeometry)
    } else {
      this.canvas.setConnectionDraft(undefined)
    }
    this.canvas.render(renderBlocks, connectionBlocks, selectedIds, editingId)
    this.input.setCamera(this.camera)
    if (editingId) {
      const block = renderBlocks.find(b => b.ided[0] === editingId)
      if (block) this.input.updateEditingPosition(block, this.camera)
    }
  }

  async start(): Promise<void> {
    try {
      const blocks = await this.storage.initialize()
      const passiveLoadPolicy = resolveLayoutNormalizationPolicy('passive-load')
      const mountedPassport = this.storage.getMountedPassport()
      const bootLanding = this.storage.getBootLanding()
      const isProvisionalWelcome = this.storage.getActiveDeskState() === 'provisional'
      const isEmptyDeactivatedMount = Boolean(
        mountedPassport?.kind === 'verified'
        && mountedPassport.profile.account_state === 'deactivated'
        && bootLanding.kind === 'proven-empty'
      )
      const isMemoryLanding = this.isBootMemoryLanding(bootLanding)
      const shouldPersistMeta = isMemoryLanding
        ? false
        : this.applyPaperMeta(
            this.storage.getMetaBlock(),
            blocks,
            { persist: !isEmptyDeactivatedMount && !isProvisionalWelcome }
          )
      const shouldPersistDefaultSlug = isProvisionalWelcome || isMemoryLanding
        ? false
        : this.stampDefaultSlugIfMissing(blocks)
      const shouldPersistMetaFromLoad = this.storage.consumeMetaNeedsSave()
      this.seedClockFromTimeline(blocks, this.storage.getMetaBlock())
      this.document.hydrate(blocks)
      const didAlign = !isProvisionalWelcome && !isMemoryLanding && passiveLoadPolicy.alignColumns
        ? this.alignBlocksToCurrentColumns()
        : false
      this.storage.setupAutoSave()
      // Initialize camera based on device and viewport
      this.computeFitScales()
      this.initializeCameraDefaults()
      const identity = isMemoryLanding
        ? { spaceId: META_SPACES_ID, spaceEd: META_SPACES_ID }
        : this.getSpaceIdentity(blocks)
      if (identity.spaceId !== META_SPACES_ID) {
        this.focusLastEditedBlock({ requireMeaningfulEdit: true })
      }
      this.applyCssGridTransform()
      this.requestRender()
      if (
        !isEmptyDeactivatedMount
        && !isProvisionalWelcome
        && (shouldPersistMeta || shouldPersistDefaultSlug || shouldPersistMetaFromLoad || didAlign)
      ) {
        this.commitWorkbench(this.document.getStateSnapshot())
      }
      this.restoreBootSpaceIdentity(identity.spaceId)
      if (isEmptyDeactivatedMount) {
        this.setAppState('view')
      }
      if (this.shouldResumeDetachedIndex()) {
        this.setDetachedResumeIntent(undefined)
        this.pendingIndexRefresh = true
      }
      if (identity.spaceId === META_SPACES_ID) {
        this.setDetachedResumeIntent(undefined)
        this.pendingIndexRefresh = true
        this.setActiveSpace(META_SPACES_ID, 'index')
        this.primeIndexPaletteHydrationState()
        this.document.clearSelection()
        this.setAppState('view')
        this.commands.openIndexSurface()
        this.requestRender()
      }

      this.syncAccountMachineFromSnapshot()
      this.updateAccountBar()
      this.localBootReady = true
      this.setupAuthObserver()
      console.log('Lilo Write started successfully')
    } catch (error) {
      if (error instanceof LocalVolumeBootBlocked) {
        this.openAuthModal('local-memory-drive-recovery')
      }
      console.error('Failed to start Lilo Write:', error)
      throw error
    }
  }

  hasPendingRoute(): boolean {
    return Boolean(this.pendingRoute)
  }

  async startBackgroundSync(): Promise<void> {
    if (!authEnabled || !supabase) return
    await this.startRemoteLifecycle()
    this.isInitialSync = true
    try {
      this.authRefreshReady = true
      if (this.isStripeReturnCallbackPending()) {
        this.pendingAuthRefresh = undefined
        await this.awaitInitialAuthBootstrap()
        return
      }
      await this.awaitInitialAuthBootstrap()
      if (this.pendingAuthRefresh) {
        const pending = this.pendingAuthRefresh
        this.pendingAuthRefresh = undefined
        await this.refreshAuthProfile(pending.trigger, pending.options)
      }
    } finally {
      this.isInitialSync = false
      this.applySyncUiState()
    }
  }

  async startNavigationSync(): Promise<void> {
    if (!authEnabled || !supabase) return
    await this.startRemoteLifecycle()
    this.authRefreshReady = true
    if (this.pendingAuthRefresh) {
      const pending = this.pendingAuthRefresh
      this.pendingAuthRefresh = undefined
      await this.refreshAuthProfile(pending.trigger, { ...pending.options, preferAccountAirlock: true })
      return
    }
    await this.refreshAuthProfile('navigation', { preferAccountAirlock: true })
  }

  private async startRemoteLifecycle(): Promise<void> {
    if (this.remoteLifecycleStarted) return
    this.remoteLifecycleStarted = true
    this.setupSyncLifecycle()
    await this.handleStripeReturn()
  }

  private markInitialAuthBootstrapSettled(): void {
    if (this.initialAuthBootstrapSettled) return
    this.initialAuthBootstrapSettled = true
    const resolve = this.initialAuthBootstrapResolver
    this.initialAuthBootstrapResolver = undefined
    if (resolve) resolve()
  }

  private async awaitInitialAuthBootstrap(timeoutMs: number = 400): Promise<void> {
    if (this.initialAuthBootstrapSettled) return
    await new Promise<void>((resolve) => {
      let settled = false
      const finish = (): void => {
        if (settled) return
        settled = true
        window.clearTimeout(timeoutId)
        resolve()
      }
      const timeoutId = window.setTimeout(() => {
        this.initialAuthBootstrapResolver = undefined
        finish()
      }, timeoutMs)
      this.initialAuthBootstrapResolver = () => {
        this.initialAuthBootstrapResolver = undefined
        finish()
      }
    })
  }

  private async handleExportCommand(): Promise<void> {
    await this.storage.waitForActiveFiling()
    if (this.storage.getActiveDeskState() === 'provisional') {
      this.commands.setStatus('nothing to export', 'error')
      return
    }
    try {
      const authority = this.storage.getVolumeAuthority()
      const blocks = this.document.getStateSnapshot()
      const spaceId = this.getSpaceIdentity(blocks).spaceId
      const shelf = await this.storage.loadVolumeShelfSpace(spaceId, authority)
      if (
        !shelf?.blob
        || shelf.meta.payload_state !== 'hydrated'
        || !await this.storage.isVolumeAuthorityCurrent(authority)
      ) {
        throw new Error('space_export_not_hydrated')
      }
      const json = this.storage.exportToJSON(blocks)
      const filename = this.buildExportFilename()
      this.triggerDownload(filename, json)
      this.commands.setStatus('download started', 'success')
    } catch (error) {
      console.error('Export failed:', error)
      this.commands.setStatus('export failed', 'error')
    }
  }

  private async handleDeactivatedExportCloudShelf(): Promise<void> {
    await this.exportCloudShelfZip({ closeModalOnSuccess: true })
  }

  private async handleExportAllCommand(): Promise<void> {
    const lane = this.getActiveShelfLane()
    if (selectShelfExportSource(lane) === 'local' || this.resolveConnectivityState().isOfflineLike) {
      await this.exportLocalShelfZip({
        successStatus: 'library zip started',
        failureStatus: 'library export failed'
      })
      return
    }
    await this.exportCloudShelfZip({
      closeModalOnSuccess: false,
      successStatus: 'library zip started',
      failureStatus: 'library export failed'
    })
  }

  private async handleSyncTerminateConfirmStep(): Promise<void> {
    if (this.syncTerminateInFlight) return
    if (!this.canRunAccountIntent('TERMINATE_SYNC')) {
      this.setAuthModalFeedback('cancel not available for current account state', 'error')
      return
    }
    if (!this.authSession) {
      this.commands.setStatus('login required', 'error')
      this.openAuthModal('email')
      return
    }
    this.dispatchAccountMachine({ type: 'INTENT', intent: 'TERMINATE_SYNC' })
    this.syncTerminateInFlight = true
    this.setAuthActionButtonsEnabled(false)
    this.setAuthModalWaiting(true)
    try {
      const result = await this.requestTerminateSync()
      if (!result.ok) {
        this.dispatchAccountMachine({ type: 'REQUEST_FAIL', intent: 'TERMINATE_SYNC', error: result.error })
        this.dispatchModalFlow({ type: 'SUBSCRIPTION_CANCEL_FAIL' })
        this.setAuthModalFeedback(selectSyncControlFeedback('cancel', result.error ?? 'unknown'), 'error')
        return
      }
      this.dispatchAccountMachine({ type: 'REQUEST_OK', intent: 'TERMINATE_SYNC' })
      this.applyOptimisticSyncLifecycleFromApi({
        cancelAtPeriodEnd: result.cancelAtPeriodEnd,
        currentPeriodEnd: result.currentPeriodEnd
      })
      this.commands.setStatus('sync cancellation scheduled', 'success')
      await this.refreshAuthProfile('sync-lifecycle-action')
      this.transitionSubscriptionControlFlow({ type: 'SUBSCRIPTION_CANCEL_OK' })
    } finally {
      this.syncTerminateInFlight = false
      this.setAuthActionButtonsEnabled(true)
      this.setAuthModalWaiting(false)
    }
  }

  private async handleSyncReactivateConfirmStep(): Promise<void> {
    if (this.syncTerminateInFlight) return
    if (!this.canRunAccountIntent('REACTIVATE_SYNC')) {
      this.setAuthModalFeedback('reactivate not available for current account state', 'error')
      return
    }
    if (!this.authSession) {
      this.commands.setStatus('login required', 'error')
      this.openAuthModal('email')
      return
    }
    this.dispatchAccountMachine({ type: 'INTENT', intent: 'REACTIVATE_SYNC' })
    this.syncTerminateInFlight = true
    this.setAuthActionButtonsEnabled(false)
    this.setAuthModalWaiting(true)
    try {
      const result = await this.requestReactivateSync()
      if (!result.ok) {
        this.dispatchAccountMachine({ type: 'REQUEST_FAIL', intent: 'REACTIVATE_SYNC', error: result.error })
        this.dispatchModalFlow({ type: 'SUBSCRIPTION_REACTIVATE_FAIL' })
        this.setAuthModalFeedback(selectSyncControlFeedback('reactivate', result.error ?? 'unknown'), 'error')
        return
      }
      this.dispatchAccountMachine({ type: 'REQUEST_OK', intent: 'REACTIVATE_SYNC' })
      this.applyOptimisticSyncLifecycleFromApi({
        cancelAtPeriodEnd: result.cancelAtPeriodEnd,
        currentPeriodEnd: result.currentPeriodEnd
      })
      this.commands.setStatus('sync reactivated', 'success')
      await this.refreshAuthProfile('sync-lifecycle-action')
      this.transitionSubscriptionControlFlow({ type: 'SUBSCRIPTION_REACTIVATE_OK' })
    } finally {
      this.syncTerminateInFlight = false
      this.setAuthActionButtonsEnabled(true)
      this.setAuthModalWaiting(false)
    }
  }

  private async requestTerminateSync(): Promise<{ ok: boolean, error?: string, cancelAtPeriodEnd?: boolean, currentPeriodEnd?: string | null }> {
    if (!this.authSession) return { ok: false, error: 'missing_token' }
    const res = await fetch('/api/auth/terminate-sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.authSession.access_token}`
      },
      body: JSON.stringify({ action: 'terminate_sync' })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      const error = typeof data?.error === 'string' ? data.error : ''
      return { ok: false, error: error || 'unknown' }
    }
    const data = await res.json().catch(() => ({}))
    const cancelAtPeriodEnd = typeof data?.cancel_at_period_end === 'boolean' ? data.cancel_at_period_end : undefined
    const currentPeriodEnd = typeof data?.current_period_end === 'string' ? data.current_period_end : null
    return { ok: true, cancelAtPeriodEnd, currentPeriodEnd }
  }

  private async requestReactivateSync(): Promise<{ ok: boolean, error?: string, cancelAtPeriodEnd?: boolean, currentPeriodEnd?: string | null }> {
    if (!this.authSession) return { ok: false, error: 'missing_token' }
    const res = await fetch('/api/auth/reactivate-sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.authSession.access_token}`
      },
      body: JSON.stringify({ action: 'reactivate_sync' })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      const error = typeof data?.error === 'string' ? data.error : ''
      return { ok: false, error: error || 'unknown' }
    }
    const data = await res.json().catch(() => ({}))
    const cancelAtPeriodEnd = typeof data?.cancel_at_period_end === 'boolean' ? data.cancel_at_period_end : undefined
    const currentPeriodEnd = typeof data?.current_period_end === 'string' ? data.current_period_end : null
    return { ok: true, cancelAtPeriodEnd, currentPeriodEnd }
  }

  private async handleAccountDeleteExportStep(): Promise<void> {
    if (this.accountDeleteExportReady) {
      this.openAuthModal('account-delete-confirm')
      return
    }
    this.dispatchAccountMachine({ type: 'INTENT', intent: 'EXPORT_SHELF' })
    const exported = await this.exportCloudShelfZip({ closeModalOnSuccess: false })
    if (!exported) {
      this.dispatchAccountMachine({ type: 'REQUEST_FAIL', intent: 'EXPORT_SHELF', error: 'export_failed' })
      return
    }
    this.dispatchAccountMachine({ type: 'REQUEST_OK', intent: 'EXPORT_SHELF' })
    this.accountDeleteExportReady = true
    if (this.authModalMode !== 'account-delete-export') return
    const descriptor = this.selectAccountDeleteDescriptorForMode('account-delete-export')
    if (descriptor) this.applyAuthModalDescriptor(descriptor)
    this.setAuthModalFeedback('> export ready, continue to delete', 'success')
  }

  private async handleAccountDeleteConfirmStep(): Promise<void> {
    if (this.accountDeleteInFlight) return
    const confirmation = this.authModalInput?.value?.trim() ?? ''
    if (confirmation !== ACCOUNT_DELETE_CONFIRM_KEYWORD) {
      this.setAuthModalFeedback(`invalid input, type ${ACCOUNT_DELETE_CONFIRM_KEYWORD} to confirm`)
      return
    }
    if (!this.authSession) {
      this.commands.setStatus('login required', 'error')
      this.openAuthModal('email')
      return
    }
    let sessionOwnerId: string
    try {
      sessionOwnerId = getSyncOwnerId(this.authSession)
    } catch {
      this.commands.setStatus('session expired, login again', 'error')
      this.openAuthModal('email')
      return
    }
    let authority: AccountUnmountAuthority | undefined
    try {
      authority = await this.resolveAccountUnmountAuthority()
    } catch {
      this.commands.setStatus('account delete paused, memory authority unavailable', 'error')
      return
    }
    if (!authority || authority.owner_id !== sessionOwnerId) {
      this.commands.setStatus('account delete paused, mounted memory changed', 'error')
      return
    }
    if (await this.blockUnsafeSyncExitIfNeeded('account-delete', authority)) return
    this.dispatchAccountMachine({ type: 'INTENT', intent: 'DELETE_ACCOUNT' })
    this.accountDeleteInFlight = true
    this.setAuthActionButtonsEnabled(false)
    this.setAuthInputEnabled(false)
    const previousInputDisplay = this.authModalInput?.style.display
    if (this.authModalInput) {
      this.authModalInput.style.display = 'none'
    }
    this.setAuthModalWaiting(true)
    let shouldRestoreModalControls = true
    try {
      const deleted = await this.requestAccountDelete(confirmation)
      if (!deleted) {
        this.dispatchAccountMachine({ type: 'REQUEST_FAIL', intent: 'DELETE_ACCOUNT', error: 'delete_failed' })
        this.setAuthModalFeedback('account delete failed, try again')
        return
      }
      this.dispatchAccountMachine({ type: 'REQUEST_OK', intent: 'DELETE_ACCOUNT' })
      this.accountDeleteExportReady = false
      this.clearCachedAuthEmailState()
      shouldRestoreModalControls = false
      this.accountDeleteInFlight = false
      this.closeAuthModal()
      this.logoutInProgress = true
      try {
        const result = await this.unmountAccountForExit(authority, 'confirmed')
        if (!result.ok) {
          this.warnPolicyOnceDev(
            'account-delete-unmount-mismatch',
            '[policy] deleted account no longer matches mounted memory; leaving local volume untouched',
            { ownerId: authority.owner_id, reason: result.reason }
          )
          this.stopAccountExitRuntimeMovement()
          this.applyLocalSessionExit()
          await this.clearLogoutPersistenceArtifacts().catch(() => undefined)
          this.commands.setStatus('account deleted, local memory cleanup paused', 'error')
          return
        }
        await this.finishAccountExitLocalLanding({
          beforeAuthClear: () => this.broadcastAccountExitSignal(authority, 'account-delete')
        })
      } catch (error) {
        console.warn('[auth] server deleted Account but local unmount did not finish', error)
        this.stopAccountExitRuntimeMovement()
        this.applyLocalSessionExit()
        await this.clearLogoutPersistenceArtifacts().catch(() => undefined)
        this.commands.setStatus('account deleted, local memory cleanup paused', 'error')
        return
      } finally {
        this.logoutInProgress = false
      }
      this.commands.setStatus('account deleted', 'success')
    } finally {
      this.accountDeleteInFlight = false
      if (shouldRestoreModalControls) {
        this.setAuthActionButtonsEnabled(true)
        this.setAuthInputEnabled(true)
        if (this.authModalInput) {
          this.authModalInput.style.display = previousInputDisplay ?? 'block'
        }
        this.setAuthModalWaiting(false)
      }
    }
  }

  private async requestAccountDelete(confirm: string): Promise<boolean> {
    if (!this.authSession) return false
    const res = await fetch('/api/auth/delete-account', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.authSession.access_token}`
      },
      body: JSON.stringify({ confirm })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      const error = typeof data?.error === 'string' ? data.error : ''
      if (error === 'invalid_confirmation') {
        this.setAuthModalFeedback('invalid delete confirmation')
      } else if (error === 'must_deactivate_first') {
        this.setAuthModalFeedback('deleting account and data is available after scheduling sync deactivation. use cancel Sync subscription first')
        this.commands.setStatus('deleting account and data is available after scheduling sync deactivation. use cancel Sync subscription first', 'error')
      } else if (error === 'invalid_token' || error === 'missing_token') {
        this.commands.setStatus('session expired, login again', 'error')
      } else {
        this.commands.setStatus('account delete failed', 'error')
      }
      return false
    }
    return true
  }

  private async exportCloudShelfZip(opts?: {
    closeModalOnSuccess?: boolean
    successStatus?: string
    failureStatus?: string
  }): Promise<boolean> {
    if (!this.authSession) {
      this.commands.setStatus('login required', 'error')
      return false
    }
    const closeModalOnSuccess = opts?.closeModalOnSuccess !== false
    this.setAuthModalWaiting(true)
    try {
      const { default: JSZip } = await import('jszip')
      const token = this.authSession.access_token
      const spaces = await this.retryFetch(() => this.listSyncSpaces(token, { strict: true }))
      const zip = new JSZip()
      const nameCounts = new Map<string, number>()
      const exportedSpaces: Array<{
        space_id: string
        space_slug?: string | null
        space_ed?: string | null
        sync_at?: string | null
        size_bytes?: number | null
        file: string
      }> = []

      for (let i = 0; i < spaces.length; i += 1) {
        const space = spaces[i]
        const json = await this.retryFetch(() => this.loadSyncSpace(token, space.space_id, { strict: true }))
        if (!json) {
          throw new Error(`missing_remote_space:${space.space_id}`)
        }
        const rawStem = (space.space_slug?.trim() || space.space_id || `space-${i + 1}`).trim()
        const safeStem = this.toSafeExportFilenameStem(rawStem)
        const seen = nameCounts.get(safeStem) ?? 0
        nameCounts.set(safeStem, seen + 1)
        const uniqueStem = seen === 0 ? safeStem : `${safeStem}-${seen + 1}`
        const file = `spaces/${uniqueStem}.json`
        zip.file(file, JSON.stringify(await decodeSpacePayload(json)))
        exportedSpaces.push({
          space_id: space.space_id,
          space_slug: space.space_slug ?? null,
          space_ed: space.space_ed ?? null,
          sync_at: space.sync_at ?? null,
          size_bytes: space.size_bytes ?? null,
          file
        })
      }

      const ownerId = getSyncOwnerId(this.authSession)
      const manifest = {
        kind: 'lilo-cloud-shelf-export',
        exported_at: new Date().toISOString(),
        owner_id: ownerId,
        username: this.authUsername ?? null,
        spaces: exportedSpaces
      }
      zip.file('manifest.json', JSON.stringify(manifest, null, 2))
      const blob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      })
      const filename = this.buildCloudShelfExportZipFilename()
      this.triggerBlobDownload(filename, blob)
      this.commands.setStatus(opts?.successStatus ?? 'Account export started', 'success')
      if (closeModalOnSuccess) this.closeAuthModal()
      return true
    } catch (error) {
      console.error('Cloud shelf export failed:', error)
      this.commands.setStatus(opts?.failureStatus ?? 'Account export failed', 'error')
      return false
    } finally {
      this.setAuthModalWaiting(false)
    }
  }

  private async exportLocalShelfZip(opts?: {
    lane?: ShelfOwnerLane
    successStatus?: string
    failureStatus?: string
  }): Promise<boolean> {
    const authority = this.storage.getVolumeAuthority()
    const ownerId = authority?.owner_id ?? null
    try {
      if (opts?.lane && opts.lane.ownerId !== ownerId) {
        throw new Error('local_export_volume_mismatch')
      }
      if (this.getCapabilities().canEditContent) {
        await this.storage.flushActiveDocumentForVolume(
          this.document.getStateSnapshot(),
          authority
        )
      }
      const preflight = await this.preflightProductVolumeShelfExport(authority)
      if (!preflight.ok) {
        throw new Error(
          preflight.space_id
            ? `local_export_${preflight.reason}:${preflight.space_id}`
            : `local_export_${preflight.reason}`
        )
      }
      const { default: JSZip } = await import('jszip')
      const zip = new JSZip()
      const nameCounts = new Map<string, number>()
      const exportedSpaces: Array<{
        space_id: string
        space_slug?: string | null
        space_ed?: string | null
        size_bytes?: number | null
        file: string
      }> = []

      for (let i = 0; i < preflight.spaces.length; i += 1) {
        const { meta: space, blob } = preflight.spaces[i]
        const rawStem = (space.space_slug?.trim() || space.space_id || `space-${i + 1}`).trim()
        const safeStem = this.toSafeExportFilenameStem(rawStem)
        const seen = nameCounts.get(safeStem) ?? 0
        nameCounts.set(safeStem, seen + 1)
        const uniqueStem = seen === 0 ? safeStem : `${safeStem}-${seen + 1}`
        const file = `spaces/${uniqueStem}.json`
        zip.file(file, blob.payload)
        exportedSpaces.push({
          space_id: space.space_id,
          space_slug: space.space_slug ?? null,
          space_ed: space.space_ed ?? null,
          size_bytes: space.size_bytes ?? null,
          file
        })
      }

      const manifest = {
        kind: 'lilo-local-shelf-export',
        exported_at: new Date().toISOString(),
        owner_id: ownerId,
        spaces: exportedSpaces
      }
      zip.file('manifest.json', JSON.stringify(manifest, null, 2))
      const blob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      })
      if (!await this.storage.isVolumeAuthorityCurrent(authority)) {
        throw new Error('local_export_volume_changed')
      }
      this.triggerBlobDownload(this.buildShelfExportZipFilename('local'), blob)
      this.commands.setStatus(opts?.successStatus ?? 'memory export started', 'success')
      return true
    } catch (error) {
      console.error('Local shelf export failed:', error)
      this.commands.setStatus(opts?.failureStatus ?? 'memory export failed', 'error')
      return false
    }
  }

  private async downloadLocalMemoryRescueCopy(): Promise<void> {
    this.setAuthModalWaiting(true)
    try {
      const blobs = await this.storage.loadLocalRescueBlobs()
      const { default: JSZip } = await import('jszip')
      const zip = new JSZip()
      const nameCounts = new Map<string, number>()
      blobs.forEach((blob, index) => {
        const safeStem = this.toSafeExportFilenameStem(blob.space_id || `space-${index + 1}`)
        const seen = nameCounts.get(safeStem) ?? 0
        nameCounts.set(safeStem, seen + 1)
        const uniqueStem = seen === 0 ? safeStem : `${safeStem}-${seen + 1}`
        zip.file(`${uniqueStem}.json`, blob.payload)
      })
      const archive = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      })
      this.triggerBlobDownload(this.buildShelfExportZipFilename('rescue'), archive)
    } catch (error) {
      console.error('Local Memory rescue export failed:', error)
    } finally {
      this.setAuthModalWaiting(false)
    }
  }

  private async handleSpaceRenameExecute(ctx: EngineContext): Promise<void> {
    const raw = ctx.getValue('space-name')
    if (typeof raw !== 'string') return
    await this.storage.waitForActiveFiling()
    const caps = this.getCapabilities()
    const authority = this.storage.getVolumeAuthority()
    const lane: ShelfOwnerLane = { ownerId: authority?.owner_id ?? null }
    const spaceId = this.syncSpaceId ?? this.activeSpaceId
    const isProvisional = this.storage.getActiveDeskState() === 'provisional'
    if (!caps.canRenameSpace || !this.canEnterIndex() || !spaceId || this.isIndexSpace()) {
      ctx.setFeedback('space rename unavailable', 'error')
      return
    }
    const nextSlug = normalizeHandle(raw)
    const previousSlug = isProvisional
      ? this.spaceIdToDefaultSlug(spaceId)
      : this.syncSpaceSlug ?? this.spaceLabel ?? this.spaceIdToDefaultSlug(spaceId)
    if (!isProvisional && nextSlug === previousSlug) {
      this.commands.exitEngine()
      this.openIndexSurfaceDeterministic()
      return
    }
    let resolvedSlug: string | undefined = nextSlug
    if (!resolvedSlug) {
      this.syncSpaceSlug = previousSlug
      this.setActiveSpace(spaceId, previousSlug)
      ctx.setFeedback('space rename failed', 'error')
      return
    }
    const spaceEditedId = generateId()
    let renamedMeta: LocalSpaceMeta | undefined
    if (isProvisional) {
      const meta = this.storage.getMetaBlock()
      if (!meta) {
        ctx.setFeedback('space rename unavailable', 'error')
        return
      }
      this.storage.setMetaBlock({
        ...meta,
        text: updateLilodataText(meta.text, { slug: resolvedSlug }),
        ided: [meta.ided[0], spaceEditedId]
      })
      await this.fileProvisionalWelcomeAfterCommit(
        this.document.getStateSnapshot(),
        authority
      )
      if (this.storage.getActiveDeskState() !== 'filed') {
        ctx.setFeedback('space rename not saved', 'error')
        return
      }
    } else {
      renamedMeta = await this.storage.renameVolumeSpace(
        authority,
        spaceId,
        resolvedSlug,
        spaceEditedId
      )
      if (!renamedMeta) {
        ctx.setFeedback('space name already used or unavailable', 'error')
        return
      }
    }
    if (lane.ownerId && !isProvisional) {
      this.syncSpaceSlug = resolvedSlug
      this.setSyncRuntimePendingHint(spaceId, true)
      if (this.canSyncWriteNow()) {
        this.startAccountCloudSync()
      } else {
        this.applySyncUiState()
      }
    }
    this.setActiveSpace(spaceId, resolvedSlug)
    this.rememberReadyIndexSpace(spaceId, resolvedSlug, {
      sizeBytes: renamedMeta?.size_bytes,
      spaceEd: renamedMeta?.space_ed ?? spaceEditedId
    })
    this.commands.exitEngine()
    this.openIndexSurfaceDeterministic()
  }

  private async handleUserRenameExecute(ctx: EngineContext): Promise<void> {
    const raw = ctx.getValue('user-name')
    if (typeof raw !== 'string') return
    const caps = this.getCapabilities()
    const currentUsername = this.authUsername
    if (!caps.canRenameUser || !this.authSession || !currentUsername) {
      ctx.setFeedback('login first', 'error')
      this.commands.ensurePaletteVisible()
      this.commands.setStatus('login first', 'error')
      return
    }
    const nextUsername = normalizeHandle(raw)
    if (nextUsername === currentUsername) {
      if (this.commands.isEngineActive()) {
        this.commands.exitEngine()
      } else {
        this.commands.closePalette()
      }
      return
    }
    const result = await this.saveUsername(nextUsername)
    if (!result.ok) {
      ctx.setFeedback(this.mapUsernameSaveErrorToFeedback(result.error), 'error')
      return
    }
    this.authUsername = result.username
    this.updateAccountBar()
    this.commands.setStatus('username saved', 'success')
    if (this.commands.isEngineActive()) {
      this.commands.exitEngine()
    } else {
      this.commands.closePalette()
    }
  }

  private async handleSpaceDeleteExecute(ctx: EngineContext): Promise<void> {
    const raw = ctx.getValue('space-delete-confirm')
    if (typeof raw !== 'string' || raw !== 'delete') return
    await this.storage.waitForActiveFiling()
    if (this.storage.getActiveDeskState() === 'provisional') {
      await this.createLocalBlankSpacePreservingShelf()
      this.commands.ensurePaletteVisible()
      this.commands.minimizePalette()
      this.commands.setStatus('unsaved space reset', 'success')
      return
    }
    const caps = this.getCapabilities()
    const authority = this.storage.getVolumeAuthority()
    const ownerId = authority?.owner_id
    const spaceId = this.syncSpaceId ?? this.activeSpaceId
    const isAccountDelete = Boolean(ownerId && spaceId && caps.canDeleteSpace)
    let syncDeleted = false
    if (isAccountDelete && authority && ownerId && spaceId) {
      const canPushDelete = this.canSyncShelfWriteNow()
      await this.storage.flushActiveDocumentForVolume(
        this.document.getStateSnapshot(),
        authority
      )
      const inspected = await this.inspectProductShelf(authority)
      const seedProof = inspected.seedProofs.find(proof => proof.space_id === spaceId)
      const replacement = await this.openLiveShelfReplacement(
        inspected.rows,
        spaceId,
        authority
      )
      if (!replacement.opened && replacement.hadCandidates) {
        this.commands.setStatus('open another memory before deleting this space', 'error')
        return
      }
      if (seedProof) {
        const deleted = await this.storage.deleteVolumeSpace(
          authority,
          spaceId,
          seedProof
        )
        if (!deleted) {
          this.commands.setStatus('space delete failed', 'error')
          return
        }
        if (!replacement.opened) {
          await this.createLocalBlankSpacePreservingShelf({ authority })
        }
        this.indexSpaces.delete(spaceId)
        this.setSyncRuntimePendingHint(spaceId, false)
        this.openIndexSurfaceDeterministic()
        this.commands.ensurePaletteVisible()
        this.commands.minimizePalette()
        this.commands.setStatus('space deleted', 'success')
        return
      }
      const needsProvisionalWelcome = !replacement.opened
      const deletedAt = generateId()
      const tombstone = await this.storage.markVolumeSpaceDeleted(
        authority,
        spaceId,
        deletedAt
      )
      if (!tombstone) {
        this.commands.setStatus('space delete failed', 'error')
        return
      }
      if (needsProvisionalWelcome) {
        await this.createLocalBlankSpacePreservingShelf({
          authority
        })
      }
      this.indexSpaces.delete(spaceId)
      this.localSyncEchoSuppress.delete(spaceId)
      if (canPushDelete) {
        try {
          const result = await this.retryFetch(() => this.syncEngine.pushDeletedShelfSpace({
            ownerId,
            spaceId,
            volumeAuthority: authority
          }))
          syncDeleted = result.ok
        } catch {
          syncDeleted = false
        }
      }
      this.setSyncRuntimePendingHint(spaceId, !syncDeleted)
      this.openIndexSurfaceDeterministic()
      this.commands.ensurePaletteVisible()
      this.commands.minimizePalette()
      this.commands.setStatus(syncDeleted ? 'space deleted' : 'space delete pending Account sync', syncDeleted ? 'success' : 'error')
      return
    }
    if (authority === null && spaceId && caps.canDeleteSpace) {
      await this.storage.flushActiveDocumentForVolume(
        this.document.getStateSnapshot(),
        authority
      )
      const replacement = await this.openLiveShelfReplacement(
        await this.storage.listVolumeSpacesMeta(authority),
        spaceId,
        authority
      )
      if (!replacement.opened && replacement.hadCandidates) {
        this.commands.setStatus('open another memory before deleting this space', 'error')
        return
      }
      const deleted = await this.storage.deleteVolumeSpace(authority, spaceId)
      if (!deleted) {
        this.commands.setStatus('space delete failed', 'error')
        return
      }
      if (!replacement.opened) {
        await this.createLocalBlankSpacePreservingShelf({ authority })
      }
      this.indexSpaces.delete(spaceId)
      this.openIndexSurfaceDeterministic()
      this.commands.ensurePaletteVisible()
      this.commands.minimizePalette()
      this.commands.setStatus('space deleted', 'success')
      return
    }
    await this.createLocalBlankSpacePreservingShelf()
    this.commands.ensurePaletteVisible()
    this.commands.minimizePalette()
    this.commands.setStatus('space deleted', 'success')
  }

  private async handleSpaceNewCommand(ctx?: EngineContext): Promise<void> {
    const blockedMessage = 'space-new unavailable in this memory state'
    const caps = this.getCapabilities()
    if (!caps.canCreateSpace || !this.canEnterIndex()) {
      if (ctx) {
        ctx.setFeedback(blockedMessage, 'error')
        if (this.commands.isEngineActive()) {
          this.commands.exitEngine()
        }
        this.commands.ensurePaletteVisible()
        this.commands.setStatus(blockedMessage, 'error')
        return
      }
      this.commands.ensurePaletteVisible()
      this.commands.setStatus(blockedMessage, 'error')
      return
    }
    await this.createNewSpace()
    if (this.commands.isEngineActive()) {
      this.commands.exitEngine()
    } else {
      this.commands.closePalette()
    }
  }

  private continueOpenFileCommand(): void {
    this.pendingFileLoadMode = 'open'
    const input = this.ensureImportInput()
    this.commands.clearStatus()
    input.value = ''
    input.click()
  }

  private async handleOpenFileCommand(): Promise<void> {
    const guardMode = await this.resolveOpenFileGuardMode()
    if (guardMode) {
      const confirmed = await this.promptOpenFileGuard(guardMode)
      if (!confirmed) {
        this.commands.ensurePaletteVisible()
        return
      }
    }
    this.continueOpenFileCommand()
  }

  private handleImportFileCommand(): void {
    this.pendingFileLoadMode = 'import'
    const input = this.ensureImportInput()
    this.commands.clearStatus()
    input.value = ''
    input.click()
  }

  private adoptOpenedFileSpaceIdentity(): void {
    const blocks = this.document.getStateSnapshot()
    const identity = this.getSpaceIdentity(blocks)
    const meta = this.storage.getMetaBlock()
    const parsedSlug = meta ? parseLilodataText(meta.text).slug?.trim() : undefined
    const resolvedSlug = parsedSlug && parsedSlug.length
      ? parsedSlug
      : this.spaceIdToDefaultSlug(identity.spaceId)

    if (this.authSession && this.authUsername && this.authIsSync) {
      this.isSyncPaired = true
      this.syncSpaceId = identity.spaceId
      this.syncSpaceSlug = resolvedSlug
      this.rememberReadyIndexSpace(identity.spaceId, resolvedSlug, {
        spaceEd: identity.spaceEd
      })
      this.setActiveSpace(identity.spaceId, resolvedSlug)
      return
    }

    this.syncSpaceId = undefined
    this.syncSpaceSlug = undefined
    this.rememberReadyIndexSpace(identity.spaceId, resolvedSlug, {
      spaceEd: identity.spaceEd
    })
    this.setActiveSpace(identity.spaceId, resolvedSlug)
  }

  private handleUndo(): void {
    this.pendingCreateHistory = undefined
    this.pendingEditSnapshot = undefined
    this.pendingEphemeralCreate = undefined
    const snapshot = this.history.undo(this.document.getStateSnapshot())
    if (!snapshot) {
      this.commands.setStatus('nothing to undo', 'error')
      this.commands.minimizePalette()
      return
    }
    this.document.restoreSnapshot(this.filteredSnapshot(snapshot))
    this.bumpSpaceEditId()
    this.commitWorkbench(this.document.getStateSnapshot())
    this.markSyncDirty()
    this.requestRender()
    this.commands.setStatus('undo', 'success')
    this.commands.minimizePalette()
  }

  private handleRedo(): void {
    this.pendingCreateHistory = undefined
    this.pendingEditSnapshot = undefined
    this.pendingEphemeralCreate = undefined
    const snapshot = this.history.redo(this.document.getStateSnapshot())
    if (!snapshot) {
      this.commands.setStatus('nothing to redo', 'error')
      this.commands.minimizePalette()
      return
    }
    this.document.restoreSnapshot(this.filteredSnapshot(snapshot))
    this.bumpSpaceEditId()
    this.commitWorkbench(this.document.getStateSnapshot())
    this.markSyncDirty()
    this.requestRender()
    this.commands.setStatus('redo', 'success')
    this.commands.minimizePalette()
  }

  private handleGoToLastCommand(): void {
    const blocks = this.getActiveBlocks()
    if (!blocks.length) {
      this.commands.setStatus('no last block found', 'error')
      return
    }
    if (!this.focusLastEditedBlock({ selectInEditMode: true })) {
      this.commands.setStatus('no last block found', 'error')
      return
    }
    this.commands.setStatus('jumped to last edit', 'success')
    this.applyCssGridTransform()
    this.requestRender()
    // Slight delay mirrors other oneshots so the status can render before collapsing
    window.setTimeout(() => this.commands.closePalette(), 120)
  }

  private handleGoToHomeCommand(): void {
    const { xPx, yPx } = this.computeStartOffsets()
    this.camera.tx = xPx
    this.camera.ty = yPx
    this.applyCssGridTransform()
    this.requestRender()
    // No status for success; collapse after a short delay for consistency
    window.setTimeout(() => this.commands.closePalette(), 120)
  }

  private isStandaloneDisplayMode(): boolean {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isIosStandalone = (navigator as any).standalone === true
    return isStandalone || isIosStandalone
  }

  private isIosDevice(): boolean {
    const ua = navigator.userAgent
    return /iPad|iPhone|iPod/.test(ua)
      || (ua.includes('Mac') && (navigator.maxTouchPoints || 0) > 1)
  }

  private async handleInstallCommand(): Promise<void> {
    if (this.isStandaloneDisplayMode()) {
      this.commands.setStatus('already installed', 'success')
      return
    }

    const promptEvent = this.deferredInstallPrompt
    if (promptEvent) {
      try {
        await promptEvent.prompt()
        const choice = await promptEvent.userChoice
        const message = choice.outcome === 'accepted' ? 'installing' : 'install dismissed'
        this.commands.setStatus(message, choice.outcome === 'accepted' ? 'success' : 'error')
      } catch {
        this.commands.setStatus('install failed', 'error')
      }
      this.deferredInstallPrompt = undefined
      return
    }

    if (this.isIosDevice()) {
      this.commands.setStatus('Share → Add to Home Screen', 'success')
    } else {
      this.commands.setStatus('install not ready yet', 'error')
    }
  }

  private handleRefreshCommand(): void {
    this.commands.setStatus('refreshing', 'success')
    window.setTimeout(() => {
      window.location.reload()
    }, 0)
  }

  private handlePaperChangeExecute(ctx: EngineContext): void {
    const selected = ctx.getValue('paper')
    if (typeof selected !== 'string') {
      this.commands.setStatus('pick a paper format', 'error')
      return
    }
    const next = findPaperTypeIdByLabel(selected)
    if (!next) {
      this.commands.setStatus('invalid paper format', 'error')
      return
    }
    this.setPaperType(next, { resnap: true, persistMeta: true })
    this.commands.setStatus(`paper: ${this.getPaperSpec().label}`, 'success')
  }

  private handleThemeModeExecute(ctx: EngineContext): void {
    const selected = ctx.getValue('mode')
    if (selected !== 'auto' && selected !== 'light' && selected !== 'dark') {
      return
    }
    const mode = selected as ThemeMode
    const apply = () => {
      setThemeMode(mode)
      this.applyCssGridTransform()
      this.requestRender()
    }
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(apply)
      return
    }
    apply()
  }

  private handleMoveTextDeoverlapExecute(ctx: EngineContext): void {
    this.deoverlapTargetModeActive = false
    const scopeValue = ctx.getValue('scope')
    const scope = scopeValue === 'all' ? 'all' : 'selected'
    const { targetIds } = this.resolveDeoverlapTargets(scope)
    if (scope === 'selected' && targetIds.length <= 0) {
      this.commands.setStatus('select at least one block', 'error')
      return
    }

    const changed = this.resolveOverlaps({ targetIds, mode: 'soft' })
    if (changed) {
      this.commitWorkbench(this.document.getStateSnapshot())
      this.requestRender()
      this.commands.setStatus(scope === 'selected' ? 'selection deoverlapped' : 'text deoverlapped', 'success')
    } else {
      this.commands.setStatus('no overlaps found', 'success')
    }
    // Defer close one tick so the status flash lands before the palette collapses.
    window.setTimeout(() => this.commands.closePalette(), 0)
  }

  private handleMoveTextSpaceoutExecute(ctx: EngineContext): void {
    this.deoverlapTargetModeActive = false
    const scopeValue = ctx.getValue('scope')
    const scope = scopeValue === 'all' ? 'all' : 'selected'
    const spaceValue = ctx.getValue('space')
    if (typeof spaceValue !== 'string') {
      this.commands.setStatus('pick a space value', 'error')
      return
    }

    const spacingLines = Number.parseInt(spaceValue, 10)
    if (!Number.isFinite(spacingLines) || spacingLines < 0) {
      this.commands.setStatus('invalid space value', 'error')
      return
    }
    const spacingUnits = spacingLines * 2

    const { targetIds } = this.resolveDeoverlapTargets(scope)
    if (scope === 'selected' && targetIds.length <= 0) {
      this.commands.setStatus('select at least one block', 'error')
      return
    }

    const changed = this.resolveOverlaps({ targetIds, spacingUnits, mode: 'spaceout' })
    if (changed) {
      this.commitWorkbench(this.document.getStateSnapshot())
      this.requestRender()
      this.commands.setStatus(scope === 'selected' ? 'selection spaced out' : 'text spaced out', 'success')
    } else {
      this.commands.setStatus('spacing already clean', 'success')
    }
    // Defer close one tick so the status flash lands before the palette collapses.
    window.setTimeout(() => this.commands.closePalette(), 0)
  }

  private resetInteractionState(): void {
    this.copyModeActive = false
    this.clipboardSelection = undefined
    this.pasteModeActive = false
    this.pasteBlocks = undefined
    this.pastePrimaryId = undefined
    this.selectConnectedModeActive = false
    this.silentMultiSelectMode = false
    this.deleteModeActive = false
    this.areaSelectModeActive = false
    this.areaSelectDragActive = false
    this.areaSelectPointerId = undefined
    this.areaSelectOriginWorld = undefined
    this.areaSelectCurrentWorld = undefined
    this.areaSelectRect = undefined
    this.areaSelectAnchorId = undefined
    this.areaSelectTouchedIds.clear()
    this.areaSelectShiftPending = false
    this.areaSelectShiftStart = undefined
    this.cancelAreaSelectLongPress()
    this.clearAreaSelectLongPressSelectionState()
    this.multiSelectMode = false
    this.dragArmed = false
    this.isDragging = false
    this.dragBlockIds = []
    this.dragStartWorld = undefined
    this.dragStartScreen = undefined
    this.dragCurrentWorld = undefined
    this.dragPointerId = undefined
    this.dragOrigUnitsMap.clear()
    this.resizeArmed = false
    this.isResizing = false
    this.resizeBlockId = undefined
    this.resizeOrig = undefined
    this.resizeCurrentWorld = undefined
    this.resizePointerId = undefined
    this.resizeHoverTargetId = undefined
    this.outgoingConnectionIds.clear()
    this.incomingConnectionIds.clear()
    this.pendingCreateHistory = undefined
    this.pendingEditSnapshot = undefined
    this.pendingEphemeralCreate = undefined
    this.commands.clearStatus()
  }

  private handleDeleteSelectedCommand(): void {
    const selectedIds = this.document.getSelectedBlockIds()
    const selectedConnections = this.document.getSelectedConnections()
    const hasDeletable = Boolean(selectedConnections.length || selectedIds.length)
    if (hasDeletable) {
      this.performDeleteSelection()
      return
    }
    this.enterDeleteMode()
  }

  private enterDeleteMode(): void {
    if (!this.deleteDescriptor) return
    this.suppressNextClick = false
    this.deleteModeActive = true
    this.commands.runDescriptor(this.deleteDescriptor)
  }

  private exitDeleteMode(opts?: { preserveSelection?: boolean }): void {
    const wasActive = this.deleteModeActive || this.commands.isEngineActive()
    if (!wasActive) return
    this.deleteModeActive = false
    if (this.commands.isEngineActive()) {
      this.commands.exitEngine()
    }
    if (!opts?.preserveSelection) {
      this.commands.clearStatus()
    }
    // Palette hide sets suppressNextClick; re-enable canvas interaction immediately.
    this.suppressNextClick = false
  }

  private performDeleteSelection(): void {
    const selectedIds = this.document.getSelectedBlockIds()
    const selectedConnections = this.document.getSelectedConnections()
    const selectedEphemeralOnly = selectedIds.length === 1 &&
      this.pendingEphemeralCreate &&
      !this.pendingEphemeralCreate.promoted &&
      this.pendingEphemeralCreate.blockId === selectedIds[0]
    const hasDeletable = Boolean(selectedConnections.length || selectedIds.length)
    if (!hasDeletable) {
      this.commands.setStatus('nothing selected', 'error')
      this.commands.minimizePalette()
      return
    }
    if (!selectedEphemeralOnly) {
      this.pushHistorySnapshot()
    } else {
      this.pendingEphemeralCreate = undefined
      this.pendingCreateHistory = undefined
    }
    const result = this.document.deleteSelection()
    if (result.deleted) {
      let message = 'removed block'
      if (result.kind === 'connection') {
        message = selectedConnections.length > 1 ? `removed ${selectedConnections.length} connections` : 'removed connection'
      } else if (selectedIds.length > 1) {
        message = `removed ${selectedIds.length} blocks`
      }
      this.commitWorkbench(this.document.getStateSnapshot())
      this.requestRender()
      this.commands.setStatus(message, 'success')
      window.setTimeout(() => this.commands.closePalette(), 180)
      return
    }
    this.commands.setStatus('nothing selected', 'error')
    this.commands.minimizePalette()
  }

  private getMergeTextSelectionIds(): string[] {
    const mergeableIds = new Set(
      this.getActiveBlocks()
        .filter(block => !block.text.trimStart().startsWith('/lilodata'))
        .map(block => block.ided[0])
    )
    return this.document.getSelectedBlockIds().filter(id => mergeableIds.has(id))
  }

  private syncMergeTextSelection(ids: string[]): void {
    this.document.clearSelection()
    const validIds = ids.filter(id => Boolean(this.document.getBlock(id)))
    validIds.forEach((id, index) => {
      if (index === 0) {
        this.document.selectBlock(id)
      } else {
        this.document.toggleBlockSelection(id)
      }
    })
  }

  private mergeTextFeedback(count: number): string {
    if (count <= 0) return 'select blocks to merge'
    if (count === 1) return 'merge into 1 block'
    return `merge ${count} blocks into first`
  }

  private handleMergeTextDescriptorPick(value: string, ctx: EngineContext): StepSelectResult {
    const block = this.document.getBlock(value)
    if (!block || block.text.trimStart().startsWith('/lilodata')) {
      return { ok: false, message: 'tap a text block' }
    }

    const current = ctx.getValue('pick-blocks')
    const next = Array.isArray(current) ? [...current] : this.getMergeTextSelectionIds()
    const existingIndex = next.indexOf(value)
    if (existingIndex === 0) {
      const feedback = 'already destination'
      ctx.setFeedback(feedback, 'success')
      this.commands.setStatus(feedback, 'success')
      this.syncMergeTextSelection(next)
      this.requestRender()
      return { ok: true, value: next }
    }
    if (existingIndex > 0) {
      next.splice(existingIndex, 1)
    } else {
      next.push(value)
    }

    this.syncMergeTextSelection(next)
    const feedback = this.mergeTextFeedback(next.length)
    ctx.setFeedback(feedback, next.length >= 2 ? 'success' : 'error')
    this.commands.setStatus(feedback, next.length >= 2 ? 'success' : 'error')
    this.commands.minimizePalette()
    this.requestRender()
    return { ok: true, value: next }
  }

  private handleMergeTextDescriptorExecute(ctx: EngineContext): void {
    const value = ctx.getValue('pick-blocks')
    const ids = Array.isArray(value) ? value : this.getMergeTextSelectionIds()
    this.performMergeTextBlocks(ids)
  }

  private handleMergeTextCommand(): void {
    if (this.isViewMode()) return
    if (!this.mergeTextDescriptor) return
    this.input.stopEditing()
    if (this.pasteModeActive) this.exitPasteMode()
    if (this.copyModeActive) this.exitCopyMode({ preserveSelection: true })
    if (this.deleteModeActive) this.exitDeleteMode({ preserveSelection: true })
    if (this.selectConnectedModeActive) this.exitSelectConnectedMode({ preserveSelection: true })
    if (this.multiSelectMode) this.exitMultiSelectMode({ preserveNextClick: true })

    const selectedIds = this.getMergeTextSelectionIds()
    if (selectedIds.length >= 2) {
      this.performMergeTextBlocks(selectedIds)
      return
    }

    this.suppressNextClick = false
    this.commands.runDescriptor(this.mergeTextDescriptor, selectedIds.length === 1
      ? { initialValues: { 'pick-blocks': selectedIds } }
      : undefined)
    if (selectedIds.length === 1) {
      this.commands.setStatus(this.mergeTextFeedback(1), 'success')
      this.requestRender()
    }
  }

  private performMergeTextBlocks(ids: string[]): boolean {
    const activeIds = new Set(
      this.getActiveBlocks()
        .filter(block => !block.text.trimStart().startsWith('/lilodata'))
        .map(block => block.ided[0])
    )
    const seen = new Set<string>()
    const mergeIds = ids.filter(id => {
      if (!activeIds.has(id) || seen.has(id)) return false
      seen.add(id)
      return true
    })
    if (mergeIds.length < 2) {
      this.commands.setStatus('select at least 2 blocks', 'error')
      this.commands.minimizePalette()
      return false
    }

    const destinationId = mergeIds[0]
    const sourceIds = mergeIds.slice(1)
    this.pushHistorySnapshot()
    const result = this.document.mergeTextBlocks(destinationId, sourceIds)
    if (!result.merged) {
      this.commands.setStatus('merge failed', 'error')
      this.commands.minimizePalette()
      return false
    }

    this.commitWorkbench(this.document.getStateSnapshot())
    this.requestRender()
    const count = result.sourceIds.length + 1
    this.commands.setStatus(count === 2 ? 'merged 2 blocks' : `merged ${count} blocks`, 'success')
    window.setTimeout(() => this.commands.closePalette(), 180)
    return true
  }

  private buildSelectionFeedbackLine(opts?: { empty?: string }): string {
    const blocks = this.document.getSelectedBlockIds().length
    const connections = this.document.getSelectedConnections().length
    const total = blocks + connections
    if (!total) return opts?.empty ?? 'nothing selected'
    const blockLabel = blocks === 0 ? '' : `${blocks} block${blocks === 1 ? '' : 's'}`
    const connLabel = connections === 0 ? '' : `${connections} connection${connections === 1 ? '' : 's'}`
    return [blockLabel, connLabel].filter(Boolean).join(' + ') || 'selected'
  }

  private getDeleteSelectionValues(): string[] {
    const blockIds = this.document.getSelectedBlockIds()
    const connectionIds = this.document.getSelectedConnections()
      .map(connection => `conn:${connection.sourceId}->${connection.targetId}`)
    return [...blockIds, ...connectionIds]
  }

  private handleDeleteDescriptorPick(value: string, ctx: EngineContext): StepSelectResult {
    if (!this.deleteModeActive) {
      return { ok: false, message: 'delete mode not active' }
    }
    if (value.startsWith('conn:')) {
      const payload = value.slice('conn:'.length)
      const parts = payload.split('->')
      const sourceId = parts[0] ?? ''
      const targetId = parts[1] ?? ''
      if (!sourceId || !targetId) {
        return { ok: false, message: 'tap a connection', tone: 'error' }
      }
      this.document.toggleConnectionSelection(sourceId, targetId)
    } else {
      const changed = this.addBlockToSelection(value)
      if (!changed) {
        this.document.toggleBlockSelection(value)
      }
    }

    const selectionValues = this.getDeleteSelectionValues()
    const feedback = this.buildSelectionFeedbackLine({ empty: 'select something to delete' })
    const tone = selectionValues.length > 0 ? 'success' : 'error'
    ctx.setFeedback(feedback, tone)
    this.commands.setStatus(feedback, tone)
    this.commands.minimizePalette()
    this.requestRender()
    return { ok: true, value: selectionValues }
  }

  private handleCopyDescriptorPick(value: string, ctx: EngineContext): StepSelectResult {
    if (!this.copyModeActive) {
      return { ok: false, message: 'copy mode not active' }
    }
    if (value.startsWith('conn:')) {
      return { ok: false, message: 'tap a block', tone: 'error' }
    }

    const changed = this.addBlockToSelection(value)
    if (!changed) {
      this.document.toggleBlockSelection(value)
    }

    const count = this.document.getSelectedBlockIds().length
    const feedback = count === 0
      ? 'select a block first'
      : (count === 1 ? '1 block' : `${count} blocks`)
    const tone = count > 0 ? 'success' : 'error'
    ctx.setFeedback(feedback, tone)
    this.commands.setStatus(feedback, tone)
    this.commands.minimizePalette()
    this.requestRender()
    return { ok: true, value: this.document.getSelectedBlockIds() }
  }

  private handlePasteDescriptorPoint(point: { x: number, y: number }, ctx: EngineContext): StepSelectResult {
    if (!this.pasteModeActive) {
      return { ok: false, message: 'paste mode not active' }
    }
    const pasted = this.pasteAtPoint(point.x, point.y)
    if (!pasted) {
      return { ok: false, message: 'paste failed', tone: 'error' }
    }

    const current = ctx.getValue('pick-place')
    const placements = Array.isArray(current) ? [...current] : []
    placements.push(`placed-${placements.length + 1}`)
    ctx.setFeedback(undefined)
    this.requestRender()
    return { ok: true, value: placements }
  }

  private handleSelectConnectedDescriptorPick(value: string, ctx: EngineContext): StepSelectResult {
    if (!this.selectConnectedModeActive) {
      return { ok: false, message: 'select-connected mode not active' }
    }
    if (value.startsWith('conn:')) {
      return { ok: false, message: 'tap a block', tone: 'error' }
    }

    const sourceId = value
    const previous = new Set(this.document.getSelectedBlockIds())
    const downstream = collectConnectedBlockIds(this.getActiveBlocks(), sourceId, 'both')
    const union = new Set<string>([...previous, sourceId, ...downstream])
    const ids = Array.from(union)

    if (!ids.length) {
      return { ok: false, message: 'tap a block', tone: 'error' }
    }

    this.document.clearSelection()
    this.document.selectBlock(ids[0])
    for (let i = 1; i < ids.length; i++) {
      this.document.toggleBlockSelection(ids[i])
    }

    const label = downstream.length === 1 ? 'selected 1 connected block' : `selected ${downstream.length} connected blocks`
    ctx.setFeedback(label, 'success')
    this.commands.setStatus(label, 'success')
    this.commands.minimizePalette()
    this.requestRender()
    return { ok: true, value: ids }
  }


  private async handleCopyCommand(): Promise<void> {
    if (this.document.getSelectedBlockIds().length) {
      await this.performCopy()
      return
    }
    this.enterCopyMode()
  }

  private async handlePasteCommand(): Promise<void> {
    const payload = await this.readClipboardPayload()
    if (payload && payload.length) {
      this.clipboardSelection = payload
      this.pasteBlocks = this.sanitizeClipboardBlocks(payload)
      this.pastePrimaryId = this.pasteBlocks[0]?.ided[0]
    } else {
      const fallback = this.clipboardSelection
      if (fallback) {
        this.pasteBlocks = this.sanitizeClipboardBlocks(fallback)
        this.pastePrimaryId = this.pasteBlocks[0]?.ided[0]
      } else {
        this.commands.setStatus('clipboard empty', 'error')
        return
      }
    }
    if (!this.pasteBlocks || !this.pasteBlocks.length) {
      this.commands.setStatus('clipboard not usable', 'error')
      return
    }
    this.enterPasteMode()
  }

  private sanitizeClipboardBlocks(rawBlocks: ClipboardBlock[]): ClipboardBlock[] {
    return rawBlocks
      .map(block => {
        const validCoords = Array.isArray(block.xyzw) && block.xyzw.length === 4 && block.xyzw.every(n => typeof n === 'number')
        const validIded = Array.isArray(block.ided) && block.ided.length === 2 && block.ided.every(v => typeof v === 'string')
        const validLinks = Array.isArray(block.link) ? block.link.filter(id => typeof id === 'string') : []
        if (!validCoords || !validIded) return null
        return {
          text: typeof block.text === 'string' ? block.text : '',
          xyzw: [...block.xyzw] as [number, number, number, number],
          link: validLinks,
          ided: [...block.ided] as [string, string]
        }
      })
      .filter((b): b is ClipboardBlock => Boolean(b))
  }

  private async writeClipboard(text: string): Promise<boolean> {
    if ((navigator as any).clipboard && typeof (navigator as any).clipboard.writeText === 'function') {
      try {
        await (navigator as any).clipboard.writeText(text)
        return true
      } catch (error) {
        console.warn('Clipboard write blocked, falling back', error)
      }
    }
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', 'true')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      const succeeded = document.execCommand('copy')
      document.body.removeChild(textarea)
      return succeeded
    } catch (error) {
      console.error('Clipboard fallback failed', error)
      return false
    }
  }

  private parseClipboardPayload(text: string): ClipboardPayload | undefined {
    if (!text) return undefined
    try {
      const parsed = JSON.parse(text)
      if (Array.isArray(parsed)) {
        const blocks = this.sanitizeClipboardBlocks((parsed as any[]).map((item: any, index: number) => {
          return {
            text: typeof item.text === 'string' ? item.text : '',
            xyzw: Array.isArray(item.xyzw) ? item.xyzw : [],
            link: Array.isArray(item.link) ? item.link : [],
            ided: Array.isArray(item.ided) && item.ided.length === 2 ? item.ided : [`import-${index}`, `import-${index}`]
          } as ClipboardBlock
        }))
        if (blocks.length) {
          return blocks
        }
      }
    } catch (error) {
      console.warn('Clipboard parse failed', error)
    }
    return undefined
  }

  private async readClipboardPayload(): Promise<ClipboardPayload | undefined> {
    const clipboard = (navigator as any).clipboard
    if (clipboard && typeof clipboard.readText === 'function') {
      try {
        const text = await clipboard.readText()
        return this.parseClipboardPayload(text)
      } catch (error) {
        console.warn('Clipboard read blocked', error)
      }
    }
    return undefined
  }

  private async performCopy(): Promise<void> {
    const selectedIds = this.document.getSelectedBlockIds()
    if (!selectedIds.length) {
      this.commands.setStatus('select a block first', 'error')
      return
    }
    const selectedSet = new Set(selectedIds)
    const blocks = selectedIds
      .map(id => this.document.getBlock(id))
      .filter((b): b is Block => Boolean(b))
      .map(block => ({
        text: block.text,
        xyzw: [...block.xyzw] as [number, number, number, number],
        ided: [...block.ided] as [string, string],
        link: block.link.filter(id => selectedSet.has(id))
      }))
    if (!blocks.length) {
      this.commands.setStatus('select a block first', 'error')
      return
    }
    this.clipboardSelection = blocks
    const serialized = this.hugClosingBrackets(
      this.inlineArrays(JSON.stringify(blocks, null, 2), ['xyzw']),
      ['ided', 'link']
    )
    const wrote = await this.writeClipboard(serialized)
    const label = blocks.length === 1 ? 'copied 1 block' : `copied ${blocks.length} blocks`
    if (wrote) {
      this.commands.setStatus(label, 'success')
    } else {
      this.commands.setStatus('copied locally (clipboard blocked)', 'error')
    }
    this.commands.minimizePalette()
  }

  private enterCopyMode(): void {
    if (!this.copyDescriptor) return
    if (this.copyModeActive) return
    this.copyModeActive = true
    this.commands.runDescriptor(this.copyDescriptor)
  }

  private exitCopyMode(opts?: { preserveSelection?: boolean }): void {
    if (!this.copyModeActive && !this.commands.isEngineActive()) return
    this.copyModeActive = false
    if (this.commands.isEngineActive()) {
      this.commands.exitEngine()
    }
    if (!opts?.preserveSelection) {
      const selected = this.document.getSelectedBlockIds()
      if (selected.length) {
        this.pruneEmptyBlocks(selected)
        this.document.clearSelection()
      }
    }
    this.suppressNextClick = false
  }

  private enterPasteMode(): void {
    if (!this.pasteDescriptor) return
    if (this.pasteModeActive) return
    if (!this.pasteBlocks || !this.pasteBlocks.length) return
    this.suppressNextClick = false
    this.pasteModeActive = true
    this.commands.runDescriptor(this.pasteDescriptor)
  }

  private exitPasteMode(): void {
    if (!this.pasteModeActive && !this.commands.isEngineActive()) return
    this.pasteModeActive = false
    if (this.commands.isEngineActive()) {
      this.commands.exitEngine()
    }
    this.suppressNextClick = false
  }

  private pasteAtPoint(worldX: number, worldY: number): boolean {
    if (!this.pasteBlocks || !this.pasteBlocks.length) return false
    const primary = this.pasteBlocks.find(b => b.ided[0] === this.pastePrimaryId) ?? this.pasteBlocks[0]
    if (!primary) return false
    const anchorUnitsX = snapXToColumnBiased(worldX / GRID_SIZE, this.getColumnWidthUnits())
    const anchorUnitsY = snapYToEven(worldY / GRID_SIZE)
    const primaryX = primary.xyzw[0]
    const primaryY = primary.xyzw[1]
    this.pushHistorySnapshot()
    const idMap = new Map<string, string>()
    const created: string[] = []
    this.pasteBlocks.forEach(block => {
      const dx = block.xyzw[0] - primaryX
      const dy = block.xyzw[1] - primaryY
      const widthUnits = snapToPaperWidth(block.xyzw[3] * GRID_SIZE, this.getColumnWidthUnits()) // returns units
      const newX = snapXToColumn(anchorUnitsX + dx, this.getColumnWidthUnits())
      const newY = snapYToEven(anchorUnitsY + dy)
      const newId = this.document.createBlock(block.text, newX, newY, widthUnits)
      idMap.set(block.ided[0], newId)
      created.push(newId)
    })
    this.pasteBlocks.forEach(block => {
      const src = idMap.get(block.ided[0])
      if (!src) return
      block.link.forEach(targetId => {
        const mapped = idMap.get(targetId)
        if (mapped) {
          this.document.addLink(src, mapped)
        }
      })
    })
    if (created.length) {
      this.document.selectBlock(created[0])
      for (let i = 1; i < created.length; i++) {
        this.document.toggleBlockSelection(created[i])
      }
    }
    this.commitWorkbench(this.document.getStateSnapshot())
    const label = created.length === 1 ? 'pasted block' : `pasted ${created.length} blocks`
    this.commands.setStatus(label, 'success')
    return true
  }

  // Inline selected arrays (e.g., xyzw, ided) while keeping other fields pretty-printed.
  private inlineArrays(json: string, keys: string[]): string {
    if (!keys.length) return json
    const pattern = new RegExp(`"(${keys.join('|')})"\\s*:\\s*\\[(.*?)\\]`, 'gs')
    return json.replace(pattern, (match, key, contents) => {
      try {
        const parsed = JSON.parse(`[${contents}]`)
        if (!Array.isArray(parsed)) return match
        const formatted = parsed.map((value: unknown) => {
          if (typeof value === 'string') return JSON.stringify(value)
          if (typeof value === 'number') return String(value)
          if (value === null) return 'null'
          if (typeof value === 'boolean') return value ? 'true' : 'false'
          return JSON.stringify(value)
        })
        return `"${key}": [${formatted.join(', ')}]`
      } catch {
        return match
      }
    })
  }

  // Keep arrays multi-line but move the closing bracket up to the last entry line.
  private hugClosingBrackets(json: string, keys: string[]): string {
    if (!keys.length) return json
    const pattern = new RegExp(`("(?:${keys.join('|')})"\\s*:\\s*\\[\\n)([\\s\\S]*?)(\\n\\s*\\])(,?)([ \\t]*\\n?)`, 'g')
    return json.replace(pattern, (_match, start, body, _closing, comma, trailingNewline) => {
      const trimmedBody = body.replace(/\s+$/g, '')
      const newline = trailingNewline && trailingNewline.length ? '\n' : ''
      return `${start}${trimmedBody}]${comma}${newline}`
    })
  }

  private handleSelectConnectedCommand(): void {
    const selectedIds = this.document.getSelectedBlockIds()
    const sourceId = selectedIds[0]
    if (!sourceId) {
      if (this.pasteModeActive) this.exitPasteMode()
      if (this.copyModeActive) this.exitCopyMode({ preserveSelection: true })
      if (this.multiSelectMode) this.exitMultiSelectMode({ preserveNextClick: true })
      this.enterSelectConnectedMode()
      return
    }
    const downstream = collectConnectedBlockIds(this.getActiveBlocks(), sourceId, 'both')
    // Always keep the source as primary selection
    this.document.selectBlock(sourceId)
    if (!downstream.length) {
      this.commands.setStatus('no downstream connections', 'error')
      this.commands.minimizePalette()
      this.requestRender()
      return
    }
    downstream.forEach(id => {
      if (id !== sourceId) {
        this.document.toggleBlockSelection(id)
      }
    })
    this.requestRender()
    const label = downstream.length === 1 ? 'selected 1 connected block' : `selected ${downstream.length} connected blocks`
    this.commands.setStatus(label, 'success')
    this.commands.minimizePalette()
  }

  private selectConnectedCascade(sourceId: string, direction: BlockGraphDirection): { ok: boolean, message: string } {
    const connectedIds = collectConnectedBlockIds(this.getActiveBlocks(), sourceId, direction)
    const selectedIds = [sourceId, ...connectedIds]
    const selectedSet = new Set(selectedIds)
    const selectedConnections = this.getActiveBlocks().flatMap(block => {
      if (!selectedSet.has(block.ided[0])) return []
      return block.link
        .filter(targetId => selectedSet.has(targetId))
        .map(targetId => ({ sourceId: block.ided[0], targetId }))
    })
    this.document.setSelection(selectedIds, selectedConnections)
    this.requestRender()
    if (!connectedIds.length) {
      return { ok: false, message: 'no connected blocks' }
    }
    const count = connectedIds.length
    return {
      ok: true,
      message: count === 1 ? 'selected 1 connected block' : `selected ${count} connected blocks`
    }
  }

  private triggerPinCascadeSelection(sourceId: string, direction: 'incoming' | 'outgoing'): void {
    if (this.pasteModeActive) this.exitPasteMode()
    if (this.copyModeActive) this.exitCopyMode({ preserveSelection: true })
    if (this.multiSelectMode) this.exitMultiSelectMode({ preserveNextClick: true })
    this.document.clearConnectionSelection()
    const result = this.selectConnectedCascade(sourceId, direction)
    this.commands.setStatus(result.message, result.ok ? 'success' : 'error')
    this.commands.minimizePalette()
  }

  private handleConnectCommand(): void {
    if (!this.connectDescriptor) return
    const selectedIds = this.document.getSelectedBlockIds()
    const sourceId = selectedIds[0]
    if (this.pasteModeActive) this.exitPasteMode()
    if (this.copyModeActive) this.exitCopyMode({ preserveSelection: true })
    if (this.multiSelectMode) this.exitMultiSelectMode({ preserveNextClick: true })
    this.suppressNextClick = false
    if (sourceId) {
      this.document.selectBlock(sourceId)
      this.document.clearConnectionSelection()
      this.requestRender()
      this.commands.runDescriptor(this.connectDescriptor, {
        initialValues: { 'pick-source': sourceId },
        stepIndex: 1
      })
      return
    }
    this.commands.runDescriptor(this.connectDescriptor)
  }

  private handleDisconnectCommand(): void {
    if (!this.disconnectDescriptor) return
    if (this.pasteModeActive) this.exitPasteMode()
    if (this.copyModeActive) this.exitCopyMode({ preserveSelection: true })
    if (this.multiSelectMode) this.exitMultiSelectMode({ preserveNextClick: true })
    this.suppressNextClick = false
    this.commands.runDescriptor(this.disconnectDescriptor)
  }

  private handleDisconnectDescriptorPick(value: string, ctx: EngineContext): StepSelectResult {
    const block = this.document.getBlock(value)
    if (!block) {
      return { ok: false, message: 'tap a block' }
    }

    this.document.selectBlock(value)
    this.document.clearConnectionSelection()

    const connectionCount = this.document.countBlockConnections(value)
    if (connectionCount <= 0) {
      this.requestRender()
      return { ok: false, message: 'no connections to remove' }
    }

    this.pushHistorySnapshot()
    const removedCount = this.document.disconnectBlock(value)
    this.commitWorkbench(this.document.getStateSnapshot())
    this.requestRender()

    const label = removedCount === 1 ? 'removed 1 connection' : `removed ${removedCount} connections`
    ctx.setFeedback(label, 'success')
    this.commands.minimizePalette()
    return { ok: true }
  }

  private enterSelectConnectedMode(): void {
    if (!this.selectConnectedDescriptor) return
    this.selectConnectedModeActive = true
    this.suppressNextClick = false
    this.commands.runDescriptor(this.selectConnectedDescriptor)
  }

  private exitSelectConnectedMode(opts?: { preserveSelection?: boolean }): void {
    const wasActive = this.selectConnectedModeActive || this.commands.isEngineActive()
    if (!wasActive) return
    this.selectConnectedModeActive = false
    if (this.commands.isEngineActive()) {
      this.commands.exitEngine()
    }
    if (!opts?.preserveSelection) {
      this.commands.clearStatus()
    }
    this.suppressNextClick = false
  }

  private startSelectAreaCommand(descriptor: CommandDescriptor): void {
    if (this.isViewMode()) return
    this.input.stopEditing()
    this.areaSelectModeActive = true
    this.clearAreaSelectDragState()
    this.commands.runDescriptor(descriptor)
  }

  private exitAreaSelectMode(): void {
    this.areaSelectModeActive = false
    this.clearAreaSelectDragState()
    this.requestRender()
  }

  private canStartAreaSelectGesture(): boolean {
    if (this.isViewMode()) return false
    if (this.copyModeActive || this.pasteModeActive || this.deleteModeActive) return false
    if (this.selectConnectedModeActive) return false
    if (this.commands.isEngineActive() && !this.isDescriptorMultiSelectGestureMode()) return false
    return true
  }

  private isDescriptorMultiSelectGestureMode(): boolean {
    return (this.multiSelectMode || this.deoverlapTargetModeActive)
      && this.commands.isEngineCanvasMode()
      && !this.commands.isEnginePointCanvasMode()
  }

  private beginAreaSelectDrag(pointerId: number, worldX: number, worldY: number, opts?: { anchorId?: string }): void {
    this.areaSelectDragActive = true
    this.areaSelectPointerId = pointerId
    this.areaSelectOriginWorld = { x: worldX, y: worldY }
    this.areaSelectCurrentWorld = { x: worldX, y: worldY }
    this.areaSelectRect = { x: worldX, y: worldY, width: 0, height: 0 }
    this.areaSelectAnchorId = opts?.anchorId
    this.areaSelectTouchedIds = new Set(this.document.getSelectedBlockIds())
    this.document.clearConnectionSelection()
    this.updateAreaSelectHits()
    this.requestRender()
  }

  private updateAreaSelectDrag(worldX: number, worldY: number): void {
    if (!this.areaSelectDragActive || !this.areaSelectOriginWorld) return
    this.areaSelectCurrentWorld = { x: worldX, y: worldY }
    this.areaSelectRect = this.buildAreaSelectRect()
    this.updateAreaSelectHits()
    this.requestRender()
  }

  private endAreaSelectDrag(): void {
    if (!this.areaSelectDragActive) return
    this.clearAreaSelectDragState()
    this.requestRender()
  }

  private clearAreaSelectDragState(): void {
    this.areaSelectDragActive = false
    this.areaSelectPointerId = undefined
    this.areaSelectOriginWorld = undefined
    this.areaSelectCurrentWorld = undefined
    this.areaSelectRect = undefined
    this.areaSelectAnchorId = undefined
    this.areaSelectTouchedIds.clear()
    this.areaSelectShiftPending = false
    this.areaSelectShiftStart = undefined
    this.cancelPinHold()
    this.cancelAreaSelectLongPress()
    this.clearAreaSelectLongPressSelectionState()
  }

  private startPinHold(pointerId: number, blockId: string, kind: 'start' | 'end', sx: number, sy: number): void {
    this.cancelPinHold()
    this.pinHoldPointerId = pointerId
    this.pinHoldBlockId = blockId
    this.pinHoldKind = kind
    this.pinHoldStart = { sx, sy }
    this.pinHoldTimer = window.setTimeout(() => {
      this.activatePinHold()
    }, AREA_SELECT_LONG_PRESS_MS)
  }

  private handlePinHoldMove(pointerId: number, sx: number, sy: number): boolean {
    if (this.pinHoldPointerId !== pointerId || !this.pinHoldStart) return false
    const dx = sx - this.pinHoldStart.sx
    const dy = sy - this.pinHoldStart.sy
    if (Math.hypot(dx, dy) <= AREA_SELECT_LONG_PRESS_MOVE_TOLERANCE_PX) {
      return true
    }
    const blockId = this.pinHoldBlockId
    const kind = this.pinHoldKind
    this.cancelPinHold()
    if (kind === 'end' && blockId) {
      const block = this.document.getBlock(blockId)
      if (block) {
        this.armEndPinDrag(block, pointerId, sx, sy)
        return true
      }
    }
    return false
  }

  private activatePinHold(): void {
    const blockId = this.pinHoldBlockId
    const kind = this.pinHoldKind
    const pointerId = this.pinHoldPointerId
    if (!blockId || !kind) {
      this.cancelPinHold()
      return
    }
    this.resetDragState()
    this.resizeArmed = false
    this.isResizing = false
    this.resizeBlockId = undefined
    this.resizeOrig = undefined
    this.resizeCurrentWorld = undefined
    this.resizePointerId = undefined
    this.resizeHoverTargetId = undefined
    if (pointerId !== undefined && this.touchPoints.has(pointerId)) {
      this.touchTapMoved = true
      this.touchTapCandidateId = undefined
      this.touchTapStartScreen = undefined
    }
    this.suppressNextClick = true
    this.triggerPinCascadeSelection(blockId, kind === 'start' ? 'incoming' : 'outgoing')
    this.cancelPinHold()
  }

  private cancelPinHold(): void {
    if (this.pinHoldTimer) {
      clearTimeout(this.pinHoldTimer)
    }
    this.pinHoldTimer = undefined
    this.pinHoldPointerId = undefined
    this.pinHoldBlockId = undefined
    this.pinHoldStart = undefined
    this.pinHoldKind = undefined
  }

  private buildAreaSelectRect(): { x: number, y: number, width: number, height: number } | undefined {
    if (!this.areaSelectOriginWorld || !this.areaSelectCurrentWorld) return undefined
    const minX = Math.min(this.areaSelectOriginWorld.x, this.areaSelectCurrentWorld.x)
    const minY = Math.min(this.areaSelectOriginWorld.y, this.areaSelectCurrentWorld.y)
    const maxX = Math.max(this.areaSelectOriginWorld.x, this.areaSelectCurrentWorld.x)
    const maxY = Math.max(this.areaSelectOriginWorld.y, this.areaSelectCurrentWorld.y)
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
  }

  private updateAreaSelectHits(): void {
    if (!this.areaSelectRect) return
    const snapshot = createSpaceGeometrySnapshot(this.getActiveBlocks(), this.createTextLayoutOracle())
    snapshot.blocksInRect(this.areaSelectRect).forEach(geometry => {
      const id = geometry.id
      if (this.areaSelectTouchedIds.has(id)) return
      this.areaSelectTouchedIds.add(id)
      if ((this.areaSelectModeActive || this.isDescriptorMultiSelectGestureMode()) && this.commands.isEngineCanvasMode()) {
        this.commands.handleEngineCanvasSelect(id)
      } else {
        this.addBlockToSelection(id)
      }
    })
  }

  private addBlockToSelection(blockId: string): boolean {
    const selectedIds = this.document.getSelectedBlockIds()
    if (selectedIds.includes(blockId)) return false
    if (!selectedIds.length) {
      this.document.selectBlock(blockId)
    } else {
      this.document.toggleBlockSelection(blockId)
    }
    return true
  }

  private startAreaSelectLongPress(pointerId: number, sx: number, sy: number, blockId: string): void {
    this.cancelAreaSelectLongPress()
    this.areaSelectLongPressPointerId = pointerId
    this.areaSelectLongPressStart = { sx, sy }
    this.areaSelectLongPressBlockId = blockId
    this.areaSelectLongPressTimer = window.setTimeout(() => {
      this.activateAreaSelectLongPress()
    }, AREA_SELECT_LONG_PRESS_MS)
  }

  private activateAreaSelectLongPress(): void {
    const pointerId = this.areaSelectLongPressPointerId
    if (pointerId === undefined) return
    const point = this.touchPoints.get(pointerId)
    if (!point) {
      this.cancelAreaSelectLongPress()
      return
    }
    const { x, y } = this.screenToWorld(point.x, point.y)
    this.resetDragState()
    this.resizeArmed = false
    this.isResizing = false
    this.resizeBlockId = undefined
    this.resizeOrig = undefined
    this.resizeCurrentWorld = undefined
    this.resizePointerId = undefined
    this.resizeHoverTargetId = undefined
    this.areaSelectLongPressSelectPointerId = pointerId
    this.areaSelectLongPressSelectBlockId = this.areaSelectLongPressBlockId
    this.areaSelectLongPressSelectMoved = false
    this.areaSelectAnchorId = this.areaSelectLongPressBlockId
    this.beginAreaSelectDrag(pointerId, x, y, { anchorId: this.areaSelectLongPressBlockId })
    this.touchTapMoved = true
    this.touchTapCandidateId = undefined
    this.cancelAreaSelectLongPress()
  }

  private cancelAreaSelectLongPress(): void {
    if (this.areaSelectLongPressTimer) {
      clearTimeout(this.areaSelectLongPressTimer)
    }
    this.areaSelectLongPressTimer = undefined
    this.areaSelectLongPressPointerId = undefined
    this.areaSelectLongPressStart = undefined
    this.areaSelectLongPressBlockId = undefined
  }

  private clearAreaSelectLongPressSelectionState(): void {
    this.areaSelectLongPressSelectPointerId = undefined
    this.areaSelectLongPressSelectBlockId = undefined
    this.areaSelectLongPressSelectMoved = false
  }

  private startTouchZoomScrubLongPress(pointerId: number, sx: number, sy: number): void {
    this.cancelTouchZoomScrubLongPress()
    this.touchZoomScrubPointerId = pointerId
    this.touchZoomScrubStart = { sx, sy }
    this.touchZoomScrubTimer = window.setTimeout(() => {
      this.activateTouchZoomScrub()
    }, AREA_SELECT_LONG_PRESS_MS)
  }

  private activateTouchZoomScrub(): void {
    const pointerId = this.touchZoomScrubPointerId
    if (pointerId === undefined) return
    const point = this.touchPoints.get(pointerId)
    if (!point) {
      this.cancelTouchZoomScrubLongPress()
      return
    }
    const anchor = this.screenToWorld(point.x, point.y)
    this.resetDragState()
    this.resizeArmed = false
    this.isResizing = false
    this.resizeBlockId = undefined
    this.resizeOrig = undefined
    this.resizeCurrentWorld = undefined
    this.resizePointerId = undefined
    this.resizeHoverTargetId = undefined
    this.touchZoomScrubActive = true
    this.touchZoomScrubAnchor = {
      sx: point.x,
      sy: point.y,
      worldX: anchor.x,
      worldY: anchor.y,
      scale: this.camera.scale
    }
    this.touchZoomScrubIndicatorSide = point.x <= (window.innerWidth / 2) ? 'right' : 'left'
    this.updateTouchZoomScrubIndicator(point.x, point.y, true)
    this.touchTapMoved = true
    this.touchTapCandidateId = undefined
    this.touchTapStartScreen = undefined
    this.cancelTouchZoomScrubLongPress({ preserveActive: true })
  }

  private updateTouchZoomScrub(currentSy: number): void {
    const anchor = this.touchZoomScrubAnchor
    if (!anchor) return
    const deltaY = currentSy - anchor.sy
    const unclamped = anchor.scale + deltaY * TOUCH_ZOOM_SCRUB_LINEAR_STEP
    const nextScale = Math.min(Math.max(unclamped, this.scaleMin), this.scaleMax)
    this.updateTouchZoomScrubIndicator(anchor.sx, currentSy)
    if (nextScale === this.camera.scale) return
    this.camera.scale = nextScale
    this.camera.tx = anchor.sx - anchor.worldX * nextScale
    this.camera.ty = anchor.sy - anchor.worldY * nextScale
    this.applyCssGridTransform()
    this.requestRender()
  }

  private endTouchZoomScrub(): void {
    this.hideTouchZoomScrubIndicator()
    this.touchZoomScrubActive = false
    this.touchZoomScrubAnchor = undefined
    this.touchZoomScrubPointerId = undefined
    this.touchZoomScrubStart = undefined
    this.touchZoomScrubTimer = undefined
    this.touchTapCandidateId = undefined
    this.touchTapStartScreen = undefined
    this.touchTapMoved = false
    this.touchTapMultiTouch = false
    this.touchTapStartTime = 0
    this.singleTouchLast = undefined
  }

  private armEndPinDrag(block: Block, pointerId: number, sx: number, sy: number): void {
    const { x, y } = this.screenToWorld(sx, sy)
    this.resizeArmed = true
    this.resizePointerId = pointerId
    this.resizeBlockId = block.ided[0]
    this.resizeOrig = { xUnits: block.xyzw[0], yUnits: block.xyzw[1], wUnits: block.xyzw[3] }
    this.resizeCurrentWorld = { x, y }
    const target = this.findConnectionTargetAtScreenPosition(sx, sy, block.ided[0])
    this.resizeHoverTargetId = target ? target.ided[0] : undefined
    this.isResizing = !this.resizeHoverTargetId
  }

  private cancelTouchZoomScrubLongPress(opts?: { preserveActive?: boolean }): void {
    if (this.touchZoomScrubTimer) {
      clearTimeout(this.touchZoomScrubTimer)
    }
    this.touchZoomScrubTimer = undefined
    if (opts?.preserveActive) return
    if (!this.touchZoomScrubActive) {
      this.hideTouchZoomScrubIndicator()
    }
    this.touchZoomScrubPointerId = undefined
    this.touchZoomScrubStart = undefined
  }

  private ensureTouchZoomScrubIndicator(app: HTMLElement): void {
    if (this.touchZoomScrubIndicator) return
    const indicator = document.createElement('div')
    indicator.className = 'touch-zoom-scrub-indicator'
    const peel = document.createElement('div')
    peel.className = 'touch-zoom-scrub-indicator-peel'
    peel.textContent = 'zoom'
    const circle = document.createElement('div')
    circle.className = 'touch-zoom-scrub-indicator-circle'
    const label = document.createElement('span')
    label.className = 'touch-zoom-scrub-indicator-label'
    circle.appendChild(label)
    indicator.appendChild(peel)
    indicator.appendChild(circle)
    app.appendChild(indicator)
    this.touchZoomScrubIndicator = indicator
    this.touchZoomScrubIndicatorLabel = label
  }

  private updateTouchZoomScrubIndicator(sx: number, sy: number, activate = false): void {
    if (!this.touchZoomScrubIndicator) return
    this.touchZoomScrubIndicator.style.left = `${sx}px`
    this.touchZoomScrubIndicator.style.top = `${sy}px`
    this.touchZoomScrubIndicator.classList.toggle('is-peel-left', this.touchZoomScrubIndicatorSide === 'left')
    this.touchZoomScrubIndicator.classList.toggle('is-peel-right', this.touchZoomScrubIndicatorSide === 'right')
    if (this.touchZoomScrubIndicatorLabel) {
      this.touchZoomScrubIndicatorLabel.textContent = this.formatTouchZoomScrubScale(this.camera.scale)
    }
    this.touchZoomScrubIndicator.classList.add('is-visible')
    if (activate) {
      this.touchZoomScrubIndicator.classList.add('is-activating')
      window.setTimeout(() => {
        this.touchZoomScrubIndicator?.classList.remove('is-activating')
      }, 120)
    }
  }

  private hideTouchZoomScrubIndicator(): void {
    if (!this.touchZoomScrubIndicator) return
    this.touchZoomScrubIndicator.classList.remove('is-visible')
    this.touchZoomScrubIndicator.classList.remove('is-activating')
  }

  private formatTouchZoomScrubScale(scale: number): string {
    return `${Math.round(scale * 100)}x`
  }

  private runSelectAxisQuickCommand(descriptor: CommandDescriptor): void {
    const selectedIds = this.document.getSelectedBlockIds()
    this.commands.runDescriptor(descriptor)
    if (selectedIds.length === 1) {
      this.commands.handleEngineCanvasSelect(selectedIds[0])
    }
  }

  private selectAxisFromBase(
    baseId: string,
    kind: 'horizontal' | 'vertical',
    section: 'all' | 'left' | 'right' | 'above' | 'below',
    opts?: {
      preserveExisting?: boolean
      setFeedback?: (message: string, tone: 'success' | 'error') => void
    }
  ): string[] {
    const setFeedback = opts?.setFeedback
    const preserveExisting = opts?.preserveExisting === true
    const base = this.document.getBlock(baseId)
    if (!base) {
      if (setFeedback) {
        setFeedback('missing base block', 'error')
      } else {
        this.commands.setStatus('missing base block', 'error')
      }
      return []
    }
    const isRow = kind === 'horizontal'
    const matches: string[] = (!isRow && (section === 'above' || section === 'below'))
      ? createSpaceGeometrySnapshot(this.getActiveBlocks(), this.createTextLayoutOracle())
        .verticalAxisSelection(baseId, section)
      : (() => {
        const legacyMatches: string[] = []
        const baseXBucket = snapXToColumn(base.xyzw[0], this.getColumnWidthUnits())
        const baseYBucket = snapYToEven(base.xyzw[1])
        this.getActiveBlocks().forEach(block => {
          const id = block.ided[0]
          const xBucket = snapXToColumn(block.xyzw[0], this.getColumnWidthUnits())
          const yBucket = snapYToEven(block.xyzw[1])
          if (isRow) {
            if (yBucket !== baseYBucket) return
            if (section === 'left' && block.xyzw[0] >= base.xyzw[0]) return
            if (section === 'right' && block.xyzw[0] <= base.xyzw[0]) return
            legacyMatches.push(id)
          } else {
            if (xBucket !== baseXBucket) return
            if (section === 'above' && block.xyzw[1] >= base.xyzw[1]) return
            if (section === 'below' && block.xyzw[1] <= base.xyzw[1]) return
            legacyMatches.push(id)
          }
        })
        return legacyMatches
      })()
    if (!matches.includes(base.ided[0])) {
      matches.unshift(base.ided[0])
    }
    const nextSelection = preserveExisting
      ? Array.from(new Set([...this.document.getSelectedBlockIds(), ...matches]))
      : matches
    this.document.clearSelection()
    if (nextSelection.length) {
      this.document.selectBlock(nextSelection[0])
      for (let i = 1; i < nextSelection.length; i++) {
        this.document.toggleBlockSelection(nextSelection[i])
      }
    }
    if (nextSelection.length <= 1) {
      if (setFeedback) {
        setFeedback('nothing selected', 'error')
      } else {
        this.commands.setStatus('nothing selected', 'error')
      }
    } else {
      const count = nextSelection.length
      const label = count === 1 ? 'selected 1 block' : `selected ${count} blocks`
      if (setFeedback) {
        setFeedback(label, 'success')
      } else {
        this.commands.setStatus(label, 'success')
      }
    }
    this.requestRender()
    return nextSelection
  }

  private handleSelectAllCommand(): void {
    const blocks = this.getActiveBlocks()
    if (!blocks.length) {
      this.commands.setStatus('nothing to select', 'error')
      this.commands.closePalette()
      return
    }

    const blockIds = blocks.map(b => b.ided[0])
    const connections: ConnectionSelection[] = []
    blocks.forEach(block => {
      block.link.forEach(targetId => {
        connections.push({ sourceId: block.ided[0], targetId })
      })
    })

    this.document.setSelection(blockIds, connections)
    if (this.multiSelectMode) {
      this.updateMultiSelectMessage()
    }

    const blockCount = this.document.getSelectedBlockIds().length
    const connectionCount = this.document.getSelectedConnections().length
    const blockLabel = `${blockCount} block${blockCount === 1 ? '' : 's'}`
    const connLabel = `${connectionCount} connection${connectionCount === 1 ? '' : 's'}`
    const label = connectionCount > 0 ? `${blockLabel}, ${connLabel}` : blockLabel
    this.commands.setStatus(label, blockCount > 0 ? 'success' : 'error')
    this.requestRender()
    // Defer close one tick so the status flash lands before the palette collapses.
    window.setTimeout(() => this.commands.closePalette(), 0)
  }

  private handleSelectMultipleCommand(): void {
    if (this.multiSelectMode) {
      this.exitMultiSelectMode({ preserveNextClick: true })
    } else {
      this.enterMultiSelectMode()
    }
  }

  private enterMultiSelectMode(): void {
    if (this.multiSelectMode) return
    if (!this.selectDescriptor) return
    this.multiSelectMode = true
    this.silentMultiSelectMode = false
    const initialSelection = this.getDeleteSelectionValues()
    this.commands.runDescriptor(this.selectDescriptor, initialSelection.length > 0
      ? { initialValues: { 'pick-items': initialSelection } }
      : undefined)
    if (initialSelection.length > 0) {
      this.updateMultiSelectMessage()
    }
  }

  private enterSilentMultiSelectMode(anchorBlockId: string): void {
    this.multiSelectMode = true
    this.silentMultiSelectMode = true
    this.suppressNextClick = false
    if (!this.document.getSelectedBlockIds().includes(anchorBlockId)) {
      this.addBlockToSelection(anchorBlockId)
      this.requestRender()
    }
  }

  private exitMultiSelectMode(opts?: { preserveNextClick?: boolean }): void {
    const wasActive = this.multiSelectMode || this.commands.isModeActive('select') || this.commands.isEngineActive()
    if (!wasActive) return
    this.multiSelectMode = false
    this.silentMultiSelectMode = false
    if (this.commands.isEngineActive()) {
      this.commands.exitEngine()
    } else {
      this.commands.exitMode('select')
      this.commands.setModeTokenHandler(undefined)
    }
    if (opts?.preserveNextClick) {
      this.suppressNextClick = false
    }
  }

  private updateMultiSelectMessage(): void {
    if (!this.multiSelectMode) return
    if (this.silentMultiSelectMode) return
    this.commands.setStatus(this.multiSelectFeedback(), 'success')
    this.commands.minimizePalette()
  }

  private buildMultiSelectMessage(): string {
    return 'Select blocks or links'
  }

  private multiSelectFeedback(): string {
    const total = this.document.getSelectedBlockIds().length
    const connections = this.document.getSelectedConnections().length
    const blockLabel = total === 0 ? '0 blocks' : `${total} block${total === 1 ? '' : 's'}`
    const connectionLabel = connections === 0 ? '0 connections' : `${connections} connection${connections === 1 ? '' : 's'}`
    return `${blockLabel}, ${connectionLabel}`
  }

  private handleSelectDescriptorPick(value: string, ctx: EngineContext): StepSelectResult {
    if (!this.multiSelectMode) {
      return { ok: false, message: 'select mode not active' }
    }
    if (value.startsWith('conn:')) {
      const payload = value.slice('conn:'.length)
      const parts = payload.split('->')
      const sourceId = parts[0] ?? ''
      const targetId = parts[1] ?? ''
      if (!sourceId || !targetId) {
        return { ok: false, message: 'tap a connection', tone: 'error' }
      }
      this.document.toggleConnectionSelection(sourceId, targetId)
    } else {
      const changed = this.addBlockToSelection(value)
      if (!changed) {
        this.document.toggleBlockSelection(value)
      }
    }

    const feedback = this.multiSelectFeedback()
    ctx.setFeedback(feedback, 'success')
    this.commands.setStatus(feedback, 'success')
    this.commands.minimizePalette()
    this.requestRender()
    return { ok: true }
  }

  private buildExportFilename(): string {
    const meta = this.storage.getMetaBlock()
    const slug = meta ? parseLilodataText(meta.text).slug : undefined
    if (slug) {
      return `${slug}.json`
    }
    const now = new Date()
    const year = now.getFullYear()
    const monthDay = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
    const hourMinute = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`
    const second = String(now.getSeconds()).padStart(2, '0')
    const centisecond = String(Math.floor(now.getMilliseconds() / 10)).padStart(2, '0')
    const secCent = `${second}${centisecond}`
    return `${year} ${monthDay} ${hourMinute} ${secCent} lilo data.json`
  }

  private buildCloudShelfExportZipFilename(): string {
    return this.buildShelfExportZipFilename('sync')
  }

  private buildShelfExportZipFilename(kind: 'local' | 'sync' | 'rescue'): string {
    const now = new Date()
    const year = now.getFullYear()
    const monthDay = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
    const hourMinute = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`
    const second = String(now.getSeconds()).padStart(2, '0')
    const label = kind === 'rescue' ? 'memory rescue' : `${kind} shelf`
    return `${year} ${monthDay} ${hourMinute} ${second} lilo ${label}.zip`
  }

  private toSafeExportFilenameStem(value: string): string {
    const normalized = value
      .normalize('NFKD')
      .replace(/[^a-zA-Z0-9-_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
    if (normalized) return normalized.toLowerCase()
    return 'space'
  }

  private triggerDownload(filename: string, contents: string): void {
    const blob = new Blob([contents], { type: 'application/json' })
    this.triggerBlobDownload(filename, blob)
  }

  private triggerBlobDownload(filename: string, blob: Blob): void {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  private ensureImportInput(): HTMLInputElement {
    if (this.importInput) return this.importInput

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'application/json'
    input.style.display = 'none'
    input.addEventListener('change', async () => {
      if (!input.files || input.files.length === 0) {
        this.commands.clearStatus()
        return
      }

      try {
        await this.storage.waitForActiveFiling()
        const authority = this.storage.getVolumeAuthority()
        const file = input.files[0]
        const text = await file.text()
        if (
          this.pendingFileLoadMode === 'import'
          && this.storage.getActiveDeskState() === 'provisional'
        ) {
          const passiveLoadPolicy = resolveLayoutNormalizationPolicy('passive-load')
          const blocks = await this.storage.importFromJSON(text, {
            ownerId: authority?.owner_id ?? null,
            authority
          })
          if (!await this.storage.isVolumeAuthorityCurrent(authority)) {
            throw new Error('Import blocked: mounted volume changed')
          }
          const shouldPersistMeta = this.applyPaperMeta(this.storage.getMetaBlock(), blocks)
          this.history = new History()
          this.seedClockFromTimeline(blocks, this.storage.getMetaBlock())
          this.document.hydrate(blocks)
          this.adoptOpenedFileSpaceIdentity()
          const didAlign = passiveLoadPolicy.alignColumns
            ? this.alignBlocksToCurrentColumns()
            : false
          this.pendingCreateHistory = undefined
          this.requestRender()
          if (shouldPersistMeta || didAlign) {
            this.commitWorkbench(this.document.getStateSnapshot(), authority)
          }
          this.markSyncDirty()
          this.commands.closePalette()
        } else if (this.pendingFileLoadMode === 'import') {
          if (!await this.storage.isVolumeAuthorityCurrent(authority)) {
            throw new Error('Import blocked: mounted volume changed')
          }
          const passiveLoadPolicy = resolveLayoutNormalizationPolicy('passive-load')
          const importedAll = parseBlocksFromJson(text)
          const { blocks: importedBlocks } = extractLilodataBlock(importedAll)
          const localBlocks = this.document.getStateSnapshot()
          const merged = mergeBlocks(localBlocks, importedBlocks)
          const localMeta = this.storage.getMetaBlock()
          this.pushHistorySnapshot()
          await this.applyMergedBlocks(merged.blocks, localMeta, { authority })
          const didAlign = passiveLoadPolicy.alignColumns ? this.alignBlocksToCurrentColumns() : false
          this.pendingCreateHistory = undefined
          this.requestRender()
          if (didAlign) {
            this.commitWorkbench(this.document.getStateSnapshot(), authority)
          }
          this.markSyncDirty()
          this.commands.closePalette()
        } else {
          const passiveLoadPolicy = resolveLayoutNormalizationPolicy('passive-load')
          const blocks = await this.storage.importFromJSON(text, {
            ownerId: authority?.owner_id ?? null,
            authority
          })
          if (!await this.storage.isVolumeAuthorityCurrent(authority)) {
            throw new Error('Open blocked: mounted volume changed')
          }
          const shouldPersistMeta = this.applyPaperMeta(this.storage.getMetaBlock(), blocks)
          this.pushHistorySnapshot()
          this.seedClockFromTimeline(blocks, this.storage.getMetaBlock())
          this.document.hydrate(blocks)
          this.adoptOpenedFileSpaceIdentity()
          const didAlign = passiveLoadPolicy.alignColumns ? this.alignBlocksToCurrentColumns() : false
          this.pendingCreateHistory = undefined
          this.requestRender()
          if (shouldPersistMeta) {
            this.commitWorkbench(this.document.getStateSnapshot(), authority)
          }
          if (didAlign && !shouldPersistMeta) {
            this.commitWorkbench(this.document.getStateSnapshot(), authority)
          }
          this.markSyncDirty()
          if (this.isSyncPaired && this.canSyncWriteNow()) {
            void this.syncEngine.syncNow()
          }
        }
      } catch (error) {
        if (this.pendingFileLoadMode === 'import') {
          console.error('Import failed:', error)
          this.commands.setStatus('import failed', 'error')
        } else {
          console.error('Open failed:', error)
          this.commands.setStatus('open failed', 'error')
        }
      } finally {
        input.value = ''
        this.pendingFileLoadMode = 'open'
      }
    })

    document.body.appendChild(input)
    this.importInput = input
    return input
  }

  // ===== Pan+Zoom: Camera model & limits (Step 1) =====
  private viewportWidth(): number {
    return this.canvasElement.getBoundingClientRect().width
  }

  private computeFitScales(): void {
    const vw = this.viewportWidth()
    const marginPx = VIEW_FIT_MARGIN_UNITS * GRID_SIZE
    const paper = this.getPaperSpec()
    const quarterPx = paper.quarter.w * GRID_SIZE
    const halfPx = paper.quarter.w * 2 * GRID_SIZE
    const nominalPx = paper.nominal.w * GRID_SIZE

    // Fit scales with horizontal margin
    this.fitScales.quarter = Math.max(0.1, (vw - 2 * marginPx) / quarterPx)
    this.fitScales.half = Math.max(0.1, (vw - 2 * marginPx) / halfPx)
    this.fitScales.nominal = Math.max(0.1, (vw - 2 * marginPx) / nominalPx)

    // Bounds: hard-coded min zoom for simplicity
    this.fitScales.fourNominal = MIN_ZOOM
    this.scaleMin = MIN_ZOOM
    this.scaleMax = this.isTouchDevice ? Math.min(TOUCH_MAX_ZOOM, DESKTOP_MAX_ZOOM) : DESKTOP_MAX_ZOOM
  }

  private initializeCameraDefaults(): void {
    if (this.isTouchDevice) {
      // Start touch at comfortable fit without hitting max headroom
      this.camera.scale = Math.min(1, this.fitScales.quarter)
    } else {
      this.camera.scale = 1
    }
    this.clampCamera()
  }

  private clampCamera(): void {
    this.camera.scale = Math.min(Math.max(this.camera.scale, this.scaleMin), this.scaleMax)
    // tx/ty are left unconstrained for now; future: clamp to content bounds
  }

  private applyCssGridTransform(): void {
    const appEl = document.getElementById('app')
    if (!appEl) return
    appEl.style.setProperty('--scale', String(this.camera.scale))
    appEl.style.setProperty('--tx', `${this.camera.tx}px`)
    appEl.style.setProperty('--ty', `${this.camera.ty}px`)
    // Hide base dots when zoomed way out to reduce visual noise
    if (this.camera.scale < 0.25) {
      appEl.style.setProperty('--grid-color-effective', 'transparent')
    } else {
      appEl.style.removeProperty('--grid-color-effective')
    }
  }

  private screenToWorld(sx: number, sy: number): { x: number, y: number } {
    const x = (sx - this.camera.tx) / this.camera.scale
    const y = (sy - this.camera.ty) / this.camera.scale
    return { x, y }
  }

  private zoomAtScreenPoint(sx: number, sy: number, deltaY: number): void {
    const prevScale = this.camera.scale
    const step = deltaY * DESKTOP_WHEEL_ZOOM_LINEAR_STEP
    if (!Number.isFinite(step) || step === 0) return
    const unclamped = prevScale - step
    const nextScale = Math.min(Math.max(unclamped, this.scaleMin), this.scaleMax)
    if (nextScale === prevScale) return

    // Anchor zoom at screen point by adjusting translation so the world point stays under the cursor
    const wx = (sx - this.camera.tx) / prevScale
    const wy = (sy - this.camera.ty) / prevScale
    this.camera.scale = nextScale
    this.camera.tx = sx - wx * nextScale
    this.camera.ty = sy - wy * nextScale
    this.applyCssGridTransform()
    this.requestRender()
  }

  // Convert world -> screen coordinates using camera
  private worldToScreen(wx: number, wy: number): { sx: number, sy: number } {
    const sx = wx * this.camera.scale + this.camera.tx
    const sy = wy * this.camera.scale + this.camera.ty
    return { sx, sy }
  }

  private panByWorld(dxWorldPx: number, dyWorldPx: number): void {
    this.camera.tx += dxWorldPx * this.camera.scale
    this.camera.ty += dyWorldPx * this.camera.scale
    this.requestRender()
  }

  private canvasPointToClient(sx: number, sy: number): { clientX: number, clientY: number } {
    const rect = this.canvasElement.getBoundingClientRect()
    return {
      clientX: rect.left + sx,
      clientY: rect.top + sy
    }
  }

  private hasOutgoingConnections(id: string): boolean {
    return this.outgoingConnectionIds.has(id)
  }

  private hasIncomingConnections(id: string): boolean {
    return this.incomingConnectionIds.has(id)
  }

  private getStartHandleWorld(geometry: BlockGeometry): { x: number, y: number } {
    const offset = this.hasIncomingConnections(geometry.id) ? GRID_SIZE : 0
    return {
      x: geometry.handles.start.x + offset,
      y: geometry.handles.start.y,
    }
  }

  private getEndHandleWorld(geometry: BlockGeometry): { x: number, y: number } {
    const offset = this.hasOutgoingConnections(geometry.id) ? GRID_SIZE : 0
    return {
      x: geometry.handles.end.x - offset,
      y: geometry.handles.end.y,
    }
  }

  private isWithinHandleHitRadius(
    sx: number,
    sy: number,
    handleScreenPosition: { sx: number, sy: number }
  ): boolean {
    return Math.hypot(sx - handleScreenPosition.sx, sy - handleScreenPosition.sy) <= HANDLE_HIT_RADIUS_PX
  }

  private findStartHandleAtScreenPosition(sx: number, sy: number, excludeId?: string): Block | undefined {
    const snapshot = createSpaceGeometrySnapshot(this.getActiveBlocks(), this.createTextLayoutOracle())
    for (let i = snapshot.blocks.length - 1; i >= 0; i--) {
      const geometry = snapshot.blocks[i]
      if (!geometry || geometry.id === excludeId) continue
      const start = this.getStartHandleWorld(geometry)
      const handleScreenPosition = this.worldToScreen(start.x, start.y)
      if (this.isWithinHandleHitRadius(sx, sy, handleScreenPosition)) {
        return geometry.block
      }
    }
    return undefined
  }

  private findConnectionTargetAtScreenPosition(sx: number, sy: number, excludeId?: string): Block | undefined {
    const startHandleTarget = this.findStartHandleAtScreenPosition(sx, sy, excludeId)
    if (startHandleTarget) return startHandleTarget
    const { x, y } = this.screenToWorld(sx, sy)
    const blockTarget = this.findBlockAtPosition(x, y)
    if (!blockTarget || blockTarget.ided[0] === excludeId) return undefined
    return blockTarget
  }

  private findStartPinAtScreenPosition(sx: number, sy: number, excludeId?: string): Block | undefined {
    const snapshot = createSpaceGeometrySnapshot(this.getActiveBlocks(), this.createTextLayoutOracle())
    for (let i = snapshot.blocks.length - 1; i >= 0; i--) {
      const geometry = snapshot.blocks[i]
      if (!geometry || geometry.id === excludeId || !this.hasIncomingConnections(geometry.id)) continue
      const start = this.getStartHandleWorld(geometry)
      const handleScreenPosition = this.worldToScreen(start.x, start.y)
      if (this.isWithinHandleHitRadius(sx, sy, handleScreenPosition)) {
        return geometry.block
      }
    }
    return undefined
  }

  private findEndPinAtScreenPosition(sx: number, sy: number, excludeId?: string): Block | undefined {
    const selectedIds = new Set(this.document.getSelectedBlockIds())
    const snapshot = createSpaceGeometrySnapshot(this.getActiveBlocks(), this.createTextLayoutOracle())
    for (let i = snapshot.blocks.length - 1; i >= 0; i--) {
      const geometry = snapshot.blocks[i]
      if (!geometry || geometry.id === excludeId) continue
      if (!this.hasOutgoingConnections(geometry.id) && !selectedIds.has(geometry.id)) continue
      const end = this.getEndHandleWorld(geometry)
      const handleScreenPosition = this.worldToScreen(end.x, end.y)
      if (this.isWithinHandleHitRadius(sx, sy, handleScreenPosition)) {
        return geometry.block
      }
    }
    return undefined
  }

  // Hit test End+ handle for a block in SCREEN space with ±tolerance px
  private isOnEndHandle(block: Block, sx: number, sy: number): boolean {
    return this.findEndPinAtScreenPosition(sx, sy)?.ided[0] === block.ided[0]
  }
}
    const menuNote = document.createElement('p')
    menuNote.className = 'auth-modal-note'

    const footerNote = document.createElement('p')
    footerNote.className = 'auth-modal-note'
